import { researchProse } from "./domain/researchText";
import { ListingDraftWorkbench } from "./ListingDraftWorkbench";
import { SpreadsheetMigration } from "./SpreadsheetMigration";
import { migrationStatuses } from "./domain/spreadsheetMigration";
import { NotificationSettings } from "./NotificationSettings";
import { EbayPublicationDialog } from "./EbayPublicationDialog";
import { EbayListingEditor } from "./EbayListingEditor";
import { ConnectedListingDetail } from "./ConnectedListingDetail";
import { connectedListingContent } from "./domain/connectedListing";
import { ChannelSheetLayout } from "./ChannelSheetLayout";
import { ConnectedChannelManager } from "./ConnectedChannelManager";
import { channelPresentation, type ChannelPresentation } from "./domain/channelPresentation";
import { EbayOrderIssueDialog } from "./EbayOrderIssueDialog";
import { ResearchLaunchDialog, ResearchWorkspace } from "./components/research/research-workspace";
import { InventoryPhotoImport } from "./components/inventory/photo-import";
import { InventoryTargetPriceDialog } from "./components/inventory/target-price-dialog";
import { useInventoryCollection } from "./hooks/use-inventory-collection";
import { pendingPhotoImport, inventoryAttention, inventoryListingSummary, inventoryListingPresentation } from "./domain/inventoryPresentation";
import type { ResearchBatchOutcome } from "./domain/researchReview";
import { ConnectedChannelActivity, ItemChannelPhotosLink, type ChannelTab } from "./ConnectedChannelActivity";
import { SupportCenter, openSupport } from "./components/app-recovery";
import { measureTask, configureDiagnostics } from "./lib/diagnostics";
import { prioritizedWorkQueue, STOCK_REVIEW_GROUP } from "./domain/workQueue";
import { stockReviewIsDue } from "./domain/stock";
import { ItemStockControls, StockAdjustmentDialog } from "./components/stock-controls";
import { userSafeErrorMessage } from "./lib/userSafeError";
import { CatalogueCompletion } from "./CatalogueCompletion";
import { catalogueNeedsDetails, inventoryAge, inventoryAgeDays, inventoryAgeLabel, parseCatalogueCost, purchaseCostInput, purchaseCostIsKnown, saleProfitIsProvisional, validateAcquiredDate } from "./domain/inventoryFacts";
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  RefreshCw,
  BarChart3,
  Bell,
  Bookmark,
  Camera,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Crop as CropIcon,
  CreditCard,
  CircleHelp,
  Download,
  Eye,
  ExternalLink,
  FileSearch,
  Fingerprint,
  Heart,
  ImagePlus,
  LayoutDashboard,
  Link2,
  LineChart,
  ListFilter,
  LoaderCircle,
  LogOut,
  Mail,
  Maximize2,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  RotateCcw,
  RotateCw,
  ScanSearch,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Tag,
  Trash2,
  Undo2,
  Upload,
  Wand2,
  X,
  XCircle,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { OperationStatus, PhotoUploadStatus } from "./components/ui/operation-status";
import type { PhotoProgress, PhotoProgressCallback } from "./domain/photoProgress";
import { ManualListingDialog } from "./components/manual-listing-dialog";
import { ManualListingFlow } from "./components/manual-listing-flow";
import { forwardingReceiptEvidence } from "./domain/listingPresentation";
import { usePhotoUploadProgress } from "./components/photo-upload-activity";
import { isManualListingAccount, manualListingEligibility, MAX_MANUAL_LISTING_ITEMS } from "./domain/manualListings";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { Fragment, createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type ComponentProps, type CSSProperties, type FormEvent, type KeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type Ref } from "react";
import { conditionLabels, inventoryStatusLabels, listingStatusLabels, manualSaleMarketplaces, marketplaceLabels, marketplaces, saleStatusLabels } from "./domain/labels";
import { limitForEntitlement, planEntitlementsFor, privateBetaAccessForWorkspace, storedAssetUsageBytes, subscriptionGrantsPaidEntitlements } from "./domain/entitlements";
import { calculateNetProfit, cleanSellerListingDescription, isRecognizedSale } from "./domain/logic";
import { formatMoney, formatPercent, moneyInputValue, parseMoneyInput } from "./domain/money";
import { PHOTO_FILE_ACCEPT, photoUploadErrorMessage, preparePhotoFile, type PhotoPreparationStage } from "./domain/photoFiles";
import { alternativeResearchHypotheses, includedResearchEvidence, isSourceBackedVisualEvidence, itemUpdateFromResearch, manualResearchProvider, pendingResearchClaims, primaryIdentityResearchClaim, researchFindingsMode, researchIdentityLabel, researchNeedsRerun, researchPricingIsApplicable, researchRunIsStale, researchSellerContextForNextRun, reviewableResearchClaims, sellerReviewCandidateIdentification, withResearchClaimDecision } from "./domain/research";
import { MARKETPLACE_SIGNAL_DOMAIN, MAX_ITEM_PHOTOS, marketplaceListingURLValidationError } from "./domain/workflows";
import { workspaceOnboardingProgress, type WorkspaceOnboardingProgress } from "./domain/onboarding";
import {
  defaultMarketplaceProfile,
  marketplaceProfileDefinition,
  marketplaceProfilesFor
} from "./domain/marketplaceCapabilities";
import { ReOSProvider, useReOS } from "./domain/store";
import { useDraftField, useWorkspaceDraft } from "./domain/workspaceDrafts";
import { setMarketplaceImportBusy } from "./marketplace-operation-guard";
import { DeviceSignInOnboardingScreen, ProgressiveAuthScreen as AuthScreen } from "./ProgressiveAuthScreen";
import { EbayReadOnlyConnectionPanel as EbayConnectionPanel } from "./EbayReadOnlyConnectionPanel";
import { MarketplaceImportReview } from "./MarketplaceImportReview";
import { reconcileWorkspaceCollectionRecords, type WorkspaceCollectionPage, type WorkspaceCollectionQuery, type WorkspaceCollectionRecord, type WorkspaceCollectionResource } from "./domain/workspaceCollections";
import { ListworkMark } from "./components/brand/ListworkMark";
import { AssistantPanel } from "./components/assistant/AssistantPanel";
import { loadAssistantSkills, openAssistant } from "./components/assistant/assistantBridge";
import { ResearchDecisionCard } from "./components/research/research-decision-card";
import { ResearchFindingsOutcome } from "./components/research/research-findings-outcome";
import {
  ResearchEvidencePreview,
  ResearchFindingsWorkbench,
  type ResearchComparisonPhoto,
  type ResearchComparisonSource
} from "./components/research/research-findings-workbench";
import { ResearchRunOutcome } from "./components/research/research-run-outcome";
import { ResearchRunSummary } from "./components/research/research-run-summary";
import { ResearchRunStatus as ResearchRunStatusView } from "./components/research/research-run-status";
import { AppShell, AppShellWorkspaceContext } from "./components/ui/app-shell";
import { ActionPrompt } from "./components/ui/action-prompt";
import { Badge } from "./components/ui/badge";
import { Button, ButtonCountBadge, ChipButton, StatusButton, SwatchButton, ThumbnailButton } from "./components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./components/ui/card";
import { BucketChart, ChartAxis, TrendChart } from "./components/ui/chart";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./components/ui/command";
import { ContentFrame } from "./components/ui/content-frame";
import { Alert } from "./components/ui/alert";
import { DangerZone, DangerZoneConfirm, DangerZoneCopy, InlineConfirm, InlineConfirmActions } from "./components/ui/confirm";
import { DataRow, DataRowBadges, DataRowLabelValue, DataRowList, DataRowListItem, DataRowMain, DataRowMedia, DataRowMeta, DataRowStack, DataRowValue } from "./components/ui/data-row";
import { Disclosure } from "./components/ui/disclosure";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  DialogTitle
} from "./components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./components/ui/dropdown-menu";
import { CheckboxField, Field, FileInput, FormActions, FormLayout, Input, RadioInput, RangeInput, Textarea } from "./components/ui/field";
import { SelectControl } from "./components/ui/select";
import { EmptyNote, EmptyState } from "./components/ui/empty-state";
import { FocusRegion, Panel, PanelHeader, PanelNote, PanelSection, SectionLabel } from "./components/ui/panel";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { SectionHeader } from "./components/ui/section-header";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "./components/ui/sheet";
import { StatusSummary } from "./components/ui/status-summary";
import { DetailList, type DetailListItem } from "./components/ui/detail-list";
import { Stepper } from "./components/ui/stepper";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";
import { ToastProvider, useToast } from "./components/ui/toast";
import { AppliedFilters, ChoiceGroup, SegmentedControl, Toolbar, ToolbarFilters, ToolbarSearch, ToolbarSelect } from "./components/ui/toolbar";
import { Metric, MetricGroup } from "./components/ui/metric";
import { Meter } from "./components/ui/meter";
import { MarketplaceMark, marketplaceMarkIncludesLabel } from "./components/ui/marketplace-mark";
import { Timeline, type TimelineItem } from "./components/ui/timeline";
import type {
  InventoryItem,
  InventoryItemInput,
  ItemResearch,
  ItemResearchClaim,
  ItemResearchEvidence,
  ItemCondition,
  ItemPhoto,
  ListingStatus,
  Marketplace,
  MarketplaceAccount,
  MarketplaceConnectionProfile,
  MarketplaceListing,
  PhotoInput,
  PhotoTreatment,
  PhotoType,
  ProfitPoint,
  ReOSNotification,
  ResearchConfidence,
  ResearchDepth,
  ResearchEvidenceRelationship,
  ResearchEvidenceType,
  ResearchFocus,
  ResearchRunStatus,
  ResearchSellerDecision,
  ResearchSourceCategory,
  Sale,
  SaleStatus,
  StockAgeBucket,
  SubscriptionPlan,
  SubscriptionStatus,
  WorkspaceRole
} from "./domain/types";
import type { AiBackgroundStyle, AiBrushMode, AiBrushStroke, AiObjectCandidate, AiPhotoInput, AiPhotoOutput, AiOperation } from "./photo-ai/types";
import { usePhotoAi } from "./photo-ai/usePhotoAi";
import { MarketingSite, type MarketingRoute } from "./MarketingSite";
import {
  inspectPhotoAiModelStorage,
  markPhotoAiModelAccepted,
  markPhotoAiModelPrepared,
  requestPhotoAiPersistentStorage,
  type PhotoAiModelStorageState
} from "./photo-ai/modelCache";

type ViewKey = "dashboard" | "inventory" | "listings" | "orders" | "channels" | "analytics" | "settings";
type SettingsSectionKey = "notifications" | "general" | "marketplaces" | "signals" | "team" | "billing" | "data" | "account";
type InventoryLifecycleFilterKey = "all" | "active" | "in_stock" | "out_of_stock" | "ready" | "listed" | "sold" | "archived";
type InventoryIssueFilterKey = "all" | "needs_attention" | "aging" | "review_due" | "missing_photos";
type InventoryFilterKey = InventoryLifecycleFilterKey | Exclude<InventoryIssueFilterKey, "all">;
type InventoryAgeFilterKey = "all" | "0-30" | "31-60" | "61-90" | "90-plus";
type ListingFilterKey = "all" | "draft" | "ready" | "failed" | "live" | "sold" | "closed";
type ListingIssueFilterKey = "all" | "needs_action" | "aging" | "missing_photos";
type MarketplaceFilterKey = "all" | Marketplace;
type OrderFilterKey = "all" | "open" | "to_pack" | "to_ship" | "in_transit" | "completed" | "exceptions";
type NotificationFocusMessage = { title: string; body: string };
type NotificationFocus =
  | { view: "channels"; accountID?: string; tab?: ChannelTab }
  | { view: "inventory"; research?: boolean; catalogue?: boolean; spreadsheet?: boolean; itemID?: string; filter?: InventoryLifecycleFilterKey; attention?: InventoryIssueFilterKey; age?: InventoryAgeFilterKey; category?: string; marketplace?: MarketplaceFilterKey; query?: string; notice?: NotificationFocusMessage }
  | { view: "listings"; listingID?: string; itemID?: string; filter?: ListingFilterKey; marketplace?: MarketplaceFilterKey; attention?: ListingIssueFilterKey; query?: string; notice?: NotificationFocusMessage }
  | { view: "orders"; saleID?: string; filter?: OrderFilterKey; marketplace?: MarketplaceFilterKey; query?: string; notice?: NotificationFocusMessage }
  | { view: "settings"; section?: SettingsSectionKey }
  | { view: "dashboard" };
type AppRoute = {
  view: ViewKey;
  settingsSection?: SettingsSectionKey;
  focus?: NotificationFocus;
};
type AppHistoryMode = "push" | "replace" | "none";
type BillingIssue = {
  title: string;
  message: string;
  retryLabel: string;
  retry: "checkout" | "portal" | "assistant_pack";
  plan?: SubscriptionPlan;
};
type SalePreset = {
  itemID?: string;
  listingID?: string;
  externalOrderID?: string;
  orderReview?: NonNullable<ReOSNotification["marketplaceSignal"]>["orderReview"];
  marketplace?: Marketplace;
  currency?: string;
  salePriceMinor?: number;
  soldAt?: string;
  notificationID?: string;
  signalSubject?: string;
};

const listingStatusWorkPriority: Record<ListingStatus, number> = {
  failed: 0,
  draft: 1,
  ready: 2,
  publishing: 3,
  live: 4,
  sold: 5,
  ended: 6,
  delisted: 7,
  archived: 8
};
type BillingReturnState = "success" | "cancelled";
type BillingUsageRow =
  | {
      kind: "meter";
      label: string;
      value: string;
      percent: number;
      tone: "good" | "neutral" | "warn" | "danger";
    }
  | {
      kind: "entitlement";
      label: string;
      value: string;
      included: boolean;
    };

type AuthMode = "create" | "signin";
type AuthMethod = "passkey" | "email_code";
type AuthTurnstileAction = "signup" | "login";
const noProductPhotoDraftWarning = "Add at least one product photo before preparing a listing draft.";

interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  theme?: "auto" | "light" | "dark";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
      remove?: (widgetID: string) => void;
      reset?: (widgetID?: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_REOS_TURNSTILE_SITE_KEY?.trim() ?? "";
const MARKETPLACE_SIGNAL_EMAIL_DOMAIN = import.meta.env.VITE_REOS_MARKETPLACE_SIGNAL_DOMAIN?.trim().toLowerCase() || MARKETPLACE_SIGNAL_DOMAIN;
const MARKETPLACE_STAGING_SIMULATOR =
  import.meta.env.DEV || import.meta.env.VITE_REOS_ENABLE_MARKETPLACE_SIMULATORS === "true";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const LEGAL_LINKS = [
  { label: "Privacy", url: import.meta.env.VITE_REOS_PRIVACY_URL?.trim() || "/privacy" },
  { label: "Terms", url: import.meta.env.VITE_REOS_TERMS_URL?.trim() || "/terms" }
].filter((link) => link.url.length > 0);

let turnstileScriptPromise: Promise<void> | undefined;

const billingPlans: SubscriptionPlan[] = ["starter", "pro", "team"];
const subscriptionPlanLabels: Record<SubscriptionPlan, string> = {
  free: "No active plan",
  starter: "Starter",
  pro: "Pro",
  team: "Team"
};
const subscriptionStatusLabels: Record<SubscriptionStatus, string> = {
  trialing: "Trial",
  active: "Active",
  past_due: "Past due",
  canceled: "Cancelled",
  incomplete: "Incomplete"
};
const DASHBOARD_RANGE_STORAGE_KEY = "reos.dashboard.rangeDays.v1";
const ANALYTICS_RANGE_STORAGE_KEY = "reos.analytics.rangeDays.v1";
const dashboardRangeOptions = [
  { days: 1, label: "Today", footnote: "Today" },
  { days: 7, label: "Last 7 days", footnote: "Last 7 days" },
  { days: 30, label: "Last 30 days", footnote: "Last 30 days" },
  { days: 90, label: "Last 90 days", footnote: "Last 90 days" }
] as const;
type DashboardRangeDays = (typeof dashboardRangeOptions)[number]["days"];
type AnalyticsRangeDays = 7 | 30 | 90 | 365 | 730;

const navItems: Array<{ key: ViewKey; label: string; mobileLabel: string; icon: typeof LayoutDashboard }> = [
  { key: "dashboard", label: "Dashboard", mobileLabel: "Home", icon: LayoutDashboard },
  { key: "inventory", label: "Inventory", mobileLabel: "Inventory", icon: Package },
  { key: "listings", label: "Listings", mobileLabel: "Listings", icon: Tag },
  { key: "orders", label: "Orders", mobileLabel: "Orders", icon: CreditCard },
  { key: "channels", label: "Sales channels", mobileLabel: "Channels", icon: Link2 },
  { key: "analytics", label: "Analytics", mobileLabel: "Analytics", icon: BarChart3 },
  { key: "settings", label: "Settings", mobileLabel: "Settings", icon: Settings }
];

const settingsSections: Array<{ key: SettingsSectionKey; label: string }> = [
  { key: "general", label: "General" },
  { key: "signals", label: "Email monitoring" },
  { key: "team", label: "Team & access" },
  { key: "billing", label: "Billing" },
  { key: "data", label: "Data & privacy" },
  { key: "notifications", label: "Notifications" },
  { key: "account", label: "My account" }
];

const settingsSectionCopy: Record<SettingsSectionKey, { title: string }> = {
  notifications: { title: "Notifications" },
  general: { title: "General" },
  marketplaces: { title: "Marketplaces" },
  account: { title: "My account" },
  signals: { title: "Email monitoring" },
  team: { title: "Team & access" },
  billing: { title: "Billing" },
  data: { title: "Data & privacy" }
};

const workspaceCurrencyOptions = [
  { value: "GBP", label: "GBP - British pound" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "USD", label: "USD - US dollar" }
];

function settingsSectionsForMode(persistenceMode: ReturnType<typeof useReOS>["persistenceMode"]) {
  return persistenceMode === "api" ? settingsSections : settingsSections.filter((section) => section.key === "data");
}

const inventoryFilters: Array<{ key: InventoryLifecycleFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "in_stock", label: "In stock" },
  { key: "out_of_stock", label: "Out of stock" },
  { key: "ready", label: "Ready" },
  { key: "listed", label: "Listed" },
  { key: "sold", label: "Sold" },
  { key: "archived", label: "Archived" }
];

const inventoryIssueFilters: Array<{ key: InventoryIssueFilterKey; label: string }> = [
  { key: "all", label: "Any attention" },
  { key: "needs_attention", label: "Needs attention" },
  { key: "aging", label: "Aging stock" },
  { key: "review_due", label: "Stock review due" },
  { key: "missing_photos", label: "Needs photos" }
];

const inventoryAgeFilters: Array<{ key: InventoryAgeFilterKey; label: string }> = [
  { key: "all", label: "Any age" },
  { key: "0-30", label: "0-30 days" },
  { key: "31-60", label: "31-60 days" },
  { key: "61-90", label: "61-90 days" },
  { key: "90-plus", label: "90+ days" }
];

const listingFilters: Array<{ key: ListingFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "ready", label: "Ready" },
  { key: "failed", label: "Failed" },
  { key: "live", label: "Live" },
  { key: "sold", label: "Sold" },
  { key: "closed", label: "Closed" }
];

const listingIssueFilters: Array<{ key: ListingIssueFilterKey; label: string }> = [
  { key: "all", label: "Any state" },
  { key: "needs_action", label: "Needs action" },
  { key: "aging", label: "Aging stock" },
  { key: "missing_photos", label: "Needs photos" }
];

const orderFilters: Array<{ key: OrderFilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "open", label: "Open" },
  { key: "to_pack", label: "To pack" },
  { key: "to_ship", label: "To ship" },
  { key: "in_transit", label: "In transit" },
  { key: "completed", label: "Completed" },
  { key: "exceptions", label: "Issues" }
];

const shippingCarrierOptions = [
  { key: "", label: "Choose carrier" },
  { key: "Royal Mail", label: "Royal Mail" },
  { key: "Evri", label: "Evri" },
  { key: "DPD", label: "DPD" },
  { key: "Yodel", label: "Yodel" },
  { key: "Parcelforce", label: "Parcelforce" },
  { key: "UPS", label: "UPS" },
  { key: "FedEx", label: "FedEx" },
  { key: "Local pickup", label: "Local pickup" },
  { key: "Other", label: "Other" }
];

const appViewPaths: Record<ViewKey, string> = {
  dashboard: "/dashboard",
  inventory: "/inventory",
  listings: "/listings",
  orders: "/orders",
  channels: "/channels",
  analytics: "/analytics",
  settings: "/settings/general"
};
const appRouteSearchParams = ["catalogue", "research", "spreadsheet", "item", "listing", "sale", "filter", "attention", "age", "category", "channel", "marketplace", "q", "billing", "integration", "status"];
const MAX_CROP_EXPORT_EDGE = 2200;
const PHOTO_THUMB_EDGE = 260;

function readAppRouteFromLocation(): AppRoute {
  if (typeof window === "undefined") {
    return { view: "dashboard" };
  }
  return appRouteFromURL(new URL(window.location.href));
}

function readBillingReturnFromLocation(): BillingReturnState | null {
  if (typeof window === "undefined") {
    return null;
  }
  const value = new URLSearchParams(window.location.search).get("billing");
  return value === "success" || value === "cancelled" ? value : null;
}

function appRouteFromURL(url: URL): AppRoute {
  const segments = url.pathname.split("/").filter(Boolean);
  const primary = segments[0];
  const search = url.searchParams;

  if (primary === "inventory") {
    const routeFilter = inventoryFilterFromURL(search.get("filter"));
    const legacyAttention = routeFilter === "aging" || routeFilter === "review_due" || routeFilter === "missing_photos" || routeFilter === "needs_attention" ? routeFilter : undefined;
    const filter: InventoryLifecycleFilterKey | undefined =
      routeFilter === "aging" || routeFilter === "review_due" || routeFilter === "missing_photos" || routeFilter === "needs_attention" ? "active" : routeFilter;
    const attention = inventoryIssueFilterFromURL(search.get("attention")) ?? legacyAttention;
    const age = inventoryAgeFilterFromURL(search.get("age"));
    const category = search.get("category") ?? undefined;
    const marketplace = marketplaceFilterFromURL(search.get("channel") ?? search.get("marketplace"));
    const query = search.get("q") ?? undefined;
    const itemID = search.get("item") ?? undefined;
    return {
      view: "inventory",
      focus: search.get("research") === "1" || search.get("catalogue") === "1" || search.get("spreadsheet") === "1" || itemID || filter || attention || age || category || marketplace || query
        ? { view: "inventory", research: search.get("research") === "1", catalogue: search.get("catalogue") === "1", spreadsheet: search.get("spreadsheet") === "1", itemID, filter: filter ?? (itemID ? "all" : undefined), attention, age, category, marketplace, query }
        : undefined
    };
  }

  if (primary === "listings") {
    const listingID = search.get("listing") ?? undefined;
    const itemID = search.get("item") ?? undefined;
    const filter = listingFilterFromURL(search.get("filter"));
    const marketplace = marketplaceFilterFromURL(search.get("channel") ?? search.get("marketplace"));
    const attention = listingIssueFilterFromURL(search.get("attention"));
    const query = search.get("q") ?? undefined;
    return {
      view: "listings",
      focus: listingID || itemID || filter || marketplace || attention || query
        ? { view: "listings", listingID, itemID, filter, marketplace, attention, query }
        : undefined
    };
  }

  if (primary === "orders") {
    const saleID = search.get("sale") ?? undefined;
    const filter = orderFilterFromURL(search.get("filter"));
    const marketplace = marketplaceFilterFromURL(search.get("channel") ?? search.get("marketplace"));
    const query = search.get("q") ?? undefined;
    return {
      view: "orders",
      focus: saleID || filter || marketplace || query ? { view: "orders", saleID, filter, marketplace, query } : undefined
    };
  }

  if (primary === "analytics") {
    return { view: "analytics" };
  }

  if (primary === "channels" || (primary === "settings" && segments[1] === "marketplaces")) {
    return { view: "channels" };
  }

  if (primary === "settings") {
    const section = settingsSectionFromURL(segments[1]);
    return { view: "settings", settingsSection: section, focus: { view: "settings", section } };
  }

  return { view: "dashboard" };
}

function appRouteURL(route: AppRoute) {
  const url = new URL(window.location.href);
  url.pathname = appRoutePath(route);
  const setupContext = route.view === "channels"
    ? ["setupChannel", "channelView", "importRun", "importPage", "channelActivity", "activityTab", "photoItem"].map((key) => [key, url.searchParams.get(key)] as const)
    : [];
  [...appRouteSearchParams, "setupChannel", "channelView", "importRun", "importPage", "channelActivity", "activityTab", "photoItem"].forEach((param) => url.searchParams.delete(param));
  for (const [key, value] of setupContext) if (value) url.searchParams.set(key, value);
  const focus = route.focus;
  if (focus?.view === "channels" && focus.accountID) { url.searchParams.set("channelActivity", focus.accountID); url.searchParams.set("activityTab", focus.tab ?? "new"); }
  if (focus?.view === "inventory") {
    if (focus.catalogue) url.searchParams.set("catalogue", "1");
    if (focus.spreadsheet) url.searchParams.set("spreadsheet", "1");
    if (focus.itemID) {
      url.searchParams.set("item", focus.itemID);
    }
    if (focus.filter) {
      url.searchParams.set("filter", focus.filter);
    }
    if (focus.research) url.searchParams.set("research", "1");
    if (focus.attention && focus.attention !== "all") {
      url.searchParams.set("attention", focus.attention);
    }
    if (focus.age && focus.age !== "all") {
      url.searchParams.set("age", focus.age);
    }
    if (focus.category && focus.category !== "all") {
      url.searchParams.set("category", focus.category);
    }
    if (focus.marketplace && focus.marketplace !== "all") {
      url.searchParams.set("channel", focus.marketplace);
    }
    if (focus.query) {
      url.searchParams.set("q", focus.query);
    }
  }
  if (focus?.view === "listings") {
    if (focus.listingID) {
      url.searchParams.set("listing", focus.listingID);
    }
    if (focus.itemID) {
      url.searchParams.set("item", focus.itemID);
    }
    if (focus.filter && focus.filter !== "all") {
      url.searchParams.set("filter", focus.filter);
    }
    if (focus.marketplace && focus.marketplace !== "all") {
      url.searchParams.set("channel", focus.marketplace);
    }
    if (focus.attention && focus.attention !== "all") {
      url.searchParams.set("attention", focus.attention);
    }
    if (focus.query) {
      url.searchParams.set("q", focus.query);
    }
  }
  if (focus?.view === "orders") {
    if (focus.saleID) {
      url.searchParams.set("sale", focus.saleID);
    }
    if (focus.filter && focus.filter !== "all") {
      url.searchParams.set("filter", focus.filter);
    }
    if (focus.marketplace && focus.marketplace !== "all") {
      url.searchParams.set("channel", focus.marketplace);
    }
    if (focus.query) {
      url.searchParams.set("q", focus.query);
    }
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

function appRoutePath(route: AppRoute) {
  if (route.view === "settings") {
    const section = route.settingsSection ?? (route.focus?.view === "settings" ? route.focus.section : undefined) ?? "general";
    return `/settings/${section}`;
  }
  return appViewPaths[route.view] ?? "/dashboard";
}

function writeAppRouteToHistory(route: AppRoute, mode: AppHistoryMode) {
  if (typeof window === "undefined" || mode === "none") {
    return;
  }
  const nextURL = appRouteURL(route);
  const currentURL = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextURL === currentURL) {
    return;
  }
  if (mode === "replace") {
    window.history.replaceState(null, "", nextURL);
    return;
  }
  window.history.pushState(null, "", nextURL);
}

function routeFromFocus(focus: NotificationFocus): AppRoute {
  if (focus.view === "settings") {
    return { view: "settings", settingsSection: focus.section ?? "general", focus };
  }
  if (focus.view === "dashboard") {
    return { view: "dashboard" };
  }
  return { view: focus.view, focus };
}

function inventoryFilterFromURL(value: string | null): InventoryFilterKey | undefined {
  return [...inventoryFilters, ...inventoryIssueFilters].some((filter) => filter.key === value) ? (value as InventoryFilterKey) : undefined;
}

function inventoryIssueFilterFromURL(value: string | null): InventoryIssueFilterKey | undefined {
  return inventoryIssueFilters.some((filter) => filter.key === value) ? (value as InventoryIssueFilterKey) : undefined;
}

function inventoryAgeFilterFromURL(value: string | null): InventoryAgeFilterKey | undefined {
  return inventoryAgeFilters.some((filter) => filter.key === value) ? (value as InventoryAgeFilterKey) : undefined;
}

function listingFilterFromURL(value: string | null): ListingFilterKey | undefined {
  return listingFilters.some((filter) => filter.key === value) ? (value as ListingFilterKey) : undefined;
}

function listingIssueFilterFromURL(value: string | null): ListingIssueFilterKey | undefined {
  return listingIssueFilters.some((filter) => filter.key === value) ? (value as ListingIssueFilterKey) : undefined;
}

function orderFilterFromURL(value: string | null): OrderFilterKey | undefined {
  return orderFilters.some((filter) => filter.key === value) ? (value as OrderFilterKey) : undefined;
}

function marketplaceFilterFromURL(value: string | null): MarketplaceFilterKey | undefined {
  if (!value) {
    return undefined;
  }
  if (value === "all" || marketplaces.includes(value as Marketplace)) {
    return value as MarketplaceFilterKey;
  }
  return undefined;
}

function settingsSectionFromURL(value: string | undefined): SettingsSectionKey {
  return settingsSections.some((section) => section.key === value) ? (value as SettingsSectionKey) : "general";
}

function App() {
  const marketingRoute = readMarketingRoute();
  if (marketingRoute) {
    return <MarketingSite route={marketingRoute} />;
  }

  return (
    <ToastProvider>
      <TooltipProvider delayDuration={250}>
        <ReOSProvider>
          <ReOSApp />
        </ReOSProvider>
      </TooltipProvider>
    </ToastProvider>
  );
}

function readMarketingRoute(): MarketingRoute | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/") {
    return "home";
  }
  if (path === "/pricing" || path === "/privacy" || path === "/terms" || path === "/cookies") {
    return path.slice(1) as MarketingRoute;
  }
  return undefined;
}

function useMediaQuery(query: string) {
  const getMatches = useCallback(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia(query).matches;
  }, [query]);
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

type EmailVerificationReturnState =
  | { status: "verifying" }
  | { status: "success" }
  | { status: "error"; message: string };

function ReOSApp() {
  const store = useReOS();
  const toast = useToast();
  const initialRoute = useMemo(readAppRouteFromLocation, []);
  const catalogueBusyRef = useRef(false);
  const catalogueReturnURL = useRef<string>();
  useEffect(() => {
    const busy = (event: Event) => {
      catalogueBusyRef.current = Boolean((event as CustomEvent<{ busy: boolean }>).detail?.busy);
      if (catalogueBusyRef.current) catalogueReturnURL.current = `${window.location.pathname}${window.location.search}`;
    };
    window.addEventListener("listwork:catalogue-busy", busy);
    return () => window.removeEventListener("listwork:catalogue-busy", busy);
  }, []);
  const marketplaceImportBusyRef = useRef(false);
  const marketplaceImportReturnURL = useRef<string | null>(null);
  useEffect(() => {
    const handleBusy = (event: Event) => {
      marketplaceImportBusyRef.current = Boolean((event as CustomEvent<{ busy: boolean }>).detail?.busy);
      setMarketplaceImportBusy(marketplaceImportBusyRef.current);
      if (marketplaceImportBusyRef.current) marketplaceImportReturnURL.current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    };
    window.addEventListener("listwork:marketplace-import-busy", handleBusy);
    return () => window.removeEventListener("listwork:marketplace-import-busy", handleBusy);
  }, []);
  const [view, setView] = useState<ViewKey>(initialRoute.view);
  const [settingsSection, setSettingsSection] = useState<SettingsSectionKey>(
    initialRoute.settingsSection ?? (initialRoute.focus?.view === "settings" ? initialRoute.focus.section : undefined) ?? "general"
  );
  const [notificationFocus, setNotificationFocus] = useState<NotificationFocus | null>(initialRoute.focus ?? null);
  const [showNotifications, setShowNotifications] = useState(false);
  const incomingAlert = useRef(new URLSearchParams(window.location.search));
  const [openingAlert, setOpeningAlert] = useState(false);
  useEffect(() => {
    const alertID = incomingAlert.current.get("alert"), workspaceID = incomingAlert.current.get("workspace");
    if (!alertID || !workspaceID || store.isLoading || !store.isAuthenticated || openingAlert) return;
    if (store.workspace?.id !== workspaceID) {
      if (!store.accountWorkspaces.some((entry) => entry.id === workspaceID)) return;
      setOpeningAlert(true);
      void store.switchWorkspace(workspaceID).catch(() => { incomingAlert.current.delete("alert"); }).finally(() => { setOpeningAlert(false); });
      return;
    }
    incomingAlert.current.delete("alert");
    const notification = store.notifications.find((entry) => entry.id === alertID && entry.userID === store.user?.id);
    if (notification) handleNotificationAction(notification); else setShowNotifications(true);
  }, [store.isLoading, store.isAuthenticated, store.workspace?.id, store.notifications, store.accountWorkspaces, openingAlert]);
  const [showingAddItem, setShowingAddItem] = useState(false);
  const [salePreset, setSalePreset] = useState<SalePreset | null>(null);
  const [ebayOrderIssue, setEbayOrderIssue] = useState<ReOSNotification | null>(null);
  const saleReturnFocusRef = useRef<HTMLElement | null>(null);
  const [billingReturn, setBillingReturn] = useState<BillingReturnState | null>(() => readBillingReturnFromLocation());
  const [isRefreshingBillingReturn, setIsRefreshingBillingReturn] = useState(false);
  const verificationToken = useMemo(
    () => new URLSearchParams(window.location.search).get("verifyEmailToken"),
    []
  );
  const [emailVerificationReturn, setEmailVerificationReturn] = useState<EmailVerificationReturnState | null>(
    verificationToken ? { status: "verifying" } : null
  );
  const handledEmailVerificationToken = useRef<string | null>(null);
  const [invitationMessage, setInvitationMessage] = useState<string | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [handledInvitationToken, setHandledInvitationToken] = useState<string | null>(null);
  const availableSettingsSections = useMemo(() => settingsSectionsForMode(store.persistenceMode), [store.persistenceMode]);
  const activeSettingsSection = availableSettingsSections.some((section) => section.key === settingsSection)
    ? settingsSection
    : availableSettingsSections[0]?.key ?? "data";
  const activeViewLabel = navItems.find((item) => item.key === view)?.label ?? "Workspace";
  const activeContentLabel = view === "settings" ? `${settingsSectionCopy[activeSettingsSection].title} settings` : activeViewLabel;
  const showEmailVerificationBanner =
    store.persistenceMode === "api" &&
    Boolean(store.user && !store.user.emailVerifiedAt) &&
    view === "dashboard";

  useEffect(() => {
    if (activeSettingsSection !== settingsSection) {
      setSettingsSection(activeSettingsSection);
      if (view === "settings") {
        const canonicalFocus: NotificationFocus = { view: "settings", section: activeSettingsSection };
        setNotificationFocus(canonicalFocus);
        writeAppRouteToHistory(
          { view: "settings", settingsSection: activeSettingsSection, focus: canonicalFocus },
          "replace"
        );
      }
    }
  }, [activeSettingsSection, settingsSection, view]);

  useEffect(() => {
    if (store.isLoading) {
      document.title = "Loading | Listwork";
      return;
    }
    if (!store.isAuthenticated) {
      const search = new URLSearchParams(window.location.search);
      document.title = window.location.pathname === "/verify-email" && search.has("verifyEmailToken")
        ? "Confirm email | Listwork"
        : search.get("mode") === "create"
          ? "Create account | Listwork"
          : "Sign in | Listwork";
      return;
    }
    if (emailVerificationReturn) {
      document.title = emailVerificationReturn.status === "error"
        ? "Email verification problem | Listwork"
        : emailVerificationReturn.status === "success"
          ? "Email verified | Listwork"
          : "Verifying email | Listwork";
      return;
    }
    document.title = `${activeContentLabel} | Listwork`;
  }, [activeContentLabel, emailVerificationReturn, store.isAuthenticated, store.isLoading]);

  useEffect(() => {
    if (store.isLoading || store.isAuthenticated) {
      return;
    }
    if (window.location.pathname !== "/login" && window.location.pathname !== "/verify-email") {
      const current = new URL(window.location.href);
      const next = new URL("/login", current.origin);
      next.searchParams.set("mode", current.searchParams.get("mode") === "create" ? "create" : "signin");
      for (const parameter of ["inviteToken", "verifyEmailToken"]) {
        const value = current.searchParams.get(parameter);
        if (value) next.searchParams.set(parameter, value);
      }
      window.history.replaceState(null, "", `${next.pathname}${next.search}`);
    }
    requestAnimationFrame(() => document.getElementById("auth-heading")?.focus({ preventScroll: true }));
  }, [store.isAuthenticated, store.isLoading]);

  const applyAppRoute = useCallback(
    (route: AppRoute, historyMode: AppHistoryMode = "push", dismissToasts = true) => {
      if (catalogueBusyRef.current) {
        window.history.replaceState(window.history.state, "", catalogueReturnURL.current ?? "/inventory?catalogue=1");
        toast.notify({ title: "Saving inventory details", description: "Wait for this save to finish before leaving.", tone: "info" });
        return;
      }
      if (marketplaceImportBusyRef.current && route.view !== "channels") {
        window.history.replaceState(window.history.state, "", marketplaceImportReturnURL.current ?? "/channels");
        toast.notify({ title: "Import in progress", description: "Stay here until the import has finished.", tone: "info" });
        return;
      }
      if (dismissToasts) {
        toast.dismissAll();
      }
      setNotificationFocus(route.focus ?? null);
      if (route.view === "settings") {
        setSettingsSection(route.settingsSection ?? (route.focus?.view === "settings" ? route.focus.section : undefined) ?? "general");
      }
      setView(route.view);
      writeAppRouteToHistory(route, historyMode);
    },
    [toast]
  );

  useEffect(() => {
    const handlePopState = () => applyAppRoute(readAppRouteFromLocation(), "none", false);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [applyAppRoute]);

  useEffect(() => {
    if (store.isLoading || !store.isAuthenticated) {
      return;
    }
    applyAppRoute(readAppRouteFromLocation(), "replace", false);
  }, [applyAppRoute, store.isAuthenticated, store.isLoading]);

  useEffect(() => {
    if (store.isLoading || !store.isAuthenticated) {
      return;
    }
    const result = readBillingReturnFromLocation();
    if (!result) {
      return;
    }
    setBillingReturn(result);
    applyAppRoute({ view: "settings", settingsSection: "billing", focus: { view: "settings", section: "billing" } }, "replace", false);
    if (result !== "success") {
      return;
    }
    setIsRefreshingBillingReturn(true);
    store.refreshWorkspace().catch(() => undefined).finally(() => setIsRefreshingBillingReturn(false));
  }, [applyAppRoute, store]);

  useEffect(() => {
    if (
      store.isLoading ||
      !store.isAuthenticated ||
      !verificationToken ||
      handledEmailVerificationToken.current === verificationToken
    ) {
      return;
    }
    handledEmailVerificationToken.current = verificationToken;
    setEmailVerificationReturn({ status: "verifying" });
    const finishReturnURL = () => {
      const url = new URL(window.location.href);
      url.pathname = "/dashboard";
      url.searchParams.delete("verifyEmailToken");
      window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    };
    if (store.user?.emailVerifiedAt) {
      finishReturnURL();
      setEmailVerificationReturn({ status: "success" });
      return;
    }
    store
      .completeEmailVerification(verificationToken)
      .then(() => {
        try {
          localStorage.setItem("listwork:email-verified", String(Date.now()));
          const channel = new BroadcastChannel("listwork:account");
          channel.postMessage({ type: "email-verified" });
          channel.close();
        } catch {
          // The current workspace is refreshed; cross-tab notification is optional.
        }
        finishReturnURL();
        setEmailVerificationReturn({ status: "success" });
      })
      .catch((error: unknown) => {
        finishReturnURL();
        setEmailVerificationReturn({
          status: "error",
          message: userSafeErrorMessage(error, "This verification link could not be used.")
        });
      });
  }, [
    store.completeEmailVerification,
    store.isAuthenticated,
    store.isLoading,
    store.user?.emailVerifiedAt,
    verificationToken
  ]);

  useEffect(() => {
    if (store.isLoading || !store.isAuthenticated) {
      return;
    }
    const token = new URLSearchParams(window.location.search).get("inviteToken");
    if (!token || token === handledInvitationToken) {
      return;
    }
    setHandledInvitationToken(token);
    setInvitationError(null);
    setInvitationMessage(null);
    store
      .acceptWorkspaceInvitation(token)
      .then(() => {
        clearWorkspaceInvitationURL();
        setInvitationMessage("You're in. Welcome to the workspace.");
      })
      .catch((error: unknown) => {
        setInvitationError(userSafeErrorMessage(error, "Unable to accept this invitation."));
      });
  }, [handledInvitationToken, store.isAuthenticated, store.isLoading]);

  if (store.isLoading) {
    return (
      <main className="workspace-loading-screen">
        <div className="workspace-loading-content">
          <div className="brand-lockup">
            <ListworkMark className="brand-mark" />
            <h1>listwork</h1>
          </div>
          <OperationStatus variant="inline" title="Loading workspace" slowDetail="This is taking longer than usual. We’re still trying to reach your workspace." />
        </div>
      </main>
    );
  }

  if (!store.isAuthenticated) {
    return <AuthScreen onOpenLocalDemo={store.persistenceMode === "local_demo" ? store.loadDemoData : undefined} />;
  }

  if (store.pendingDeviceSignInSetup) {
    return <DeviceSignInOnboardingScreen />;
  }

  function handleNotificationAction(notification: ReOSNotification) {
    if (notification.type === "marketplace_connection_error" && notification.marketplaceSignal?.marketplace === "ebay" && notification.marketplaceSignal.externalOrderID) {
      setShowNotifications(false); setEbayOrderIssue(notification); return;
    }
    if (notification.type === "marketplace_sale_detected") {
      const signal = notification.marketplaceSignal;
      setShowNotifications(false);
      openSalePreset({
        itemID: notification.itemID,
        listingID: notification.listingID,
        externalOrderID: signal?.externalOrderID,
        orderReview: signal?.orderReview,
        marketplace: signal?.marketplace,
        currency: signal?.currency,
        salePriceMinor: signal?.amountMinor,
        soldAt: signal?.occurredAt,
        notificationID: notification.id,
        signalSubject: signal?.subject
      }, true);
      return;
    }
    const target = targetForNotification(notification, store);
    applyAppRoute(routeFromFocus(target));
    if (
      notification.marketplaceSignal?.marketplaceEventType !== "order.canceled" &&
      notification.id !== STOCK_REVIEW_GROUP && notificationHasConcreteTarget(notification, target, store)
    ) {
      void store.markNotificationRead(notification.id);
    }
  }

  function selectView(nextView: ViewKey) {
    applyAppRoute({ view: nextView, settingsSection: nextView === "settings" ? activeSettingsSection : undefined });
  }

  function selectSettingsSection(section: SettingsSectionKey) {
    if (section === "marketplaces") {
      applyAppRoute({ view: "channels" });
      return;
    }
    applyAppRoute({ view: "settings", settingsSection: section, focus: { view: "settings", section } });
  }

  function openSalePreset(preset: SalePreset, returnToAttention = false) {
    saleReturnFocusRef.current = returnToAttention
      ? document.querySelector<HTMLElement>('[aria-label^="Open Needs attention"]')
      : document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setSalePreset(preset);
  }

  function closeSalePreset() {
    const returnTarget = saleReturnFocusRef.current;
    setSalePreset(null);
    requestAnimationFrame(() => {
      const fallback = document.querySelector<HTMLElement>('[aria-label^="Open Needs attention"]');
      (returnTarget?.isConnected ? returnTarget : fallback)?.focus();
      saleReturnFocusRef.current = null;
    });
  }

  async function signOut() {
    configureDiagnostics();
    if (catalogueBusyRef.current) { toast.notify({ title: "Wait for the inventory save to finish before signing out", tone: "info" }); return; }
    if (marketplaceImportBusyRef.current) { toast.notify({ title: "Wait for the import to finish before signing out", tone: "info" }); return; }
    setShowNotifications(false);
    setShowingAddItem(false);
    setSalePreset(null);
    try {
      await store.signOut();
      window.history.replaceState(null, "", "/login?mode=signin");
    } catch (caught) {
      toast.notify({
        title: "Sign out didn't finish",
        description: userSafeErrorMessage(caught, "Try again in a moment."),
        tone: "danger"
      });
    }
  }

  function focusInventoryItem(
    itemID: string,
    filter: InventoryLifecycleFilterKey = "all",
    attention: InventoryIssueFilterKey = "all",
    notice?: NotificationFocusMessage,
    age: InventoryAgeFilterKey = "all",
    category = "all",
    marketplace: MarketplaceFilterKey = "all",
    query = ""
  ) {
    applyAppRoute({
      view: "inventory",
      focus: {
        view: "inventory",
        itemID,
        filter,
        attention,
        age,
        category,
        marketplace,
        query: query.trim() || undefined,
        notice
      }
    });
  }

  function focusListing(
    listingID: string,
    itemID?: string,
    filter: ListingFilterKey = "all",
    marketplace?: MarketplaceFilterKey,
    attention?: ListingIssueFilterKey,
    query?: string
  ) {
    applyAppRoute({ view: "listings", focus: { view: "listings", itemID, listingID, filter, marketplace, attention, query } });
  }

  function updateListingFilters(
    filter: ListingFilterKey,
    marketplace: MarketplaceFilterKey,
    attention: ListingIssueFilterKey,
    query: string
  ) {
    const normalizedQuery = query.trim() || undefined;
    const hasFilters = filter !== "all" || marketplace !== "all" || attention !== "all" || Boolean(normalizedQuery);
    applyAppRoute(
      {
        view: "listings",
        focus: hasFilters ? { view: "listings", filter, marketplace, attention, query: normalizedQuery } : undefined
      },
      "replace",
      false
    );
  }

  function updateInventoryFilters(
    filter: InventoryLifecycleFilterKey,
    issue: InventoryIssueFilterKey,
    age: InventoryAgeFilterKey,
    category: string,
    marketplace: MarketplaceFilterKey,
    query: string
  ) {
    const normalizedQuery = query.trim() || undefined;
    const hasFilters =
      filter !== "in_stock"
      || issue !== "all"
      || age !== "all"
      || category !== "all"
      || marketplace !== "all"
      || Boolean(normalizedQuery);
    applyAppRoute(
      {
        view: "inventory",
        focus: hasFilters
          ? { view: "inventory", filter, attention: issue, age, category, marketplace, query: normalizedQuery }
          : undefined
      },
      "replace",
      false
    );
  }

  function focusSale(
    saleID: string,
    filter: OrderFilterKey = "all",
    marketplace: MarketplaceFilterKey = "all",
    query = ""
  ) {
    applyAppRoute({ view: "orders", focus: { view: "orders", saleID, filter, marketplace, query: query.trim() || undefined } });
  }

  function updateOrderFilters(filter: OrderFilterKey, marketplace: MarketplaceFilterKey, query: string) {
    const normalizedQuery = query.trim() || undefined;
    const hasFilters = filter !== "open" || marketplace !== "all" || Boolean(normalizedQuery);
    applyAppRoute(
      {
        view: "orders",
        focus: hasFilters ? { view: "orders", filter, marketplace, query: normalizedQuery } : undefined
      },
      "replace",
      false
    );
  }

  function reviewMarketplaceListings(marketplace: Marketplace) {
    applyAppRoute({ view: "listings", focus: { view: "listings", filter: "all", marketplace, query: "" } });
  }

  if (emailVerificationReturn) {
    return (
      <EmailVerificationResultScreen
        state={emailVerificationReturn}
        onContinue={() => {
          setEmailVerificationReturn(null);
          applyAppRoute({ view: "dashboard" }, "replace", false);
        }}
      />
    );
  }

  return (
    <AppShell
      sidebar={
        <Sidebar
          selected={view}
          selectedSettingsSection={activeSettingsSection}
          settingsSections={availableSettingsSections}
          onSelect={selectView}
          onSettingsSectionSelect={selectSettingsSection}
          onSignOut={signOut}
        />
      }
      topbar={
        <TopBar
          view={view}
          open={showNotifications}
          onOpenChange={setShowNotifications}
          onAdd={() => setShowingAddItem(true)}
          onNotificationAction={handleNotificationAction}
          onOpenBilling={() => selectSettingsSection("billing")}
          onOpenItem={(itemID) => focusInventoryItem(itemID)}
          onOpenListing={(listingID, itemID) => focusListing(listingID, itemID)}
          onOpenOrder={focusSale}
        />
      }
      mobileNav={<MobileNav selected={view} onSelect={selectView} onSignOut={signOut} />}
      contentFocusKey={`${view}:${view === "settings" ? activeSettingsSection : "primary"}`}
      contentLabel={activeContentLabel}
      overlays={
        <>
          <SupportCenter />
          <AssistantPanel onOpenRecord={({ kind, id }) => {
            if (kind === "inventory") focusInventoryItem(id);
            else if (kind === "listing") focusListing(id);
            else focusSale(id);
          }} />
          {showingAddItem && (
            <AddItemWizard
              key={`${store.persistenceMode}:${store.workspace?.id ?? "local"}:${store.user?.id ?? "local"}`}
              onClose={() => setShowingAddItem(false)}
              onCheckInventory={() => {
                setShowingAddItem(false);
                applyAppRoute({ view: "inventory" });
              }}
              onOpenChannels={() => {
                setShowingAddItem(false);
                applyAppRoute({ view: "channels" });
              }}
              onCreated={(itemID) => {
                setShowingAddItem(false);
                if (itemID) {
                  focusInventoryItem(itemID);
                  return;
                }
                applyAppRoute({ view: "inventory" });
              }}
            />
          )}
          {salePreset && <RecordSaleDialog preset={salePreset} onClose={closeSalePreset} />}
          {ebayOrderIssue && <EbayOrderIssueDialog notification={ebayOrderIssue} onClose={() => setEbayOrderIssue(null)} />}
        </>
      }
    >
      <DetailSheetSuspendedContext.Provider value={showingAddItem}>
      {invitationError && <Alert className="ui-alert-shell" tone="danger">{invitationError}</Alert>}
      {invitationMessage && <Alert className="ui-alert-shell" tone="success">{invitationMessage}</Alert>}
      {showEmailVerificationBanner && <EmailVerificationBanner />}
      {view === "dashboard" && (
        <DashboardScreen
          onAdd={() => setShowingAddItem(true)}
          onOpenChannels={() => applyAppRoute({ view: "channels" })}
          onOpenInventory={() => applyAppRoute({ view: "inventory" })}
          onOpenNotifications={() => setShowNotifications(true)}
          onNotificationAction={handleNotificationAction}
          onReviewItem={(itemID) => focusInventoryItem(itemID, "active", "aging")}
        />
      )}
      {view === "inventory" && notificationFocus?.view === "inventory" && notificationFocus.spreadsheet && <SpreadsheetMigration onBack={() => applyAppRoute({ view: "inventory" })} />}
      {view === "inventory" && notificationFocus?.view === "inventory" && notificationFocus.catalogue && <CatalogueCompletion onBack={() => applyAppRoute({ view: "inventory" })} />}
      {view === "inventory" && !(notificationFocus?.view === "inventory" && (notificationFocus.catalogue || notificationFocus.spreadsheet)) && (
        <InventoryScreen
          onResearchRoute={(itemID) => applyAppRoute({ view: "inventory", focus: { view: "inventory", research: true, itemID } })}
          onImportSpreadsheet={() => applyAppRoute({ view: "inventory", focus: { view: "inventory", spreadsheet: true } })}
          onCompleteDetails={() => applyAppRoute({ view: "inventory", focus: { view: "inventory", catalogue: true } })}
          focus={notificationFocus?.view === "inventory" ? notificationFocus : null}
          onAdd={() => setShowingAddItem(true)}
          onOpenListing={focusListing}
          onOpenOrder={focusSale}
          onRecordSale={(itemID) => openSalePreset({ itemID })}
          onSelectItem={(itemID, filter, issue, age, category, marketplace, query) =>
            focusInventoryItem(itemID, filter, issue, undefined, age, category, marketplace, query)}
          onFiltersChange={updateInventoryFilters}
        />
      )}
      {view === "listings" && (
        <ListingsScreen
          focus={notificationFocus?.view === "listings" ? notificationFocus : null}
          onAdd={() => setShowingAddItem(true)}
          onOpenInventory={() => applyAppRoute({ view: "inventory" })}
          onOpenItem={(itemID) => focusInventoryItem(itemID)}
          onRecordSale={openSalePreset}
          onSelectListing={focusListing}
          onFiltersChange={updateListingFilters}
          onSelectSale={focusSale}
        />
      )}
      {view === "orders" && (
        <OrdersScreen
          focus={notificationFocus?.view === "orders" ? notificationFocus : null}
          onAdd={() => setShowingAddItem(true)}
          onRecordSale={openSalePreset}
          onSelectSale={focusSale}
          onFiltersChange={updateOrderFilters}
        />
      )}
      {view === "analytics" && (
        <AnalyticsScreen
          onAdd={() => setShowingAddItem(true)}
          onRecordSale={() => openSalePreset({})}
          onReviewAgingStock={(itemID, age = "all") => applyAppRoute({
            view: "inventory",
            focus: { view: "inventory", itemID, filter: "active", attention: age === "all" ? "aging" : "all", age }
          })}
          onReviewMarketplace={reviewMarketplaceListings}
        />
      )}
      {view === "channels" && (
        <ChannelsScreen onOpenSignals={() => selectSettingsSection("signals")} />
      )}
      {view === "settings" && (
        <SettingsScreen
          selectedSection={activeSettingsSection}
          onSelectSection={selectSettingsSection}
          onOpenChannels={() => selectView("channels")}
          billingReturn={billingReturn}
          isRefreshingBillingReturn={isRefreshingBillingReturn}
          onDismissBillingReturn={() => setBillingReturn(null)}
        />
      )}
      </DetailSheetSuspendedContext.Provider>
    </AppShell>
  );
}

function LegacyAuthScreen() {
  const store = useReOS();
  const [mode, setMode] = useState<AuthMode>(() => new URLSearchParams(window.location.search).get("mode") === "signin" ? "signin" : "create");
  const [authMethod, setAuthMethod] = useState<AuthMethod>("passkey");
  const [name, setName] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");
  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [invitationToken, setInvitationToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileAction = TURNSTILE_SITE_KEY ? authTurnstileAction(mode, authMethod, emailCodeSent) : undefined;
  const handleTurnstileTokenChange = useCallback((token?: string) => {
    setTurnstileToken(token);
  }, []);
  const handleTurnstileError = useCallback((nextError: string) => {
    setError(nextError);
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(undefined);
    setTurnstileResetKey((current) => current + 1);
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("inviteToken");
    if (!token) {
      return;
    }
    setInvitationToken(token);
    setMode("create");
    setAuthMethod("passkey");
    setMessage("Create your account to join this workspace. Already have an account? Sign in instead.");
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("verifyEmailToken");
    if (!token) {
      return;
    }
    setMode("signin");
    setAuthMethod("passkey");
    setError(null);
    setMessage(null);
    store
      .completeEmailVerification(token)
      .then(() => {
        clearEmailVerificationURL();
        setMessage("Email verified. Sign in to continue.");
      })
      .catch((caught: unknown) => {
        clearEmailVerificationURL();
        setError(userSafeErrorMessage(caught, "Unable to verify your email."));
      });
  }, []);

  function switchAuthMethod(nextMethod: AuthMethod) {
    setAuthMethod(nextMethod);
    setEmailCode("");
    setEmailCodeSent(false);
    setError(null);
    setMessage(null);
    resetTurnstile();
  }

  function switchMode(nextMode: AuthMode) {
    setMode(nextMode);
    setAuthMethod("passkey");
    setEmailCode("");
    setEmailCodeSent(false);
    setError(null);
    setMessage(null);
    resetTurnstile();
  }

  async function submit() {
    const authChallengeAction = turnstileAction;
    const authChallengeToken = authChallengeAction ? turnstileToken : undefined;
    const creatingStandaloneWorkspace = mode === "create" && !invitationToken;

    try {
      setIsSubmitting(true);
      setError(null);
      setMessage(null);
      if (store.persistenceMode === "local_demo") {
        store.loadDemoData();
        return;
      }
      const normalizedEmail = email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
        setError("Enter a valid email address.");
        return;
      }
      if (creatingStandaloneWorkspace && !workspaceName.trim()) {
        setError("Enter your store name.");
        return;
      }
      if (authChallengeAction && !authChallengeToken) {
        setError("Complete the security check.");
        return;
      }
      if (authMethod === "email_code") {
        if (!emailCodeSent) {
          const result = await store.requestEmailSignInCode({
            email: normalizedEmail,
            name,
            workspaceName: creatingStandaloneWorkspace ? workspaceName : undefined,
            invitationToken: invitationToken || undefined,
            intent: mode === "create" ? "create" : "signin",
            turnstileToken: authChallengeToken
          });
          setEmailCodeSent(true);
          if (result.code) {
            setEmailCode(result.code);
          }
          setMessage(mode === "create" ? "Enter the code we sent to continue." : "If this email has a workspace, enter the code we sent.");
          return;
        }
        await store.completeEmailSignInCode(normalizedEmail, emailCode);
        if (invitationToken) {
          clearWorkspaceInvitationURL();
        }
        return;
      }
      if (!window.PublicKeyCredential) {
        throw new Error(
          mode === "signin"
            ? "This device can't use passkeys. Open Listwork on a device with your saved passkey."
            : "This device can't create a passkey. No account was created."
        );
      }
      if (mode === "create") {
        const result = await store.createPasskeyRegistrationOptions({
          email: normalizedEmail,
          name,
          workspaceName: creatingStandaloneWorkspace ? workspaceName : undefined,
          invitationToken: invitationToken || undefined,
          turnstileToken: authChallengeToken
        });
        const credential = await startRegistration({
          optionsJSON: result.options as Parameters<typeof startRegistration>[0]["optionsJSON"]
        });
        await store.completePasskeyRegistration(result.challengeID, credential);
        if (invitationToken) {
          clearWorkspaceInvitationURL();
        }
      } else {
        const result = await store.createPasskeyAuthenticationOptions(normalizedEmail, authChallengeToken);
        const credential = await startAuthentication({
          optionsJSON: result.options as Parameters<typeof startAuthentication>[0]["optionsJSON"]
        });
        await store.completePasskeyAuthentication(result.challengeID, credential);
      }
    } catch (caught) {
      setError(
        authMethod === "passkey"
          ? passkeyErrorMessage(
              caught,
              mode === "signin"
                ? "Passkey sign-in didn't finish. Try again."
                : "Passkey setup didn't finish. Try again. No account was created."
            )
          : caught instanceof Error
            ? caught.message
            : "Unable to continue."
      );
    } finally {
      setIsSubmitting(false);
      if (authChallengeAction) {
        resetTurnstile();
      }
    }
  }

  const showEmailCodeField = authMethod === "email_code" && emailCodeSent;
  const showWorkspaceNameField = mode === "create" && !invitationToken;
  const primaryLabel = store.persistenceMode === "local_demo"
    ? "Open local workspace"
    : isSubmitting
    ? authMethod === "passkey"
      ? mode === "create"
        ? "Creating account"
        : "Signing in"
      : emailCodeSent
        ? "Verifying code"
        : "Sending code"
    : authMethod === "passkey"
      ? "Continue with passkey"
      : authMethod === "email_code"
        ? emailCodeSent
          ? "Verify code"
          : "Continue with email code"
        : "Continue";
  const primaryIcon = store.persistenceMode === "local_demo"
    ? <Package size={18} />
    : isSubmitting
    ? <RotateCw className="auth-progress-icon" size={18} aria-hidden="true" />
    : authMethod === "passkey"
      ? <Fingerprint size={18} />
      : authMethod === "email_code" && !emailCodeSent
        ? <Mail size={18} />
        : <ChevronRight size={18} />;

  return (
    <main className="auth-screen">
      <Panel as="section" className="ui-panel-auth">
        <div className="brand-lockup">
          <ListworkMark className="brand-mark" />
          <div>
            <h1 id="auth-heading" tabIndex={-1}>listwork</h1>
            <p>{invitationToken ? "Join your team workspace" : "Research and listing tools for specialist resellers"}</p>
          </div>
        </div>
        {store.persistenceMode !== "local_demo" && <div className="auth-grid">
          {mode === "create" && (
            <Field label="Name">
              <Input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" placeholder="Mara Finch" />
            </Field>
          )}
          {showWorkspaceNameField && (
            <Field label="Store name">
              <Input value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} autoComplete="organization" placeholder="North Loop Vintage" />
            </Field>
          )}
          <Field label="Email">
            <Input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              inputMode="email"
              placeholder="mara@northloopvintage.co.uk"
            />
          </Field>
          <fieldset className="auth-method-selector">
            <legend id="auth-method-title">Sign-in method</legend>
            <div className="auth-method-options">
              <label className="auth-method-option">
                <input
                  className="sr-only"
                  type="radio"
                  name="auth-method"
                  value="passkey"
                  checked={authMethod === "passkey"}
                  onChange={() => switchAuthMethod("passkey")}
                />
                <Fingerprint size={18} aria-hidden="true" />
                <span className="auth-method-copy">
                  <strong>Passkey</strong>
                  <span>Use your device unlock to sign in.</span>
                </span>
              </label>
              <label className="auth-method-option">
                <input
                  className="sr-only"
                  type="radio"
                  name="auth-method"
                  value="email_code"
                  checked={authMethod === "email_code"}
                  onChange={() => switchAuthMethod("email_code")}
                />
                <Mail size={18} aria-hidden="true" />
                <span className="auth-method-copy">
                  <strong>Email code</strong>
                  <span>{mode === "create" ? "Use one-time codes for this account." : "For accounts that use one-time codes."}</span>
                </span>
              </label>
            </div>
          </fieldset>
          {showEmailCodeField && (
            <Field label="Code">
              <Input value={emailCode} onChange={(event) => setEmailCode(event.target.value)} autoComplete="one-time-code" inputMode="numeric" placeholder="6-digit code" />
            </Field>
          )}
        </div>}
        {(error || store.persistenceError) && <Alert tone="danger">{error ?? userSafeErrorMessage(new Error(store.persistenceError ?? ""), "Listwork couldn't reach your workspace. Check your connection and try again.")}</Alert>}
        {message && <Alert tone="success">{message}</Alert>}
        {store.persistenceMode !== "local_demo" && turnstileAction && (
          <TurnstileChallenge
            key={`${turnstileAction}-${turnstileResetKey}`}
            action={turnstileAction}
            siteKey={TURNSTILE_SITE_KEY}
            onError={handleTurnstileError}
            onTokenChange={handleTurnstileTokenChange}
          />
        )}
        <div className="auth-actions">
          <Button full variant="primary" type="button" onClick={submit} disabled={isSubmitting} aria-live="polite">
            {primaryLabel} {primaryIcon}
          </Button>
          {store.persistenceMode !== "local_demo" && (
            <Button full type="button" onClick={() => switchMode(mode === "create" ? "signin" : "create")}>
              {mode === "create" ? "Sign in instead" : "Create new workspace"}
            </Button>
          )}
        </div>
        <LegalLinks className="auth-legal" />
      </Panel>
    </main>
  );
}

function authTurnstileAction(mode: AuthMode, authMethod: AuthMethod, emailCodeSent: boolean): AuthTurnstileAction | undefined {
  if (mode === "create") {
    if (authMethod === "email_code" && emailCodeSent) {
      return undefined;
    }
    return "signup";
  }
  if (mode === "signin") {
    if (authMethod === "email_code" && emailCodeSent) {
      return undefined;
    }
    return "login";
  }
}

function TurnstileChallenge({
  action,
  siteKey,
  onError,
  onTokenChange
}: {
  action: AuthTurnstileAction;
  siteKey: string;
  onError: (message: string) => void;
  onTokenChange: (token?: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let disposed = false;
    let widgetID: string | undefined;
    onTokenChange(undefined);

    loadTurnstileScript()
      .then(() => {
        if (disposed || !containerRef.current) {
          return;
        }
        if (!window.turnstile) {
          throw new Error("Security check is unavailable.");
        }
        widgetID = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: "auto",
          callback: (token) => {
            if (!disposed) {
              onTokenChange(token);
            }
          },
          "expired-callback": () => {
            if (!disposed) {
              onTokenChange(undefined);
            }
          },
          "error-callback": () => {
            if (!disposed) {
              onTokenChange(undefined);
              onError("Security check failed. Refresh and try again.");
            }
          }
        });
      })
      .catch(() => {
        if (!disposed) {
          onError("Security check could not load. Check your connection and try again.");
        }
      });

    return () => {
      disposed = true;
      onTokenChange(undefined);
      if (widgetID && window.turnstile?.remove) {
        window.turnstile.remove(widgetID);
      } else if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [action, onError, onTokenChange, siteKey]);

  return <div className="turnstile-slot" ref={containerRef} role="group" aria-label="Security check" />;
}

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      if (existing.dataset.reosLoaded === "true" || window.turnstile) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => {
          turnstileScriptPromise = undefined;
          reject(new Error("Security check could not load."));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.reosLoaded = "true";
      resolve();
    };
    script.onerror = () => {
      turnstileScriptPromise = undefined;
      reject(new Error("Security check could not load."));
    };
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

function clearEmailVerificationURL() {
  clearURLSearchParam("verifyEmailToken");
}

function clearWorkspaceInvitationURL() {
  clearURLSearchParam("inviteToken");
}

function clearURLSearchParam(name: string) {
  const url = new URL(window.location.href);
  url.searchParams.delete(name);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

function readDashboardRangeDays(): DashboardRangeDays {
  try {
    const value = window.localStorage.getItem(DASHBOARD_RANGE_STORAGE_KEY);
    const numericValue = Number(value);
    return dashboardRangeOptions.some((option) => option.days === numericValue) ? (numericValue as DashboardRangeDays) : 30;
  } catch {
    return 30;
  }
}

function saveDashboardRangeDays(rangeDays: DashboardRangeDays) {
  try {
    window.localStorage.setItem(DASHBOARD_RANGE_STORAGE_KEY, String(rangeDays));
  } catch {
    return;
  }
}

function readAnalyticsRangeDays(): AnalyticsRangeDays {
  try {
    const value = Number(new URLSearchParams(window.location.search).get("days") ?? window.localStorage.getItem(ANALYTICS_RANGE_STORAGE_KEY));
    return value === 7 || value === 30 || value === 90 || value === 365 || value === 730 ? value : 30;
  } catch {
    return 30;
  }
}

function saveAnalyticsRangeDays(rangeDays: AnalyticsRangeDays) {
  try {
    window.localStorage.setItem(ANALYTICS_RANGE_STORAGE_KEY, String(rangeDays));
  } catch {
    return;
  }
}

function dashboardRangeOption(rangeDays: DashboardRangeDays) {
  return dashboardRangeOptions.find((option) => option.days === rangeDays) ?? dashboardRangeOptions[2];
}

function dashboardRangeStart(rangeDays: DashboardRangeDays | AnalyticsRangeDays) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - rangeDays + 1);
  return start;
}

function dashboardRangeEnd() {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return end;
}

function isWithinDashboardRange(timestamp: string, rangeDays: DashboardRangeDays | AnalyticsRangeDays) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date >= dashboardRangeStart(rangeDays) && date <= dashboardRangeEnd();
}

function orderCountLabel(count: number) {
  return `${count} ${count === 1 ? "order" : "orders"}`;
}

function itemCountLabel(count: number) {
  return `${count} ${count === 1 ? "item" : "items"}`;
}

function stockRecordCountLabel(count: number) {
  return `${count} stock ${count === 1 ? "record" : "records"}`;
}

function profitChartSummary(points: ProfitPoint[], recognizedOrderCount = 0) {
  const activeDays = points.filter((point) => point.netProfit.amountMinor !== 0);
  if (activeDays.length === 0) {
    return recognizedOrderCount > 0
      ? `${orderCountLabel(recognizedOrderCount)} in range · £0 net profit.`
      : "No completed sales in this range.";
  }

  const bestDay = activeDays.reduce((best, point) => (point.netProfit.amountMinor > best.netProfit.amountMinor ? point : best), activeDays[0]);
  return `${activeDays.length} active ${activeDays.length === 1 ? "day" : "days"} in range. Best day ${formatMoney(bestDay.netProfit)} on ${formatOrderDate(bestDay.date)}.`;
}

function stockAgeChartSummary(rows: StockAgeBucket[]) {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  if (total === 0) {
    return "No active stock to age yet.";
  }

  const overSixtyDays = rows.filter((row) => row.lowerBoundDays >= 61).reduce((sum, row) => sum + row.count, 0);
  if (overSixtyDays === 0) {
    return `${itemCountLabel(total)} active, all inside the 60 day review window.`;
  }
  return `${itemCountLabel(overSixtyDays)} over 60 days and ready for stock review.`;
}

function inventoryAgeFilterForBucket(bucket: StockAgeBucket): InventoryAgeFilterKey {
  if (bucket.lowerBoundDays >= 91) {
    return "90-plus";
  }
  if (bucket.lowerBoundDays >= 61) {
    return "61-90";
  }
  if (bucket.lowerBoundDays >= 31) {
    return "31-60";
  }
  return "0-30";
}

function EmailVerificationBanner() {
  return <EmailVerificationPrompt surface="banner" />;
}

function EmailVerificationResultScreen({
  state,
  onContinue
}: {
  state: EmailVerificationReturnState;
  onContinue: () => void;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [state.status]);

  const isVerifying = state.status === "verifying";
  const isSuccess = state.status === "success";
  const heading = isVerifying ? "Verifying your email" : isSuccess ? "Email verified" : "Link not verified";
  const detail = isVerifying
    ? "This should only take a moment."
    : isSuccess
      ? "Your account is ready and security notices will reach the right inbox."
      : state.message;

  return (
    <main className="auth-progressive-shell email-verification-result-page">
      <section
        className="auth-progressive-card email-verification-result-card"
        aria-labelledby="email-verification-result-heading"
        aria-busy={isVerifying}
      >
        <span className="email-verification-result-brand">LISTWORK</span>
        <div
          className="email-verification-result-icon"
          data-state={state.status}
          aria-hidden="true"
        >
          {isVerifying
            ? <LoaderCircle className="spin" size={24} />
            : isSuccess
              ? <CheckCircle2 size={24} />
              : <AlertTriangle size={24} />}
        </div>
        <h1 id="email-verification-result-heading" ref={headingRef} tabIndex={-1}>{heading}</h1>
        <p>{detail}</p>
        {!isVerifying ? (
          <Button type="button" variant="primary" onClick={onContinue}>
            Continue to Listwork
          </Button>
        ) : null}
      </section>
    </main>
  );
}

function EmailVerificationPrompt({ surface }: { surface: "banner" | "panel" }) {
  const store = useReOS();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sentAt, setSentAt] = useState<string | null>(null);
  const verified = Boolean(store.user?.emailVerifiedAt);
  const hasSent = Boolean(sentAt);
  const email = store.user?.email;
  useEffect(() => {
    if (verified) return;
    let active = true;
    const refresh = () => {
      if (active) void store.refreshWorkspace();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === "listwork:email-verified") refresh();
    };
    const channel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel("listwork:account");
    channel?.addEventListener("message", refresh);
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);
    const poll = hasSent ? window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 10_000) : undefined;
    return () => {
      active = false;
      if (poll !== undefined) window.clearInterval(poll);
      channel?.close();
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [hasSent, store.refreshWorkspace, verified]);
  const emailLabel = surface === "banner" ? "your address" : "this address";
  const heading = verified ? "Email verified" : hasSent ? "Check your inbox" : "Verify email address";
  const detail = verified
    ? surface === "banner"
      ? "Your address is confirmed for account notices."
      : "This address is confirmed for account notices."
    : hasSent
      ? surface === "banner"
        ? "Open the confirmation email, then return to Listwork."
        : `Open the confirmation email sent to ${emailLabel}, then return to Listwork.`
      : surface === "banner"
        ? "Confirm your address for security notices."
        : `Confirm ${emailLabel} so security notices reach the right inbox.`;
  const verificationState = verified ? "verified" : hasSent ? "sent" : "idle";

  async function sendVerification() {
    try {
      setIsSending(true);
      setError(null);
      const result = await store.requestEmailVerification();
      if (result.verified) {
        await store.refreshWorkspace();
        toast.notify({ title: "Email already verified", tone: "success" });
        return;
      }
      setSentAt(new Date().toISOString());
      toast.notify({ title: "Verification email sent", tone: "success" });
    } catch (caught) {
      setError(emailVerificationErrorMessage(caught));
    } finally {
      setIsSending(false);
    }
  }

  const action = verified ? undefined : (
    <Button variant={hasSent ? "secondary" : "primary"} size={surface === "panel" ? "compact" : "default"} type="button" onClick={() => void sendVerification()} disabled={isSending}>
      {isSending ? (hasSent ? "Resending" : "Sending") : hasSent ? "Resend email" : "Verify address"}
    </Button>
  );

  return (
    <DataRow
      as="div"
      className={surface === "banner"
        ? "ui-data-row-account-verification ui-data-row-account-verification-shell"
        : "ui-data-row-account-verification"}
      role="group"
      aria-label="Email verification"
      data-state={verificationState}
    >
      <DataRowMedia className="ui-data-row-media-email-verification" aria-hidden="true">
        {verified ? <CheckCircle2 size={18} /> : <Mail size={18} />}
      </DataRowMedia>
      <DataRowMain className="ui-data-row-main-email-verification">
        <strong>{heading}</strong>
        <span>{detail}</span>
        {error && <Alert className="ui-alert-inline-feedback" tone="danger">{error}</Alert>}
      </DataRowMain>
      {action && <DataRowValue className="ui-data-row-value-email-verification-actions">{action}</DataRowValue>}
    </DataRow>
  );
}

function emailVerificationErrorMessage(caught: unknown) {
  const message = caught instanceof Error ? caught.message : "";
  if (/configured|provider|resend|email_from|app_base_url/i.test(message)) {
    return "Verification email couldn't be sent right now. Try again later.";
  }
  return message || "Unable to send verification email.";
}


function Sidebar({
  selected,
  selectedSettingsSection,
  settingsSections,
  onSelect,
  onSettingsSectionSelect,
  onSignOut
}: {
  selected: ViewKey;
  selectedSettingsSection: SettingsSectionKey;
  settingsSections: Array<{ key: SettingsSectionKey; label: string }>;
  onSelect: (view: ViewKey) => void;
  onSettingsSectionSelect: (section: SettingsSectionKey) => void;
  onSignOut: () => Promise<void>;
}) {
  const workspaceSettings = settingsSections.filter((section) => !["account","notifications"].includes(section.key));
  const personalSettings = settingsSections.filter((section) => ["account","notifications"].includes(section.key));
  return (
    <aside className="sidebar" aria-label="Primary navigation">
      <div className="sidebar-brand">
        <ListworkMark className="brand-mark small" />
        <div>
          <strong>listwork</strong>
          <WorkspaceContext />
        </div>
      </div>
      <nav className="desktop-nav" aria-label="Primary">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <div className="desktop-nav-group" key={item.key}>
              <Button
                variant="ghost"
                className="ui-button-desktop-nav"
                active={selected === item.key}
                type="button"
                aria-current={
                  selected === item.key && (item.key !== "settings" || settingsSections.length === 1)
                    ? "page"
                    : undefined
                }
                aria-expanded={item.key === "settings" && settingsSections.length > 1 ? selected === "settings" : undefined}
                aria-controls={item.key === "settings" && settingsSections.length > 1 ? "settings-sections" : undefined}
                onClick={() => onSelect(item.key)}
              >
                <Icon size={19} />
                {item.label}
              </Button>
              {item.key === "settings" && selected === "settings" && settingsSections.length > 1 && (
                <div id="settings-sections" className="desktop-settings-subnav" role="group" aria-label="Settings sections">
                  {workspaceSettings.length > 0 && <p className="desktop-settings-subnav-label">Workspace</p>}
                  {workspaceSettings.map((section) => (
                    <Button
                      variant="ghost"
                      size="compact"
                      className="desktop-settings-subnav-item"
                      active={selectedSettingsSection === section.key}
                      type="button"
                      key={section.key}
                      aria-label={section.label}
                      aria-current={selectedSettingsSection === section.key ? "page" : undefined}
                      onClick={() => onSettingsSectionSelect(section.key)}
                    >
                      {section.label}
                    </Button>
                  ))}
                  {personalSettings.length > 0 && <p className="desktop-settings-subnav-label">Personal</p>}
                  {personalSettings.map((section) => (
                    <Button
                      variant="ghost"
                      size="compact"
                      className="desktop-settings-subnav-item"
                      active={selectedSettingsSection === section.key}
                      type="button"
                      key={section.key}
                      aria-label={section.label}
                      aria-current={selectedSettingsSection === section.key ? "page" : undefined}
                      onClick={() => onSettingsSectionSelect(section.key)}
                    >
                      {section.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <Button variant="ghost" className="sidebar-support" type="button" onClick={openSupport}><CircleHelp size={18} /> Report a problem</Button>
      <Button variant="ghost" className="sidebar-signout" type="button" onClick={() => void onSignOut()}>
        <LogOut size={18} /> Sign out
      </Button>
    </aside>
  );
}

function openActionNotifications(store: ReturnType<typeof useReOS>) {
  return prioritizedWorkQueue(store.notifications
    .filter((notification) => notification.source === "system" || notification.id.startsWith("system_") || !notification.readAt)
    .filter((notification) => notificationHasConcreteTarget(notification, targetForNotification(notification, store), store)), store.database);
}

function targetForNotification(notification: ReOSNotification, store: ReturnType<typeof useReOS>): NotificationFocus {
  if (notification.channelActivity) return { view: "channels", ...notification.channelActivity };
  if (notification.type.startsWith("research_") && notification.itemID) return { view: "inventory", itemID: notification.itemID, research: true };
  if (notification.id === STOCK_REVIEW_GROUP) return { view: "inventory", filter: "active", attention: "review_due" };
  if (notification.type === "marketplace_sale_detected" || notification.type === "marketplace_forwarding_verification") {
    return { view: "settings", section: "signals" };
  }
  if (notification.type === "marketplace_order_updated" && !notification.saleID && !notification.listingID) {
    return { view: "settings", section: "marketplaces" };
  }
  if (notification.type === "cross_listing_conflict" && notification.listingID && store.listings.some((listing) => listing.id === notification.listingID)) {
    return { view: "listings", listingID: notification.listingID, itemID: notification.itemID, notice: notificationFocusMessage(notification) };
  }

  if (notification.saleID && store.sales.some((sale) => sale.id === notification.saleID)) {
    return { view: "orders", saleID: notification.saleID, notice: notificationFocusMessage(notification) };
  }

  if (notification.type === "aging_stock" && notification.itemID) {
    return {
      view: "inventory",
      itemID: notification.itemID,
      filter: "active",
      attention: "aging",
      notice: notificationFocusMessage(notification)
    };
  }

  if (notification.listingID && store.listings.some((listing) => listing.id === notification.listingID)) {
    return { view: "listings", listingID: notification.listingID, itemID: notification.itemID, notice: notificationFocusMessage(notification) };
  }

  if (notification.itemID && store.inventoryItems.some((item) => item.id === notification.itemID)) {
    return { view: "inventory", itemID: notification.itemID, filter: "all", notice: notificationFocusMessage(notification) };
  }

  if (notification.type === "listing_ready" || notification.type === "listing_published" || notification.type === "listing_failed") {
    return { view: "dashboard" };
  }

  return { view: "dashboard" };
}

function notificationHasConcreteTarget(notification: ReOSNotification, target: NotificationFocus, store: ReturnType<typeof useReOS>) {
  if (notification.type === "marketplace_connection_error" && notification.marketplaceSignal?.marketplace === "ebay" && notification.marketplaceSignal.externalOrderID) return true;
  if (target.view === "orders") {
    return Boolean(target.saleID && store.sales.some((sale) => sale.id === target.saleID));
  }
  if (target.view === "channels") return Boolean(target.accountID && store.marketplaceAccounts.some((account) => account.id === target.accountID));
  if (target.view === "listings") {
    return Boolean(target.listingID && store.listings.some((listing) => listing.id === target.listingID));
  }
  if (target.view === "inventory") {
    return Boolean(target.itemID && store.inventoryItems.some((item) => item.id === target.itemID));
  }
  if (target.view === "settings") {
    return (
      notification.type === "marketplace_connection_error" ||
      notification.type === "marketplace_sale_detected" ||
      notification.type === "marketplace_order_updated" ||
      notification.type === "marketplace_forwarding_verification"
    ) && Boolean(target.section);
  }
  return false;
}

function notificationFocusMessage(notification: ReOSNotification): NotificationFocusMessage | undefined {
  switch (notification.type) {
    case "research_ready":
    case "research_failed":
    case "research_needs_input":
      return { title: notification.title, body: notification.body };
    case "listing_ready": {
      const isDraftAction = /^Finish\s/i.test(notification.title) || /missing details|before you post/i.test(notification.body);
      return {
        title: isDraftAction ? "Finish draft" : "Ready to review",
        body: isDraftAction
          ? notification.body
          : "Review the copy and photos, list on the marketplace, then paste the live URL and mark it live."
      };
    }
    case "listing_failed":
      return {
        title: "Listing needs changes",
        body: notification.body
      };
    case "listing_published":
    case "item_sold":
      return undefined;
    case "aging_stock":
      return {
        title: "Review aging stock",
        body: "Check the price, photos, and listing status. Mark it sold, end it, or relist after you decide the next move."
      };
    case "shipping_due":
      return {
        title: notification.title,
        body: notification.body
      };
    case "cross_listing_conflict":
      return {
        title: "End other marketplace listings",
        body: "No stock remains for these live listings. Remove them from the marketplace, then mark them ended here."
      };
    case "channel_activity":
      return { title: notification.title, body: notification.body };
    case "marketplace_connection_error":
      return {
        title: "Marketplace issue",
        body: notification.body
      };
    case "marketplace_sale_detected":
      return {
        title: "Review detected sale",
        body: notification.body
      };
    case "marketplace_order_updated":
      return {
        title: notification.title,
        body: notification.body
      };
    case "marketplace_forwarding_verification":
      return {
        title: "Confirm forwarding",
        body: notification.body
      };
    case "price_recommendation":
      return {
        title: "Review price",
        body: notification.body
      };
  }

  return assertNeverNotificationType(notification.type);
}

function TopBar({
  view,
  open,
  onOpenChange,
  onAdd,
  onNotificationAction,
  onOpenBilling,
  onOpenItem,
  onOpenListing,
  onOpenOrder
}: {
  view: ViewKey;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: () => void;
  onNotificationAction: (notification: ReOSNotification) => void;
  onOpenBilling: () => void;
  onOpenItem: (itemID: string) => void;
  onOpenListing: (listingID: string, itemID?: string) => void;
  onOpenOrder: (saleID: string) => void;
}) {
  const store = useReOS();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const assistantHandoffRef = useRef<(() => void) | undefined>();
  const notifications = openActionNotifications(store);
  const openCount = notifications.length;
  const notificationButtonLabel = openCount > 0 ? `Open Needs attention, ${openCount} open action${openCount === 1 ? "" : "s"}` : "Open Needs attention, work queue clear";
  const billingSummary = topBarBillingSummary(
    store.subscription,
    privateBetaAccessForWorkspace(store.database, store.workspace?.id)
  );
  const isDesktopTopbar = useMediaQuery("(min-width: 1040px)");
  const searchShortcutLabel = /Mac|iPhone|iPad/i.test(navigator.platform) ? "⌘ K" : "Ctrl K";
  const currentArea = navItems.find((item) => item.key === view)?.label ?? "Workspace";
  const assistantCommands = [
    { name: "Today's business summary", prompt: "Give me today's business summary with the most important next actions." },
    { name: "Review aging stock", prompt: "Review my aging stock and recommend what I should do next." },
    { name: "Find profit opportunities", prompt: "Find the best opportunities to improve profit in this workspace." },
    { name: "Prepare my daily plan", prompt: "Prepare a prioritized plan for running my reseller business today." }
  ];
  const savedAssistantSkills = useMemo(() => loadAssistantSkills(store.workspace?.id), [searchOpen, store.workspace?.id]);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!normalizedSearchQuery) {
      return [];
    }

    const inventoryResults = store.inventoryItems
      .filter((item) => [item.title, item.sku, item.brand, item.stockLocation].filter(Boolean).join(" ").toLowerCase().includes(normalizedSearchQuery))
      .slice(0, 4)
      .map((item) => ({
        key: `item:${item.id}`,
        kind: "item" as const,
        itemID: item.id,
        title: item.title,
        meta: `${item.sku} · ${inventoryStatusLabels[item.status]}`
      }));
    const listingResults = store.listings
      .filter((listing) => [listing.title, listing.externalListingID, marketplaceLabels[listing.marketplace]].filter(Boolean).join(" ").toLowerCase().includes(normalizedSearchQuery))
      .slice(0, 4)
      .map((listing) => ({
        key: `listing:${listing.id}`,
        kind: "listing" as const,
        itemID: listing.itemID,
        listingID: listing.id,
        title: listing.title,
        meta: `${marketplaceLabels[listing.marketplace]} · ${listingStatusLabels[listing.status]}`
      }));
    const orderResults = store.sales
      .map((sale) => ({ sale, item: store.inventoryItems.find((item) => item.id === sale.itemID) }))
      .filter(({ sale, item }) => [item?.title, item?.sku, sale.buyerName, marketplaceLabels[sale.marketplace]].filter(Boolean).join(" ").toLowerCase().includes(normalizedSearchQuery))
      .slice(0, 4)
      .map(({ sale, item }) => ({
        key: `order:${sale.id}`,
        kind: "order" as const,
        saleID: sale.id,
        title: item?.title ?? "Order",
        meta: `${marketplaceLabels[sale.marketplace]} · ${formatMoney(sale.salePrice)} · ${saleStatusLabels[sale.status]}`
      }));

    return [...inventoryResults, ...listingResults, ...orderResults].slice(0, 8);
  }, [normalizedSearchQuery, store.inventoryItems, store.listings, store.sales]);

  useEffect(() => {
    const handleSearchShortcut = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleSearchShortcut);
    return () => window.removeEventListener("keydown", handleSearchShortcut);
  }, []);

  function selectSearchResult(result: (typeof searchResults)[number]) {
    setSearchOpen(false);
    setSearchQuery("");
    if (result.kind === "item") {
      onOpenItem(result.itemID);
      return;
    }
    if (result.kind === "listing") {
      onOpenListing(result.listingID, result.itemID);
      return;
    }
    onOpenOrder(result.saleID);
  }

  function askAssistant(prompt: string, source: "command" | "attention" = "command") {
    const invoke = () => openAssistant({ prompt, autoSubmit: true, context: { area: currentArea, source } });
    // The closing overlay still owns focus until its focus scope is removed.
    // Open the assistant at that handoff, including for an immediate reply.
    if (searchOpen || open) assistantHandoffRef.current = invoke;
    else invoke();
    setSearchOpen(false);
    setSearchQuery("");
    onOpenChange(false);
  }

  function finishAssistantHandoff(event: Event) {
    const invoke = assistantHandoffRef.current;
    if (!invoke) return false;
    event.preventDefault();
    assistantHandoffRef.current = undefined;
    invoke();
    return true;
  }

  const notificationQueue = (
    <>
      <div className="notification-popover-head">
        <div>
          <strong>Needs attention</strong>
          {openCount > 0 && (
            <small>{openCount} {openCount === 1 ? "item" : "items"}</small>
          )}
        </div>
        <Button variant="ghost" size="icon" type="button" onClick={() => onOpenChange(false)} aria-label="Close Needs attention">
          <X size={18} />
        </Button>
      </div>
      {notifications.length === 0 ? (
        <EmptyState
          as="h3"
          compact
          layout="inline"
          icon={<Bell size={22} />}
          title="No open actions"
        />
      ) : (
        <div className="notification-list">
          {notifications.map((notification) => {
            const notificationItem = itemForNotification(notification, store);
            const notificationPhoto = notificationItem ? store.primaryPhoto(notificationItem.id) : undefined;
            const presentation = notificationQueuePresentation(notification, store);
            const mediaClassName = [
              notificationPhoto ? "ui-data-row-media-notification-photo" : "ui-data-row-media-activity",
              notification.readAt ? "" : "ui-data-row-media-activity-unread"
            ].filter(Boolean).join(" ");

            return (
              <DataRow
                className={notification.readAt
                  ? "ui-data-row-notification"
                  : "ui-data-row-notification ui-data-row-notification-unread"}
                key={notification.id}
                onClick={() => {
                  onNotificationAction(notification);
                  onOpenChange(false);
                }}
              >
                <DataRowMedia className={mediaClassName}>
                  {notificationPhoto
                    ? <img src={notificationPhoto.thumbnailURL || notificationPhoto.url} alt="" style={photoStyle(notificationPhoto.treatment, "fit")} />
                    : notificationIcon(notification, 17)}
                </DataRowMedia>
                <DataRowMain>
                  <span className="notification-row-heading">
                    <strong>{presentation.task}</strong>
                    <time dateTime={notification.createdAt}>{formatNotificationTime(notification.createdAt)}</time>
                  </span>
                  {presentation.entity ? <span className="notification-row-entity">{presentation.entity}</span> : null}
                  <DataRowMeta className="ui-data-row-meta-notification-body">{presentation.context}</DataRowMeta>
                </DataRowMain>
                <DataRowValue className="ui-data-row-value-notification-affordance" aria-hidden="true">
                  <ChevronRight size={16} />
                </DataRowValue>
              </DataRow>
            );
          })}
        </div>
      )}
      <button className="assistant-attention-review" type="button" onClick={() => askAssistant("Review this workspace and identify anything that needs my attention.", "attention")}>
        <span className="assistant-attention-review-icon"><Search size={16} /></span>
        <span><strong>Ask Listwork to review the business</strong><small>Find opportunities beyond rule-based alerts</small></span>
        <ChevronRight size={16} />
      </button>
    </>
  );

  return (
    <header className="topbar">
      {!isDesktopTopbar ? (
        <div className="topbar-title">
          <ListworkMark className="topbar-brand-mark" />
          <span className="topbar-area">{currentArea}</span>
          <WorkspaceContext />
        </div>
      ) : null}
      <Dialog open={searchOpen} onOpenChange={(nextOpen) => {
        setSearchOpen(nextOpen);
        if (!nextOpen) {
          setSearchQuery("");
        }
      }}>
        {isDesktopTopbar ? (
          <DialogTrigger asChild>
            <Button ref={searchTriggerRef} variant="secondary" className="workspace-search-trigger" type="button" aria-label="Search workspace">
              <Search size={17} />
              <span>Search, ask, or run an action</span>
              <kbd>{searchShortcutLabel}</kbd>
            </Button>
          </DialogTrigger>
        ) : (
          <IconControlTooltip label="Search or ask Listwork">
            <DialogTrigger asChild>
              <Button ref={searchTriggerRef} variant="secondary" className="workspace-search-icon" size="icon" type="button" aria-label="Search or ask Listwork">
                <Search size={19} />
              </Button>
            </DialogTrigger>
          </IconControlTooltip>
        )}
        <DialogContent
          className="workspace-search-dialog"
          presentation="compact"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (finishAssistantHandoff(event)) return;
            searchTriggerRef.current?.focus();
          }}
        >
          <DialogTitle className="sr-only">Search and command workspace</DialogTitle>
          <DialogDescription className="sr-only">Find records, navigate, ask Listwork, or run a saved workflow.</DialogDescription>
          <Command className={`workspace-command ${normalizedSearchQuery ? "workspace-command-has-query" : ""}`} shouldFilter={false}>
            <CommandInput
              autoFocus
              aria-label="Search, ask, or run an action"
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder="Search records or ask Listwork"
            />
            <CommandList className="workspace-search-results" aria-live="polite">
              {!normalizedSearchQuery ? (
                <>
                  <CommandGroup heading="Actions">
                    <CommandItem className="workspace-search-result workspace-assistant-result" value="action:add-item" onSelect={() => { setSearchOpen(false); onAdd(); }}>
                      <span className="workspace-search-result-icon"><Plus size={17} /></span><span className="workspace-search-result-copy"><strong>Add inventory item</strong><small>Start the reseller intake workflow</small></span><ChevronRight size={17} />
                    </CommandItem>
                    {assistantCommands.map((command) => <CommandItem className="workspace-search-result workspace-assistant-result" key={command.name} value={`assistant:${command.name}`} onSelect={() => askAssistant(command.prompt)}>
                      <span className="workspace-search-result-icon assistant"><Search size={17} /></span><span className="workspace-search-result-copy"><strong>{command.name}</strong><small>Ask Listwork using live workspace context</small></span><ChevronRight size={17} />
                    </CommandItem>)}
                  </CommandGroup>
                  {savedAssistantSkills.length > 0 && <CommandGroup heading="Saved workflows">{savedAssistantSkills.map((skill) => <CommandItem className="workspace-search-result workspace-assistant-result" key={skill.id} value={`skill:${skill.id}`} onSelect={() => askAssistant(skill.prompt)}>
                    <span className="workspace-search-result-icon assistant"><Search size={17} /></span><span className="workspace-search-result-copy"><strong>{skill.name}</strong><small>{skill.prompt}</small></span><ChevronRight size={17} />
                  </CommandItem>)}</CommandGroup>}
                </>
              ) : (
                <>
                  {searchResults.length > 0 && ([
                  { kind: "item" as const, label: "Inventory" },
                  { kind: "listing" as const, label: "Listings" },
                  { kind: "order" as const, label: "Orders" }
                ]).map((group) => {
                  const groupResults = searchResults.filter((result) => result.kind === group.kind);
                  if (groupResults.length === 0) return null;
                  return (
                    <CommandGroup heading={group.label} key={group.kind}>
                      {groupResults.map((result) => (
                        <CommandItem
                          className="workspace-search-result"
                          key={result.key}
                          value={result.key}
                          onSelect={() => selectSearchResult(result)}
                        >
                          <span className={`workspace-search-result-icon ${result.kind}`}>
                            {result.kind === "item" ? <Package size={17} /> : result.kind === "listing" ? <Tag size={17} /> : <ShoppingBag size={17} />}
                          </span>
                          <span className="workspace-search-result-copy">
                            <strong>{result.title}</strong>
                            <small>{result.meta}</small>
                          </span>
                          <ChevronRight size={17} />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                  })}
                  <CommandGroup heading="Ask Listwork">
                    <CommandItem className="workspace-search-result workspace-assistant-result" value={`ask:${normalizedSearchQuery}`} onSelect={() => askAssistant(searchQuery.trim())}>
                      <span className="workspace-search-result-icon assistant"><Search size={17} /></span>
                      <span className="workspace-search-result-copy"><strong>Ask “{searchQuery.trim()}”</strong><small>{searchResults.length ? "Use workspace context instead of opening a record" : "No exact record found. Ask the assistant instead."}</small></span>
                      <ChevronRight size={17} />
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
      <div className="topbar-actions">
        {view !== "inventory" && (isDesktopTopbar ? (
          <Button variant="primary" type="button" onClick={onAdd}>
            <Plus size={18} /> Add item
          </Button>
        ) : (
          <IconControlTooltip label="Add item">
            <Button variant="primary" size="icon" type="button" onClick={onAdd} aria-label="Add item">
              <Plus size={19} />
            </Button>
          </IconControlTooltip>
        ))}
        {isDesktopTopbar ? (
          <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="tool"
                className="topbar-attention-trigger"
                type="button"
                aria-label={notificationButtonLabel}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls="notification-popover"
              >
                <Bell size={17} aria-hidden="true" />
                <span>Needs attention</span>
                {openCount > 0 ? <ButtonCountBadge>{openCount}</ButtonCountBadge> : <CheckCircle2 size={16} aria-hidden="true" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="notification-popover" id="notification-popover" role="dialog" aria-label="Needs attention" onCloseAutoFocus={finishAssistantHandoff}>
              {notificationQueue}
            </PopoverContent>
          </Popover>
        ) : (
          <Sheet open={open} onOpenChange={onOpenChange}>
            <IconControlTooltip label="Open Needs attention">
              <SheetTrigger asChild>
                <Button
                  variant="secondary"
                  className="ui-button-notification-icon"
                  size="icon"
                  type="button"
                  aria-label={notificationButtonLabel}
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  aria-controls="notification-popover"
                >
                  <Bell size={19} />
                  {openCount > 0 && <ButtonCountBadge>{openCount}</ButtonCountBadge>}
                </Button>
              </SheetTrigger>
            </IconControlTooltip>
            <SheetContent className="notification-sheet" showClose={false} onCloseAutoFocus={finishAssistantHandoff}>
              <SheetTitle className="sr-only">Needs attention</SheetTitle>
              <SheetDescription className="sr-only">Open work requiring a decision or follow-up.</SheetDescription>
              <SheetBody className="notification-sheet-body" id="notification-popover">
                {notificationQueue}
              </SheetBody>
            </SheetContent>
          </Sheet>
        )}
        {store.persistenceMode === "api" && (
          <StatusButton badge={billingSummary.badge} detail={billingSummary.detail} title={billingSummary.title} type="button" onClick={onOpenBilling} />
        )}
      </div>
    </header>
  );
}

function topBarBillingSummary(
  subscription: ReturnType<typeof useReOS>["subscription"],
  privateBetaAccess?: ReturnType<typeof privateBetaAccessForWorkspace>
) {
  if (privateBetaAccess && (!subscription || !subscriptionGrantsPaidEntitlements(subscription))) {
    return {
      badge: "Beta",
      title: "Private beta",
      detail: privateBetaAccess.expiresAt ? `Until ${formatBillingDate(privateBetaAccess.expiresAt)}` : "Access active"
    };
  }
  if (!subscription) {
    return {
      badge: "Plan",
      title: "Choose plan",
      detail: "Review limits"
    };
  }

  const plan = subscriptionPlanLabels[subscription.plan];
  if (subscription.status === "trialing" && !subscriptionGrantsPaidEntitlements(subscription)) {
    return {
      badge: "Expired",
      title: "Trial ended",
      detail: "Choose a plan"
    };
  }
  if (subscription.status === "trialing") {
    return {
      badge: "Trial",
      title: plan,
      detail: subscription.trialEndsAt ? `Ends ${formatBillingDate(subscription.trialEndsAt)}` : "Trial active"
    };
  }
  if (subscription.status === "active") {
    return {
      badge: "Plan",
      title: plan,
      detail: subscription.currentPeriodEndsAt ? `Renews ${formatBillingDate(subscription.currentPeriodEndsAt)}` : "Active"
    };
  }
  if (subscription.status === "past_due") {
    return {
      badge: "Due",
      title: "Payment due",
      detail: "Review billing"
    };
  }
  if (subscription.status === "canceled") {
    return {
      badge: "Plan",
      title: `${plan} ended`,
      detail: "Review options"
    };
  }
  return {
    badge: subscriptionStatusLabels[subscription.status],
    title: plan,
    detail: "Review billing"
  };
}

function WorkspaceContext() {
  const store = useReOS();
  const [isSwitching, setIsSwitching] = useState(false);
  const availableWorkspaces =
    store.accountWorkspaces.length > 0
      ? store.accountWorkspaces
      : store.workspace
        ? [
            {
              id: store.workspace.id,
              name: store.workspace.name,
              defaultCurrency: store.workspace.defaultCurrency,
              createdAt: store.workspace.createdAt,
              updatedAt: store.workspace.updatedAt
            }
          ]
        : [];
  const currentWorkspaceID = store.workspace?.id ?? store.database.activeWorkspaceID ?? "";
  if (availableWorkspaces.length <= 1) {
    return <AppShellWorkspaceContext as="span">{store.workspace?.name ?? "Workspace"}</AppShellWorkspaceContext>;
  }

  async function changeWorkspace(nextWorkspaceID: string) {
    try {
      setIsSwitching(true);
      await store.switchWorkspace(nextWorkspaceID);
    } finally {
      setIsSwitching(false);
    }
  }

  return (
    <AppShellWorkspaceContext>
      <SelectControl
        className="ui-select-workspace"
        aria-label="Workspace"
        value={currentWorkspaceID}
        disabled={isSwitching}
        onValueChange={(nextValue) => void changeWorkspace(nextValue)}
        options={availableWorkspaces.map((workspace) => ({
          value: workspace.id,
          label: workspace.name
        }))}
      />
    </AppShellWorkspaceContext>
  );
}

function IconControlTooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function formatNotificationTime(createdAt: string) {
  const timestamp = new Date(createdAt).getTime();
  if (!Number.isFinite(timestamp)) {
    return "";
  }
  const elapsedMs = Date.now() - timestamp;
  const elapsedMinutes = Math.max(0, Math.floor(elapsedMs / 60_000));
  if (elapsedMinutes < 1) {
    return "Just now";
  }
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m ago`;
  }
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours}h ago`;
  }
  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) {
    return `${elapsedDays}d ago`;
  }
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(timestamp);
}

function notificationIcon(notification: ReOSNotification, size = 18): ReactNode {
  switch (notification.type) {
    case "research_ready":
    case "research_failed":
    case "research_needs_input":
      return <Search size={size} />;
    case "listing_ready":
    case "listing_failed":
      return <Tag size={size} />;
    case "listing_published":
      return <Share2 size={size} />;
    case "item_sold":
    case "shipping_due":
      return <ShoppingBag size={size} />;
    case "cross_listing_conflict":
      return <Link2 size={size} />;
    case "aging_stock":
      return <Package size={size} />;
    case "channel_activity":
      return <RefreshCw size={size} />;
    case "marketplace_connection_error":
      return <Settings size={size} />;
    case "marketplace_sale_detected":
    case "marketplace_forwarding_verification":
      return <Mail size={size} />;
    case "marketplace_order_updated":
      return <ShoppingBag size={size} />;
    case "price_recommendation":
      return <CreditCard size={size} />;
  }

  return assertNeverNotificationType(notification.type);
}

function itemForNotification(
  notification: ReOSNotification,
  store: ReturnType<typeof useReOS>
): InventoryItem | undefined {
  if (notification.itemID) {
    return store.itemFor(notification.itemID);
  }
  if (notification.listingID) {
    const listing = store.listings.find((candidate) => candidate.id === notification.listingID);
    return listing ? store.itemFor(listing.itemID) : undefined;
  }
  if (notification.saleID) {
    const sale = store.sales.find((candidate) => candidate.id === notification.saleID);
    return sale ? store.itemFor(sale.itemID) : undefined;
  }
  return undefined;
}

function notificationQueuePresentation(
  notification: ReOSNotification,
  store: ReturnType<typeof useReOS>
): { task: string; entity?: string; context: string } {
  const item = itemForNotification(notification, store);
  const listing = notification.listingID
    ? store.listings.find((candidate) => candidate.id === notification.listingID)
    : undefined;
  const sale = notification.saleID
    ? store.sales.find((candidate) => candidate.id === notification.saleID)
    : undefined;
  const marketplace = notification.marketplaceSignal?.marketplace ?? listing?.marketplace ?? sale?.marketplace;
  const marketplaceLabel = marketplace ? marketplaceLabels[marketplace] : undefined;

  switch (notification.type) {
    case "research_ready":
    case "research_failed":
    case "research_needs_input":
      return { task: notification.title, entity: item?.title, context: notification.body };
    case "listing_ready":
      return {
        task: listing?.status === "draft" ? "Finish draft" : "Review listing",
        entity: item?.title,
        context: [marketplaceLabel, listing?.status === "draft" ? "Missing details" : "Ready to publish"].filter(Boolean).join(" · ")
      };
    case "listing_failed":
      return {
        task: "Fix listing",
        entity: item?.title,
        context: [marketplaceLabel, listing?.validationWarnings[0] ?? "Needs changes"].filter(Boolean).join(" · ")
      };
    case "shipping_due": {
      const task = sale?.status === "packed" ? "Ship order" : sale?.status === "sold" ? "Confirm payment" : "Pack order";
      return {
        task,
        entity: item?.title,
        context: [marketplaceLabel, sale ? saleStatusLabels[sale.status] : undefined, sale ? shipByMeta(sale) : undefined].filter(Boolean).join(" · ")
      };
    }
    case "marketplace_sale_detected": {
      const amount = notification.marketplaceSignal?.amountMinor;
      const amountLabel = amount === undefined
        ? undefined
        : formatMoney({ amountMinor: amount, currency: item?.targetPrice.currency ?? "GBP" });
      return {
        task: "Confirm detected sale",
        entity: item?.title,
        context: [marketplaceLabel, amountLabel, notification.marketplaceSignal?.orderReview ? "Paid order" : "Email signal"].filter(Boolean).join(" · ")
      };
    }
    case "aging_stock": {
      if (notification.groupCount) return { task: `Review ${notification.groupCount} aging items`, context: "Check prices, photos and listing status" };
      const liveCount = item ? store.listingsFor(item.id).filter((candidate) => candidate.status === "live").length : 0;
      return {
        task: "Review aging stock",
        entity: item?.title,
        context: item
          ? `${inventoryAgeLabel(item)} · ${liveCount} live ${liveCount === 1 ? "listing" : "listings"}`
          : notification.body
      };
    }
    case "cross_listing_conflict":
      return {
        task: "End listings with no stock",
        entity: item?.title,
        context: [marketplaceLabel, "No stock available"].filter(Boolean).join(" · ")
      };
    case "marketplace_forwarding_verification":
      return {
        task: "Confirm email forwarding",
        entity: marketplaceLabel,
        context: notification.marketplaceSignal?.verificationCode
          ? `Verification code ${notification.marketplaceSignal.verificationCode}`
          : "Verification required"
      };
    case "channel_activity":
      return { task: notification.title, context: notification.body };
    case "marketplace_connection_error":
      return {
        task: notification.marketplaceSignal?.externalOrderID ? "Check marketplace order" : "Reconnect marketplace",
        entity: marketplaceLabel,
        context: notification.body
      };
    case "price_recommendation":
      return {
        task: "Review price",
        entity: item?.title,
        context: notification.body
      };
    case "listing_published":
    case "item_sold":
    case "marketplace_order_updated":
      return {
        task: notification.title,
        entity: item?.title,
        context: [marketplaceLabel, notification.body].filter(Boolean).join(" · ")
      };
  }

  return assertNeverNotificationType(notification.type);
}

function NotificationFocusCallout({
  actionLabel,
  busy = false,
  notice,
  onAction
}: {
  actionLabel?: string;
  busy?: boolean;
  notice: NotificationFocusMessage;
  onAction?: () => void;
}) {
  return (
    <Alert tone="info" className="ui-alert-notification-focus" role="status">
      <Bell size={18} />
      <span>
        <strong>{notice.title}</strong>
        <small>{notice.body}</small>
      </span>
      {actionLabel && onAction ? (
        <Button variant="primary" size="compact" type="button" onClick={onAction} disabled={busy} loading={Boolean(busy)} loadingLabel="Updating">
          {busy ? "Updating" : actionLabel}
        </Button>
      ) : null}
    </Alert>
  );
}

function assertNeverNotificationType(type: never): never {
  throw new Error(`Unhandled notification type: ${type}`);
}

function MobileNav({
  selected,
  onSelect,
  onSignOut
}: {
  selected: ViewKey;
  onSelect: (view: ViewKey) => void;
  onSignOut: () => Promise<void>;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const primaryItems = navItems.filter((item) => !["channels", "analytics", "settings"].includes(item.key));
  const moreItems = navItems.filter((item) => ["channels", "analytics", "settings"].includes(item.key));
  const selectedMoreItem = moreItems.find((item) => item.key === selected);

  useEffect(() => {
    setMoreOpen(false);
  }, [selected]);

  return (
    <nav className="mobile-nav" aria-label="Primary">
      {primaryItems.map((item) => {
        const Icon = item.icon;
        return (
          <Button
            variant="ghost"
            className="ui-button-mobile-nav"
            key={item.key}
            active={selected === item.key}
            type="button"
            aria-label={item.label}
            aria-current={selected === item.key ? "page" : undefined}
            onClick={() => onSelect(item.key)}
          >
            <Icon size={20} />
            <span>{item.mobileLabel}</span>
          </Button>
        );
      })}
      <DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="ui-button-mobile-nav"
            active={Boolean(selectedMoreItem)}
            type="button"
            data-support-menu-trigger
            aria-label={selectedMoreItem ? `More, ${selectedMoreItem.label} selected` : "More"}
            aria-current={selectedMoreItem ? "page" : undefined}
          >
            <MoreHorizontal size={20} />
            <span>More</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="mobile-nav-more-menu"
          side="top"
          align="end"
          sideOffset={8}
          onEscapeKeyDown={() => setMoreOpen(false)}
        >
          <DropdownMenuLabel>More</DropdownMenuLabel>
          <DropdownMenuGroup>
            {moreItems.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem
                  key={item.key}
                  aria-current={selected === item.key ? "page" : undefined}
                  onSelect={() => {
                    setMoreOpen(false);
                    onSelect(item.key);
                  }}
                >
                  <Icon aria-hidden="true" />
                  {item.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={openSupport}><CircleHelp aria-hidden="true" />Report a problem</DropdownMenuItem>
          <DropdownMenuLabel>Account</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() => {
              setMoreOpen(false);
              void onSignOut();
            }}
          >
            <LogOut aria-hidden="true" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}

const DetailSheetSuspendedContext = createContext(false);

function DetailSheet({
  open,
  title,
  onOpenChange,
  onReturnFocus,
  covered = false,
  children
}: {
  open: boolean;
  title: string;
  onOpenChange: (open: boolean) => void;
  onReturnFocus?: () => void;
  covered?: boolean;
  children: ReactNode;
}) {
  const suspended = useContext(DetailSheetSuspendedContext);
  return (
    <Sheet open={open && !suspended} onOpenChange={onOpenChange}>
      <SheetContent
        variant="detail"
        showClose={false}
        onOpenAutoFocus={(event) => { if (covered) event.preventDefault(); }}
        onEscapeKeyDown={(event) => { if (covered) event.preventDefault(); }}
        onInteractOutside={(event) => { if (covered) event.preventDefault(); }}
        onCloseAutoFocus={(event) => {
          if (suspended || covered) { event.preventDefault(); return; }
          if (!onReturnFocus) return;
          event.preventDefault();
          onReturnFocus();
        }}
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        <SheetDescription className="sr-only">Review details and available actions without leaving the current workspace view.</SheetDescription>
        <SheetBody>{children}</SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function DashboardScreen({
  onAdd,
  onOpenChannels,
  onOpenInventory,
  onOpenNotifications,
  onNotificationAction,
  onReviewItem
}: {
  onAdd: () => void;
  onOpenChannels: () => void;
  onOpenInventory: () => void;
  onOpenNotifications: () => void;
  onNotificationAction: (notification: ReOSNotification) => void;
  onReviewItem: (itemID: string) => void;
}) {
  const store = useReOS();
  const [rangeDays, setRangeDays] = useState<DashboardRangeDays>(() => readDashboardRangeDays());
  const onboardingStorageKey = workspaceOnboardingDismissalKey(store.workspace?.id);
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => readWorkspaceOnboardingDismissal(onboardingStorageKey)
  );
  const range = dashboardRangeOption(rangeDays);
  const summary = store.dashboardSummary(store.database, rangeDays);
  const activeStockItems = store.inventoryItems.filter((item) => item.status !== "sold" && item.status !== "archived");
  const actionNotifications = useMemo(
    () => openActionNotifications(store),
    [store]
  );
  const isCompactDashboard = useMediaQuery("(max-width: 420px)");
  const openActionCount = actionNotifications.length;
  const visibleActionNotifications = actionNotifications.slice(0, isCompactDashboard ? 3 : 5);
  const totalOpenActionCount = openActionCount;
  const canManageChannels = ["owner", "admin"].includes(workspaceRoleForStore(store));
  const hasConnectedChannel = store.marketplaceAccounts.some(
    (account) => account.connectionStatus === "connected" && account.environment === "production"
  );
  const simulatorRunIDs = new Set(
    (store.database.marketplaceImportRuns ?? [])
      .filter((run) => run.sourceOrigin === "simulator" || run.sourceOrigin === "sample")
      .map((run) => run.id)
  );
  const simulatorCreatedItemIDs = new Set(
    (store.database.marketplaceImportRecords ?? [])
      .filter((record) =>
        simulatorRunIDs.has(record.runID) &&
        record.status === "created" &&
        Boolean(record.targetItemID)
      )
      .map((record) => record.targetItemID as string)
  );
  const onboardingProgress = workspaceOnboardingProgress({
    canManageChannels,
    hasConnectedChannel,
    hasManualListing: store.listings.some((listing) => listing.sourceOrigin !== "simulator" && listing.sourceOrigin !== "sample" && listing.sourceEnvironment !== "simulator" && ["live", "sold", "ended", "delisted"].includes(listing.status) && store.marketplaceAccounts.some((account) => account.marketplace === listing.marketplace && isManualListingAccount(account))),
    inventoryItemCount: store.inventoryItems.filter((item) => !simulatorCreatedItemIDs.has(item.id)).length,
    listingCount: store.listings.filter((listing) =>
      listing.sourceOrigin !== "simulator" &&
      listing.sourceOrigin !== "sample" &&
      listing.sourceEnvironment !== "simulator" && ["live", "sold", "ended", "delisted"].includes(listing.status)
    ).length
  });
  const showOnboarding = !onboardingProgress.complete && !onboardingDismissed;
  const isFirstRunDashboard =
    store.inventoryItems.length === 0 &&
    store.listings.length === 0 &&
    store.sales.length === 0 &&
    totalOpenActionCount === 0;

  useEffect(() => {
    saveDashboardRangeDays(rangeDays);
  }, [rangeDays]);

  useEffect(() => {
    setOnboardingDismissed(readWorkspaceOnboardingDismissal(onboardingStorageKey));
  }, [onboardingStorageKey]);

  const onboardingPanel = showOnboarding ? (
    <WorkspaceOnboardingChecklist
      compact={!isFirstRunDashboard}
      progress={onboardingProgress}
      hasConnectedChannel={hasConnectedChannel}
      onAdd={onAdd}
      onOpenChannels={onOpenChannels}
      onOpenInventory={onOpenInventory}
      onDismiss={() => {
        setOnboardingDismissed(true);
        saveWorkspaceOnboardingDismissal(onboardingStorageKey);
      }}
    />
  ) : null;

  if (isFirstRunDashboard) {
    return (
      <div className="screen-content workflow-empty-layout first-run-dashboard">
        <Toolbar title="Dashboard" />

        {onboardingPanel ?? (
          <FirstRunPanel
            icon={<Package size={32} />}
            title="Add your first item"
            message="Keep its photographs, cost, SKU, location, and marketplace listings together."
            actionLabel="Add item"
            onAction={onAdd}
          />
        )}
      </div>
    );
  }

  return (
    <div className="screen-content dashboard-layout">
      <section className="dashboard-header">
        <h1>Dashboard</h1>
        <label className="dashboard-range-control">
          <Clock3 size={18} />
          <SelectControl
            className="ui-select-range"
            value={String(rangeDays)}
            aria-label="Dashboard date range"
            onValueChange={(value) => {
              const nextRange = Number(value);
              if (dashboardRangeOptions.some((option) => option.days === nextRange)) {
                setRangeDays(nextRange as DashboardRangeDays);
              }
            }}
            options={dashboardRangeOptions.map((option) => ({
              value: String(option.days),
              label: option.label
            }))}
          />
        </label>
      </section>

      <section className="dashboard-grid">
        <Card className="dashboard-work-card dashboard-needs-attention-card">
          <CardHeader>
            <div>
              <CardTitle as="h2">Needs attention</CardTitle>
              <CardDescription>
                {openActionCount > visibleActionNotifications.length
                  ? `Showing ${visibleActionNotifications.length} of ${openActionCount}`
                  : openActionCount > 0
                    ? `${openActionCount} open ${openActionCount === 1 ? "action" : "actions"}`
                    : "Work queue clear"}
              </CardDescription>
            </div>
            {actionNotifications.length > 0 ? (
              <CardAction>
                <Button variant="secondary" size="compact" type="button" onClick={onOpenNotifications}>
                  View all
                </Button>
              </CardAction>
            ) : null}
          </CardHeader>
          <CardContent>
            {actionNotifications.length === 0 ? (
              <div className="dashboard-queue-clear" role="status">
                <span><CheckCircle2 size={24} /></span>
                <div>
                  <strong>You're caught up</strong>
                  <p>No listing, order, or research work needs a decision.</p>
                </div>
              </div>
            ) : (
              <div className="activity-list">
                {visibleActionNotifications.map((notification) => {
                  const notificationItem = itemForNotification(notification, store);
                  const notificationPhoto = notificationItem ? store.primaryPhoto(notificationItem.id) : undefined;
                  const presentation = notificationQueuePresentation(notification, store);
                  const mediaClassName = [
                    notificationPhoto ? "ui-data-row-media-activity-photo" : "ui-data-row-media-activity",
                    notification.readAt ? "" : "ui-data-row-media-activity-unread"
                  ].filter(Boolean).join(" ");
                  return (
                    <DataRow
                      className="ui-data-row-activity"
                      key={notification.id}
                      onClick={() => onNotificationAction(notification)}
                    >
                      <DataRowMedia className={mediaClassName}>
                        {notificationPhoto
                          ? <img src={notificationPhoto.thumbnailURL || notificationPhoto.url} alt="" style={photoStyle(notificationPhoto.treatment, "fit")} />
                          : notificationIcon(notification, 18)}
                      </DataRowMedia>
                      <DataRowMain>
                        <strong>{presentation.task}</strong>
                        {presentation.entity ? <span className="notification-row-entity">{presentation.entity}</span> : null}
                        <small>{presentation.context}</small>
                        <time dateTime={notification.createdAt}>{formatNotificationTime(notification.createdAt)}</time>
                      </DataRowMain>
                      <DataRowValue className="ui-data-row-value-activity-action">
                        <ChevronRight size={18} />
                      </DataRowValue>
                    </DataRow>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>


      </section>

      {onboardingPanel}
      {activeStockItems.some(catalogueNeedsDetails) && <div className="catalogue-entry"><div><strong>Finish your inventory details</strong><p>{activeStockItems.filter(catalogueNeedsDetails).length} items have a missing cost, location or acquisition date.</p></div><Button asChild variant="secondary"><a href="/inventory?catalogue=1">Complete details</a></Button></div>}

      <MetricGroup aria-label="Workspace summary">
        <Metric title="Live listings" value={String(summary.activeListings)} footnote="Available to buy now" icon={<Tag size={19} />} tone="success" />
        <Metric title="Draft work" value={String(summary.draftListings)} footnote="Listings waiting for review" icon={<Package size={19} />} tone="accent" />
        <Metric title="Revenue" value={formatMoney(summary.revenueInRange)} footnote={`${orderCountLabel(summary.salesInRange)} - ${range.footnote}`} icon={<CreditCard size={19} />} tone="warning" />
        <Metric title="Net Profit" value={formatMoney(summary.netProfitInRange)} footnote={store.sales.some((sale) => isRecognizedSale(sale) && isWithinDashboardRange(sale.soldAt, rangeDays) && saleProfitIsProvisional(sale)) ? `Provisional · costs incomplete · ${range.footnote}` : `After recorded costs - ${range.footnote}`} icon={<LineChart size={19} />} tone="info" />
      </MetricGroup>

    </div>
  );
}

function WorkspaceOnboardingChecklist({
  compact = false,
  progress,
  hasConnectedChannel,
  onAdd,
  onOpenChannels,
  onOpenInventory,
  onDismiss
}: {
  compact?: boolean;
  progress: WorkspaceOnboardingProgress;
  hasConnectedChannel: boolean;
  onAdd: () => void;
  onOpenChannels: () => void;
  onOpenInventory: () => void;
  onDismiss: () => void;
}) {
  const nextStep = progress.steps.find((step) => step.id === progress.nextStepID);
  const actionLabel = nextStep?.id === "channel"
    ? "Open channels"
    : nextStep?.id === "inventory"
      ? "Add item"
      : "Record your first listing";
  const runNextAction = () => {
    if (nextStep?.id === "channel") {
      onOpenChannels();
      return;
    }
    if (nextStep?.id === "inventory") {
      onAdd();
      return;
    }
    onOpenInventory();
  };

  if (compact && nextStep) {
    return (
      <section className="workspace-onboarding-next" aria-label="Workspace setup">
        <div>
          <span>{progress.completed} of {progress.total} setup steps complete</span>
          <strong>{nextStep.title}</strong>
        </div>
        <Button variant="secondary" type="button" onClick={runNextAction}>{actionLabel}<ChevronRight size={17} /></Button>
        <Button variant="ghost" size="icon" type="button" aria-label="Dismiss workspace setup" onClick={onDismiss}><X size={18} /></Button>
      </section>
    );
  }

  return (
    <Card className="workspace-onboarding" aria-labelledby="workspace-onboarding-heading">
      <CardHeader>
        <div>
          <CardTitle as="h2" id="workspace-onboarding-heading">Set up your workspace</CardTitle>
          <CardDescription>{progress.completed} of {progress.total} complete</CardDescription>
        </div>
        <CardAction>
          <Button
            className="workspace-onboarding-dismiss"
            variant="ghost"
            size="icon"
            type="button"
            aria-label="Dismiss workspace setup"
            onClick={onDismiss}
          >
            <X size={18} />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <ol className="workspace-onboarding-steps">
          {progress.steps.map((step, index) => {
            const current = step.id === progress.nextStepID;
            return (
              <li
                key={step.id}
                className={step.complete ? "is-complete" : current ? "is-current" : ""}
                aria-current={current ? "step" : undefined}
              >
                <span className="workspace-onboarding-marker" aria-hidden="true">
                  {step.complete ? <Check size={15} strokeWidth={2.5} /> : index + 1}
                </span>
                <span>
                  <strong>{step.title}</strong>
                  {current ? <small>{step.description}</small> : null}
                </span>
              </li>
            );
          })}
        </ol>
        {nextStep ? (
          <Button type="button" variant="primary" onClick={runNextAction}>
            {actionLabel}
            <ChevronRight size={17} />
          </Button>
        ) : null}
        {nextStep?.id === "inventory" && <Button variant="ghost" onClick={onOpenChannels}>Import from a sales channel</Button>}
      </CardContent>
    </Card>
  );
}

function workspaceOnboardingDismissalKey(workspaceID?: string) {
  return `listwork:onboarding-dismissed:${workspaceID ?? "unknown"}`;
}

function readWorkspaceOnboardingDismissal(key: string) {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function saveWorkspaceOnboardingDismissal(key: string) {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // Dismissal persistence is optional when storage is unavailable.
  }
}

function useWorkbenchCollection<T extends WorkspaceCollectionRecord>(
  resource: WorkspaceCollectionResource,
  fallbackRecords: T[],
  filters: NonNullable<WorkspaceCollectionQuery["filters"]>,
  authoritativeRecords: T[] = fallbackRecords
) {
  const store = useReOS();
  const database = store.database;
  const key = `${store.workspace?.id ?? "none"}:${resource}:${JSON.stringify(filters)}`;
  const [navigation, setNavigation] = useState<{ key: string; cursor?: string; history: Array<string | undefined> }>({ key, history: [] });
  const cursor = navigation.key === key ? navigation.cursor : undefined;
  const history = navigation.key === key ? navigation.history : [];
  const requestKey = `${key}:${cursor ?? "first"}`;
  const [result, setResult] = useState<{ key: string; database: typeof database; page: WorkspaceCollectionPage<T> }>();
  const [loadingKey, setLoadingKey] = useState<string>();
  const [error, setError] = useState<string>();
  const [retryVersion, setRetryVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadingKey(requestKey);
    setError(undefined);
    void store.listWorkspaceCollection(resource, { cursor, limit: 50, filters })
      .then((page) => {
        if (!cancelled) setResult({ key: requestKey, database, page: page as WorkspaceCollectionPage<T> });
      })
      .catch((caught) => {
        if (!cancelled) setError(userSafeErrorMessage(caught, "Unable to load this result page."));
      })
      .finally(() => {
        if (!cancelled) setLoadingKey(undefined);
      });
    return () => { cancelled = true; };
  }, [cursor, database, key, requestKey, resource, retryVersion]);

  const currentPage = result?.key === requestKey ? result.page : undefined;
  const fallbackPage = fallbackRecords.slice(0, 50);
  const records = currentPage
    ? reconcileWorkspaceCollectionRecords(currentPage.records, fallbackRecords, authoritativeRecords)
    : fallbackPage;
  const removedStaleRecords = currentPage ? currentPage.records.length - records.length : 0;
  return {
    records,
    total: currentPage ? Math.max(0, currentPage.total - removedStaleRecords) : fallbackRecords.length,
    offset: currentPage?.offset ?? 0,
    limit: currentPage?.limit ?? 50,
    loading: loadingKey === requestKey || (!currentPage && !error),
    error,
    retry: () => setRetryVersion((version) => version + 1),
    hasPrevious: history.length > 0,
    hasNext: Boolean(currentPage?.nextCursor),
    previous: () => setNavigation((current) => {
      const activeHistory = current.key === key ? current.history : [];
      return { key, cursor: activeHistory[activeHistory.length - 1], history: activeHistory.slice(0, -1) };
    }),
    next: () => {
      if (!currentPage?.nextCursor) return;
      setNavigation({ key, cursor: currentPage.nextCursor, history: [...history, cursor] });
    }
  };
}

function WorkbenchPagination({
  page
}: {
  page: ReturnType<typeof useWorkbenchCollection>;
}) {
  if (page.total === 0 && !page.loading && !page.error) return null;
  const start = page.offset + 1;
  const end = Math.min(page.total, page.offset + page.records.length);
  return (
    <nav className="workbench-pagination" aria-label="Result pages">
      {page.loading && <OperationStatus title="Loading results" />}
      {page.error && <Alert tone="danger">{page.error} <Button size="compact" onClick={page.retry} disabled={page.loading}>Try again</Button></Alert>}
      <span aria-live="polite">
        {!page.loading && !page.error && page.total > 0 ? `Showing ${start.toLocaleString("en-GB")}-${end.toLocaleString("en-GB")} of ${page.total.toLocaleString("en-GB")}` : null}
      </span>
      <div>
        <Button variant="secondary" size="compact" type="button" onClick={page.previous} disabled={!page.hasPrevious || page.loading}>
          <ChevronLeft size={15} /> Previous
        </Button>
        <Button variant="secondary" size="compact" type="button" onClick={page.next} disabled={!page.hasNext || page.loading}>
          Next <ChevronRight size={15} />
        </Button>
      </div>
    </nav>
  );
}

function InventoryScreen({
  onResearchRoute,
  onCompleteDetails,
  onImportSpreadsheet,
  focus,
  onAdd,
  onOpenListing,
  onOpenOrder,
  onRecordSale,
  onSelectItem,
  onFiltersChange
}: {
  onResearchRoute: (itemID?: string) => void;
  onCompleteDetails: () => void;
  onImportSpreadsheet: () => void;
  focus: Extract<NotificationFocus, { view: "inventory" }> | null;
  onAdd: () => void;
  onOpenListing: (listingID: string, itemID?: string) => void;
  onOpenOrder: (saleID: string) => void;
  onRecordSale: (itemID: string) => void;
  onSelectItem: (
    itemID: string,
    filter: InventoryLifecycleFilterKey,
    issue: InventoryIssueFilterKey,
    age: InventoryAgeFilterKey,
    category: string,
    marketplace: MarketplaceFilterKey,
    query: string
  ) => void;
  onFiltersChange: (
    filter: InventoryLifecycleFilterKey,
    issue: InventoryIssueFilterKey,
    age: InventoryAgeFilterKey,
    category: string,
    marketplace: MarketplaceFilterKey,
    query: string
  ) => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const [query, setQuery] = useState(() => focus?.query ?? "");
  const [filter, setFilter] = useState<InventoryLifecycleFilterKey>(() => focus?.filter ?? "in_stock");
  const [issueFilter, setIssueFilter] = useState<InventoryIssueFilterKey>(() => focus?.attention ?? "all");
  const [ageFilter, setAgeFilter] = useState<InventoryAgeFilterKey>(() => focus?.age ?? "all");
  const [categoryFilter, setCategoryFilter] = useState(() => focus?.category ?? "all");
  const [marketplaceFilter, setMarketplaceFilter] = useState<MarketplaceFilterKey>(() => focus?.marketplace ?? "all");
  const [selectedID, setSelectedID] = useState<string | undefined>();
  const [selectionMode, setSelectionMode] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [photoImportIDs, setPhotoImportIDs] = useState<string[] | null>(null);
  const [selectedItemIDs, setSelectedItemIDs] = useState<Set<string>>(() => new Set());
  const [manualListingOpen, setManualListingOpen] = useState(false);
  const [manualListingItemIDs, setManualListingItemIDs] = useState<string[] | null>(null);
  const [prepareListing, setPrepareListing] = useState<{ itemID: string; accountID?: string }>();
  const [createListingItemID, setCreateListingItemID] = useState<string>();
  const [connectedDraftItemID, setConnectedDraftItemID] = useState<string>();
  const [stockItemID, setStockItemID] = useState<string | null>(null);
  const [researchItemID, setResearchItemID] = useState<string | null>(null);
  const [researchOpen, setResearchOpen] = useState(false);
  const [researchPhotoItemID, setResearchPhotoItemID] = useState<string | null>(null);
  const researchPhotoInputRef = useRef<HTMLInputElement>(null);
  const researchPhotoUploadItemRef = useRef<string>();
  const [researchPhotoError, setResearchPhotoError] = useState("");
  const [researchLaunchIDs, setResearchLaunchIDs] = useState<string[] | null>(null);
  const [researchBatchOutcomes, setResearchBatchOutcomes] = useState<ResearchBatchOutcome[]>([]);
  function openResearch(itemID?: string) {
    const item = itemID ? store.itemFor(itemID) : undefined;
    if (item && !item.research?.run) { setResearchLaunchIDs([item.id]); return; }
    setResearchItemID(itemID ?? null); setResearchOpen(true);
    if (!focus?.research || focus.itemID !== itemID) onResearchRoute(itemID);
  }
  useEffect(() => { if (focus?.research) openResearch(focus.itemID); else setResearchOpen(false); }, [focus?.research, focus?.itemID]);
  const [cohortFocusRestoreRequest, setCohortFocusRestoreRequest] = useState(0);
  const itemRowRefs = useRef(new Map<string, HTMLButtonElement>());
  const inventoryResultsRef = useRef<HTMLDivElement | null>(null);
  const lastSelectedItemID = useRef<string | undefined>(focus?.itemID);
  const completedCohortFocusRestore = useRef(0);
  const categoryOptions = useMemo(
    () => [
      { key: "all", label: "All categories" },
      ...Array.from(new Set(store.inventoryItems.map((item) => item.categoryID).filter(Boolean)))
        .sort((left, right) => left.localeCompare(right))
        .map((category) => ({ key: category, label: category }))
    ],
    [store.inventoryItems]
  );
  const inventoryMarketplaceOptions = useMemo(
    () => marketplaceOptionsFor(store.inventoryItems.flatMap((item) => store.listingsFor(item.id).map((listing) => listing.marketplace))),
    [store]
  );

  function itemMatchesCohort(
    item: InventoryItem,
    nextFilter: InventoryLifecycleFilterKey,
    nextIssue: InventoryIssueFilterKey,
    nextAge: InventoryAgeFilterKey,
    nextCategory: string,
    nextMarketplace: MarketplaceFilterKey,
    nextQuery: string
  ) {
    const listings = store.listingsFor(item.id);
    const photos = store.photosFor(item.id);
    const searchable = [
      item.title,
      item.sku,
      item.brand,
      item.stockLocation,
      item.categoryID,
      item.color,
      item.size,
      item.research?.identification.model,
      item.research?.identification.period,
      item.research?.identification.origin,
      item.research?.identification.identifiers
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesQuery = !nextQuery.trim() || searchable.includes(nextQuery.trim().toLowerCase());
    const matchesFilter =
      nextFilter === "all" ||
      (nextFilter === "active" && item.status !== "sold" && item.status !== "archived") ||
      (nextFilter === "in_stock" && item.quantity > 0 && !["sold", "archived"].includes(item.status)) ||
      (nextFilter === "out_of_stock" && item.quantity === 0 && !["sold", "archived"].includes(item.status)) ||
      (nextFilter === "ready" && item.status === "ready_to_list") ||
      (nextFilter === "listed" && item.status === "listed") ||
      (nextFilter === "sold" && item.status === "sold") ||
      (nextFilter === "archived" && item.status === "archived");
    const matchesIssue =
      nextIssue === "all" ||
      (nextIssue === "needs_attention" && Boolean(inventoryAttention(item, photos.length, listings, store.database.channelPhotoJobs))) ||
      (nextIssue === "aging" && isAgingInventoryItem(item)) ||
      (nextIssue === "review_due" && stockReviewIsDue(item)) ||
      (nextIssue === "missing_photos" && photos.length === 0);
    const matchesAge = inventoryItemMatchesAgeFilter(item, nextAge);
    const matchesCategory = nextCategory === "all" || item.categoryID === nextCategory;
    const matchesMarketplace = nextMarketplace === "all" || listings.some((listing) => listing.marketplace === nextMarketplace);
    return matchesQuery
      && matchesFilter
      && matchesIssue
      && matchesAge
      && matchesCategory
      && matchesMarketplace
      && (nextFilter !== "listed" || listings.length > 0 || item.status === "listed");
  }

  const filteredItems = useMemo(
    () => store.inventoryItems.filter((item) =>
      itemMatchesCohort(item, filter, issueFilter, ageFilter, categoryFilter, marketplaceFilter, query)
    ),
    [ageFilter, categoryFilter, filter, issueFilter, marketplaceFilter, query, store]
  );
  const inventoryPage = useInventoryCollection(filteredItems, {
    q: query,
    lifecycle: filter,
    attention: issueFilter,
    age: ageFilter,
    category: categoryFilter,
    marketplace: marketplaceFilter
  });
  const visibleItems = inventoryPage.records;
  const moreInventoryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!moreInventoryRef.current || !inventoryPage.hasNext || inventoryPage.loading || inventoryPage.error) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void inventoryPage.loadMore();
    }, { rootMargin: "240px" });
    observer.observe(moreInventoryRef.current);
    return () => observer.disconnect();
  }, [inventoryPage.hasNext, inventoryPage.loading, inventoryPage.error, inventoryPage.loadMore, researchOpen]);

  const isMobileDetail = useMediaQuery("(max-width: 899px)");
  const selectedFromID = selectedID ? visibleItems.find((item) => item.id === selectedID) ?? filteredItems.find((item) => item.id === selectedID) : undefined;
  const selected = isMobileDetail
    ? focus?.itemID ? selectedFromID : undefined
    : selectedFromID ?? visibleItems[0];
  const selectedFocus = selected && focus?.itemID === selected.id ? focus : null;

  function selectItem(itemID: string) {
    lastSelectedItemID.current = itemID;
    setSelectedID(itemID);
    onSelectItem(itemID, filter, issueFilter, ageFilter, categoryFilter, marketplaceFilter, query);
  }

  function clearItemSelection() {
    setSelectedID(undefined);
    onFiltersChange(filter, issueFilter, ageFilter, categoryFilter, marketplaceFilter, query);
  }

  function clearInventoryFilters() {
    setQuery("");
    setFilter("all");
    setIssueFilter("all");
    setAgeFilter("all");
    setCategoryFilter("all");
    setMarketplaceFilter("all");
    onFiltersChange("all", "all", "all", "all", "all", "");
  }

  function restoreInventoryListFocus() {
    window.requestAnimationFrame(() => {
      const previousTrigger = lastSelectedItemID.current
        ? itemRowRefs.current.get(lastSelectedItemID.current)
        : undefined;
      const firstVisibleTrigger = visibleItems[0]
        ? itemRowRefs.current.get(visibleItems[0].id)
        : undefined;
      (previousTrigger ?? firstVisibleTrigger ?? inventoryResultsRef.current)?.focus();
    });
  }

  useEffect(() => {
    if (isMobileDetail) {
      return;
    }

    if (visibleItems[0] && (!selectedID || !visibleItems.some((item) => item.id === selectedID))) {
      setSelectedID(visibleItems[0].id);
    }
  }, [isMobileDetail, selectedID, visibleItems]);

  useEffect(() => {
    if (!focus) {
      setQuery("");
      setFilter("in_stock");
      setIssueFilter("all");
      setAgeFilter("all");
      setCategoryFilter("all");
      setMarketplaceFilter("all");
      setSelectedID(undefined);
      return;
    }
    const nextQuery = focus.query ?? "";
    const nextFilter = focus.filter ?? "in_stock";
    const nextIssue = focus.attention ?? "all";
    const nextAge = focus.age ?? "all";
    const nextCategory = focus.category ?? "all";
    const nextMarketplace = focus.marketplace ?? "all";
    setQuery(nextQuery);
    setFilter(nextFilter);
    setIssueFilter(nextIssue);
    setAgeFilter(nextAge);
    setCategoryFilter(nextCategory);
    setMarketplaceFilter(nextMarketplace);
    const target = focus.itemID
      ? store.inventoryItems.find((item) => item.id === focus.itemID)
      : undefined;
    const targetIsValid = Boolean(
      target
      && itemMatchesCohort(
        target,
        nextFilter,
        nextIssue,
        nextAge,
        nextCategory,
        nextMarketplace,
        nextQuery
      )
    );
    if (focus.itemID && targetIsValid) {
      lastSelectedItemID.current = focus.itemID;
      setSelectedID(focus.itemID);
      return;
    }
    setSelectedID(undefined);
    if (focus.itemID) {
      setCohortFocusRestoreRequest((request) => request + 1);
      onFiltersChange(nextFilter, nextIssue, nextAge, nextCategory, nextMarketplace, nextQuery);
    }
  }, [
    focus?.itemID,
    focus?.filter,
    focus?.attention,
    focus?.age,
    focus?.category,
    focus?.marketplace,
    focus?.query,
    filteredItems,
    visibleItems,
    onFiltersChange,
    store
  ]);

  useEffect(() => {
    if (cohortFocusRestoreRequest === 0 || completedCohortFocusRestore.current === cohortFocusRestoreRequest) {
      return;
    }
    const currentCohortIDs = new Set(filteredItems.map((item) => item.id));
    const firstVisibleItem = visibleItems.find((item) => currentCohortIDs.has(item.id));
    const firstVisibleTrigger = firstVisibleItem
      ? itemRowRefs.current.get(firstVisibleItem.id)
      : undefined;
    const target = firstVisibleTrigger ?? inventoryResultsRef.current;
    if (!target) {
      return;
    }
    target.focus({ preventScroll: true });
    completedCohortFocusRestore.current = cohortFocusRestoreRequest;
  }, [cohortFocusRestoreRequest, filteredItems, visibleItems]);

  useEffect(() => {
    if (isMobileDetail && !focus?.itemID) {
      setSelectedID(undefined);
    }
  }, [focus?.itemID, isMobileDetail]);

  async function addResearchPhotos(itemID: string, files: File[]) {
      const available = MAX_ITEM_PHOTOS - store.photosFor(itemID).length;
      if (files.length > available) throw new Error(`This item has room for ${available} more photos.`);
      const result = await readPhotoDraftBatch(files);
      if (result.drafts.length) await store.addItemPhotos(itemID, result.drafts.map(toPhotoInput));
      if (result.errors.length) throw new Error(photoDraftReadErrorMessage(result.errors));

  }
  const researchPhotoItem = researchPhotoItemID ? store.itemFor(researchPhotoItemID) : undefined;
  const researchPhotoTools = <>
    {researchPhotoError && <Alert tone="danger">{researchPhotoError}<Button onClick={() => setResearchPhotoError("")}>Dismiss</Button></Alert>}
    <FileInput aria-label="Add research photos" className="sr-only" tabIndex={-1} ref={researchPhotoInputRef} multiple accept="image/*,.heic,.heif" onChange={(event) => { const files = Array.from(event.target.files ?? []); event.target.value = ""; const id = researchPhotoUploadItemRef.current; if (id && files.length) void addResearchPhotos(id, files).catch((error) => setResearchPhotoError(error instanceof Error ? error.message : "Could not add photos.")); }} />
    {researchPhotoItem && store.photosFor(researchPhotoItem.id).length > 0 && <ItemPhotoReviewDialog item={researchPhotoItem} photos={store.photosFor(researchPhotoItem.id)} initialPhotoID={store.photosFor(researchPhotoItem.id)[0].id} onClose={() => setResearchPhotoItemID(null)} onReturnFocus={() => window.requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[aria-label="Manage item photos"]')?.focus())} onAddPhotos={() => { researchPhotoUploadItemRef.current = researchPhotoItem.id; setResearchPhotoItemID(null); researchPhotoInputRef.current?.click(); }} />}
  </>;
  const researchLaunch = researchLaunchIDs ? <ResearchLaunchDialog key={[...researchLaunchIDs].sort().join(",")} itemIDs={researchLaunchIDs} onAddPhotos={addResearchPhotos} onManagePhotos={setResearchPhotoItemID} onClose={() => setResearchLaunchIDs(null)} onStarted={(outcomes) => {
    setResearchLaunchIDs(null); setResearchBatchOutcomes(outcomes); setResearchItemID(outcomes.find((entry) => ["started", "already_started", "running"].includes(entry.status))?.itemID ?? null); setResearchOpen(true); onResearchRoute(outcomes.find((entry) => ["started", "already_started", "running"].includes(entry.status))?.itemID);
  }} /> : null;

  if (researchOpen) return <>
    {researchBatchOutcomes.some((entry) => !["started", "already_started", "running"].includes(entry.status)) && <Alert tone="warning" className="rw-batch-notice"><strong>Some items couldn’t start</strong><ul>{researchBatchOutcomes.filter((entry) => !["started", "already_started", "running"].includes(entry.status)).map((entry) => <li key={entry.itemID}>{store.itemFor(entry.itemID)?.title ?? "Item"}: {entry.message ?? (entry.status === "missing_photos" ? "Add a photo first." : entry.status === "capacity" ? "Wait for a research task to finish, then retry." : "Open the item and try again.")}</li>)}</ul><Button variant="ghost" onClick={() => setResearchBatchOutcomes([])}>Dismiss</Button></Alert>}
    <ResearchWorkspace initialItemID={researchItemID ?? undefined} onSelect={onResearchRoute} onClose={() => { setResearchOpen(false); setResearchItemID(null); onFiltersChange(filter, issueFilter, ageFilter, categoryFilter, marketplaceFilter, query); }} onResearch={(ids) => setResearchLaunchIDs(ids)} onAddPhotos={addResearchPhotos} onManagePhotos={setResearchPhotoItemID} />
    {researchLaunch}
    {researchPhotoTools}
  </>;

  if (photoImportIDs) return <InventoryPhotoImport itemIDs={photoImportIDs} onClose={() => { setPhotoImportIDs(null); window.requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-inventory-photo-import]')?.focus()); }} />;

  if (store.inventoryItems.length === 0) {
    return (
      <div className="screen-content workflow-empty-layout">
        <Toolbar title="Inventory" />
        <FirstRunPanel
          icon={<Package size={32} />}
          title="Add your first item"
          message="Keep its photos, cost, SKU, storage location, and marketplace listings together."
          actionLabel="Add item"
          onAction={onAdd}
        />
      </div>
    );
  }

  const appliedFilters = [
    issueFilter !== "all"
      ? {
          key: "attention",
          label: `Attention: ${inventoryIssueFilters.find((option) => option.key === issueFilter)?.label ?? issueFilter}`,
          onRemove: () => {
            setIssueFilter("all");
            onFiltersChange(filter, "all", ageFilter, categoryFilter, marketplaceFilter, query);
          }
        }
      : null,
    ageFilter !== "all"
      ? {
          key: "age",
          label: `Stock age: ${inventoryAgeFilters.find((option) => option.key === ageFilter)?.label ?? ageFilter}`,
          onRemove: () => {
            setAgeFilter("all");
            onFiltersChange(filter, issueFilter, "all", categoryFilter, marketplaceFilter, query);
          }
        }
      : null,
    categoryFilter !== "all"
      ? {
          key: "category",
          label: `Category: ${categoryFilter}`,
          onRemove: () => {
            setCategoryFilter("all");
            onFiltersChange(filter, issueFilter, ageFilter, "all", marketplaceFilter, query);
          }
        }
      : null,
    marketplaceFilter !== "all"
      ? {
          key: "marketplace",
          label: `Marketplace: ${marketplaceLabels[marketplaceFilter]}`,
          onRemove: () => {
            setMarketplaceFilter("all");
            onFiltersChange(filter, issueFilter, ageFilter, categoryFilter, "all", query);
          }
        }
      : null
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate));

  const inventoryView = issueFilter === "needs_attention" ? "attention" : filter;
  const inventoryViews = [
    { key: "in_stock", label: "In stock" },
    { key: "attention", label: "Needs attention" },
    { key: "sold", label: "Sold" },
    ...(!["in_stock", "attention", "sold"].includes(inventoryView) ? [{ key: inventoryView, label: inventoryFilters.find((option) => option.key === filter)?.label ?? "Filtered" }] : [])
  ];
  const activeFilterCount = appliedFilters.filter((entry) => entry.key !== "attention" || issueFilter !== "needs_attention").length;

  return (
    <div className="screen-content inventory-workspace">
      <header className="inventory-page-header">
        <h1>Inventory</h1>
        <div className="inventory-header-actions">
          <ToolbarSearch label="Search inventory" value={query} placeholder="Search inventory" onChange={(value) => { setQuery(value); onFiltersChange(filter, issueFilter, ageFilter, categoryFilter, marketplaceFilter, value); }} />
          <Button variant="secondary" onClick={onAdd}><Plus size={17} />Add item</Button>
        </div>
      </header>
      <div className="inventory-view-bar">
        <SegmentedControl className="inventory-view-tabs" label="Inventory views" value={inventoryView} options={inventoryViews} onChange={(value) => {
          const nextFilter = value === "attention" ? "all" : value as InventoryLifecycleFilterKey;
          const nextIssue = value === "attention" ? "needs_attention" : "all";
          setFilter(nextFilter); setIssueFilter(nextIssue);
          onFiltersChange(nextFilter, nextIssue, ageFilter, categoryFilter, marketplaceFilter, query);
        }} />
        <div className="inventory-list-tools">
          <Button variant="ghost" aria-label="Research" onClick={() => openResearch()}><ScanSearch size={17} /><span className="inventory-tool-label">Research</span></Button>
          <Button variant="secondary" aria-pressed={selectionMode} onClick={() => { setSelectionMode(!selectionMode); setSelectedItemIDs(new Set()); }}>{selectionMode ? "Cancel selection" : "Select items"}</Button>
          <Button variant="secondary" aria-expanded={filtersOpen} aria-controls="inventory-filters" onClick={() => setFiltersOpen(!filtersOpen)}><ListFilter size={17} />Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}</Button>
          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" aria-label="More inventory tools"><MoreHorizontal size={18} /></Button></DropdownMenuTrigger><DropdownMenuContent><DropdownMenuGroup><DropdownMenuItem onSelect={onImportSpreadsheet}>Import spreadsheet</DropdownMenuItem><DropdownMenuItem onSelect={onCompleteDetails}>Complete details from a spreadsheet</DropdownMenuItem></DropdownMenuGroup></DropdownMenuContent></DropdownMenu>
        </div>
      </div>
      {filtersOpen && <section className="inventory-filter-panel" id="inventory-filters" aria-label="Inventory filters">
        <ToolbarSelect label="Stock" value={filter} options={inventoryFilters} onChange={(value) => { setFilter(value); onFiltersChange(value, issueFilter, ageFilter, categoryFilter, marketplaceFilter, query); }} />
            <ToolbarSelect
              label="Attention"
              value={issueFilter}
              options={inventoryIssueFilters}
              onChange={(nextIssue) => {
                setIssueFilter(nextIssue);
                onFiltersChange(filter, nextIssue, ageFilter, categoryFilter, marketplaceFilter, query);
              }}
            />
            <ToolbarSelect
              label="Stock age"
              value={ageFilter}
              options={inventoryAgeFilters}
              onChange={(nextAge) => {
                setAgeFilter(nextAge);
                onFiltersChange(filter, issueFilter, nextAge, categoryFilter, marketplaceFilter, query);
              }}
            />
            <ToolbarSelect
              label="Category"
              value={categoryFilter}
              options={categoryOptions}
              onChange={(nextCategory) => {
                setCategoryFilter(nextCategory);
                onFiltersChange(filter, issueFilter, ageFilter, nextCategory, marketplaceFilter, query);
              }}
            />
            <ToolbarSelect
              label="Marketplace"
              value={marketplaceFilter}
              options={inventoryMarketplaceOptions}
              onChange={(nextMarketplace) => {
                setMarketplaceFilter(nextMarketplace);
                onFiltersChange(filter, issueFilter, ageFilter, categoryFilter, nextMarketplace, query);
              }}
            />

      </section>}
      <AppliedFilters filters={appliedFilters.filter((entry) => entry.key !== "attention" || issueFilter !== "needs_attention")} onClear={clearInventoryFilters} />
      <div className={`inventory-columns ${inventoryPage.total === 0 ? "is-empty" : ""}`}>
      <Panel as="section" className="inventory-list-surface" aria-label="Inventory items">
        <div className="inventory-column-head" aria-hidden="true"><span>Item</span><span>Listings</span><span>Target</span></div>
        <FocusRegion ref={inventoryResultsRef} className="inventory-results-region" aria-label="Inventory results">
          {selectionMode && <div className="workbench-bulk-controls">
            <span role="status">{selectedItemIDs.size} selected{[...selectedItemIDs].some((id) => !visibleItems.some((item) => item.id === id)) ? " including hidden items" : ""}</span>
            <Button size="compact" variant="ghost" onClick={() => setSelectedItemIDs((current) => new Set([...current, ...visibleItems.map((item) => item.id)].slice(0, MAX_MANUAL_LISTING_ITEMS)))}>Select shown</Button>
            <Button size="compact" variant="ghost" disabled={selectedItemIDs.size === 0} onClick={() => setSelectedItemIDs(new Set())}>Clear</Button>
            <Button size="compact" variant="secondary" disabled={selectedItemIDs.size === 0} onClick={() => setResearchLaunchIDs([...selectedItemIDs])}>Research selected</Button>
            {store.persistenceMode === "api" && <Button data-inventory-photo-import size="compact" variant="secondary" disabled={selectedItemIDs.size === 0 || selectedItemIDs.size > 50} onClick={() => setPhotoImportIDs([...selectedItemIDs])}>Import photos</Button>}
            {store.persistenceMode === "api" && selectedItemIDs.size > 50 && <small>Import photos for up to 50 items at a time.</small>}
            <Button size="compact" variant="primary" disabled={selectedItemIDs.size === 0} onClick={() => setManualListingOpen(true)}>Mark {selectedItemIDs.size || "selected"} {selectedItemIDs.size === 1 ? "item" : "items"} as listed</Button>
          </div>}
          {inventoryPage.total === 0 && !inventoryPage.loading && !inventoryPage.error ? (
            <EmptyState
              className="ui-empty-state-filtered"
              icon={<Package size={28} />}
              title={store.inventoryItems.length === 0 ? "No stock yet" : "No stock matches this view"}
              message={store.inventoryItems.length === 0 ? "Add an item or bring in your existing spreadsheet." : "Adjust the search, lifecycle, attention, category, or marketplace filters."}
              action={(
                store.inventoryItems.length === 0 ? <Button variant="secondary" onClick={onImportSpreadsheet}>Import spreadsheet</Button> : <Button variant="secondary" type="button" onClick={clearInventoryFilters}>
                  <X size={17} /> Clear filters
                </Button>
              )}
            />
          ) : (
            <div className="listing-list">
              {visibleItems.map((item) => (
                <div key={item.id} className={selectionMode ? "workbench-selectable-row" : undefined}>
                {selectionMode && <CheckboxField label={<span className="sr-only">Select {item.title}</span>} checked={selectedItemIDs.has(item.id)} disabled={!selectedItemIDs.has(item.id) && selectedItemIDs.size >= MAX_MANUAL_LISTING_ITEMS}
                  onChange={(event) => setSelectedItemIDs((current) => { const next = new Set(current); if (event.target.checked) next.add(item.id); else next.delete(item.id); return next; })} />}
                <InventoryRow
                  item={item}
                  selected={selected?.id === item.id}
                  rowRef={(node) => {
                    if (node) itemRowRefs.current.set(item.id, node);
                    else itemRowRefs.current.delete(item.id);
                  }}
                  onClick={() => selectItem(item.id)}
                />
                </div>
              ))}
            </div>
          )}
          <div className="inventory-load-more" ref={moreInventoryRef}>
            {inventoryPage.loading && <OperationStatus title={visibleItems.length ? "Loading items" : "Loading inventory"} />}
            {inventoryPage.error && <Alert tone="danger">{inventoryPage.error}<Button variant="secondary" onClick={inventoryPage.retry}>Try again</Button></Alert>}
            {!inventoryPage.loading && !inventoryPage.error && <span role="status">{visibleItems.length.toLocaleString("en-GB")} of {inventoryPage.total.toLocaleString("en-GB")} items</span>}
            {inventoryPage.hasNext && <Button variant="ghost" disabled={inventoryPage.loading} onClick={() => void inventoryPage.loadMore()}>Load more items</Button>}
          </div>
        </FocusRegion>
      </Panel>
      {!isMobileDetail && inventoryPage.total > 0 && (
        <section className="inventory-detail-surface">
          {selected ? (
            <InventoryDetail
              item={selected}
              onAdjustStock={() => setStockItemID(selected.id)}
              onMarkAsListed={() => setManualListingItemIDs([selected.id])}
              onPrepareListing={(accountID) => accountID ? setPrepareListing({ itemID: selected.id, accountID }) : setCreateListingItemID(selected.id)}
              focus={selectedFocus}
              onBack={clearItemSelection}
              onOpenListing={onOpenListing}
              onOpenOrder={onOpenOrder}
              onOpenResearch={() => openResearch(selected.id)}
              onRecordSale={() => onRecordSale(selected.id)}
            />
          ) : (
            <EmptyState icon={<Package size={28} />} title="Select an item" message="Review photos, cost, age, listing coverage, and stock actions." />
          )}
        </section>
      )}
      </div>
      <DetailSheet
        open={isMobileDetail && Boolean(selected) && !researchOpen && !researchLaunchIDs && !stockItemID && !manualListingItemIDs && !manualListingOpen && !prepareListing && !connectedDraftItemID}
        title="Inventory item details"
        covered={Boolean(createListingItemID)}
        onOpenChange={(open) => {
          if (!open) {
            clearItemSelection();
          }
        }}
        onReturnFocus={restoreInventoryListFocus}
      >
        {selected && (
          <InventoryDetail
            item={selected}
            onAdjustStock={() => setStockItemID(selected.id)}
            onMarkAsListed={() => setManualListingItemIDs([selected.id])}
            onPrepareListing={(accountID) => accountID ? setPrepareListing({ itemID: selected.id, accountID }) : setCreateListingItemID(selected.id)}
            focus={selectedFocus}
            onBack={clearItemSelection}
            onOpenListing={onOpenListing}
            onOpenOrder={onOpenOrder}
            onOpenResearch={() => openResearch(selected.id)}
            onRecordSale={() => {
              if (isMobileDetail) {
                setSelectedID(undefined);
              }
              onRecordSale(selected.id);
            }}
            />
          )}
      </DetailSheet>
      {researchLaunch}
    {researchPhotoTools}

      {(manualListingOpen || manualListingItemIDs) && <ManualListingDialog linkExisting={Boolean(manualListingItemIDs)} itemIDs={manualListingItemIDs ?? [...selectedItemIDs]} onClose={() => { setManualListingOpen(false); setManualListingItemIDs(null); }} onDone={(result) => {
        if (!manualListingOpen) return;
        const completed = new Set([...result.updatedItemIDs, ...result.alreadyListedItemIDs]);
        const remaining = new Set([...selectedItemIDs].filter((id) => !completed.has(id)));
        setSelectedItemIDs(remaining);
        setSelectionMode(remaining.size > 0);
      }} />}
      {createListingItemID && store.itemFor(createListingItemID) && <InventoryListingChannelDialog item={store.itemFor(createListingItemID)!} mobile={isMobileDetail} onClose={() => setCreateListingItemID(undefined)}
        onManual={(accountID) => { setPrepareListing({ itemID: createListingItemID, accountID }); setCreateListingItemID(undefined); }}
        onCreated={(listing) => { setCreateListingItemID(undefined); onOpenListing(listing.id, createListingItemID); }} />}
      {prepareListing && <ManualListingFlow initialItemIDs={[prepareListing.itemID]} initialAccountID={prepareListing.accountID} onClose={() => setPrepareListing(undefined)}
        onOtherChannels={store.marketplaceAccounts.some((account) => !isManualListingAccount(account)) ? () => { setConnectedDraftItemID(prepareListing.itemID); setPrepareListing(undefined); } : undefined} />}
      {connectedDraftItemID && store.itemFor(connectedDraftItemID) && <CreateListingDraftsDialog item={store.itemFor(connectedDraftItemID)!} existingListings={store.listingsFor(connectedDraftItemID)} onlyDirect onClose={() => setConnectedDraftItemID(undefined)} onCreated={(created) => { setConnectedDraftItemID(undefined); if (created[0]) onOpenListing(created[0].id, connectedDraftItemID); }} />}
      {stockItemID && <StockAdjustmentDialog itemID={stockItemID} onClose={() => setStockItemID(null)} />}
    </div>
  );
}

function ListingsScreen({
  focus,
  onAdd,
  onOpenInventory,
  onOpenItem,
  onRecordSale,
  onSelectListing,
  onFiltersChange,
  onSelectSale
}: {
  focus: Extract<NotificationFocus, { view: "listings" }> | null;
  onAdd: () => void;
  onOpenInventory: () => void;
  onOpenItem: (itemID: string) => void;
  onRecordSale: (preset: { itemID?: string; listingID?: string; marketplace?: Marketplace }) => void;
  onSelectListing: (listingID: string, itemID?: string, filter?: ListingFilterKey, marketplace?: MarketplaceFilterKey, attention?: ListingIssueFilterKey, query?: string) => void;
  onFiltersChange: (filter: ListingFilterKey, marketplace: MarketplaceFilterKey, attention: ListingIssueFilterKey, query: string) => void;
  onSelectSale: (saleID: string) => void;
}) {
  const store = useReOS();
  const [publishListingID, setPublishListingID] = useState<string | undefined>(() => new URLSearchParams(window.location.search).get("publish") ?? undefined);
  const publishingListing = store.listings.find((entry) => entry.id === publishListingID);
  const publishingAccount = store.marketplaceAccounts.find((entry) => entry.id === publishingListing?.marketplaceAccountID);
  const [editListingID, setEditListingID] = useState<string>();
  const editedListing = store.listings.find((entry) => entry.id === editListingID);
  const editedAccount = store.marketplaceAccounts.find((entry) => entry.id === editedListing?.marketplaceAccountID);
  const [query, setQuery] = useState(() => focus?.query ?? "");
  const [filter, setFilter] = useState<ListingFilterKey>(() => focus?.filter ?? "all");
  const [marketplaceFilter, setMarketplaceFilter] = useState<MarketplaceFilterKey>(() => focus?.marketplace ?? "all");
  const [issueFilter, setIssueFilter] = useState<ListingIssueFilterKey>(() => focus?.attention ?? "all");
  const [selectedID, setSelectedID] = useState<string | undefined>();
  const listingRowRefs = useRef(new Map<string, HTMLButtonElement>());
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedManualListingIDs, setSelectedManualListingIDs] = useState<Set<string>>(() => new Set());
  const [manualListingOpen, setManualListingOpen] = useState(false);
  const lastSelectedListingID = useRef<string | undefined>(focus?.listingID);
  const listingMarketplaceOptions = useMemo(
    () => marketplaceOptionsFor(store.listings.map((listing) => listing.marketplace)),
    [store.listings]
  );

  function listingMatchesCohort(
    listing: MarketplaceListing,
    nextFilter: ListingFilterKey,
    nextMarketplace: MarketplaceFilterKey,
    nextIssue: ListingIssueFilterKey,
    nextQuery: string
  ) {
    const item = store.itemFor(listing.itemID);
    const searchable = [connectedListingContent(listing).title, listing.title, item?.sku, item?.brand, item?.stockLocation, marketplaceLabels[listing.marketplace]]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchesQuery = !nextQuery.trim() || searchable.includes(nextQuery.trim().toLowerCase());
    const matchesMarketplace = nextMarketplace === "all" || listing.marketplace === nextMarketplace;
    const matchesIssue =
      nextIssue === "all" ||
      (nextIssue === "needs_action" && ["draft", "ready", "failed"].includes(listing.status)) ||
      (nextIssue === "aging" && Boolean(item && isAgingInventoryItem(item))) ||
      (nextIssue === "missing_photos" && Boolean(item && store.photosFor(item.id).length === 0));
    const matchesFilter =
      nextFilter === "all" ||
      (nextFilter === "live" && listing.status === "live") ||
      (nextFilter === "draft" && listing.status === "draft") ||
      (nextFilter === "ready" && listing.status === "ready") ||
      (nextFilter === "failed" && listing.status === "failed") ||
      (nextFilter === "sold" && listing.status === "sold") ||
      (nextFilter === "closed" && ["ended", "delisted", "archived"].includes(listing.status));
    return matchesQuery && matchesFilter && matchesMarketplace && matchesIssue;
  }

  const filteredListings = useMemo(() => {
    return store.listings
      .filter((listing) => listingMatchesCohort(listing, filter, marketplaceFilter, issueFilter, query))
      .sort(compareListingsForWorkPriority);
  }, [filter, issueFilter, marketplaceFilter, query, store]);
  const listingsPage = useWorkbenchCollection<MarketplaceListing>("listings", filteredListings, {
    q: query,
    status: filter,
    marketplace: marketplaceFilter,
    attention: issueFilter
  }, store.listings);
  const visibleListings = listingsPage.records;

  const isMobileDetail = useMediaQuery("(max-width: 899px)");
  const selectedFromID = selectedID ? filteredListings.find((listing) => listing.id === selectedID) : undefined;
  const selected = isMobileDetail
    ? focus?.listingID ? selectedFromID : undefined
    : selectedFromID ?? visibleListings[0];
  const selectedFocus = selected && focus && (focus.listingID ? focus.listingID === selected.id : focus.itemID === selected.itemID) ? focus : null;

  function selectListing(listing: MarketplaceListing) {
    lastSelectedListingID.current = listing.id;
    setSelectedID(listing.id);
    onSelectListing(listing.id, listing.itemID, filter, marketplaceFilter, issueFilter, query.trim() || undefined);
  }

  function clearListingSelection() {
    setSelectedID(undefined);
    onFiltersChange(filter, marketplaceFilter, issueFilter, query);
  }

  function clearListingFilters() {
    setQuery("");
    setFilter("all");
    setMarketplaceFilter("all");
    setIssueFilter("all");
    onFiltersChange("all", "all", "all", "");
  }

  useEffect(() => {
    if (!window.matchMedia("(min-width: 760px)").matches) {
      return;
    }

    if (visibleListings[0] && (!selectedID || !visibleListings.some((listing) => listing.id === selectedID))) {
      setSelectedID(visibleListings[0].id);
    }
  }, [selectedID, visibleListings]);

  useEffect(() => {
    if (!focus) {
      setQuery("");
      setFilter("all");
      setMarketplaceFilter("all");
      setIssueFilter("all");
      setSelectedID(undefined);
      return;
    }
    setQuery(focus.query ?? "");
    setFilter(focus.filter ?? "all");
    setMarketplaceFilter(focus.marketplace ?? "all");
    setIssueFilter(focus.attention ?? "all");
    const nextFilter = focus.filter ?? "all";
    const nextMarketplace = focus.marketplace ?? "all";
    const nextIssue = focus.attention ?? "all";
    const nextQuery = focus.query ?? "";
    const target = focus.listingID
      ? store.listings.find((listing) => listing.id === focus.listingID)
      : store.listings.find(
          (listing) =>
            listing.itemID === focus.itemID
            && listingMatchesCohort(listing, nextFilter, nextMarketplace, nextIssue, nextQuery)
        );
    const targetIsValid = Boolean(
      target && listingMatchesCohort(target, nextFilter, nextMarketplace, nextIssue, nextQuery)
    );
    if ((focus.listingID || focus.itemID) && !targetIsValid) {
      setSelectedID(undefined);
      onFiltersChange(nextFilter, nextMarketplace, nextIssue, nextQuery);
      return;
    }
    if (target) {
      lastSelectedListingID.current = target.id;
    }
    setSelectedID(target?.id);
  }, [
    focus?.listingID,
    focus?.itemID,
    focus?.filter,
    focus?.marketplace,
    focus?.attention,
    focus?.query,
    onFiltersChange,
    store
  ]);

  useEffect(() => {
    if (isMobileDetail && !focus?.listingID) {
      setSelectedID(undefined);
    }
  }, [focus?.listingID, isMobileDetail]);

  if (store.listings.length === 0) {
    const hasInventory = store.inventoryItems.length > 0;
    return (
      <div className="screen-content workflow-empty-layout">
        <Toolbar title="Listings" />
        <FirstRunPanel
          icon={<Tag size={32} />}
          title={hasInventory ? "Prepare your first listing" : "Add your first item"}
          message={hasInventory
            ? `You already have ${store.inventoryItems.length} inventory item${store.inventoryItems.length === 1 ? "" : "s"}. Choose one to review its photos, pricing, and marketplace drafts.`
            : "Choose its marketplaces when you save it. Listwork will prepare the listing drafts."}
          actionLabel={hasInventory ? "Choose inventory" : "Add item"}
          onAction={hasInventory ? onOpenInventory : onAdd}
        />
      </div>
    );
  }

  const appliedFilters = [
    marketplaceFilter !== "all"
      ? {
          key: "marketplace",
          label: `Marketplace: ${marketplaceLabels[marketplaceFilter]}`,
          onRemove: () => {
            setMarketplaceFilter("all");
            onFiltersChange(filter, "all", issueFilter, query);
          }
        }
      : null,
    issueFilter !== "all"
      ? {
          key: "attention",
          label: `Attention: ${listingIssueFilters.find((option) => option.key === issueFilter)?.label ?? issueFilter}`,
          onRemove: () => {
            setIssueFilter("all");
            onFiltersChange(filter, marketplaceFilter, "all", query);
          }
        }
      : null
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate));

  return (
    <div className={`screen-content workbench listings-workbench ${listingsPage.total === 0 ? "workbench-filtered-empty" : ""} ${selected && !isMobileDetail ? "has-detail" : ""}`}>
      <Panel as="section" className="ui-panel-workbench-list">
        <Toolbar title="Listings">
          <ToolbarSearch
            label="Search marketplace listings"
            value={query}
            onChange={(nextQuery) => {
              setQuery(nextQuery);
              onFiltersChange(filter, marketplaceFilter, issueFilter, nextQuery);
            }}
            placeholder="Search listings"
          />
          <SegmentedControl
            label="Listing status filter"
            value={filter}
            options={listingFilters}
            onChange={(nextFilter) => {
              setFilter(nextFilter);
              onFiltersChange(nextFilter, marketplaceFilter, issueFilter, query);
            }}
          />
          <ToolbarFilters activeCount={Number(marketplaceFilter !== "all") + Number(issueFilter !== "all")}>
            <ToolbarSelect
              label="Marketplace"
              value={marketplaceFilter}
              options={listingMarketplaceOptions}
              onChange={(nextMarketplace) => {
                setMarketplaceFilter(nextMarketplace);
                onFiltersChange(filter, nextMarketplace, issueFilter, query);
              }}
            />
            <ToolbarSelect
              label="Attention"
              value={issueFilter}
              options={listingIssueFilters}
              onChange={(nextIssue) => {
                setIssueFilter(nextIssue);
                onFiltersChange(filter, marketplaceFilter, nextIssue, query);
              }}
            />
          </ToolbarFilters>
          <AppliedFilters
            filters={appliedFilters}
            onClear={clearListingFilters}
          />
        </Toolbar>
        <div className="workbench-bulk-controls">
          <Button size="compact" variant="secondary" onClick={() => { setSelectionMode(!selectionMode); setSelectedManualListingIDs(new Set()); }}>{selectionMode ? "Cancel selection" : "Select listings"}</Button>
          {selectionMode && <>
            <span role="status">{selectedManualListingIDs.size} selected</span>
            <Button size="compact" variant="ghost" onClick={() => setSelectedManualListingIDs((current) => new Set([...current, ...visibleListings.map((listing) => listing.id)].slice(0, MAX_MANUAL_LISTING_ITEMS)))}>Select this page</Button>
            <Button size="compact" variant="primary" disabled={selectedManualListingIDs.size === 0} onClick={() => setManualListingOpen(true)}>Mark as listed</Button>
          </>}
        </div>
        {manualListingOpen && <ManualListingDialog itemIDs={[...new Set(store.listings.filter((listing) => selectedManualListingIDs.has(listing.id)).map((listing) => listing.itemID))]}
          initialAccountID={store.listings.find((listing) => selectedManualListingIDs.has(listing.id))?.marketplaceAccountID}
          onClose={() => setManualListingOpen(false)} onDone={() => { setSelectedManualListingIDs(new Set()); setSelectionMode(false); }} />}
        {listingsPage.total === 0 && !listingsPage.loading && !listingsPage.error ? (
          <EmptyState
            className="ui-empty-state-filtered"
            icon={<Tag size={28} />}
            title="No listings match these filters"
            message="Clear the filters to return to all marketplace listings."
            action={(
              <Button variant="secondary" type="button" onClick={clearListingFilters}>
                <X size={17} /> Clear filters
              </Button>
            )}
          />
        ) : (
          <div className="listing-list">
            {visibleListings.map((listing) => (
              <div key={listing.id} className={selectionMode ? "workbench-selectable-row" : undefined}>
              {selectionMode && <CheckboxField label={<span className="sr-only">Select {listing.title} on {marketplaceLabels[listing.marketplace]}</span>} checked={selectedManualListingIDs.has(listing.id)} disabled={!selectedManualListingIDs.has(listing.id) && selectedManualListingIDs.size >= MAX_MANUAL_LISTING_ITEMS}
                onChange={(event) => setSelectedManualListingIDs((current) => { const next = new Set(current); if (event.target.checked) next.add(listing.id); else next.delete(listing.id); return next; })} />}
              <ListingRow
                listing={listing}
                selected={selected?.id === listing.id}
                rowRef={(node) => {
                  if (node) listingRowRefs.current.set(listing.id, node);
                  else listingRowRefs.current.delete(listing.id);
                }}
                onClick={() => selectListing(listing)}
              />
              </div>
            ))}
            <WorkbenchPagination page={listingsPage} />
          </div>
        )}
      </Panel>
      {!isMobileDetail && listingsPage.total > 0 && (
        <Panel as="section" className="ui-panel-workbench-detail">
          {selected ? (
            <ListingDetail
              listing={selected}
              onEditConnected={() => setEditListingID(selected.id)}
              onPublishEbay={() => setPublishListingID(selected.id)}
              focus={selectedFocus}
              onBack={clearListingSelection}
              onOpenItem={onOpenItem}
              onOpenOrder={onSelectSale}
              onRecordSale={() => onRecordSale({ itemID: selected.itemID, listingID: selected.id, marketplace: selected.marketplace })}
            />
          ) : (
            <EmptyState icon={<Tag size={28} />} title="Select a listing" message="Review marketplace copy, URLs, photos, and listing status." />
          )}
        </Panel>
      )}
      <DetailSheet
        open={isMobileDetail && Boolean(selected) && !editedListing && !publishingListing}
        title={selected?.managementMode === "read_only" ? `${marketplaceLabels[selected.marketplace]} listing` : "Listing details"}
        onOpenChange={(open) => {
          if (!open) {
            clearListingSelection();
          }
        }}
        onReturnFocus={() => {
          const trigger = lastSelectedListingID.current
            ? listingRowRefs.current.get(lastSelectedListingID.current)
            : undefined;
          window.requestAnimationFrame(() => trigger?.focus());
        }}
      >
        {selected && (
          <ListingDetail
            listing={selected}
            onEditConnected={() => setEditListingID(selected.id)}
              onPublishEbay={() => setPublishListingID(selected.id)}
            focus={selectedFocus}
            onBack={clearListingSelection}
            onOpenItem={(itemID) => {
              setSelectedID(undefined);
              onOpenItem(itemID);
            }}
            onOpenOrder={(saleID) => {
              setSelectedID(undefined);
              onSelectSale(saleID);
            }}
            onRecordSale={() => {
              if (isMobileDetail) {
                setSelectedID(undefined);
              }
              onRecordSale({ itemID: selected.itemID, listingID: selected.id, marketplace: selected.marketplace });
            }}
          />
        )}
      </DetailSheet>
      {publishingListing && publishingAccount && <EbayPublicationDialog key={`${publishingListing.id}:${publishingAccount.id}:${publishingAccount.generation}:${publishingAccount.externalAccountID}`} listing={publishingListing} account={publishingAccount}
        onClose={() => { setPublishListingID(undefined); const url = new URL(window.location.href); url.searchParams.delete("publish"); window.history.replaceState(window.history.state, "", url); requestAnimationFrame(() => document.querySelector<HTMLButtonElement>("[data-ebay-publication]")?.focus()); }} />}
      {editedListing && editedAccount && <EbayListingEditor key={editedListing.id} listing={editedListing} account={editedAccount}
        onClose={() => { setEditListingID(undefined); requestAnimationFrame(() => document.querySelector<HTMLButtonElement>("[data-connected-listing-edit]")?.focus()); }} />}
    </div>
  );
}

function compareListingsForWorkPriority(left: MarketplaceListing, right: MarketplaceListing) {
  const priorityDifference = listingStatusWorkPriority[left.status] - listingStatusWorkPriority[right.status];
  if (priorityDifference !== 0) {
    return priorityDifference;
  }
  return Date.parse(right.updatedAt) - Date.parse(left.updatedAt);
}

function OrdersScreen({
  focus,
  onAdd,
  onRecordSale,
  onSelectSale,
  onFiltersChange
}: {
  focus: Extract<NotificationFocus, { view: "orders" }> | null;
  onAdd: () => void;
  onRecordSale: (preset: { itemID?: string; listingID?: string; marketplace?: Marketplace }) => void;
  onSelectSale: (saleID: string, filter?: OrderFilterKey, marketplace?: MarketplaceFilterKey, query?: string) => void;
  onFiltersChange: (filter: OrderFilterKey, marketplace: MarketplaceFilterKey, query: string) => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const [query, setQuery] = useState(() => focus?.query ?? "");
  const [filter, setFilter] = useState<OrderFilterKey>(() => focus?.filter ?? "open");
  const [marketplaceFilter, setMarketplaceFilter] = useState<MarketplaceFilterKey>(() => focus?.marketplace ?? "all");
  const [selectedID, setSelectedID] = useState<string | undefined>();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedSaleIDs, setSelectedSaleIDs] = useState<Set<string>>(() => new Set());
  const [bulkPacking, setBulkPacking] = useState(false);
  const saleRowRefs = useRef(new Map<string, HTMLButtonElement>());
  const lastSelectedSaleID = useRef<string | undefined>(focus?.saleID);
  const orderMarketplaceOptions = useMemo(
    () => marketplaceOptionsFor(store.sales.map((sale) => sale.marketplace)),
    [store.sales]
  );
  const filteredSales = useMemo(
    () =>
      store.sales
        .filter((sale) => {
          const item = store.itemFor(sale.itemID);
          const searchable = [item?.title, item?.sku, sale.buyerName, sale.buyerPostcode, marketplaceLabels[sale.marketplace]]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          const matchesQuery = !query.trim() || searchable.includes(query.trim().toLowerCase());
          const matchesMarketplace = marketplaceFilter === "all" || sale.marketplace === marketplaceFilter;
          const matchesFilter =
            filter === "all" ||
            (filter === "open" && saleIsOpen(sale)) ||
            (filter === "to_pack" && saleNeedsPacking(sale)) ||
            (filter === "to_ship" && sale.status === "packed") ||
            (filter === "in_transit" && sale.status === "shipped") ||
            (filter === "completed" && sale.status === "completed") ||
            (filter === "exceptions" && ["cancelled", "refunded"].includes(sale.status));
          return matchesQuery && matchesMarketplace && matchesFilter;
        })
        .sort(compareSalesForFulfilment),
    [filter, marketplaceFilter, query, store]
  );
  const ordersPage = useWorkbenchCollection<Sale>("orders", filteredSales, {
    q: query,
    status: filter,
    marketplace: marketplaceFilter
  }, store.sales);
  const visibleSales = ordersPage.records;
  const hasInventory = store.inventoryItems.some((item) => item.status !== "archived");
  const isMobileDetail = useMediaQuery("(max-width: 899px)");
  const selectedFromID = selectedID ? filteredSales.find((sale) => sale.id === selectedID) : undefined;
  const selected = isMobileDetail
    ? focus?.saleID ? selectedFromID : undefined
    : selectedFromID ?? visibleSales[0];
  const selectedNotice = selected?.id === focus?.saleID ? focus?.notice : undefined;
  const emptyCopy = orderEmptyStateCopy(filter);

  function selectSale(saleID: string) {
    lastSelectedSaleID.current = saleID;
    setSelectedID(saleID);
    onSelectSale(saleID, filter, marketplaceFilter, query);
  }

  function clearSaleSelection() {
    setSelectedID(undefined);
    onFiltersChange(filter, marketplaceFilter, query);
  }

  function clearOrderFilters() {
    setQuery("");
    setFilter("all");
    setMarketplaceFilter("all");
    onFiltersChange("all", "all", "");
  }

  function toggleSaleSelection(saleID: string) {
    setSelectedSaleIDs((current) => {
      const next = new Set(current);
      if (next.has(saleID)) next.delete(saleID);
      else next.add(saleID);
      return next;
    });
  }

  async function packSelectedSales() {
    if (selectedSaleIDs.size === 0) return;
    try {
      setBulkPacking(true);
      const result = await store.bulkPackSales([...selectedSaleIDs]);
      setSelectedSaleIDs(new Set(result.skipped.map((item) => item.saleID)));
      toast.notify({
        title: result.updatedSaleIDs.length === 1 ? "1 order packed" : `${result.updatedSaleIDs.length} orders packed`,
        description: result.skipped.length ? `${result.skipped.length} skipped because delivery details or status need review.` : undefined,
        tone: result.skipped.length ? "warning" : "success"
      });
    } catch (caught) {
      toast.notify({ title: "Orders could not be packed", description: userSafeErrorMessage(caught, "Review the selected orders and try again."), tone: "danger" });
    } finally {
      setBulkPacking(false);
    }
  }

  useEffect(() => {
    if (!window.matchMedia("(min-width: 760px)").matches) {
      return;
    }
    if (!selectedID || !visibleSales.some((sale) => sale.id === selectedID)) {
      setSelectedID(visibleSales[0]?.id);
    }
  }, [selectedID, visibleSales]);

  useEffect(() => {
    if (!focus) {
      setQuery("");
      setFilter("open");
      setMarketplaceFilter("all");
      setSelectedID(undefined);
      return;
    }
    const nextQuery = focus.query ?? "";
    const nextFilter = focus.filter ?? "all";
    const nextMarketplace = focus.marketplace ?? "all";
    setQuery(nextQuery);
    setFilter(nextFilter);
    setMarketplaceFilter(nextMarketplace);
    if (focus.saleID && store.sales.some((sale) => sale.id === focus.saleID)) {
      lastSelectedSaleID.current = focus.saleID;
      setSelectedID(focus.saleID);
      return;
    }
    setSelectedID(undefined);
    if (focus.saleID) {
      onFiltersChange(nextFilter, nextMarketplace, nextQuery);
    }
  }, [focus?.saleID, focus?.filter, focus?.marketplace, focus?.query, onFiltersChange, store.sales]);

  useEffect(() => {
    if (isMobileDetail && !focus?.saleID) {
      setSelectedID(undefined);
    }
  }, [focus?.saleID, isMobileDetail]);

  if (store.sales.length === 0) {
    return (
      <div className="screen-content workflow-empty-layout">
        <Toolbar title="Orders and sales">
        </Toolbar>
        <FirstRunPanel
          icon={<CreditCard size={32} />}
          title={hasInventory ? "Record the first sale" : "Add inventory before sales"}
          message={
            hasInventory
              ? "Close the matching listing, capture fees and shipping, and keep profit current from one sale form."
              : "Sales need a stock record first so Listwork can match the order, deduct costs, and calculate profit."
          }
          actionLabel={hasInventory ? "Record sale" : "Add item"}
          onAction={hasInventory ? () => onRecordSale({}) : onAdd}
        />
      </div>
    );
  }

  return (
    <div className={`screen-content workbench orders-workbench ${ordersPage.total === 0 ? "workbench-filtered-empty" : ""} ${selected && !isMobileDetail ? "has-detail" : ""}`}>
      <Panel as="section" className="ui-panel-workbench-list">
        <Toolbar title="Orders and sales" actionLabel="Record sale" onAction={() => onRecordSale({})}>
          <ToolbarSearch
            label="Search orders"
            value={query}
            onChange={(nextQuery) => {
              setQuery(nextQuery);
              onFiltersChange(filter, marketplaceFilter, nextQuery);
            }}
            placeholder="Search orders"
          />
          <SegmentedControl
            className="orders-status-filter"
            label="Order status"
            value={filter}
            options={orderFilters}
            onChange={(nextFilter) => {
              setFilter(nextFilter);
              onFiltersChange(nextFilter, marketplaceFilter, query);
            }}
          />
          <ToolbarFilters activeCount={Number(marketplaceFilter !== "all")}>
            <ToolbarSelect
              label="Marketplace"
              value={marketplaceFilter}
              options={orderMarketplaceOptions}
              onChange={(nextMarketplace) => {
                setMarketplaceFilter(nextMarketplace);
                onFiltersChange(filter, nextMarketplace, query);
              }}
            />
          </ToolbarFilters>
          <AppliedFilters
            filters={marketplaceFilter !== "all"
              ? [{
                  key: "marketplace",
                  label: `Marketplace: ${marketplaceLabels[marketplaceFilter]}`,
                  onRemove: () => {
                    setMarketplaceFilter("all");
                    onFiltersChange(filter, "all", query);
                  }
                }]
              : []}
          />
          <div className="workbench-bulk-controls" aria-live="polite">
            <Button
              variant="secondary"
              size="compact"
              type="button"
              onClick={() => {
                setSelectionMode((current) => !current);
                setSelectedSaleIDs(new Set());
              }}
            >
              {selectionMode ? "Finish selecting" : "Select orders"}
            </Button>
            {selectionMode ? (
              <>
                <Button
                  variant="ghost"
                  size="compact"
                  type="button"
                  onClick={() => setSelectedSaleIDs(new Set(visibleSales.map((sale) => sale.id)))}
                >
                  Select this page
                </Button>
                <span>{selectedSaleIDs.size} selected</span>
                <Button variant="primary" size="compact" type="button" onClick={() => void packSelectedSales()} disabled={selectedSaleIDs.size === 0 || bulkPacking} loading={bulkPacking} loadingLabel="Packing">
                  {bulkPacking ? "Packing" : "Mark packed"}
                </Button>
              </>
            ) : null}
          </div>
        </Toolbar>
        {ordersPage.total === 0 && !ordersPage.loading && !ordersPage.error ? (
          <EmptyState
            className="ui-empty-state-filtered"
            icon={<CreditCard size={28} />}
            title={emptyCopy.title}
            message={emptyCopy.message}
            action={(
              <Button variant="secondary" type="button" onClick={clearOrderFilters}>
                <X size={17} /> Clear filters
              </Button>
            )}
          />
        ) : (
          <div className="listing-list">
            {visibleSales.map((sale) => (
              <SaleRow
                key={sale.id}
                sale={sale}
                selected={sale.id === selected?.id}
                selectionMode={selectionMode}
                bulkSelected={selectedSaleIDs.has(sale.id)}
                rowRef={(node) => {
                  if (node) saleRowRefs.current.set(sale.id, node);
                  else saleRowRefs.current.delete(sale.id);
                }}
                onClick={() => selectionMode ? toggleSaleSelection(sale.id) : selectSale(sale.id)}
              />
            ))}
            <WorkbenchPagination page={ordersPage} />
          </div>
        )}
      </Panel>
      {!isMobileDetail && (
        <Panel as="section" className="ui-panel-workbench-detail">
          {selected ? <SaleDetail sale={selected} notice={selectedNotice} onBack={clearSaleSelection} /> : <EmptyState icon={<CreditCard size={28} />} title="Select a sale" message="Review fulfilment and profit breakdown." />}
        </Panel>
      )}
      <DetailSheet
        open={isMobileDetail && Boolean(selected)}
        title="Order details"
        onOpenChange={(open) => {
          if (!open) {
            clearSaleSelection();
          }
        }}
        onReturnFocus={() => {
          const trigger = lastSelectedSaleID.current
            ? saleRowRefs.current.get(lastSelectedSaleID.current)
            : undefined;
          window.requestAnimationFrame(() => trigger?.focus());
        }}
      >
        {selected && <SaleDetail sale={selected} notice={selectedNotice} onBack={clearSaleSelection} />}
      </DetailSheet>
    </div>
  );
}

function AnalyticsScreen({
  onAdd,
  onRecordSale,
  onReviewAgingStock,
  onReviewMarketplace
}: {
  onAdd: () => void;
  onRecordSale: () => void;
  onReviewAgingStock: (itemID?: string, age?: InventoryAgeFilterKey) => void;
  onReviewMarketplace: (marketplace: Marketplace) => void;
}) {
  const store = useReOS();
  const [rangeDays, setRangeDays] = useState<AnalyticsRangeDays>(() => readAnalyticsRangeDays());
  const summary = store.analyticsSummary(store.database, rangeDays);
  const profit = store.profitOverTime(store.database, rangeDays);
  const chronologicalProfit = [...profit].sort(
    (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime()
  );
  const rangeSales = store.sales.filter((sale) => isRecognizedSale(sale) && isWithinDashboardRange(sale.soldAt, rangeDays));
  const excludedRangeSales = store.sales.filter(
    (sale) => !isRecognizedSale(sale) && isWithinDashboardRange(sale.soldAt, rangeDays)
  );
  const hasProfitActivity = rangeSales.length > 0;
  const ageBuckets = store.stockAgeBuckets(store.database);
  const platformRows = store.platformPerformance(store.database, rangeDays);
  const activePlatformRows = platformRows.filter((platform) => platform.soldCount > 0 || platform.liveListingCount > 0);
  const stockRecordCount = store.inventoryItems.length;
  const recognizedSaleItemIDs = new Set(store.sales.filter(isRecognizedSale).map((sale) => sale.itemID));
  const exceptionSaleItemIDs = new Set(store.sales.filter((sale) => !isRecognizedSale(sale)).map((sale) => sale.itemID));
  const soldStockRecordCount = store.inventoryItems.filter(
    (item) => item.status === "sold" && (!exceptionSaleItemIDs.has(item.id) || recognizedSaleItemIDs.has(item.id))
  ).length;
  const hasRecognizedSales = store.sales.some(isRecognizedSale);
  const hasAnalyticsData = hasRecognizedSales || stockRecordCount > 0 || store.listings.length > 0 || store.sales.length > 0;
  const canRecordSale = store.inventoryItems.some((item) => item.status !== "archived") || store.listings.length > 0;
  const profitStart = chronologicalProfit[0]?.date;
  const profitEnd = chronologicalProfit[chronologicalProfit.length - 1]?.date;
  const profitSummary = profitChartSummary(chronologicalProfit, rangeSales.length);
  const stockAgeSummary = stockAgeChartSummary(ageBuckets);
  const agedStockItems = store.inventoryItems
    .filter(isAgingInventoryItem)
    .sort((left, right) => stockAgeDays(right) - stockAgeDays(left));
  const agedStockCount = agedStockItems.length;
  const agedStockValue = {
    amountMinor: agedStockItems.reduce((total, item) => total + item.targetPrice.amountMinor, 0),
    currency: agedStockItems[0]?.targetPrice.currency ?? summary.revenue.currency
  };
  const oldestAgedStockDays = agedStockItems[0] ? stockAgeDays(agedStockItems[0]) : 0;
  const agedLiveListingCount = agedStockItems.reduce(
    (total, item) => total + store.listingsFor(item.id).filter((listing) => listing.status === "live").length,
    0
  );
  const netMargin = summary.revenue.amountMinor === 0 ? 0 : summary.netProfit.amountMinor / summary.revenue.amountMinor;
  const profitAxisFormatter = useMemo(
    () => new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: summary.netProfit.currency,
      notation: "compact",
      maximumFractionDigits: 1
    }),
    [summary.netProfit.currency]
  );
  const leadingPlatform = activePlatformRows.reduce<(typeof activePlatformRows)[number] | undefined>((leader, platform) => {
    if (!leader) {
      return platform;
    }
    if (platform.netProfit.amountMinor !== leader.netProfit.amountMinor) {
      return platform.netProfit.amountMinor > leader.netProfit.amountMinor ? platform : leader;
    }
    if (platform.soldCount !== leader.soldCount) {
      return platform.soldCount > leader.soldCount ? platform : leader;
    }
    return platform.liveListingCount > leader.liveListingCount ? platform : leader;
  }, undefined);

  useEffect(() => {
    saveAnalyticsRangeDays(rangeDays);
    const url = new URL(window.location.href);
    if (url.searchParams.has("days")) {
      url.searchParams.set("days", String(rangeDays));
      window.history.replaceState(window.history.state, "", url);
    }
  }, [rangeDays]);

  return (
    <div className={`screen-content ${hasAnalyticsData ? "analytics-layout" : "workflow-empty-layout"}`}>
      <section className="dashboard-header">
        <h1>Analytics</h1>
        {(hasRecognizedSales || store.sales.length > 0) && (
          <label className="dashboard-range-control">
            <Clock3 size={18} />
            <SelectControl
              className="ui-select-range"
              value={String(rangeDays)}
              aria-label="Analytics date range"
              onValueChange={(value) => setRangeDays(Number(value) as AnalyticsRangeDays)}
              options={[
                { value: "7", label: "7 days" },
                { value: "30", label: "30 days" },
                { value: "90", label: "90 days" },
                { value: "365", label: "1 year" },
                { value: "730", label: "2 years" }
              ]}
            />
          </label>
        )}
      </section>
      {!hasAnalyticsData ? (
        <FirstRunPanel
          icon={<LineChart size={32} />}
          title="Reporting starts after a sale"
          message={
            canRecordSale
              ? "Revenue, fees, profit, sell-through, and marketplace results populate from recorded sales."
              : "Add inventory first, then record sales to build profit and marketplace reports."
          }
          actionLabel={canRecordSale ? "Record sale" : "Add item"}
          onAction={canRecordSale ? onRecordSale : onAdd}
        />
      ) : (
        <>
      <div className="analytics-summary-groups">
        <section className="analytics-summary-section" aria-labelledby="analytics-period-heading">
          <h2 id="analytics-period-heading">Period performance</h2>
          {rangeSales.length > 0 ? (
            <MetricGroup className="analytics-summary analytics-summary-period" aria-label={`Period performance for the last ${rangeDays} days`}>
              <Metric title="Revenue" value={formatMoney(summary.revenue)} footnote={`${orderCountLabel(rangeSales.length)} · includes shipping charged`} icon={<ShoppingBag size={16} />} tone="info" />
              <Metric title="Net profit" value={formatMoney(summary.netProfit)} footnote={rangeSales.some((sale) => saleProfitIsProvisional(sale)) ? `Provisional · ${rangeSales.filter((sale) => saleProfitIsProvisional(sale)).length} ${rangeSales.filter((sale) => saleProfitIsProvisional(sale)).length === 1 ? "order has incomplete costs" : "orders have incomplete costs"}` : "After recorded costs"} icon={<LineChart size={16} />} tone="success" />
              <Metric title="Margin" value={formatPercent(netMargin)} footnote={rangeSales.some((sale) => saleProfitIsProvisional(sale)) ? "Provisional · costs incomplete" : "Of order revenue"} icon={<BarChart3 size={16} />} tone="accent" />
            </MetricGroup>
          ) : (
            <Card className="analytics-period-empty-card" size="compact">
              <CardContent>
                <EmptyState
                  as="h3"
                  compact
                  layout="inline"
                  icon={<LineChart size={22} />}
                  title={excludedRangeSales.length > 0 ? "No recognized sales in this period" : "No completed sales in this period"}
                  message={
                    excludedRangeSales.length > 0
                      ? `${orderCountLabel(excludedRangeSales.length)} cancelled or refunded and excluded from financial results.`
                      : hasRecognizedSales
                        ? "Choose a longer date range to review earlier orders."
                        : "Record a completed sale to start period reporting."
                  }
                  action={canRecordSale ? (
                    <Button variant="secondary" size="compact" type="button" onClick={onRecordSale}>
                      Record sale
                    </Button>
                  ) : undefined}
                />
              </CardContent>
            </Card>
          )}
        </section>
        <section className="analytics-summary-section analytics-summary-section-current" aria-labelledby="analytics-catalogue-heading">
          <h2 id="analytics-catalogue-heading">Current catalogue</h2>
          <MetricGroup className="analytics-summary analytics-summary-current" aria-label="Current catalogue">
            <Metric title="Sell-through" value={formatPercent(summary.sellThroughRate)} footnote={stockRecordCountLabel(stockRecordCount)} icon={<Package size={16} />} tone="warning" />
          </MetricGroup>
        </section>
      </div>
      {agedStockCount > 0 ? (
        <Card className="analytics-aging-card" size="compact">
          <CardHeader>
            <div className="analytics-aging-heading">
              <span className="ui-data-row-media-analytics-attention" aria-hidden="true">
                <AlertTriangle size={18} />
              </span>
              <div>
                <CardTitle as="h2">Stock requiring review</CardTitle>
                <CardDescription>{agedStockCount} {agedStockCount === 1 ? "item is" : "items are"} past 60 days</CardDescription>
              </div>
            </div>
            <CardAction>
              <Button variant="secondary" size="compact" type="button" onClick={() => onReviewAgingStock()}>
                <ListFilter size={15} /> Review all
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            <dl className="analytics-aging-facts" aria-label="Aging stock summary">
              <div>
                <dt>Target total</dt>
                <dd>{formatMoney(agedStockValue)}</dd>
              </div>
              <div>
                <dt>Oldest item</dt>
                <dd>{oldestAgedStockDays} days</dd>
              </div>
              <div>
                <dt>Live listings</dt>
                <dd>{agedLiveListingCount} {agedLiveListingCount === 1 ? "listing" : "listings"}</dd>
              </div>
            </dl>
            <div className="analytics-aging-queue">
              <div className="analytics-aging-queue-heading">
                <h3>Oldest first</h3>
                <span>{agedStockCount} {agedStockCount === 1 ? "item" : "items"}</span>
              </div>
              <div className="analytics-aging-list" aria-label="Stock requiring review">
                {agedStockItems.slice(0, 3).map((item) => {
                  const photo = store.primaryPhoto(item.id);
                  const liveListings = store.listingsFor(item.id).filter((listing) => listing.status === "live");
                  const liveMarketplaces = Array.from(new Set(liveListings.map((listing) => listing.marketplace)));
                  return (
                    <DataRow
                      responsive="inline"
                      className="ui-data-row-analytics-aging"
                      key={item.id}
                      onClick={() => onReviewAgingStock(item.id)}
                    >
                      <DataRowMedia className="ui-data-row-media-activity-photo">
                        {photo
                          ? <img src={photo.thumbnailURL || photo.url} alt="" style={photoStyle(photo.treatment, "fit")} />
                          : <Package size={18} aria-hidden="true" />}
                      </DataRowMedia>
                      <DataRowMain>
                        <strong>{item.title}</strong>
                        <DataRowMeta>{item.sku}</DataRowMeta>
                        <DataRowBadges>
                          <Badge tone="warning">{stockAgeDays(item)} days</Badge>
                          {liveListings.length > 0 ? (
                            <span
                              className="analytics-aging-marketplaces"
                              aria-label={`Live on ${liveMarketplaces.map((marketplace) => marketplaceLabels[marketplace]).join(", ")}`}
                            >
                              <span className="analytics-aging-marketplace-marks" aria-hidden="true">
                                {liveMarketplaces.map((marketplace) => (
                                  <MarketplaceMark key={marketplace} marketplace={marketplace} />
                                ))}
                              </span>
                              {liveListings.length} live
                            </span>
                          ) : (
                            <span className="analytics-aging-unlisted">Not live</span>
                          )}
                        </DataRowBadges>
                      </DataRowMain>
                      <DataRowValue className="ui-data-row-value-analytics-aging">
                        <span>
                          <strong>{formatMoney(item.targetPrice)}</strong>
                          <small>Target</small>
                        </span>
                        <ChevronRight size={17} aria-hidden="true" />
                      </DataRowValue>
                    </DataRow>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
      <section className="analytics-grid">
        {hasProfitActivity ? (
          <Card className="analytics-chart-card">
            <CardHeader>
              <CardTitle as="h2">Net profit over time</CardTitle>
              <CardDescription>{profitSummary}</CardDescription>
            </CardHeader>
            <CardContent>
              <>
              <TrendChart
                title="Net profit over time"
                points={chronologicalProfit.map((point) => point.netProfit.amountMinor / 100)}
                labels={chronologicalProfit.map((point) => formatOrderDate(point.date))}
                valueLabels={chronologicalProfit.map((point) => formatMoney(point.netProfit))}
                axisValueLabel={(value) => profitAxisFormatter.format(value)}
                description={profitSummary}
              />
              {profitStart && profitEnd ? (
                <ChartAxis start={formatOrderDate(profitStart)} end={formatOrderDate(profitEnd)} />
              ) : null}
              </>
            </CardContent>
          </Card>
        ) : null}
        <Card className="analytics-chart-card">
          <CardHeader>
            <CardTitle as="h2">Stock age</CardTitle>
            <CardDescription>{store.inventoryItems.some((item) => !["sold", "archived"].includes(item.status) && inventoryAge(item)) ? `${Math.round(summary.averageAgeOfStockDays)} day average across dated items. ` : "Stock age is unknown. "}{stockAgeSummary} {store.inventoryItems.filter((item) => !["sold", "archived"].includes(item.status) && !inventoryAge(item)).length} items without dates excluded. Original listing dates provide a minimum age.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="analytics-bucket-chart-stage">
              <BucketChart
                ariaLabel="Stock age buckets"
                rows={ageBuckets.map((bucket) => {
                  const age = inventoryAgeFilterForBucket(bucket);
                  return {
                    label: bucket.label,
                    value: bucket.count,
                    tone: bucket.lowerBoundDays >= 61 ? "attention" : "default",
                    actionLabel: `View ${itemCountLabel(bucket.count)} aged ${bucket.label} days in inventory`,
                    onSelect: bucket.count > 0 ? () => onReviewAgingStock(undefined, age) : undefined
                  };
                })}
                valueLabel={(row) => itemCountLabel(row.value)}
              />
            </div>
          </CardContent>
        </Card>
        <Card className="analytics-marketplace-card">
          <CardHeader>
            <CardTitle as="h2">Marketplace performance</CardTitle>
            <CardDescription>{leadingPlatform ? `${marketplaceLabels[leadingPlatform.marketplace]} leads this period` : "Sales and live-listing coverage by marketplace"}</CardDescription>
          </CardHeader>
          <CardContent>
            {activePlatformRows.length === 0 ? (
              <EmptyState as="h3" compact icon={<Tag size={24} />} title="No marketplace data" message="Marketplace results appear after a listing is live or a sale is recorded in this date range." />
            ) : (
              <Table responsive="stack" className="ui-table-platform" aria-label="Marketplace performance">
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Marketplace</TableHead>
                  <TableHead variant="number" scope="col">Net profit</TableHead>
                  <TableHead variant="number" scope="col">Sold</TableHead>
                  <TableHead variant="number" scope="col">Current live</TableHead>
                  <TableHead variant="action" scope="col">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activePlatformRows.map((platform) => (
                  <TableRow key={platform.marketplace}>
                    <TableCell variant="leading" data-label="Marketplace">
                      <MarketplaceBadge marketplace={platform.marketplace} />
                    </TableCell>
                    <TableCell variant="number" data-label="Net profit">
                      <strong>{formatMoney(platform.netProfit)}</strong>
                    </TableCell>
                    <TableCell variant="number" data-label="Sold">
                      {platform.soldCount}
                    </TableCell>
                    <TableCell variant="number" data-label="Current live">
                      {platform.liveListingCount}
                    </TableCell>
                    <TableCell variant="action" data-label="Action">
                      <Button variant="secondary" size="compact" type="button" onClick={() => onReviewMarketplace(platform.marketplace)}>
                        Review listings
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            )}
          </CardContent>
        </Card>
      </section>
      <ContentFrame size="full">
        <Disclosure
          className="analytics-calculation-details"
          label="How these figures are calculated"
          description="Definitions and included records"
          meta={orderCountLabel(rangeSales.length)}
        >
          <dl className="analytics-calculation-grid" aria-label="Analytics calculation details">
            <div>
              <dt>Revenue</dt>
              <dd>Item sale prices plus buyer-paid shipping, less recorded refunds, from recognized orders in the selected period.</dd>
            </div>
            <div>
              <dt>Net profit</dt>
              <dd>Sale price plus shipping charged, less refunds, item cost, marketplace and payment fees, shipping, and packaging.</dd>
            </div>
            <div>
              <dt>Margin</dt>
              <dd>Net profit divided by total order revenue for the selected period.</dd>
            </div>
            <div>
              <dt>Sell-through</dt>
              <dd>{soldStockRecordCount} sold from {stockRecordCountLabel(stockRecordCount)} across the full catalogue, independent of the date range.</dd>
            </div>
          </dl>
        </Disclosure>
      </ContentFrame>
        </>
      )}
    </div>
  );
}

function ChannelsScreen({ onOpenSignals }: { onOpenSignals: () => void }) {
  const store = useReOS();
  const [activityOpen, setActivityOpen] = useState(() => Boolean(new URLSearchParams(window.location.search).get("channelActivity")));

  return (
    <div className="screen-content channels-layout">
      {!activityOpen && <header className="channels-header">
        <div>
          <h1>Sales channels</h1>

        </div>

      </header>}
      {store.persistenceMode === "api" ? <MarketplaceConnectionsPanel onActivityChange={setActivityOpen} /> : store.persistenceMode === "local_demo" ? (
        <EmptyState
          icon={<Link2 size={28} />}
          title="Connections need a workspace"
          message="Demo mode keeps your work on this device. Connect eBay or Etsy when you sign in to a workspace."
        />
      ) : <WorkspaceUnavailablePanel />}
      {!activityOpen && store.persistenceMode === "api" && <Button variant="ghost" className="channels-email-utility" onClick={onOpenSignals}><Mail aria-hidden="true" /><span><strong>Sale email forwarding</strong><small>For Vinted and Depop sale notifications</small></span><ChevronRight aria-hidden="true" /></Button>}
    </div>
  );
}

function SettingsScreen({
  selectedSection,
  onSelectSection,
  onOpenChannels,
  billingReturn,
  isRefreshingBillingReturn,
  onDismissBillingReturn
}: {
  selectedSection: SettingsSectionKey;
  onSelectSection: (section: SettingsSectionKey) => void;
  onOpenChannels: () => void;
  billingReturn: BillingReturnState | null;
  isRefreshingBillingReturn: boolean;
  onDismissBillingReturn: () => void;
}) {
  const store = useReOS();
  const availableSections = settingsSectionsForMode(store.persistenceMode);
  const activeSection = store.persistenceMode === "api" ? selectedSection : "data";
  const sectionCopy = settingsSectionCopy[activeSection];
  const selectedPanelID = `settings-panel-${activeSection}`;
  const selectedHeadingID = `settings-heading-${activeSection}`;

  return (
    <div className="screen-content settings-layout">
      <section className="settings-shell" aria-label="Settings">
        <div className="settings-content">
          {availableSections.length > 1 && (
            <label className="settings-section-picker">
              <span>Settings section</span>
              <SelectControl
                aria-label="Settings section"
                value={activeSection}
                onValueChange={(value) => onSelectSection(value as SettingsSectionKey)}
                options={availableSections.map((section) => ({
                  value: section.key,
                  label: section.label
                }))}
              />
            </label>
          )}

          <header className="settings-header">
            <h1 id={selectedHeadingID}>{sectionCopy.title}</h1>
          </header>

          <div className="settings-detail" id={selectedPanelID} role="region" aria-labelledby={selectedHeadingID}>
            {store.persistenceMode !== "api" ? (
              store.persistenceMode === "local_demo" ? (
                <LocalDataPanel />
              ) : (
                <WorkspaceUnavailablePanel />
              )
            ) : (
              <>
                {selectedSection === "general" && (
                  <>
                    <StoreProfilePanel />
                    <MarketplaceSettingsLink onOpenChannels={onOpenChannels} />
                  </>
                )}
                {selectedSection === "marketplaces" && <MarketplaceSettingsLink onOpenChannels={onOpenChannels} />}
                {selectedSection === "account" && <AccountSecurityPanel />}
                {selectedSection === "notifications" && <NotificationSettings key={`${store.user?.id}:${store.workspace?.id}`} />}
                {selectedSection === "signals" && <MarketplaceSignalsPanel />}
                {selectedSection === "team" && <TeamAccessPanel onOpenBilling={() => onSelectSection("billing")} />}
                {selectedSection === "billing" && (
                  <BillingPanel
                    billingReturn={billingReturn}
                    isRefreshingBillingReturn={isRefreshingBillingReturn}
                    onDismissBillingReturn={onDismissBillingReturn}
                  />
                )}
                {selectedSection === "data" && <AccountDataPanel onOpenTeam={() => onSelectSection("team")} />}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function MarketplaceSettingsLink({ onOpenChannels }: { onOpenChannels: () => void }) {
  return (
    <Panel className="settings-channels-link">
      <div>
        <h2>Sales channels</h2>
        <p>Connections and imports are managed in one place.</p>
      </div>
      <Button variant="secondary" type="button" onClick={onOpenChannels}>
        Open sales channels <ChevronRight size={16} />
      </Button>
    </Panel>
  );
}

function StoreProfilePanel() {
  const store = useReOS();
  const toast = useToast();
  const workspace = store.workspace;
  const currentRole = workspaceRoleForStore(store);
  const canManageWorkspace = currentRole === "owner" || currentRole === "admin";
  const [name, setName] = useState(workspace?.name ?? "");
  const [defaultCurrency, setDefaultCurrency] = useState(workspace?.defaultCurrency ?? "GBP");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(workspace?.name ?? "");
    setDefaultCurrency(workspace?.defaultCurrency ?? "GBP");
    setError(null);
  }, [workspace?.id, workspace?.name, workspace?.defaultCurrency]);

  const cleanName = name.trim();
  const cleanCurrency = defaultCurrency.trim().toUpperCase();
  const hasChanges = cleanName !== (workspace?.name ?? "") || cleanCurrency !== (workspace?.defaultCurrency ?? "GBP");
  const canSave = canManageWorkspace && Boolean(cleanName) && /^[A-Z]{3}$/.test(cleanCurrency) && hasChanges && !isSaving;

  async function saveWorkspaceSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!workspace || !canSave) {
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      await store.updateWorkspaceSettings({
        name: cleanName,
        defaultCurrency: cleanCurrency
      });
      toast.notify({ title: "Store profile saved", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Store profile could not be saved."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Panel className="ui-panel-settings-store-profile">
      <PanelHeader title="Store profile" />
      {!canManageWorkspace && <Alert tone="info">A workspace owner or admin manages the store profile.</Alert>}
      <FormLayout onSubmit={(event) => void saveWorkspaceSettings(event)}>
        <Field label="Store name" helper="Shown in navigation, exports, and shared access.">
          <Input value={name} onChange={(event) => setName(event.target.value)} autoComplete="organization" disabled={!canManageWorkspace} />
        </Field>
        <Field label="Default currency" helper="Used for new inventory, listing drafts, and order records. Existing records keep their original currency.">
          <SelectControl
            value={defaultCurrency}
            onValueChange={setDefaultCurrency}
            disabled={!canManageWorkspace}
            options={workspaceCurrencyOptions.map((option) => ({
              value: option.value,
              label: option.label
            }))}
          />
        </Field>
        {error && <Alert tone="danger">{error}</Alert>}
        {canManageWorkspace && (hasChanges || isSaving) && (
          <FormActions>
            <Button type="submit" disabled={!canSave} loading={isSaving} loadingLabel="Saving">
              <CheckCircle2 size={18} /> {isSaving ? "Saving" : "Save changes"}
            </Button>
          </FormActions>
        )}
      </FormLayout>
    </Panel>
  );
}

function ebayReadinessLabel(value: string) {
  const labels: Record<string, string> = {
    "eBay seller connection": "connect the seller account",
    "seller reconnection": "reconnect the seller account",
    "seller setup refresh": "refresh seller setup",
    "eBay payment policy": "create an eBay payment policy",
    "eBay fulfilment policy": "create an eBay delivery policy",
    "eBay return policy": "create an eBay return policy",
    "eBay inventory location": "create an eBay inventory location",
    "selected payment policy": "choose a payment policy",
    "selected fulfilment policy": "choose a delivery policy",
    "selected return policy": "choose a return policy",
    "selected inventory location": "choose an inventory location",
    "eBay event destination": "enable eBay order notifications",
    "eBay order subscription": "enable eBay order notifications"
  };
  return labels[value] ?? "finish the seller setup";
}

type MarketplaceHubStatus = ChannelPresentation;

type MarketplaceProfileChange = {
  marketplace: Marketplace;
  currentProfile: MarketplaceConnectionProfile;
  nextProfile: MarketplaceConnectionProfile;
  disposition: "none" | "pause" | "disconnect";
};

function marketplaceAccountFor(accounts: MarketplaceAccount[], marketplace: Marketplace) {
  return accounts.find((account) => account.marketplace === marketplace);
}

function marketplaceEnvironmentLabel(
  marketplace: Marketplace,
  account: MarketplaceAccount | undefined,
  ebayStatus: ReturnType<typeof useReOS>["ebayIntegration"]
) {
  if (marketplace === "ebay") {
    if (ebayStatus?.environment === "production") return "Production";
    if (ebayStatus?.environment === "sandbox") return "eBay Sandbox";
    if (ebayStatus?.environment === "simulated") return "Simulator";
    return "Environment unavailable";
  }
  if (account?.environment === "production") return "Production planned";
  if (account?.environment === "sandbox") return "Sandbox";
  if (account?.environment === "simulator" || MARKETPLACE_STAGING_SIMULATOR) return "Staging simulator";
  return "Production unavailable";
}

function marketplaceHubStatus(
  marketplace: Marketplace,
  direct: boolean,
  _requiresApproval: boolean,
  account: MarketplaceAccount | undefined,
  ebayStatus: ReturnType<typeof useReOS>["ebayIntegration"],
  etsyStatus?: ReturnType<typeof useReOS>["etsyIntegration"]
): MarketplaceHubStatus {
  return channelPresentation({ marketplace, direct, account, ebay: ebayStatus, etsy: etsyStatus, simulator: MARKETPLACE_STAGING_SIMULATOR });
}

function marketplaceIntegrationIsActive(
  marketplace: Marketplace,
  account: MarketplaceAccount | undefined,
  ebayStatus: ReturnType<typeof useReOS>["ebayIntegration"]
) {
  if (marketplace === "ebay") return Boolean(ebayStatus?.connection);
  return Boolean(account && !["not_configured", "disconnected", "paused"].includes(account.lifecycleState ?? "not_configured"));
}


function MarketplaceConnectionsPanel({ onActivityChange }: { onActivityChange: (open: boolean) => void }) {
  const store = useReOS();
  const [activityAccountID, setActivityAccountID] = useState(() => new URLSearchParams(window.location.search).get("channelActivity"));
  const toast = useToast();
  const workspace = store.workspace;
  const canManage = ["owner", "admin"].includes(workspaceRoleForStore(store));
  const [busyMarketplace, setBusyMarketplace] = useState<Marketplace | null>(null);
  const [activeSetup, setActiveSetup] = useState<Marketplace | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupTitle, setSetupTitle] = useState<string | null>(null);
  const [channelFooter, setChannelFooter] = useState<HTMLDivElement | null>(null);
  const [channelIdentity, setChannelIdentity] = useState<HTMLDivElement | null>(null);
  const [profileChange, setProfileChange] = useState<MarketplaceProfileChange | null>(null);
  const [error, setError] = useState<string | null>(null);
  const setupTriggerRef = useRef<HTMLButtonElement | null>(null);
  const setupMarketplaceRef = useRef<Marketplace | null>(null);
  const profileTransitionRef = useRef(false);
  const [importBusy, setImportBusy] = useState(false);
  const importBusyRef = useRef(false);

  function rememberSetup(marketplace: Marketplace | null) {
    const url = new URL(window.location.href);
    if (marketplace) url.searchParams.set("setupChannel", marketplace);
    else {
      url.searchParams.delete("setupChannel");
      url.searchParams.delete("channelView");
      url.searchParams.delete("importRun");
      url.searchParams.delete("importPage");
    }
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }

  useEffect(() => {
    const handleBusy = (event: Event) => {
      const detail = (event as CustomEvent<{ marketplace: Marketplace; busy: boolean }>).detail;
      if (!detail || detail.marketplace !== setupMarketplaceRef.current) return;
      importBusyRef.current = detail.busy;
      setImportBusy(detail.busy);
    };
    const warn = (event: BeforeUnloadEvent) => {
      if (importBusyRef.current) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("listwork:marketplace-import-busy", handleBusy);
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("listwork:marketplace-import-busy", handleBusy);
      window.removeEventListener("beforeunload", warn);
    };
  }, []);

  useEffect(() => {
    const restore = () => {
      const url = new URL(window.location.href);
      setActivityAccountID(url.searchParams.get("channelActivity"));
      const selected = url.searchParams.get("setupChannel") ?? url.searchParams.get("integration");
      if (selected && marketplaces.includes(selected as Marketplace)) {
        const marketplace = selected as Marketplace;
        setupMarketplaceRef.current = marketplace;
        setActiveSetup(marketplace);
        setSetupOpen(true);
        rememberSetup(marketplace);
      } else if (!importBusyRef.current) {
        setSetupOpen(false);
      }
    };
    restore();
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);

  useEffect(() => {
    if (!workspace?.id) return;
    void store.refreshMarketplaceIntegrations().catch((caught) => {
      setError(userSafeErrorMessage(caught, "Marketplace status could not be refreshed."));
    });
  }, [workspace?.id]);

  function beginProfileChange(marketplace: Marketplace, currentProfile: MarketplaceConnectionProfile) {
    const active = marketplaceIntegrationIsActive(
      marketplace,
      marketplaceAccountFor(store.marketplaceAccounts, marketplace),
      store.ebayIntegration
    );
    setError(null);
    setProfileChange({
      marketplace,
      currentProfile,
      nextProfile: currentProfile,
      disposition: active ? marketplace === "ebay" ? "disconnect" : "pause" : "none"
    });
  }

  function reopenMarketplaceSetup(marketplace: Marketplace) {
    profileTransitionRef.current = false;
    rememberSetup(marketplace);
    setupMarketplaceRef.current = marketplace;
    setActiveSetup(marketplace);
    window.setTimeout(() => setSetupOpen(true), 0);
  }

  function beginProfileChangeFromSetup(marketplace: Marketplace, currentProfile: MarketplaceConnectionProfile) {
    if (importBusyRef.current) return;
    profileTransitionRef.current = true;
    setSetupOpen(false);
    window.setTimeout(() => beginProfileChange(marketplace, currentProfile), 0);
  }

  function cancelProfileChange() {
    if (!profileChange || busyMarketplace !== null) return;
    const marketplace = profileChange.marketplace;
    setProfileChange(null);
    reopenMarketplaceSetup(marketplace);
  }

  function finishClosingMarketplaceSetup() {
    if (profileTransitionRef.current) return;
    const marketplace = setupMarketplaceRef.current;
    const liveTrigger = marketplace
      ? document.querySelector<HTMLElement>(`[data-marketplace-action="${marketplace}"]`)
      : null;
    const fallback = marketplace
      ? document.querySelector<HTMLElement>(`[data-marketplace-heading="${marketplace}"]`)
      : null;
    const focusTarget = liveTrigger ?? (setupTriggerRef.current?.isConnected ? setupTriggerRef.current : fallback);
    queueMicrotask(() => {
      const currentTrigger = marketplace
        ? document.querySelector<HTMLElement>(`[data-marketplace-action="${marketplace}"]`)
        : null;
      (currentTrigger ?? focusTarget)?.focus({ preventScroll: true });
    });
  }

  async function applyProfileChange() {
    if (!workspace || !canManage || !profileChange || profileChange.nextProfile === profileChange.currentProfile) return;
    const { marketplace, nextProfile, disposition } = profileChange;
    try {
      setBusyMarketplace(marketplace);
      setError(null);
      const account = marketplaceAccountFor(store.marketplaceAccounts, marketplace);
      const active = marketplaceIntegrationIsActive(marketplace, account, store.ebayIntegration);
      if (active) {
        if (marketplace === "ebay") {
          if (disposition !== "disconnect") throw new Error("Disconnect eBay before changing its management method.");
          await store.disconnectEbay();
        } else if (disposition === "pause" || disposition === "disconnect") {
          try {
            await store.runMarketplaceLifecycle(marketplace, disposition);
          } catch (lifecycleError) {
            const lifecycleMessage = userSafeErrorMessage(lifecycleError, "");
            if (!/Only an active marketplace integration can be paused|already disconnected/i.test(lifecycleMessage)) {
              throw lifecycleError;
            }
          }
        } else {
          throw new Error("Choose what should happen to the active connection.");
        }
      }
      await store.updateWorkspaceSettings({
        marketplaceProfiles: { ...(workspace.marketplaceProfiles ?? {}), [marketplace]: nextProfile }
      });
      await store.refreshMarketplaceIntegrations();
      const nextDefinition = marketplaceProfileDefinition(nextProfile, marketplace);
      setProfileChange(null);
      reopenMarketplaceSetup(marketplace);
      toast.notify({
        title: `${marketplaceLabels[marketplace]} setup updated`,
        description: nextDefinition?.direct
          ? "Continue setup to connect the seller account."
          : "Listwork will guide you through the marketplace steps.",
        tone: "success"
      });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "The marketplace management method could not be changed."));
    } finally {
      setBusyMarketplace(null);
    }
  }

  const manualSetup = activeSetup && !marketplaceProfileDefinition(workspace?.marketplaceProfiles?.[activeSetup] ?? defaultMarketplaceProfile(activeSetup), activeSetup)?.direct ? activeSetup : null;

  const marketplaceSetupOrder: Marketplace[] = ["ebay", "etsy", "vinted", "depop", "shopify"];

  const activityAccount = store.marketplaceAccounts.find((account) => account.id === activityAccountID && ["ebay", "etsy"].includes(account.marketplace));
  useEffect(() => { onActivityChange(Boolean(activityAccount)); }, [activityAccount?.id, onActivityChange]);
  if (activityAccount) {
    const params = new URLSearchParams(window.location.search);
    const requestedTab = params.get("activityTab") as ChannelTab;
    return <ConnectedChannelActivity key={`${activityAccount.id}:${activityAccount.generation ?? 0}`} account={activityAccount} initialTab={["new", "photos", "stock", "activity"].includes(requestedTab) ? requestedTab : "new"} itemID={params.get("photoItem") ?? undefined} onBack={() => {
      const url = new URL(window.location.href);
      for (const key of ["channelActivity", "activityTab", "photoItem"]) url.searchParams.delete(key);
      window.history.replaceState(window.history.state, "", url); setActivityAccountID(null);
      requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(`[data-marketplace-action="${activityAccount.marketplace}"]`)?.focus());
    }} />;
  }

  return (
    <Panel className="marketplace-hub">

      {!canManage && <Alert tone="info">Only workspace owners and admins can change connections.</Alert>}
      <DataRowStack className="marketplace-connections-grid">
        {marketplaceSetupOrder.map((marketplace) => {
          const selected = workspace?.marketplaceProfiles?.[marketplace] ?? defaultMarketplaceProfile(marketplace);
          const definition = marketplaceProfileDefinition(selected, marketplace);
          const account = marketplaceAccountFor(store.marketplaceAccounts, marketplace);
          const status = marketplaceHubStatus(marketplace, Boolean(definition?.direct), Boolean(definition?.requiresApproval), account, store.ebayIntegration, store.etsyIntegration);
          const action = status.action;
          return (
            <DataRow key={marketplace} as="div" interactive={false} className="ui-data-row-marketplace-profile">
              <DataRowMain>
                <strong data-marketplace-heading={marketplace} tabIndex={-1}><MarketplaceMark marketplace={marketplace} labelled /></strong>
                <DataRowBadges>
                  <span className="channel-overview-status" data-tone={status.tone}>{status.tone === "success" && <CheckCircle2 size={16} aria-hidden="true" />}{status.label}</span>
                </DataRowBadges>


              </DataRowMain>
              <DataRowValue className="ui-data-row-value-marketplace-profile">
                <div className="marketplace-row-actions">
                  <Button
                    variant="secondary"
                    size="compact"
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={`${action} ${marketplaceLabels[marketplace]}`}
                    data-marketplace-action={marketplace}
                    onClick={(event) => {
                      rememberSetup(marketplace);
                      setSetupTitle(null);
                      setupTriggerRef.current = event.currentTarget;
                      setupMarketplaceRef.current = marketplace;
                      setActiveSetup(marketplace);
                      setSetupOpen(true);
                    }}
                  >
                    {action}<ChevronRight size={16} />
                  </Button>
                </div>
              </DataRowValue>
            </DataRow>
          );
        })}
      </DataRowStack>
      {error && !profileChange && <Alert tone="danger">{error}</Alert>}

      <Dialog open={Boolean(profileChange)} onOpenChange={(open) => { if (!open) cancelProfileChange(); }}>
        {profileChange && (
          <DialogContent presentation="workflow" className="marketplace-method-dialog">
            <DialogHeader>
              <DialogTitle>Choose how to use {marketplaceLabels[profileChange.marketplace]}</DialogTitle>
              <DialogDescription>Choose a workflow. You can change it later.</DialogDescription>
            </DialogHeader>
            <DialogBody>
              <fieldset className="channel-profile-options">
                <legend>Workflow</legend>
                {marketplaceProfilesFor(profileChange.marketplace).map((profile) => (
                  <label key={profile.id} data-selected={profileChange.nextProfile === profile.id || undefined}>
                    <input
                      type="radio"
                      name="marketplace-profile"
                      value={profile.id}
                      checked={profileChange.nextProfile === profile.id}
                      disabled={busyMarketplace !== null}
                      onChange={() => setProfileChange((current) => current ? { ...current, nextProfile: profile.id } : current)}
                    />
                    <span><strong>{profile.label}</strong><small>{profile.description}</small></span>
                  </label>
                ))}
              </fieldset>
              {marketplaceIntegrationIsActive(profileChange.marketplace, marketplaceAccountFor(store.marketplaceAccounts, profileChange.marketplace), store.ebayIntegration) && profileChange.marketplace === "ebay" ? (
                <p className="marketplace-switch-note">Switching workflows disconnects eBay. Imported items stay in Listwork.</p>
              ) : marketplaceIntegrationIsActive(profileChange.marketplace, marketplaceAccountFor(store.marketplaceAccounts, profileChange.marketplace), store.ebayIntegration) && (
                <fieldset className="marketplace-disposition-options">
                  <legend>Existing connection</legend>
                  {profileChange.marketplace !== "ebay" && (
                    <label><input type="radio" name="marketplace-disposition" value="pause" checked={profileChange.disposition === "pause"} onChange={() => setProfileChange((current) => current ? { ...current, disposition: "pause" } : current)} /><span><strong>Pause connection</strong>Keep the account details so you can resume later.</span></label>
                  )}
                  <label><input type="radio" name="marketplace-disposition" value="disconnect" checked={profileChange.disposition === "disconnect"} onChange={() => setProfileChange((current) => current ? { ...current, disposition: "disconnect" } : current)} /><span><strong>Disconnect account</strong>Remove access before switching. Imported items stay in Listwork.</span></label>
                </fieldset>
              )}
              {error && <Alert tone="danger">{error}</Alert>}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={cancelProfileChange} disabled={busyMarketplace !== null}>Cancel</Button>
              <Button type="button" onClick={() => void applyProfileChange()} disabled={busyMarketplace !== null || profileChange.nextProfile === profileChange.currentProfile} loading={Boolean(busyMarketplace)} loadingLabel="Saving">
                {busyMarketplace ? "Saving" : "Use this setup"}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {setupOpen && manualSetup && <ManualListingFlow initialTask="home" initialAccountID={marketplaceAccountFor(store.marketplaceAccounts, manualSetup)?.id}
        emailContent={["vinted", "depop"].includes(manualSetup) ? <MarketplaceSignalsPanel marketplace={manualSetup} embedded /> : undefined}
        onChangeWorkflow={canManage && marketplaceProfilesFor(manualSetup).length > 1 ? () => beginProfileChangeFromSetup(manualSetup, workspace?.marketplaceProfiles?.[manualSetup] ?? defaultMarketplaceProfile(manualSetup)) : undefined}
        onClose={() => { rememberSetup(null); setSetupOpen(false); }} onReturnFocus={finishClosingMarketplaceSetup} />}

      <ChannelSheetLayout.Provider value={{ footer: channelFooter, identity: channelIdentity, setTitle: setSetupTitle, onClose: () => { if (!importBusyRef.current) { rememberSetup(null); setSetupOpen(false); } } }}>
      <Sheet open={setupOpen && !manualSetup} onOpenChange={(open) => { if (!open && importBusyRef.current) return; if (!open) rememberSetup(null); setSetupOpen(open); }}>
        {activeSetup && (
          <SheetContent
            className="marketplace-setup-sheet"
            closeDisabled={importBusy}
            onEscapeKeyDown={(event) => { if (importBusyRef.current) event.preventDefault(); }}
            onInteractOutside={(event) => { if (importBusyRef.current) event.preventDefault(); }}
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              document.querySelector<HTMLElement>(".marketplace-setup-sheet [data-marketplace-step-heading]")?.focus({ preventScroll: true });
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              finishClosingMarketplaceSetup();
            }}
          >
            <SheetHeader>
              <div ref={setChannelIdentity} className="channel-sheet-identity" />
              <SheetTitle tabIndex={-1} data-marketplace-step-heading>
                {setupTitle ?? marketplaceLabels[activeSetup]}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {marketplaceProfileDefinition(workspace?.marketplaceProfiles?.[activeSetup] ?? defaultMarketplaceProfile(activeSetup), activeSetup)?.direct
                  ? `Connect ${marketplaceLabels[activeSetup]} and choose what to bring into Listwork.`
                  : "No account connection required."}
              </SheetDescription>
            </SheetHeader>
            <SheetBody>
              <fieldset className="channel-setup-fields" disabled={importBusy} aria-busy={importBusy}>
              {!(activeSetup === "ebay" && store.ebayIntegration?.connection?.status === "connected" && marketplaceProfileDefinition(
                workspace?.marketplaceProfiles?.ebay ?? defaultMarketplaceProfile("ebay"),
                "ebay"
              )?.direct) && <div className="channel-setup-choice">
                <div>
                  <span>{activeSetup === "vinted" ? "Account type" : "Workflow"}</span>
                  <strong>{marketplaceProfileDefinition(workspace?.marketplaceProfiles?.[activeSetup] ?? defaultMarketplaceProfile(activeSetup), activeSetup)?.label}</strong>
                </div>
                {canManage && marketplaceProfilesFor(activeSetup).length > 1 && (
                  <Button
                    variant="ghost"
                    size="compact"
                    type="button"
                    aria-label={`Change ${marketplaceLabels[activeSetup]} setup`}
                    onClick={() => beginProfileChangeFromSetup(activeSetup, workspace?.marketplaceProfiles?.[activeSetup] ?? defaultMarketplaceProfile(activeSetup))}
                  >
                    Change
                  </Button>
                )}
              </div>}
              {activeSetup === "ebay" && marketplaceProfileDefinition(
                workspace?.marketplaceProfiles?.ebay ?? defaultMarketplaceProfile("ebay"),
                "ebay"
              )?.direct ? (
                <EbayConnectionPanel
                  embedded
                  canManage={canManage}
                  workflowLabel={marketplaceProfileDefinition(workspace?.marketplaceProfiles?.ebay ?? defaultMarketplaceProfile("ebay"), "ebay")?.label}
                  onChangeWorkflow={canManage && marketplaceProfilesFor("ebay").length > 1
                    ? () => beginProfileChangeFromSetup("ebay", workspace?.marketplaceProfiles?.ebay ?? defaultMarketplaceProfile("ebay"))
                    : undefined}
                />
              ) : activeSetup === "etsy" && !MARKETPLACE_STAGING_SIMULATOR && marketplaceProfileDefinition(
                workspace?.marketplaceProfiles?.etsy ?? defaultMarketplaceProfile("etsy"),
                "etsy"
              )?.direct ? (
                <EtsyConnectionPanel canManage={canManage} />
              ) : (
                <MarketplaceConnectorSetup marketplace={activeSetup} profile={workspace?.marketplaceProfiles?.[activeSetup] ?? defaultMarketplaceProfile(activeSetup)} account={marketplaceAccountFor(store.marketplaceAccounts, activeSetup)} canManage={canManage} />
              )}
              </fieldset>
            </SheetBody>
            <div ref={setChannelFooter} className="channel-sheet-footer" />
          </SheetContent>
        )}
      </Sheet>
      </ChannelSheetLayout.Provider>
    </Panel>
  );
}

type ProviderJourney = {
  requirementTitle: string;
  requirementCopy: string;
  authorizationTitle: string;
  authorizationCopy: string;
  validationTitle: string;
  validationCopy: string;
  access: string[];
};

const providerJourneys: Record<"vinted" | "depop" | "etsy" | "shopify", ProviderJourney> = {
  vinted: {
    requirementTitle: "Check your Vinted Pro access",
    requirementCopy: "Use this option if Vinted has already approved your business for Vinted Pro.",
    authorizationTitle: "Add your Vinted Pro account",
    authorizationCopy: "Listwork will use the account details supplied by Vinted. You will review access before anything goes live.",
    validationTitle: "Check products and orders",
    validationCopy: "Review product categories, sold-item updates, and available shipping options.",
    access: ["Vinted Pro approval", "Products and stock", "Orders and sale updates", "Available shipping options"]
  },
  depop: {
    requirementTitle: "Check your Depop access",
    requirementCopy: "Use this option if Depop has approved your business for partner access.",
    authorizationTitle: "Connect your Depop account",
    authorizationCopy: "Depop will show the access Listwork is requesting before you continue.",
    validationTitle: "Check products and orders",
    validationCopy: "Review products, sale updates, fulfilment, and the features Depop enabled for your account.",
    access: ["Depop partner approval", "Products and stock", "Orders and fulfilment", "Enabled account features"]
  },
  etsy: {
    requirementTitle: "Start a test connection",
    requirementCopy: "Use sample shop data. Etsy is not contacted.",
    authorizationTitle: "Check sample shop data",
    authorizationCopy: "Review the listings, stock, and paid orders Listwork would read.",
    validationTitle: "Review the import plan",
    validationCopy: "Choose whether each listing creates a new item or links to stock already in Listwork.",
    access: ["Shop name", "Active listings", "Stock quantities", "Paid orders"]
  },
  shopify: {
    requirementTitle: "Enter your Shopify store",
    requirementCopy: "Use the permanent .myshopify.com address shown in your Shopify settings.",
    authorizationTitle: "Add Listwork to Shopify",
    authorizationCopy: "Shopify will show the access Listwork is requesting before you install it.",
    validationTitle: "Choose stock locations",
    validationCopy: "Choose which locations Listwork should read, then review products, orders, fulfilment, and refunds.",
    access: ["Shopify store", "Products and stock", "Orders and fulfilment", "Inventory locations"]
  }
};

function MarketplaceConnectorSetup({ marketplace, profile, account, canManage }: {
  marketplace: Marketplace;
  profile: MarketplaceConnectionProfile;
  account?: MarketplaceAccount;
  canManage: boolean;
}) {
  const store = useReOS();
  const definition = marketplaceProfileDefinition(profile, marketplace);
  const [busyAction, setBusyAction] = useState<"connect" | "reconnect" | "sync" | "pause" | "disconnect" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [shopDomain, setShopDomain] = useState(
    typeof account?.configuration?.shopDomain === "string" ? account.configuration.shopDomain : ""
  );

  if (!definition?.direct) return null;

  const journey = providerJourneys[marketplace as keyof typeof providerJourneys];
  const simulatorAvailable = account?.environment === "simulator" || MARKETPLACE_STAGING_SIMULATOR;
  const productionUnavailable = !simulatorAvailable || account?.environment === "production";
  const lifecycle = account?.lifecycleState ?? "not_configured";
  const authorized = account?.authorizationState === "authorized";
  const synced = account?.initialSyncState === "completed";
  const ready = lifecycle === "ready" && account?.environment !== "production";
  const currentStep = !authorized ? 0 : !synced ? 1 : 2;
  const showingEtsyImportReview = marketplace === "etsy" && synced;
  const normalizedShopDomain = shopDomain.trim();
  const validShopDomain = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(normalizedShopDomain);
  const invalidShopDomain = Boolean(normalizedShopDomain && !validShopDomain);

  async function runOperation(operation: "connect" | "reconnect" | "import_catalog" | "pause" | "disconnect") {
    try {
      setBusyAction(operation === "import_catalog" ? "sync" : operation);
      setError(null);
      const result = await store.runMarketplaceLifecycle(
        marketplace,
        operation,
        marketplace === "shopify" && (operation === "connect" || operation === "reconnect")
          ? { shopDomain: shopDomain.trim() }
          : undefined
      );
      const authorizationURL = typeof result.data.authorizationURL === "string" ? result.data.authorizationURL : "";
      if (authorizationURL && operation !== "disconnect") {
        window.location.assign(authorizationURL);
        return;
      }
      if (operation === "disconnect") setDisconnectOpen(false);
      window.requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(".marketplace-setup-sheet [data-marketplace-step-heading]")?.focus({ preventScroll: true });
      });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, `${marketplaceLabels[marketplace]} test could not be completed.`));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <section className="marketplace-journey" aria-label={`${marketplaceLabels[marketplace]} setup`}>
      <div className="marketplace-journey-statusline" role="status" aria-live="polite">
        <Badge tone={productionUnavailable ? "neutral" : "info"}>{productionUnavailable ? "Live connection unavailable" : "Test mode"}</Badge>
        <Badge tone={ready ? "info" : lifecycle === "paused" ? "neutral" : "warning"}>{ready ? "Test ready" : lifecycle === "paused" ? "Paused" : authorized ? "One step left" : definition.requiresApproval ? "Approval needed" : "Not connected"}</Badge>
      </div>
      <ol className="marketplace-persistent-stepper" aria-label={`${marketplaceLabels[marketplace]} setup progress`}>
        <li data-state={currentStep === 0 ? "current" : currentStep > 0 ? "complete" : undefined}><span>{currentStep > 0 ? <Check size={14} /> : "1"}</span><div><strong>{journey.requirementTitle}</strong></div></li>
        <li data-state={currentStep === 1 ? "current" : currentStep > 1 ? "complete" : undefined}><span>{currentStep > 1 ? <Check size={14} /> : "2"}</span><div><strong>{journey.authorizationTitle}</strong></div></li>
        <li data-state={currentStep === 2 ? "current" : undefined}><span>3</span><div><strong>{journey.validationTitle}</strong></div></li>
      </ol>
      <div className="marketplace-current-step">
        <h2 data-marketplace-step-heading tabIndex={-1}>{showingEtsyImportReview ? journey.validationTitle : ready ? `${marketplaceLabels[marketplace]} test is ready` : currentStep === 0 ? journey.requirementTitle : currentStep === 1 ? journey.authorizationTitle : journey.validationTitle}</h2>
        <p>{showingEtsyImportReview ? journey.validationCopy : ready ? "The sample setup is saved for this workspace. No real marketplace data was used." : currentStep === 0 ? journey.requirementCopy : currentStep === 1 ? journey.authorizationCopy : journey.validationCopy}</p>
      </div>
      {showingEtsyImportReview && <MarketplaceImportReview marketplace="etsy" canManage={canManage} />}
      {marketplace === "shopify" && typeof account?.configuration?.shopDomain === "string" && (
        <PanelNote>Shop identity: <strong>{account.configuration.shopDomain}</strong></PanelNote>
      )}
      {marketplace === "shopify" && !authorized && (
        <Field
          label="Shop identity"
          helper={invalidShopDomain
            ? "Enter the permanent .myshopify.com domain, for example your-store.myshopify.com."
            : "Simulator-only validation. Production installation remains unavailable."}
        >
          <Input value={shopDomain} onChange={(event) => setShopDomain(event.target.value.toLowerCase())} placeholder="your-store.myshopify.com" aria-invalid={invalidShopDomain} disabled={!canManage || busyAction !== null} />
        </Field>
      )}
      {!showingEtsyImportReview && (
        <>
          <Disclosure label="What this test checks"><ul>{journey.access.map((item) => <li key={item}><ShieldCheck size={16} /> {item}</li>)}</ul></Disclosure>
          <Alert tone="info">{productionUnavailable ? `The live ${marketplaceLabels[marketplace]} connection is not available here. Nothing in the marketplace will change.` : `Test mode uses sample data and does not contact ${marketplaceLabels[marketplace]}.`}</Alert>
        </>
      )}
      {account?.lastError && <Alert tone="danger">{account.lastError}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      {!canManage ? (
        <Alert tone="info">An owner or admin can change this connection.</Alert>
      ) : productionUnavailable ? (
        <div className="marketplace-primary-action"><Button type="button" disabled>Live connection unavailable</Button></div>
      ) : lifecycle === "paused" ? (
        <div className="marketplace-primary-action"><Button type="button" disabled>Disconnect before restarting setup</Button></div>
      ) : !authorized ? (
        <div className="marketplace-primary-action"><Button type="button" disabled={busyAction !== null || (marketplace === "shopify" && !validShopDomain)} onClick={() => void runOperation(["action_required", "revocation_failed", "disconnected"].includes(lifecycle) ? "reconnect" : "connect")} loading={Boolean(busyAction === "connect" || busyAction === "reconnect")} loadingLabel="Starting test">{busyAction === "connect" || busyAction === "reconnect" ? "Starting test" : "Start test connection"}</Button></div>
      ) : !synced ? (
        <div className="marketplace-primary-action"><Button type="button" disabled={busyAction !== null} onClick={() => void runOperation("import_catalog")} loading={Boolean(busyAction === "sync")} loadingLabel="Checking sample data">{busyAction === "sync" ? "Checking sample data" : "Check sample data"}</Button></div>
      ) : (
        <div className="marketplace-primary-action"><Button variant="secondary" type="button" disabled={busyAction !== null} onClick={() => void runOperation("pause")} loading={Boolean(busyAction === "pause")} loadingLabel="Pausing">{busyAction === "pause" ? "Pausing" : "Pause test"}</Button></div>
      )}
      {account && !["not_configured", "disconnected"].includes(lifecycle) && canManage && (
        <DangerZone compact className="marketplace-connection-danger">
          <DangerZoneCopy><strong>Reset test connection</strong><p>Clears this sample setup so you can test it again from the beginning.</p></DangerZoneCopy>
          {!disconnectOpen ? <Button variant="danger" type="button" onClick={() => setDisconnectOpen(true)}>Reset test</Button> : (
            <DangerZoneConfirm><InlineConfirm title={`Reset the ${marketplaceLabels[marketplace]} test?`} description="No real marketplace account is affected." actions={<><Button variant="ghost" type="button" onClick={() => setDisconnectOpen(false)}>Cancel</Button><Button variant="danger" type="button" disabled={busyAction !== null} onClick={() => void runOperation("disconnect")} loading={Boolean(busyAction === "disconnect")} loadingLabel="Resetting">{busyAction === "disconnect" ? "Resetting" : "Reset test"}</Button></>} /></DangerZoneConfirm>
          )}
        </DangerZone>
      )}
    </section>
  );
}

function EtsyConnectionPanel({ canManage }: { canManage: boolean }) {
  const store = useReOS();
  const toast = useToast();
  const status = store.etsyIntegration;
  const [shopID, setShopID] = useState("");
  const [changingShop, setChangingShop] = useState(false);
  const [busyAction, setBusyAction] = useState<"connect" | "save" | "refresh" | "disconnect" | null>(null);
  const busyRef = useRef(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reconnectRequired = status?.connection?.status === "reconnect_required";
  const connected = status?.connection?.status === "connected";
  const selectedShopSaved = Boolean(status?.configuration?.shopID);
  const selectedShop = status?.choices.shops.find((shop) => shop.id === status.configuration?.shopID);
  const accountName = selectedShop?.name || status?.connection?.shopName || "Etsy shop";

  useEffect(() => {
    setShopID(status?.configuration?.shopID ?? "");
  }, [status?.configuration?.shopID]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("integration") !== "etsy") return;
    const result = url.searchParams.get("status");
    url.searchParams.delete("integration");
    url.searchParams.delete("status");
    url.searchParams.delete("reason");
    url.searchParams.set("setupChannel", "etsy");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    if (result === "connected") {
      toast.notify({ title: "Etsy connected", tone: "success" });
      void store.refreshEtsyIntegration().catch(() => setError("Etsy connected, but the shop details could not be loaded. Try refreshing."));
    } else if (result === "declined") {
      toast.notify({ title: "Connection cancelled", description: "You can try again when you are ready.", tone: "info" });
    } else if (result === "error") {
      setError("Etsy could not be connected. Try connecting again.");
    }
  }, [store.refreshEtsyIntegration, toast]);

  async function run(action: "connect" | "save" | "refresh" | "disconnect", operation: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyAction(action);
    setError(null);
    try {
      await operation();
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "The Etsy connection could not be updated. Try again."));
    } finally {
      busyRef.current = false;
      setBusyAction(null);
    }
  }

  function saveShop(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canManage || !shopID) return;
    void run("save", async () => {
      const sameShop = shopID === status?.configuration?.shopID;
      await store.updateEtsyIntegration({
        shopID,
        shippingProfileID: sameShop ? status?.configuration?.shippingProfileID ?? "" : "",
        returnPolicyID: sameShop ? status?.configuration?.returnPolicyID ?? "" : ""
      });
      setChangingShop(false);
      toast.notify({ title: "Shop selected", tone: "success" });
    });
  }

  return (
    <section className="etsy-connection-panel marketplace-journey" aria-label="Etsy connection">
      {!status ? (
        <div className="ebay-connection-loading">
          <span>Shop details could not be loaded.</span>
          <Button variant="secondary" type="button" disabled={busyAction !== null}
            onClick={() => void run("refresh", async () => { await store.refreshEtsyIntegration(); })}>Try again</Button>
        </div>
      ) : !status.configured ? (
        <EmptyNote>Etsy connections are not available here yet.</EmptyNote>
      ) : !connected ? (
        <div className="marketplace-connect-entry">
          <h2 data-marketplace-step-heading tabIndex={-1}>{reconnectRequired ? "Reconnect your Etsy shop" : "Connect your Etsy shop"}</h2>
          <p>Sign in on Etsy, then choose which listings to bring into Listwork.</p>
          {canManage ? (
            <Button variant="primary" type="button" disabled={busyAction !== null}
              onClick={() => void run("connect", async () => { window.location.assign(await store.startEtsyConnection()); })} loading={Boolean(busyAction === "connect")} loadingLabel="Opening Etsy">
              {busyAction === "connect" ? "Opening Etsy" : "Continue to Etsy"}<ExternalLink size={17} />
            </Button>
          ) : <EmptyNote>Ask the workspace owner to connect Etsy.</EmptyNote>}
        </div>
      ) : !selectedShopSaved || changingShop ? (
        <FormLayout onSubmit={saveShop}>
          <div className="marketplace-current-step">
            <h2 data-marketplace-step-heading tabIndex={-1}>Choose your shop</h2>
            <p>Imported listings will stay linked to this shop.</p>
          </div>
          <Field label="Etsy shop">
            <SelectControl value={shopID} onValueChange={setShopID} disabled={!canManage || busyAction !== null}
              options={[{ value: "", label: "Choose shop" }, ...status.choices.shops.map((shop) => ({ value: shop.id, label: shop.name }))]} />
          </Field>
          {status.choices.shops.length === 0 && (
            <div className="marketplace-empty-shops">
              <p>No shops were returned by Etsy. Check that you connected the right seller account.</p>
              <Button variant="secondary" type="button" disabled={busyAction !== null}
                onClick={() => void run("refresh", async () => { await store.refreshEtsyIntegration(); })}>Refresh shops</Button>
              {canManage && <Button variant="ghost" type="button" disabled={busyAction !== null}
                onClick={() => void run("connect", async () => { window.location.assign(await store.startEtsyConnection()); })}>Use another Etsy account</Button>}
            </div>
          )}
          {canManage && <FormActions>
            {selectedShopSaved && <Button variant="secondary" type="button" onClick={() => setChangingShop(false)} disabled={busyAction !== null}>Cancel</Button>}
            <Button variant="primary" type="submit" disabled={!shopID || busyAction !== null} loading={Boolean(busyAction === "save")} loadingLabel="Saving">{busyAction === "save" ? "Saving" : "Use this shop"}</Button>
          </FormActions>}
        </FormLayout>
      ) : (
        <>
          <ConnectedChannelManager marketplace="etsy" canManage={canManage} accountName={accountName}
            ordersReady={status.webhooks.configured}
            onRefresh={store.refreshEtsyIntegration} onDisconnect={store.disconnectEtsy}
            details={<><p>{accountName} · Read and import</p>{canManage && status.choices.shops.length > 1 && <Button variant="ghost" onClick={() => setChangingShop(true)}>Change shop</Button>}</>} />
        </>
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      {connected && (!selectedShopSaved || changingShop) && (
        <Disclosure label="Connection details" className="marketplace-connection-details">
          <dl className="connection-facts">
            <div><dt>Shop</dt><dd>{accountName}</dd></div>
            <div><dt>Order updates</dt><dd>{status?.webhooks.configured ? "Set up" : "Not available"}</dd></div>
          </dl>
          <Button variant="secondary" type="button" disabled={busyAction !== null}
            onClick={() => void run("refresh", async () => { await store.refreshEtsyIntegration(); })} loading={Boolean(busyAction === "refresh")} loadingLabel="Refreshing">{busyAction === "refresh" ? "Refreshing" : "Refresh connection"}</Button>
          {canManage && (!disconnectOpen ? (
            <Button variant="ghost" type="button" disabled={busyAction !== null} onClick={() => setDisconnectOpen(true)}>Disconnect Etsy</Button>
          ) : (
            <InlineConfirm title={`Disconnect ${accountName}?`} description="Imported items and orders stay in Listwork. Etsy updates will stop."
              actions={<>
                <Button variant="secondary" type="button" disabled={busyAction !== null} onClick={() => setDisconnectOpen(false)}>Keep connected</Button>
                <Button variant="danger" type="button" disabled={busyAction !== null} onClick={() => void run("disconnect", async () => {
                  await store.disconnectEtsy();
                  setDisconnectOpen(false);
                  toast.notify({ title: "Etsy disconnected", tone: "success" });
                })} loading={Boolean(busyAction === "disconnect")} loadingLabel="Disconnecting">{busyAction === "disconnect" ? "Disconnecting" : "Disconnect Etsy"}</Button>
              </>} />
          ))}
        </Disclosure>
      )}
    </section>
  );
}
function LegacyEbayConnectionPanel({ embedded = false, canManage: canManageOverride }: { embedded?: boolean; canManage?: boolean }) {
  const store = useReOS();
  const toast = useToast();
  const status = store.ebayIntegration;
  const canManage = canManageOverride ?? ["owner", "admin"].includes(workspaceRoleForStore(store));
  const [paymentPolicyID, setPaymentPolicyID] = useState("");
  const [fulfillmentPolicyID, setFulfillmentPolicyID] = useState("");
  const [returnPolicyID, setReturnPolicyID] = useState("");
  const [merchantLocationKey, setMerchantLocationKey] = useState("");
  const [busyAction, setBusyAction] = useState<"connect" | "save" | "disconnect" | "refresh" | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function focusCurrentStep() {
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(".marketplace-setup-sheet [data-marketplace-step-heading]")?.focus({ preventScroll: true });
    });
  }

  useEffect(() => {
    setPaymentPolicyID(status?.configuration?.paymentPolicyID ?? "");
    setFulfillmentPolicyID(status?.configuration?.fulfillmentPolicyID ?? "");
    setReturnPolicyID(status?.configuration?.returnPolicyID ?? "");
    setMerchantLocationKey(status?.configuration?.merchantLocationKey ?? "");
  }, [status]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("integration") !== "ebay") return;
    const result = url.searchParams.get("status");
    if (result === "connected") {
      toast.notify({ title: "eBay authorization complete", description: "Now verify policies, inventory location, and notification health.", tone: "success" });
      void store.refreshEbayIntegration();
    } else if (result === "declined") {
      toast.notify({ title: "eBay authorization cancelled", description: "No connection was changed.", tone: "info" });
    }
    url.searchParams.delete("integration");
    url.searchParams.delete("status");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, [store.refreshEbayIntegration, toast]);

  const configurationChanged = Boolean(status?.connection) && (paymentPolicyID !== (status?.configuration?.paymentPolicyID ?? "") || fulfillmentPolicyID !== (status?.configuration?.fulfillmentPolicyID ?? "") || returnPolicyID !== (status?.configuration?.returnPolicyID ?? "") || merchantLocationKey !== (status?.configuration?.merchantLocationKey ?? ""));

  async function refresh() {
    try { setBusyAction("refresh"); setError(null); await store.refreshEbayIntegration(); focusCurrentStep(); }
    catch (caught) { setError(userSafeErrorMessage(caught, "The eBay connection could not be refreshed.")); }
    finally { setBusyAction(null); }
  }

  async function connect() {
    try { setBusyAction("connect"); setError(null); window.location.assign(await store.startEbayConnection()); }
    catch (caught) { setError(userSafeErrorMessage(caught, "eBay authorization could not be started.")); setBusyAction(null); }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setBusyAction("save");
      setError(null);
      const verifyNotifications = currentStep === 2 && !configurationChanged;
      await store.updateEbayIntegration({ paymentPolicyID, fulfillmentPolicyID, returnPolicyID, merchantLocationKey, verifyNotifications });
      focusCurrentStep();
      toast.notify({ title: verifyNotifications ? "eBay notifications verified" : "eBay policies saved", tone: "success" });
    }
    catch (caught) { setError(userSafeErrorMessage(caught, "eBay setup could not be verified.")); }
    finally { setBusyAction(null); }
  }

  async function disconnect() {
    try { setBusyAction("disconnect"); setError(null); await store.disconnectEbay(); setDisconnectOpen(false); focusCurrentStep(); toast.notify({ title: "eBay disconnected", tone: "success" }); }
    catch (caught) { setError(userSafeErrorMessage(caught, "eBay could not be disconnected. Remote cleanup can be retried.")); }
    finally { setBusyAction(null); }
  }

  const environmentLabel = status?.environment === "production" ? "Production" : status?.environment === "sandbox" ? "eBay Sandbox" : status?.environment === "simulated" ? "Simulator" : "Unavailable";
  const readinessActions = status ? Array.from(new Set(status.readiness.missing.map(ebayReadinessLabel))) : [];
  const reconnectRequired = status?.connection?.status === "reconnect_required";
  const policyOrLocationIssue = Boolean(
    status?.readiness.missing.some((item) => /policy|location|seller setup refresh/i.test(item))
  );
  const selectedPoliciesValid = Boolean(
    paymentPolicyID &&
    fulfillmentPolicyID &&
    returnPolicyID &&
    merchantLocationKey &&
    status?.choices.paymentPolicies.some((choice) => choice.id === paymentPolicyID) &&
    status.choices.fulfillmentPolicies.some((choice) => choice.id === fulfillmentPolicyID) &&
    status.choices.returnPolicies.some((choice) => choice.id === returnPolicyID) &&
    status.choices.locations.some((choice) => choice.id === merchantLocationKey)
  );
  const savedPoliciesReady = Boolean(
    status?.configuration?.paymentPolicyID &&
    status.configuration.fulfillmentPolicyID &&
    status.configuration.returnPolicyID &&
    status.configuration.merchantLocationKey &&
    !policyOrLocationIssue
  );
  const currentStep = !status?.connection || reconnectRequired ? 0 : !savedPoliciesReady ? 1 : !status.notifications.ready ? 2 : 3;
  const authorizationNotice = status?.environment === "production"
    ? "Listings created through eBay's Inventory API must continue to be managed through that API. Confirm Listwork as the management path before the first live publication."
    : status?.environment === "sandbox"
      ? "This opens eBay Sandbox. Use a sandbox seller account; production listings, orders, and stock are not accessible."
      : "This creates an isolated Listwork simulator account without contacting eBay or changing marketplace data.";
  const connectionActionLabel = reconnectRequired
    ? "Reconnect eBay"
    : status?.environment === "simulated"
      ? "Connect eBay simulator"
      : status?.environment === "sandbox"
        ? "Continue to eBay Sandbox"
        : "Continue to eBay";

  return (
    <Panel className="ebay-connection-panel marketplace-journey">
      <div className="marketplace-journey-statusline" role="status" aria-live="polite">
        <Badge tone={status?.environment === "production" ? "success" : status?.configured ? "info" : "neutral"}>{environmentLabel}</Badge>
        <Badge tone={reconnectRequired ? "danger" : status?.readiness.ready ? status.environment === "production" ? "success" : "info" : "warning"}>{reconnectRequired ? "Action required" : status?.readiness.ready ? status.environment === "production" ? "Live" : "Simulator ready" : status?.connection ? "Configuration required" : "Connection required"}</Badge>
      </div>
      <ol className="marketplace-persistent-stepper" aria-label="eBay setup progress">
        {["Authorize seller", "Choose policies and location", "Verify notifications", "Review readiness"].map((label, index) => <li key={label} data-state={currentStep === index ? "current" : currentStep > index ? "complete" : undefined}><span>{currentStep > index ? <Check size={14} /> : index + 1}</span><div><strong>{label}</strong></div></li>)}
      </ol>
      <div className="marketplace-current-step">
        <p className="marketplace-step-kicker">{status?.readiness.ready ? "Manage connection" : `Step ${currentStep + 1} of 4`}</p>
        <h2 data-marketplace-step-heading tabIndex={-1}>{reconnectRequired ? "Reconnect the eBay seller account" : currentStep === 0 ? "Authorize an eBay seller account" : currentStep === 1 ? "Choose business policies and inventory location" : currentStep === 2 ? "Verify order notifications" : "Review eBay readiness"}</h2>
        <p>{status?.connection?.username ? `Seller account: ${status.connection.username}` : "eBay sign-in happens on eBay. Listwork validates the returned account before continuing."}</p>
      </div>
      {!canManage && <Alert tone="info">A workspace owner or admin manages marketplace connections.</Alert>}
      {!status ? (
        <div className="ebay-connection-loading"><span>Connection status unavailable.</span><Button variant="secondary" size="compact" type="button" onClick={() => void refresh()} disabled={busyAction === "refresh"} loading={Boolean(busyAction === "refresh")} loadingLabel="Refreshing"><RotateCw size={16} /> {busyAction === "refresh" ? "Refreshing" : "Retry"}</Button></div>
      ) : !status.configured ? (
        <Alert tone="info">eBay connections are not available in this environment.</Alert>
      ) : !status.connection || reconnectRequired ? (
        <>
          <div className="marketplace-access-summary"><strong>Requested access</strong><ul><li><ShieldCheck size={16} /> Create, revise, and end Inventory API listings</li><li><ShieldCheck size={16} /> Read seller policies and inventory locations</li><li><ShieldCheck size={16} /> Receive supported order notifications</li></ul></div>
          <Alert tone={status.environment === "production" ? "warning" : "info"}>{authorizationNotice}</Alert>
          {error && <Alert tone="danger">{error}</Alert>}
          {canManage && <div className="marketplace-primary-action"><Button type="button" onClick={() => void connect()} disabled={busyAction !== null} loading={Boolean(busyAction === "connect")} loadingLabel="Opening eBay"><Link2 size={18} /> {busyAction === "connect" ? "Opening eBay" : connectionActionLabel}</Button></div>}
        </>
      ) : (
        <FormLayout onSubmit={(event) => void save(event)}>
          {status.connection.lastError && <Alert tone="danger">{userSafeErrorMessage(new Error(status.connection.lastError), "eBay needs attention. Reconnect or retry provider checks.")}</Alert>}
          {!status.readiness.ready && <Alert tone="warning"><strong>Complete eBay setup</strong><p>{readinessActions.length ? `${readinessActions.join(", ")}.` : "Refresh provider checks to continue."}</p></Alert>}
          <div className="ebay-configuration-grid">
            <Field label="Payment policy"><SelectControl value={paymentPolicyID} onValueChange={setPaymentPolicyID} disabled={!canManage || busyAction !== null} options={[{ value: "", label: "Choose policy" }, ...status.choices.paymentPolicies.map((choice) => ({ value: choice.id, label: choice.name }))]} /></Field>
            <Field label="Delivery policy"><SelectControl value={fulfillmentPolicyID} onValueChange={setFulfillmentPolicyID} disabled={!canManage || busyAction !== null} options={[{ value: "", label: "Choose policy" }, ...status.choices.fulfillmentPolicies.map((choice) => ({ value: choice.id, label: choice.name }))]} /></Field>
            <Field label="Return policy"><SelectControl value={returnPolicyID} onValueChange={setReturnPolicyID} disabled={!canManage || busyAction !== null} options={[{ value: "", label: "Choose policy" }, ...status.choices.returnPolicies.map((choice) => ({ value: choice.id, label: choice.name }))]} /></Field>
            <Field label="Inventory location"><SelectControl value={merchantLocationKey} onValueChange={setMerchantLocationKey} disabled={!canManage || busyAction !== null} options={[{ value: "", label: "Choose location" }, ...status.choices.locations.map((choice) => ({ value: choice.id, label: choice.name }))]} /></Field>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          {canManage && (
            <div className="marketplace-primary-action">
              {currentStep === 1 || configurationChanged ? (
                <Button type="submit" disabled={!selectedPoliciesValid || busyAction !== null} loading={Boolean(busyAction === "save")} loadingLabel="Saving policies">
                  <CheckCircle2 size={18} /> {busyAction === "save" ? "Saving policies" : "Save policies and continue"}
                </Button>
              ) : currentStep === 2 ? (
                <Button type="submit" disabled={!selectedPoliciesValid || busyAction !== null} loading={Boolean(busyAction === "save")} loadingLabel="Verifying notifications">
                  <ShieldCheck size={18} /> {busyAction === "save" ? "Verifying notifications" : "Verify order notifications"}
                </Button>
              ) : (
                <Button variant="secondary" type="button" onClick={() => void refresh()} disabled={busyAction !== null} loading={Boolean(busyAction === "refresh")} loadingLabel="Refreshing health">
                  <RotateCw size={18} /> {busyAction === "refresh" ? "Refreshing health" : "Refresh connection health"}
                </Button>
              )}
            </div>
          )}
          {status.readiness.ready && <Alert tone={status.environment === "production" ? "success" : "info"}>{status.environment === "production" ? "eBay authorization, policies, inventory location, and notification prerequisites are verified." : "The eBay simulator is ready. No real seller account, listing, order, or stock is affected."}</Alert>}
          {canManage && (
            <DangerZone compact className="marketplace-connection-danger">
              <DangerZoneCopy><strong>Disconnect eBay</strong><p>Stops new operations. Existing Listwork records and publication mappings are retained.</p></DangerZoneCopy>
              {!disconnectOpen ? <Button variant="danger" type="button" aria-expanded={disconnectOpen} aria-controls="ebay-disconnect-confirm" onClick={() => setDisconnectOpen(true)} disabled={busyAction !== null}>Disconnect eBay</Button> : <DangerZoneConfirm><InlineConfirm id="ebay-disconnect-confirm" title={`Disconnect ${status.connection.username || "this eBay account"}?`} description="Listwork will attempt remote subscription cleanup and token revocation. Failed cleanup remains recoverable for retry." actions={<><Button variant="ghost" type="button" onClick={() => setDisconnectOpen(false)} disabled={busyAction === "disconnect"}>Keep connected</Button><Button variant="danger" type="button" onClick={() => void disconnect()} disabled={busyAction === "disconnect"} loading={Boolean(busyAction === "disconnect")} loadingLabel="Disconnecting">{busyAction === "disconnect" ? "Disconnecting" : "Confirm disconnect"}</Button></>} /></DangerZoneConfirm>}
            </DangerZone>
          )}
        </FormLayout>
      )}
    </Panel>
  );
}

function MarketplaceSignalsPanel({ marketplace, embedded = false }: { marketplace?: Marketplace; embedded?: boolean } = {}) {
  const store = useReOS();
  const toast = useToast();
  const workspace = store.workspace;
  const currentMember = store.workspaceMembers.find((member) => member.current);
  const canManage = currentMember?.role === "owner" || currentMember?.role === "admin";
  const signalEmail = workspace?.marketplaceSignalEmail;
  const evidence = forwardingReceiptEvidence(workspace, store.notifications, marketplace);
  const channelLabel = marketplace ? marketplaceLabels[marketplace] : "marketplace";
  const [busyAction, setBusyAction] = useState<"create" | "replace" | "disable" | null>(null);
  const [confirmAction, setConfirmAction] = useState<"replace" | "disable" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const forwardingConfirmations = evidence.current.filter((notification) => notification.marketplaceSignal?.eventType === "forwarding_verification").slice(0, 3);

  async function setSignalAddress(action: "create" | "replace") {
    try {
      setBusyAction(action);
      setError(null);
      await store.updateWorkspaceSettings({
        marketplaceSignalEmail: createMarketplaceSignalAddress(),
        marketplaceSignalsEnabledAt: new Date().toISOString()
      });
      setConfirmAction(null);
      toast.notify({ title: action === "replace" ? "Private address replaced" : "Private address ready", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Email monitoring could not be updated."));
    } finally {
      setBusyAction(null);
    }
  }

  async function disableSignals() {
    try {
      setBusyAction("disable");
      setError(null);
      await store.updateWorkspaceSettings({ marketplaceSignalEmail: null, marketplaceSignalsEnabledAt: null });
      setConfirmAction(null);
      toast.notify({ title: "Email monitoring turned off", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Email monitoring could not be turned off."));
    } finally {
      setBusyAction(null);
    }
  }

  async function copySignalAddress() {
    if (!signalEmail || !navigator.clipboard?.writeText) {
      setError("Clipboard access is unavailable. Select the address and copy it manually.");
      return;
    }
    try {
      await navigator.clipboard.writeText(signalEmail);
      toast.notify({ title: "Forwarding address copied", tone: "success" });
    } catch {
      setError("The forwarding address could not be copied. Select it and copy it manually.");
    }
  }

  async function copyVerificationCode(notification: ReOSNotification) {
    const code = notification.marketplaceSignal?.verificationCode;
    if (!code || !navigator.clipboard?.writeText) {
      setError("Clipboard access is unavailable. Select the confirmation code and copy it manually.");
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      void store.markNotificationRead(notification.id);
      toast.notify({ title: "Confirmation code copied", tone: "success" });
    } catch {
      setError("The confirmation code could not be copied. Select it and copy it manually.");
    }
  }

  return (
    <Panel className="marketplace-signals-panel">
      {!embedded && <PanelHeader title="Email forwarding" />}

      {!canManage && <Alert tone="info">A workspace owner or admin manages email forwarding.</Alert>}

      {signalEmail ? (
        <>
          <Field label="Forwarding address">
            <div className="marketplace-signal-address"><Input aria-label="Private forwarding address" value={signalEmail} readOnly spellCheck={false} />
              <Button variant="secondary" size="icon" type="button" aria-label="Copy forwarding address" onClick={() => void copySignalAddress()}><Copy size={18} /></Button>
            </div>
          </Field>
          <div className="forwarding-receipt-evidence">
            <strong>{evidence.latestSale ? `${marketplace ? marketplaceLabels[marketplace] : "Marketplace"} sale email received` : evidence.legacy ? "Receipt history unavailable for this address" : `Awaiting a ${channelLabel} sale email`}</strong>
            {evidence.latest && <dl><div><dt>Last received</dt><dd>{formatSessionDate(evidence.latest.createdAt)}</dd></div><div><dt>Email type</dt><dd>{evidence.latest.marketplaceSignal?.eventType === "forwarding_verification" ? "Forwarding confirmation" : `${marketplaceLabels[evidence.latest.marketplaceSignal!.marketplace!]} sale notification`}</dd></div></dl>}
          </div>
          <Disclosure className="forwarding-instructions" label="Forwarding instructions"><ol><li>Copy the address above into your email account’s forwarding settings.</li><li>Complete your email provider’s confirmation step. Any confirmation code appears below.</li><li>Forward {marketplace ? marketplaceLabels[marketplace] : "Vinted and Depop"} sale notifications to this address.</li></ol></Disclosure>
          <p className="forwarding-stock-note">Review detected sales in Needs attention. Stock changes after you confirm a sale.</p>

          {forwardingConfirmations.length > 0 && (
            <PanelSection>
              <SectionLabel>Forwarding confirmations</SectionLabel>
              <DataRowList>
                {forwardingConfirmations.map((notification) => (
                  <DataRowListItem key={notification.id}>
                    <DataRow as="div" className="ui-data-row-settings-data-record">
                      <DataRowMain>
                        <strong>{notification.title}</strong>
                        <span>{notification.body}</span>
                      </DataRowMain>
                      <DataRowValue className="marketplace-confirmation-action">
                        <code>{notification.marketplaceSignal?.verificationCode}</code>
                        <Button variant="secondary" size="compact" type="button" onClick={() => void copyVerificationCode(notification)}>Copy code</Button>
                      </DataRowValue>
                    </DataRow>
                  </DataRowListItem>
                ))}
              </DataRowList>
            </PanelSection>
          )}

          {canManage && <Disclosure className="forwarding-instructions" label="Manage forwarding address">
          {canManage && (
            <FormActions>
              <Button variant="secondary" type="button" onClick={() => setConfirmAction("replace")} disabled={Boolean(busyAction)}>
                <RotateCw size={17} /> Replace address
              </Button>
              <Button variant="danger" type="button" onClick={() => setConfirmAction("disable")} disabled={Boolean(busyAction)}>
                Turn off monitoring
              </Button>
            </FormActions>
          )}

          {confirmAction === "replace" && (
            <InlineConfirm
              title="Replace the private address?"
              description="The current address will stop accepting email immediately. Update every forwarding rule with the replacement address."
              actions={
                <>
                  <Button variant="ghost" type="button" onClick={() => setConfirmAction(null)} disabled={Boolean(busyAction)}>Cancel</Button>
                  <Button type="button" onClick={() => void setSignalAddress("replace")} disabled={Boolean(busyAction)} loading={Boolean(busyAction === "replace")} loadingLabel="Replacing">
                    {busyAction === "replace" ? "Replacing" : "Replace address"}
                  </Button>
                </>
              }
            />
          )}
          {confirmAction === "disable" && (
            <InlineConfirm
              title="Turn off email monitoring?"
              description="Forwarded messages will stop being accepted. Existing notifications and sale records will remain."
              actions={
                <>
                  <Button variant="ghost" type="button" onClick={() => setConfirmAction(null)} disabled={Boolean(busyAction)}>Cancel</Button>
                  <Button variant="danger" type="button" onClick={() => void disableSignals()} disabled={Boolean(busyAction)} loading={Boolean(busyAction === "disable")} loadingLabel="Turning off">
                    {busyAction === "disable" ? "Turning off" : "Turn off monitoring"}
                  </Button>
                </>
              }
            />
          )}
          </Disclosure>}
        </>
      ) : (
        <>
          <p>Forward {marketplace ? marketplaceLabels[marketplace] : "Vinted and Depop"} sale emails to Listwork to review detected sales.</p>
          {canManage && (
            <Button full type="button" onClick={() => void setSignalAddress("create")} disabled={Boolean(busyAction)} loading={Boolean(busyAction === "create")} loadingLabel="Creating">
              <Mail size={18} /> {busyAction === "create" ? "Creating" : "Create forwarding address"}
            </Button>
          )}
        </>
      )}

      {error && <Alert tone="danger">{error}</Alert>}
    </Panel>
  );
}

function createMarketplaceSignalAddress() {
  const token = crypto.randomUUID().replace(/-/g, "").toLowerCase();
  return `events+${token}@${MARKETPLACE_SIGNAL_EMAIL_DOMAIN}`;
}

function BillingPanel({
  billingReturn,
  isRefreshingBillingReturn,
  onDismissBillingReturn
}: {
  billingReturn: BillingReturnState | null;
  isRefreshingBillingReturn: boolean;
  onDismissBillingReturn: () => void;
}) {
  const store = useReOS();
  const billingIssueRef = useRef<HTMLDivElement | null>(null);
  const [busyPlan, setBusyPlan] = useState<SubscriptionPlan | null>(null);
  const [isBuyingAssistantPack, setIsBuyingAssistantPack] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [billingIssue, setBillingIssue] = useState<BillingIssue | null>(null);
  const [assistantUsage, setAssistantUsage] = useState<Awaited<ReturnType<typeof store.getAssistantUsage>> | null>(null);
  const [assistantUsageError, setAssistantUsageError] = useState(false);
  const currentPlan = store.subscription?.plan ?? "free";
  const currentStatus = store.subscription?.status;
  const subscriptionAccessActive = Boolean(store.subscription && subscriptionGrantsPaidEntitlements(store.subscription));
  const privateBetaAccess = subscriptionAccessActive
    ? undefined
    : privateBetaAccessForWorkspace(store.database, store.workspace?.id);
  const workspaceAccessActive = subscriptionAccessActive || Boolean(privateBetaAccess);
  const trialExpired = currentStatus === "trialing" && !workspaceAccessActive;
  const canManageBilling = workspaceRoleForStore(store) === "owner";
  const canOpenPortal = Boolean(store.subscription?.stripeCustomerID);
  const workspaceID = store.workspace?.id;
  const reservedSeats = store.workspaceMembers.length + store.workspaceInvitations.length;
  const itemLimit = limitForEntitlement(store.database, workspaceID, "inventory_items");
  const seatLimit = limitForEntitlement(store.database, workspaceID, "workspace_members");
  const storageLimitMB = limitForEntitlement(store.database, workspaceID, "cloud_storage_mb");
  const assistantLimit = assistantUsage?.limit ?? limitForEntitlement(store.database, workspaceID, "assistant_model_requests_monthly");
  const assistantUsed = assistantUsage?.used ?? 0;
  const assistantUsageLabel = !workspaceAccessActive
    ? "Plan inactive · choose a plan"
    : assistantUsage
      ? `${assistantUsed.toLocaleString()} of ${formatLimit(assistantLimit)} this month`
      : assistantUsageError
        ? "Temporarily unavailable"
        : "Loading usage";
  const storedAssetBytes = storedAssetUsageBytes(store.database, workspaceID);
  const usageRows: BillingUsageRow[] = [
    {
      kind: "meter",
      label: "Inventory records",
      value: workspaceAccessActive
        ? `${store.inventoryItems.length} of ${formatLimit(itemLimit)} items`
        : `${store.inventoryItems.length} retained · plan inactive`,
      percent: usagePercent(store.inventoryItems.length, itemLimit),
      tone: usageTone(store.inventoryItems.length, itemLimit)
    },
    {
      kind: "meter",
      label: "Seats",
      value: formatSeatUsage(reservedSeats, seatLimit),
      percent: usagePercent(reservedSeats, seatLimit),
      tone: seatUsageTone(reservedSeats, seatLimit)
    },
    {
      kind: "meter",
      label: "Photo storage",
      value: workspaceAccessActive
        ? `${formatStorageBytes(storedAssetBytes)} of ${formatStorageLimit(storageLimitMB)}`
        : `${formatStorageBytes(storedAssetBytes)} retained · plan inactive`,
      percent: usagePercent(storedAssetBytes, storageLimitMB * 1024 * 1024),
      tone: usageTone(storedAssetBytes, storageLimitMB * 1024 * 1024)
    },
    {
      kind: "meter",
      label: "Model-assisted requests",
      value: assistantUsageLabel,
      percent: usagePercent(assistantUsed, assistantLimit),
      tone: usageTone(assistantUsed, assistantLimit)
    },
    {
      kind: "entitlement",
      label: "Marketplace workflows",
      value: store.hasEntitlement("assisted_marketplace_publishing") ? "Included" : "Choose a plan",
      included: store.hasEntitlement("assisted_marketplace_publishing")
    }
  ];

  useEffect(() => {
    if (billingIssue) {
      window.requestAnimationFrame(() => {
        billingIssueRef.current?.scrollIntoView({ block: "nearest" });
        billingIssueRef.current?.focus({ preventScroll: true });
      });
    }
  }, [billingIssue]);

  useEffect(() => {
    let cancelled = false;
    setAssistantUsage(null);
    setAssistantUsageError(false);
    void store.getAssistantUsage()
      .then((usage) => {
        if (!cancelled) {
          setAssistantUsage(usage);
          setAssistantUsageError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAssistantUsage(null);
          setAssistantUsageError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceID]);

  async function startCheckout(plan: SubscriptionPlan) {
    if (!canManageBilling) {
      return;
    }
    try {
      setBusyPlan(plan);
      setBillingIssue(null);
      window.location.assign(await store.startBillingCheckout(plan));
    } catch (caught) {
      setBillingIssue(billingCheckoutIssue(plan));
      setBusyPlan(null);
    }
  }

  async function openPortal() {
    if (!canManageBilling) {
      return;
    }
    try {
      setIsOpeningPortal(true);
      setBillingIssue(null);
      window.location.assign(await store.openBillingPortal());
    } catch (caught) {
      setBillingIssue(billingPortalIssue());
      setIsOpeningPortal(false);
    }
  }

  async function buyAssistantRequestPack() {
    if (!canManageBilling || !subscriptionAccessActive) return;
    try {
      setIsBuyingAssistantPack(true);
      setBillingIssue(null);
      window.location.assign(await store.startAssistantRequestPackCheckout());
    } catch {
      setBillingIssue({
        title: "Request pack checkout unavailable",
        message: "We could not open secure checkout for the assistant request pack.",
        retryLabel: "Try again",
        retry: "assistant_pack"
      });
      setIsBuyingAssistantPack(false);
    }
  }

  function retryBillingIssue() {
    if (!billingIssue) {
      return;
    }
    if (billingIssue.retry === "portal") {
      void openPortal();
      return;
    }
    if (billingIssue.retry === "assistant_pack") {
      void buyAssistantRequestPack();
      return;
    }
    if (billingIssue.plan) {
      void startCheckout(billingIssue.plan);
    }
  }

  return (
    <Panel className="ui-panel-settings-billing">
      <PanelHeader title="Plan and usage" />
      {!canManageBilling && <Alert tone="info">Only the workspace owner can change plans, payment details, or cancellation settings.</Alert>}
      {trialExpired && (
        <Alert tone="warning">Your Starter trial has ended. Existing records are retained, but creating records, marketplace workflows, uploads, team changes, and model-assisted requests remain paused until you choose a plan.</Alert>
      )}
      {privateBetaAccess && (
        <Alert tone="info">
          <span>
            <strong>Private beta access is active</strong>
            <small>{privateBetaAccess.expiresAt ? `Pro-scale workspace limits are included through ${formatBillingDate(privateBetaAccess.expiresAt)}.` : "Pro-scale workspace limits are included during the private beta."} This is separate from billing; no paid plan starts unless you review and confirm checkout.</small>
          </span>
        </Alert>
      )}
      {billingReturn === "success" && (
        <Alert tone="success" className="ui-alert-billing-issue">
          <span>
            <strong>{isRefreshingBillingReturn ? "Checkout complete" : "Billing details updated"}</strong>
            <small>{isRefreshingBillingReturn ? "Refreshing plan details..." : `Current plan: ${subscriptionPlanLabels[currentPlan]}.`}</small>
          </span>
          <Button variant="secondary" size="compact" type="button" onClick={onDismissBillingReturn}>
            Dismiss
          </Button>
        </Alert>
      )}
      {billingReturn === "cancelled" && (
        <Alert tone="warning" className="ui-alert-billing-issue">
          <span>
            <strong>No plan changes made</strong>
            <small>You left checkout before confirming. Your current plan is unchanged.</small>
          </span>
          <Button variant="secondary" size="compact" type="button" onClick={onDismissBillingReturn}>
            Dismiss
          </Button>
        </Alert>
      )}
      <DataRow as="div" className="ui-data-row-billing-current">
        <DataRowMain className="ui-data-row-main-billing-current">
          <span>{privateBetaAccess ? "Access programme" : workspaceAccessActive ? "Current plan" : "Workspace access"}</span>
          <strong>{privateBetaAccess ? "Private beta" : workspaceAccessActive ? subscriptionPlanLabels[currentPlan] : "No active plan"}</strong>
          <small>{privateBetaAccess ? privateBetaStatusDetail(privateBetaAccess) : billingStatusDetail(store.subscription)}</small>
        </DataRowMain>
        <DataRowBadges className="ui-data-row-badges-billing-current-actions">
          {privateBetaAccess
            ? <Badge tone="info">Beta access</Badge>
            : trialExpired
            ? <Badge tone="warning">Trial expired</Badge>
            : currentStatus && <Badge tone={currentStatus === "active" || currentStatus === "trialing" ? "success" : currentStatus === "past_due" ? "warning" : "neutral"}>{subscriptionStatusLabels[currentStatus]}</Badge>}
          {store.subscription?.cancelAtPeriodEnd && <Badge tone="warning">Cancels at renewal</Badge>}
        </DataRowBadges>
      </DataRow>
      {currentStatus === "past_due" && (
        <Alert tone="warning" className="ui-alert-billing-issue">
          <span>
            <strong>Payment needs attention</strong>
            <small>Update payment to keep uploads, seats, and listing workflows available.</small>
          </span>
          {canManageBilling && canOpenPortal ? (
            <Button variant="secondary" size="compact" type="button" onClick={() => void openPortal()} disabled={isOpeningPortal || Boolean(busyPlan)}>
              Update payment
            </Button>
          ) : canManageBilling ? (
            <Button variant="secondary" size="compact" type="button" onClick={() => void startCheckout(currentPlan === "free" ? "starter" : currentPlan)} disabled={Boolean(busyPlan) || isOpeningPortal}>
              Review plan
            </Button>
          ) : null}
        </Alert>
      )}
      {currentStatus === "canceled" && !privateBetaAccess && (
        <Alert tone="warning">This workspace is read-only. Choose a paid plan to create inventory, upload photos, invite teammates, and prepare listing drafts.</Alert>
      )}
      <DataRowList aria-label="Plan usage">
        {usageRows.map((row) => (
          <DataRowListItem key={row.label}>
            <DataRow
              as="div"
              className={row.kind === "meter"
                ? "ui-data-row-billing-usage ui-data-row-billing-usage-meter"
                : "ui-data-row-billing-usage"}
            >
              <DataRowMain className="ui-data-row-main-billing-usage">
                {row.kind === "meter" ? (
                  <>
                    <DataRowLabelValue label={row.label} value={row.value} />
                    <Meter label={`${row.label}: ${row.value}`} value={row.percent} valueText={row.value} tone={row.tone} />
                  </>
                ) : (
                  <strong>{row.label}</strong>
                )}
              </DataRowMain>
              {row.kind === "entitlement" && (
                <DataRowValue className="ui-data-row-value-billing-usage">
                  <Badge tone={row.included ? "success" : "warning"}>{row.value}</Badge>
                </DataRowValue>
              )}
            </DataRow>
          </DataRowListItem>
        ))}
      </DataRowList>
      <PanelNote>Inventory usage counts every retained item record, including sold and archived items.</PanelNote>
      {workspaceAccessActive && assistantUsage && (
        <PanelNote>
          {assistantUsage.remaining.toLocaleString()} model-assisted requests remaining. Included usage resets {new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long" }).format(new Date(assistantUsage.resetsAt))}. Conversations used {assistantUsage.breakdown.conversations.toLocaleString()}; automations used {assistantUsage.breakdown.automations.toLocaleString()}.{assistantUsage.packRemaining > 0 ? ` ${assistantUsage.packRemaining.toLocaleString()} rollover requests remain.` : ""}
        </PanelNote>
      )}
      {assistantUsageError && (
        <PanelNote>Assistant usage could not be loaded. No consumption estimate is shown until the usage service responds.</PanelNote>
      )}
      <PanelSection>
        <SectionLabel>Plans</SectionLabel>
        <DataRowStack>
          {billingPlans.map((plan) => {
            const selected = subscriptionAccessActive && plan === currentPlan;
            const planEntitlements = planEntitlementsFor(plan);
            const rowContent = (
              <>
                <DataRowMain>
                  <strong>{subscriptionPlanLabels[plan]}</strong>
                  <DataRowMeta>{billingPlanCopy(plan)}</DataRowMeta>
                  <DataRowMeta className="ui-data-row-meta-billing-plan-capacity">{billingPlanCapacity(planEntitlements)} · {billingPlanPrice(plan)}</DataRowMeta>
                </DataRowMain>
                <DataRowValue className="ui-data-row-value-billing-plan-action">
                  {selected ? (
                    <>
                      Current <CheckCircle2 size={16} />
                    </>
                  ) : busyPlan === plan ? (
                    "Opening"
                  ) : canManageBilling ? (
                    <>
                      Review {subscriptionPlanLabels[plan]} <ChevronRight size={16} />
                    </>
                  ) : (
                    "Owner managed"
                  )}
                </DataRowValue>
              </>
            );

            return selected || !canManageBilling ? (
              <DataRow as="div" className="ui-data-row-billing-plan" key={plan} selected={selected} aria-current={selected ? "true" : undefined}>
                {rowContent}
              </DataRow>
            ) : (
              <DataRow
                className="ui-data-row-billing-plan"
                key={plan}
                onClick={() => void startCheckout(plan)}
                disabled={Boolean(busyPlan) || isOpeningPortal}
              >
                {rowContent}
              </DataRow>
            );
          })}
        </DataRowStack>
      </PanelSection>
      {canManageBilling && subscriptionAccessActive && (
        <PanelSection>
          <SectionLabel>Additional AI usage</SectionLabel>
          <DataRowStack>
            <DataRow as="div" interactive={false} className="ui-data-row-billing-plan">
              <DataRowMain>
                <strong>250 model-assisted requests</strong>
                <DataRowMeta>One-time request pack. Unused requests roll over.</DataRowMeta>
                <DataRowMeta>£5 including VAT</DataRowMeta>
              </DataRowMain>
              <DataRowValue>
                <Button variant="secondary" size="compact" type="button" onClick={() => void buyAssistantRequestPack()} disabled={isBuyingAssistantPack || Boolean(busyPlan) || isOpeningPortal} loading={Boolean(isBuyingAssistantPack)} loadingLabel="Opening...">
                  {isBuyingAssistantPack ? "Opening..." : "Add requests"}
                </Button>
              </DataRowValue>
            </DataRow>
          </DataRowStack>
        </PanelSection>
      )}
      {billingIssue && (
        <FocusRegion ref={billingIssueRef} aria-live="assertive">
          <Alert tone="danger" className="ui-alert-billing-issue">
            <span>
              <strong>{billingIssue.title}</strong>
              <small>{billingIssue.message}</small>
            </span>
            <Button variant="secondary" size="compact" type="button" onClick={retryBillingIssue} disabled={Boolean(busyPlan) || isOpeningPortal}>
              {billingIssue.retryLabel}
            </Button>
          </Alert>
        </FocusRegion>
      )}
      {canManageBilling && <PanelNote>Plan changes open a secure checkout first. Nothing changes until you confirm.</PanelNote>}
      {canManageBilling && canOpenPortal && (
        <Button full type="button" onClick={() => void openPortal()} disabled={isOpeningPortal || Boolean(busyPlan)} loading={Boolean(isOpeningPortal)} loadingLabel="Opening">
          <CreditCard size={18} /> {isOpeningPortal ? "Opening" : "Manage billing"}
        </Button>
      )}
      {canManageBilling && !canOpenPortal && (
        <PanelNote>Receipts, payment method, and cancellation controls appear here after the first checkout.</PanelNote>
      )}
    </Panel>
  );
}

function billingCheckoutIssue(plan: SubscriptionPlan): BillingIssue {
  return {
    title: "Plan review didn't open",
    message: `Your ${subscriptionPlanLabels[plan]} plan was not changed. Try again, or keep using your current plan while we resolve billing.`,
    retryLabel: "Try again",
    retry: "checkout",
    plan
  };
}

function billingPortalIssue(): BillingIssue {
  return {
    title: "Billing management didn't open",
    message: "Your plan is unchanged. Try again in a moment.",
    retryLabel: "Try again",
    retry: "portal"
  };
}

function billingPlanCopy(plan: SubscriptionPlan) {
  switch (plan) {
    case "starter":
      return "Solo inventory, listing workflows, and photo storage";
    case "pro":
      return "More inventory, photo storage, and 3 team seats";
    case "team":
      return "Shared inventory, photo storage, and 10 team seats";
    default:
      return "Select a plan to activate this workspace";
  }
}

function billingStatusDetail(subscription: ReturnType<typeof useReOS>["subscription"]) {
  if (!subscription) {
    return "Select a plan to create or change workspace records.";
  }
  if (subscription.status === "trialing" && !subscriptionGrantsPaidEntitlements(subscription)) {
    return subscription.trialEndsAt
      ? `Trial ended ${formatBillingDate(subscription.trialEndsAt)}. Choose a plan to resume workspace changes.`
      : "Trial access has ended. Choose a plan to resume workspace changes.";
  }
  if (subscription.status === "trialing" && subscription.trialEndsAt) {
    return `Trial access until ${formatBillingDate(subscription.trialEndsAt)}.`;
  }
  if (subscription.status === "active" && subscription.currentPeriodEndsAt) {
    return subscription.cancelAtPeriodEnd ? `Access remains active until ${formatBillingDate(subscription.currentPeriodEndsAt)}.` : `Next renewal ${formatBillingDate(subscription.currentPeriodEndsAt)}.`;
  }
  if (subscription.status === "past_due") {
    return "Update payment to avoid workspace limits.";
  }
  if (subscription.status === "canceled") {
    return "Paid plan features are no longer active.";
  }
  return "Checkout will confirm the final price before payment.";
}

function privateBetaStatusDetail(access: NonNullable<ReturnType<typeof privateBetaAccessForWorkspace>>) {
  return access.expiresAt
    ? `Included access through ${formatBillingDate(access.expiresAt)} with Pro-scale workspace limits.`
    : "Included access with Pro-scale workspace limits during the private beta.";
}

function billingPlanCapacity(entitlements: ReturnType<typeof planEntitlementsFor>) {
  const items = entitlements.inventory_items;
  const seats = entitlements.workspace_members;
  const storage = entitlements.cloud_storage_mb;
  const assistantRequests = entitlements.assistant_model_requests_monthly;
  return `${formatLimit(typeof items === "number" ? items : 0)} items · ${formatSeatLimit(typeof seats === "number" ? seats : 0)} · ${formatStorageLimit(typeof storage === "number" ? storage : 0)} · ${formatLimit(typeof assistantRequests === "number" ? assistantRequests : 0)} model-assisted requests`;
}

function billingPlanPrice(plan: SubscriptionPlan) {
  const monthlyPrice = plan === "starter" ? "£12" : plan === "pro" ? "£24" : plan === "team" ? "£49" : "£0";
  return `${monthlyPrice}/month including VAT`;
}

function usagePercent(used: number, limit: number) {
  if (!Number.isFinite(limit) || limit <= 0) {
    return used > 0 ? 100 : 0;
  }
  if (used <= 0) {
    return 0;
  }
  return Math.max(3, Math.min(100, Math.round((used / limit) * 100)));
}

function usageTone(used: number, limit: number): "good" | "warn" | "danger" {
  if (!Number.isFinite(limit) || limit <= 0) {
    return used > 0 ? "danger" : "warn";
  }
  if (used > limit) {
    return "danger";
  }
  const ratio = used / limit;
  if (ratio >= 0.8) {
    return "warn";
  }
  return "good";
}

function seatUsageTone(used: number, limit: number): "good" | "neutral" | "warn" | "danger" {
  if (!Number.isFinite(limit) || limit <= 0) {
    return used > 0 ? "danger" : "warn";
  }
  if (used > limit) {
    return "danger";
  }
  if (used === limit) {
    return limit === 1 ? "neutral" : "warn";
  }
  return usageTone(used, limit);
}

function formatLimit(limit: number) {
  if (!Number.isFinite(limit)) {
    return "unlimited";
  }
  return new Intl.NumberFormat("en-GB").format(limit);
}

function formatSeatLimit(limit: number) {
  return `${formatLimit(limit)} ${limit === 1 ? "seat" : "seats"}`;
}

function formatSeatUsage(used: number, limit: number) {
  if (!Number.isFinite(limit)) {
    return `${formatLimit(used)} ${used === 1 ? "seat" : "seats"} active`;
  }
  if (limit === 1 && used <= 1) {
    return "1 seat included";
  }
  return `${formatLimit(used)} of ${formatLimit(limit)} reserved`;
}

function formatStorageLimit(limitMB: number) {
  if (!Number.isFinite(limitMB)) {
    return "unlimited";
  }
  if (limitMB <= 0) {
    return "0 MB";
  }
  if (limitMB >= 1000) {
    return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: limitMB % 1000 === 0 ? 0 : 1 }).format(limitMB / 1000)} GB`;
  }
  return `${new Intl.NumberFormat("en-GB").format(limitMB)} MB`;
}

function formatStorageBytes(bytes: number) {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024 * 1024))} GB`;
  }
  if (bytes >= 1024 * 1024) {
    return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
  }
  if (bytes >= 1024) {
    return `${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 }).format(bytes / 1024)} KB`;
  }
  return `${new Intl.NumberFormat("en-GB").format(bytes)} B`;
}

function formatBillingDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
}

function TeamAccessPanel({ onOpenBilling }: { onOpenBilling: () => void }) {
  const store = useReOS();
  const toast = useToast();
  const currentMember = store.workspaceMembers.find((member) => member.current);
  const canManage = currentMember?.role === "owner" || currentMember?.role === "admin";
  const reservedSeats = store.workspaceMembers.length + store.workspaceInvitations.length;
  const hasSeatLimit = Number.isFinite(store.workspaceMemberLimit);
  const hasAvailableSeats = !hasSeatLimit || reservedSeats < store.workspaceMemberLimit;
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [busyMemberID, setBusyMemberID] = useState<string | null>(null);
  const [busyInvitationID, setBusyInvitationID] = useState<string | null>(null);
  const [memberRemovalPromptID, setMemberRemovalPromptID] = useState<string | null>(null);
  const [invitationRevokePromptID, setInvitationRevokePromptID] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const workspaceID = store.workspace?.id;

  useEffect(() => {
    if (!workspaceID) {
      return;
    }
    let cancelled = false;
    Promise.all([store.refreshWorkspaceMembers(), store.refreshWorkspaceInvitations()]).catch((caught) => {
      if (!cancelled) {
        setError(userSafeErrorMessage(caught, "Unable to load team access."));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceID]);

  async function sendInvitation() {
    const normalizedEmail = email.trim();
    if (!validEmailAddress(normalizedEmail)) {
      setEmailError("Enter a valid email address.");
      requestAnimationFrame(() => emailInputRef.current?.focus({ preventScroll: true }));
      return;
    }
    try {
      setIsAdding(true);
      setError(null);
      setEmailError(null);
      await store.createWorkspaceInvitation(normalizedEmail, role);
      setEmail("");
      setRole("member");
      toast.notify({ title: "Invite sent", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to send this invite."));
    } finally {
      setIsAdding(false);
    }
  }

  async function removeMember(memberID: string) {
    try {
      setBusyMemberID(memberID);
      setError(null);
      await store.removeWorkspaceMember(memberID);
      setMemberRemovalPromptID(null);
      toast.notify({ title: "Teammate removed", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to remove this teammate."));
    } finally {
      setBusyMemberID(null);
    }
  }

  async function revokeInvitation(invitationID: string) {
    try {
      setBusyInvitationID(invitationID);
      setError(null);
      await store.revokeWorkspaceInvitation(invitationID);
      setInvitationRevokePromptID(null);
      toast.notify({ title: "Invite cancelled", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to cancel this invite."));
    } finally {
      setBusyInvitationID(null);
    }
  }

  async function refreshTeam() {
    try {
      setError(null);
      await Promise.all([store.refreshWorkspaceMembers(), store.refreshWorkspaceInvitations()]);
      toast.notify({ title: "Team refreshed", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to refresh team access."));
    }
  }

  return (
    <Panel className="ui-panel-settings-team-access">
      <PanelHeader
        title="Team access"
        meta={hasSeatLimit ? `${reservedSeats} of ${store.workspaceMemberLimit} ${store.workspaceMemberLimit === 1 ? "seat" : "seats"} reserved` : "Seats available"}
        action={
          <IconControlTooltip label="Refresh team">
            <Button
              variant="secondary"
              size="icon"
              type="button"
              aria-label="Refresh team"
              onClick={() => void refreshTeam()}
            >
              <RotateCw size={18} />
            </Button>
          </IconControlTooltip>
        }
      />
      <DataRowList>
        {store.workspaceMembers.map((member) => {
          const memberName = member.name || member.email;
          const removalPromptOpen = memberRemovalPromptID === member.id;
          const removalPromptID = `member-remove-confirm-${member.id}`;

          return (
            <DataRowListItem key={member.id}>
              <DataRow as="div" className="ui-data-row-settings-member-access">
                <DataRowMedia className="ui-data-row-media-member-avatar">{initialsForName(memberName)}</DataRowMedia>
                <DataRowMain>
                  <strong>{member.current ? `${memberName} (you)` : memberName}</strong>
                  <span>{member.email}</span>
                </DataRowMain>
                <Badge tone={member.role === "owner" ? "strong" : member.role === "admin" ? "info" : "neutral"}>{workspaceRoleLabel(member.role)}</Badge>
                {canManage && member.role !== "owner" && !member.current && (
                  <IconControlTooltip label={`Remove ${memberName}`}>
                    <Button
                      variant="danger"
                      size="icon"
                      type="button"
                      aria-label={`Remove ${memberName}`}
                      aria-expanded={removalPromptOpen}
                      aria-controls={removalPromptID}
                      onClick={() => {
                        setError(null);
                        setMemberRemovalPromptID(member.id);
                        setInvitationRevokePromptID(null);
                      }}
                      disabled={busyMemberID === member.id}
                    >
                      <X size={17} />
                    </Button>
                  </IconControlTooltip>
                )}
              </DataRow>
              {removalPromptOpen && (
                <InlineConfirm
                  id={removalPromptID}
                  title={`Remove ${memberName}?`}
                  description="They will lose access to this workspace immediately. Inventory, listings, sales, and activity history stay in Listwork."
                  actions={
                    <>
                      <Button variant="ghost" type="button" onClick={() => setMemberRemovalPromptID(null)} disabled={busyMemberID === member.id}>
                        Keep teammate
                      </Button>
                      <Button variant="danger" type="button" onClick={() => void removeMember(member.id)} disabled={busyMemberID === member.id} loading={Boolean(busyMemberID === member.id)} loadingLabel="Removing...">
                        {busyMemberID === member.id ? "Removing..." : "Remove teammate"}
                      </Button>
                    </>
                  }
                />
              )}
            </DataRowListItem>
          );
        })}
      </DataRowList>
      {store.workspaceInvitations.length > 0 && (
        <PanelSection as="section" aria-label="Pending invites">
          <SectionLabel>Pending invites</SectionLabel>
          <DataRowList>
            {store.workspaceInvitations.map((invitation) => {
              const invitationPromptOpen = invitationRevokePromptID === invitation.id;
              const invitationPromptID = `invitation-revoke-confirm-${invitation.id}`;

              return (
                <DataRowListItem key={invitation.id}>
                  <DataRow as="div" className="ui-data-row-settings-member-access">
                    <DataRowMedia className="ui-data-row-media-member-avatar ui-data-row-media-member-avatar-muted">
                      <Clock3 size={17} />
                    </DataRowMedia>
                    <DataRowMain>
                      <strong>{invitation.email}</strong>
                      <span>Expires {formatInvitationDate(invitation.expiresAt)}</span>
                    </DataRowMain>
                    <Badge tone="info">{workspaceRoleLabel(invitation.role)}</Badge>
                    {canManage && (
                      <IconControlTooltip label={`Cancel invite for ${invitation.email}`}>
                        <Button
                          variant="danger"
                          size="icon"
                          type="button"
                          aria-label={`Cancel invite for ${invitation.email}`}
                          aria-expanded={invitationPromptOpen}
                          aria-controls={invitationPromptID}
                          onClick={() => {
                            setError(null);
                            setInvitationRevokePromptID(invitation.id);
                            setMemberRemovalPromptID(null);
                          }}
                          disabled={busyInvitationID === invitation.id}
                        >
                          <X size={17} />
                        </Button>
                      </IconControlTooltip>
                    )}
                  </DataRow>
                  {invitationPromptOpen && (
                    <InlineConfirm
                      id={invitationPromptID}
                      title={`Cancel invite to ${invitation.email}?`}
                      description="The current invite link will stop working. You can send a new invite later if this person still needs access."
                      actions={
                        <>
                          <Button variant="ghost" type="button" onClick={() => setInvitationRevokePromptID(null)} disabled={busyInvitationID === invitation.id}>
                            Keep invite
                          </Button>
                          <Button variant="danger" type="button" onClick={() => void revokeInvitation(invitation.id)} disabled={busyInvitationID === invitation.id} loading={Boolean(busyInvitationID === invitation.id)} loadingLabel="Cancelling...">
                            {busyInvitationID === invitation.id ? "Cancelling..." : "Cancel invite"}
                          </Button>
                        </>
                      }
                    />
                  )}
                </DataRowListItem>
              );
            })}
          </DataRowList>
        </PanelSection>
      )}
      {canManage && (
        hasAvailableSeats ? (
          <FormLayout
            noValidate
            aria-busy={isAdding}
            onSubmit={(event) => {
              event.preventDefault();
              void sendInvitation();
            }}
          >
            <Field label="Email" error={emailError}>
              <Input
                ref={emailInputRef}
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) setEmailError(null);
                }}
                placeholder="jules@northloopvintage.co.uk"
              />
            </Field>
            <Field label="Role">
              <SelectControl
                value={role}
                onValueChange={(value) => setRole(value as "admin" | "member")}
                options={[
                  { value: "member", label: "Member" },
                  { value: "admin", label: "Admin" }
                ]}
              />
            </Field>
            <Button full type="submit" disabled={isAdding || !email.trim()} loading={isAdding} loadingLabel="Sending">
              <Plus size={18} /> {isAdding ? "Sending" : "Send invite"}
            </Button>
          </FormLayout>
        ) : (
          <Alert tone="warning" className="ui-alert-seat-limit">
            <span>
              <strong>No seats available</strong>
              <small>Choose a plan with more seats before inviting another teammate.</small>
            </span>
            <Button variant="secondary" size="compact" type="button" onClick={onOpenBilling}>
              View plans
            </Button>
          </Alert>
        )
      )}
      {error && <Alert tone="danger">{error}</Alert>}
    </Panel>
  );
}

function LocalDataPanel() {
  const store = useReOS();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function exportData() {
    try {
      setIsExporting(true);
      setError(null);
      const accountExport = await store.exportAccountData();
      const workspaceSlug = slugForFile(accountExport.workspace?.name ?? "workspace");
      downloadJsonFile(`listwork-local-${workspaceSlug}-${accountExport.exportedAt.slice(0, 10)}.json`, accountExport);
      toast.notify({ title: "Backup downloaded", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to prepare your download."));
    } finally {
      setIsExporting(false);
    }
  }

  function reloadDemoData() {
    store.loadDemoData();
    setResetOpen(false);
    setError(null);
    toast.notify({ title: "Demo data reloaded", tone: "success" });
  }

  function resetDemoData() {
    store.resetAllData();
    setResetOpen(false);
    setError(null);
    toast.notify({ title: "Demo data reset", tone: "success" });
  }

  return (
    <Panel>
      <PanelHeader title="Local demo data" />
      <FormActions>
        <Button type="button" onClick={() => void exportData()} disabled={isExporting} loading={Boolean(isExporting)} loadingLabel="Preparing">
          <Download size={18} /> {isExporting ? "Preparing" : "Download backup"}
        </Button>
        <Button type="button" onClick={reloadDemoData}>
          <Sparkles size={18} /> Reload demo
        </Button>
      </FormActions>
      <DangerZone compact>
        <DangerZoneCopy>
          <strong>Reset demo data</strong>
          <small>This clears the demo workspace saved on this device.</small>
        </DangerZoneCopy>
        {!resetOpen ? (
          <Button variant="danger" type="button" data-inline-confirm-return onClick={() => setResetOpen(true)}>
            <Trash2 size={18} /> Reset
          </Button>
        ) : (
          <InlineConfirm
            title="Reset the local workspace?"
            description="All local changes on this device will be removed. You can reload the demonstration catalogue afterwards."
            actions={(
              <>
                <Button variant="ghost" type="button" onClick={() => setResetOpen(false)}>
                  Keep data
                </Button>
                <Button variant="danger" type="button" onClick={resetDemoData}>
                  Reset demo
                </Button>
              </>
            )}
          />
        )}
      </DangerZone>
      {error && <Alert tone="danger">{error}</Alert>}
    </Panel>
  );
}

function WorkspaceUnavailablePanel() {
  return (
    <Panel>
      <PanelHeader title="Workspace unavailable" />
      <Alert tone="warning">Listwork cannot save changes until this workspace is connected. Reopen the workspace link or contact support before continuing.</Alert>
    </Panel>
  );
}

function AccountDataPanel({ onOpenTeam }: { onOpenTeam: () => void }) {
  const store = useReOS();
  const toast = useToast();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const deleteConfirmRef = useRef<HTMLDivElement | null>(null);
  const deleteConfirmInputRef = useRef<HTMLInputElement | null>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const workspaceID = store.workspace?.id;
  const workspaceName = store.workspace?.name ?? "this workspace";
  const currentMember = store.workspaceMembers.find((member) => member.current);
  const isWorkspaceOwner = currentMember?.role === "owner" || store.workspace?.ownerUserID === store.user?.id;
  const ownsAnyWorkspace = isWorkspaceOwner || store.accountWorkspaces.some((workspace) => workspace.role === "owner");
  const otherWorkspaceMemberCount = store.workspaceMembers.filter((member) => !member.current).length;
  const deletionBlockedByMembers = isWorkspaceOwner && otherWorkspaceMemberCount > 0;
  const itemIDs = new Set(store.inventoryItems.map((item) => item.id));
  const photoCount = store.database.itemPhotos.filter((photo) => itemIDs.has(photo.itemID)).length;
  const storedAssetBytes = store.database.storedAssets
    .filter((asset) => asset.workspaceID === workspaceID)
    .reduce((total, asset) => total + asset.byteSize, 0);
  const dataSummaryRows = [
    { label: "Inventory", value: countWithFallback(store.inventoryItems.length, "item") },
    { label: "Listings", value: countWithFallback(store.listings.length, "listing") },
    { label: "Orders", value: countWithFallback(store.sales.length, "order") },
    { label: "Photos", value: `${countWithFallback(photoCount, "photo")} · ${formatStorageBytes(storedAssetBytes)}` }
  ];
  const [isExporting, setIsExporting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ message: string; tone: "success" | "warning" } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  function closeDeleteConfirmation() {
    setDeleteOpen(false);
    setConfirmation("");
    setDeleteError(null);
    requestAnimationFrame(() => deleteTriggerRef.current?.focus({ preventScroll: true }));
  }

  useEffect(() => {
    if (!deleteOpen) {
      return;
    }
    window.requestAnimationFrame(() => {
      deleteConfirmRef.current?.scrollIntoView({ block: "end" });
      deleteConfirmInputRef.current?.focus({ preventScroll: true });
    });
  }, [deleteOpen]);

  async function exportData() {
    try {
      setIsExporting(true);
      setError(null);
      const accountExport = await store.exportAccountData();
      const accountSlug = slugForFile(accountExport.account?.email ?? accountExport.workspace?.name ?? "account");
      downloadJsonFile(`listwork-account-${accountSlug}-${accountExport.exportedAt.slice(0, 10)}.json`, accountExport);
      toast.notify({ title: "Data downloaded", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to prepare your download."));
    } finally {
      setIsExporting(false);
    }
  }

  function downloadCatalogueArchive() {
    try {
      setIsArchiving(true);
      setError(null);
      const link = document.createElement("a");
      link.href = store.accountArchiveDownloadURL();
      link.download = "";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.notify({ title: "Archive download started", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to start the catalogue archive download."));
    } finally {
      setIsArchiving(false);
    }
  }

  async function importBackup(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    try {
      setIsImporting(true);
      setError(null);
      setImportResult(null);
      const backup = JSON.parse(await file.text()) as unknown;
      const result = await store.importLocalBackup(backup);
      const importedCount = importedRecordCount(result.imported);
      const importTone = importedCount > 0 ? "success" : "warning";
      setImportResult({ message: importSummaryText(result.imported, result.skipped), tone: importTone });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to import that backup."));
    } finally {
      setIsImporting(false);
    }
  }

  async function deleteAccount() {
    try {
      setIsDeleting(true);
      setDeleteError(null);
      toast.dismissAll();
      await store.deleteAccount(confirmation);
    } catch (caught) {
      setDeleteError(userSafeErrorMessage(caught, "Unable to delete this account."));
      setIsDeleting(false);
    }
  }

  return (
    <div className="settings-data-layout">
      <Panel className="ui-panel-settings-data">
        <PanelHeader title="Exports and backups" meta={store.user?.email} />
        <PanelSection aria-label="Records included in account downloads">
          <SectionLabel>Included records</SectionLabel>
          <DataRowList role="list">
            {dataSummaryRows.map((row) => (
              <DataRowListItem key={row.label}>
                <DataRow as="div" interactive={false} className="ui-data-row-settings-data-record">
                  <DataRowMain>
                    <span>{row.label}</span>
                  </DataRowMain>
                  <DataRowValue>{row.value}</DataRowValue>
                </DataRow>
              </DataRowListItem>
            ))}
          </DataRowList>
        </PanelSection>
        <DataRowList>
          {ownsAnyWorkspace && (
            <DataRowListItem>
              <DataRow as="div" className="ui-data-row-settings-data-action">
                <DataRowMain>
                  <strong>Catalogue archive</strong>
                  <span>Download all workspace records, original photos, and saved edits as a ZIP.</span>
                </DataRowMain>
                <Button type="button" aria-label="Download catalogue archive" onClick={downloadCatalogueArchive} disabled={isArchiving} loading={isArchiving} loadingLabel="Starting">
                  <Download size={18} /> {isArchiving ? "Starting" : "Download ZIP"}
                </Button>
              </DataRow>
            </DataRowListItem>
          )}
          <DataRowListItem>
            <DataRow as="div" className="ui-data-row-settings-data-action">
              <DataRowMain>
                <strong>Account data</strong>
                <span>Download the workspace records this account can access as JSON.</span>
              </DataRowMain>
              <Button type="button" aria-label="Download account data" onClick={() => void exportData()} disabled={isExporting} loading={Boolean(isExporting)} loadingLabel="Preparing">
                <Download size={18} /> {isExporting ? "Preparing" : "Download JSON"}
              </Button>
            </DataRow>
          </DataRowListItem>
          {isWorkspaceOwner && (
            <DataRowListItem>
              <DataRow as="div" className="ui-data-row-settings-data-action">
                <DataRowMain>
                  <strong>Import backup</strong>
                  <span>Import inventory, listings, orders, and catalogue records from a Listwork JSON backup.</span>
                </DataRowMain>
                <Button type="button" aria-label="Import Listwork backup" onClick={() => importInputRef.current?.click()} disabled={isImporting} loading={Boolean(isImporting)} loadingLabel="Importing">
                  <Upload size={18} /> {isImporting ? "Importing" : "Import"}
                </Button>
                <FileInput ref={importInputRef} accept="application/json,.json" onChange={(event) => void importBackup(event)} hidden />
              </DataRow>
            </DataRowListItem>
          )}
        </DataRowList>
        <PanelNote>
          {ownsAnyWorkspace
            ? "The catalogue ZIP includes stored photo files, JSON, CSV, and a checksum manifest. The JSON-only backup does not include photo files, and the current importer does not restore photos automatically."
            : "The JSON-only backup does not include photo files, and the current importer does not restore photos automatically. Workspace owners can download the full catalogue archive."}
        </PanelNote>
        <LegalLinks />
        {importResult && <Alert tone={importResult.tone}>{importResult.message}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
      </Panel>
      <Panel className="ui-panel-settings-data">
        <PanelHeader title="Delete account" />
        <DangerZone>
          <DangerZoneCopy>
            <strong>Permanent deletion</strong>
            {deletionBlockedByMembers ? (
              <small>
                Remove {countWithFallback(otherWorkspaceMemberCount, "teammate")} before deleting this owner account. The workspace and its records stay protected until only the owner remains.
              </small>
            ) : isWorkspaceOwner ? (
              <small>
                Permanently removes {workspaceName}, {countWithFallback(store.inventoryItems.length, "item")}, {countWithFallback(store.listings.length, "listing")}, {countWithFallback(store.sales.length, "order")}, {countWithFallback(photoCount, "saved photo")}, sessions, and sign-in details.
              </small>
            ) : (
              <small>
                Permanently removes this account, active sessions, sign-in details, and access to {workspaceName}. Workspace records stay available to the owner.
              </small>
            )}
          </DangerZoneCopy>
          {deletionBlockedByMembers ? (
            <Button variant="secondary" type="button" onClick={onOpenTeam}>
              Review team <ChevronRight size={18} />
            </Button>
          ) : !deleteOpen ? (
            <Button ref={deleteTriggerRef} variant="danger" type="button" onClick={() => setDeleteOpen(true)}>
              <Trash2 size={18} /> Delete account
            </Button>
          ) : (
            <DangerZoneConfirm ref={deleteConfirmRef}>
              <Alert tone="warning">
                {isWorkspaceOwner
                  ? "This cannot be undone. Download a copy first if you need records for accounting, support, or tax reporting."
                  : "This cannot be undone for your account. The shared workspace and its records are not deleted."}
              </Alert>
              <Field label="Type DELETE">
                <Input ref={deleteConfirmInputRef} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" />
              </Field>
              <InlineConfirmActions>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={closeDeleteConfirmation}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button variant="danger" type="button" onClick={() => void deleteAccount()} disabled={isDeleting || confirmation.trim() !== "DELETE"} loading={Boolean(isDeleting)} loadingLabel="Deleting">
                  {isDeleting ? "Deleting" : "Delete permanently"}
                </Button>
              </InlineConfirmActions>
            </DangerZoneConfirm>
          )}
        </DangerZone>
        {deleteError && <Alert tone="danger">{deleteError}</Alert>}
      </Panel>
    </div>
  );
}

function LegalLinks({ className }: { className?: string }) {
  if (LEGAL_LINKS.length === 0) {
    return null;
  }
  return (
    <nav className={className ? `legal-links ${className}` : "legal-links"} aria-label="Legal">
      {LEGAL_LINKS.map((link) => (
        <a key={link.label} href={link.url} target="_blank" rel="noreferrer">
          {link.label}
        </a>
      ))}
    </nav>
  );
}

function AccountSecurityPanel() {
  const store = useReOS();
  const toast = useToast();
  const [revokingID, setRevokingID] = useState<string | null>(null);
  const [pendingSessionRevocationID, setPendingSessionRevocationID] = useState<string | null>(null);
  const [revokingPasskeyID, setRevokingPasskeyID] = useState<string | null>(null);
  const [isAddingPasskey, setIsAddingPasskey] = useState(false);
  const [isLoadingSecurity, setIsLoadingSecurity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accountID = store.user?.id;

  useEffect(() => {
    if (!accountID) {
      return;
    }
    let cancelled = false;
    setIsLoadingSecurity(true);
    Promise.all([store.refreshSessions(), store.refreshPasskeys()])
      .catch((caught) => {
        if (!cancelled) {
          setError(userSafeErrorMessage(caught, "Unable to load account security."));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSecurity(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountID]);

  async function revoke(sessionID: string) {
    try {
      setRevokingID(sessionID);
      setError(null);
      await store.revokeSession(sessionID);
      setPendingSessionRevocationID(null);
      toast.notify({ title: "Session ended", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to revoke this session."));
    } finally {
      setRevokingID(null);
    }
  }

  async function refreshSessions() {
    try {
      setIsLoadingSecurity(true);
      setError(null);
      await store.refreshSessions();
      toast.notify({ title: "Sessions refreshed", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to refresh sessions."));
    } finally {
      setIsLoadingSecurity(false);
    }
  }

  async function addPasskey() {
    try {
      setIsAddingPasskey(true);
      setError(null);
      if (!window.PublicKeyCredential) {
        throw new Error(
          store.passkeys.length > 0
            ? "Device sign-in is not available here. Use a device that supports Face ID, fingerprint, or a device PIN."
            : "Device sign-in is not available here. You can continue using email codes."
        );
      }
      const result = await store.createAccountPasskeyRegistrationOptions();
      const credential = await startRegistration({
        optionsJSON: result.options as Parameters<typeof startRegistration>[0]["optionsJSON"]
      });
      await store.completeAccountPasskeyRegistration(result.challengeID, credential);
      toast.notify({ title: "Device sign-in added", tone: "success" });
    } catch (caught) {
      setError(passkeyErrorMessage(caught, "Device sign-in setup didn't finish. You can continue using email codes."));
    } finally {
      setIsAddingPasskey(false);
    }
  }

  async function revokePasskey(passkeyID: string) {
    try {
      setRevokingPasskeyID(passkeyID);
      setError(null);
      await store.revokePasskey(passkeyID);
      toast.notify({ title: "Device sign-in removed", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to remove this passkey."));
    } finally {
      setRevokingPasskeyID(null);
    }
  }

  return (
    <Panel className="ui-panel-settings-account-security">
      <PanelHeader
        title="Account security"
        meta={store.user?.email}
        action={
          <IconControlTooltip label="Refresh sessions">
            <Button variant="secondary" size="icon" type="button" aria-label="Refresh sessions" onClick={() => void refreshSessions()}>
              <RotateCw size={18} />
            </Button>
          </IconControlTooltip>
        }
      />
      <EmailVerificationPrompt surface="panel" />
      <PasskeyManagement
        passkeys={store.passkeys}
        isAdding={isAddingPasskey}
        revokingID={revokingPasskeyID}
        onAdd={() => void addPasskey()}
        onRevoke={(passkeyID) => void revokePasskey(passkeyID)}
      />
      {error && <Alert tone="danger">{error}</Alert>}
      <SectionLabel>Active sessions</SectionLabel>
      <DataRowList>
        {isLoadingSecurity ? (
          <DataRowListItem><EmptyNote>Loading active sessions...</EmptyNote></DataRowListItem>
        ) : store.sessions.length === 0 && (
          <DataRowListItem><EmptyNote>No active sessions found.</EmptyNote></DataRowListItem>
        )}
        {store.sessions.map((session) => {
          const pending = pendingSessionRevocationID === session.id;
          return (
            <DataRowListItem key={session.id}>
              <DataRow as="div" className="ui-data-row-settings-account-access">
                <DataRowMedia className="ui-data-row-media-session">{session.current ? <CheckCircle2 size={18} /> : <Clock3 size={18} />}</DataRowMedia>
                <DataRowMain>
                  <strong>{session.current ? "This session" : "Signed-in session"}</strong>
                  <span>Started {formatSessionDate(session.createdAt)}</span>
                  <small>Expires {formatSessionDate(session.expiresAt)}</small>
                </DataRowMain>
                <Button
                  variant={session.current ? "secondary" : "danger"}
                  type="button"
                  disabled={revokingID === session.id || pending}
                  onClick={() => (session.current ? void revoke(session.id) : setPendingSessionRevocationID(session.id))}
                 loading={Boolean(revokingID === session.id)} loadingLabel="Revoking">
                  {session.current ? <LogOut size={17} /> : <X size={17} />}
                  {revokingID === session.id ? "Revoking" : session.current ? "Sign out" : pending ? "Confirm" : "Revoke"}
                </Button>
              </DataRow>
              {pending && (
                <InlineConfirm
                  title="Revoke this session?"
                  description="This signs that device out of Listwork. Your current session stays active."
                  actions={
                    <>
                      <Button variant="ghost" type="button" onClick={() => setPendingSessionRevocationID(null)} disabled={revokingID === session.id}>
                        Keep session
                      </Button>
                      <Button variant="danger" type="button" onClick={() => void revoke(session.id)} disabled={revokingID === session.id} loading={Boolean(revokingID === session.id)} loadingLabel="Revoking">
                        {revokingID === session.id ? "Revoking" : "Revoke session"}
                      </Button>
                    </>
                  }
                />
              )}
            </DataRowListItem>
          );
        })}
      </DataRowList>
    </Panel>
  );
}

function PasskeyManagement({
  passkeys,
  isAdding,
  revokingID,
  onAdd,
  onRevoke
}: {
  passkeys: Array<{ id: string; createdAt: string; lastUsedAt?: string; backedUp?: boolean; deviceType?: string }>;
  isAdding: boolean;
  revokingID: string | null;
  onAdd: () => void;
  onRevoke: (passkeyID: string) => void;
}) {
  const [pendingPasskeyRemovalID, setPendingPasskeyRemovalID] = useState<string | null>(null);
  const [pendingPasskeySwitch, setPendingPasskeySwitch] = useState(false);
  const hasPasskeys = passkeys.length > 0;

  function startPasskeySetup() {
    if (hasPasskeys) {
      onAdd();
      return;
    }
    setPendingPasskeySwitch(true);
    setPendingPasskeyRemovalID(null);
  }

  return (
    <PanelSection as="section" aria-label="Passkeys">
      <div className="section-heading-row">
        <div>
          <SectionLabel>{hasPasskeys ? "Passkeys" : "Sign-in method"}</SectionLabel>
          <span>
            {hasPasskeys
              ? "Device sign-in"
              : "Email code"}
          </span>
        </div>
        <Button type="button" size="compact" onClick={startPasskeySetup} disabled={isAdding || pendingPasskeySwitch}>
          <Fingerprint size={16} /> {hasPasskeys ? (isAdding ? "Adding" : "Add another device") : isAdding ? "Setting up" : "Set up device sign-in"}
        </Button>
      </div>
      {pendingPasskeySwitch && (
        <InlineConfirm
          title="Set up sign-in with this device?"
          description="Use Face ID, your fingerprint, or device PIN when you sign in. Email codes remain available if you need them."
          actions={
            <>
              <Button variant="ghost" type="button" onClick={() => setPendingPasskeySwitch(false)} disabled={isAdding}>
                Not now
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={() => {
                  setPendingPasskeySwitch(false);
                  onAdd();
                }}
                disabled={isAdding}
              >
                Set up on this device
              </Button>
            </>
          }
        />
      )}
      {hasPasskeys && (
        <DataRowList>
          {passkeys.map((passkey) => {
            const canRemove = passkeys.length > 1;
            const pending = pendingPasskeyRemovalID === passkey.id;
            return (
              <DataRowListItem key={passkey.id}>
                <DataRow as="div" className="ui-data-row-settings-account-access">
                  <DataRowMedia className="ui-data-row-media-session" aria-hidden="true">
                    <Fingerprint size={18} />
                  </DataRowMedia>
                  <DataRowMain>
                    <strong>{passkeyLabel(passkey)}</strong>
                    <small>{passkeyActivityLabel(passkey)}</small>
                  </DataRowMain>
                  <Button
                    type="button"
                    variant="ghost"
                    size="compact"
                    disabled={!canRemove || revokingID === passkey.id || pending}
                    onClick={() => setPendingPasskeyRemovalID(passkey.id)}
                    aria-label={`Remove ${passkeyLabel(passkey).toLowerCase()}`}
                   loading={Boolean(revokingID === passkey.id)} loadingLabel="Removing">
                    {revokingID === passkey.id ? "Removing" : pending ? "Confirm" : "Remove"}
                  </Button>
                </DataRow>
                {pending && (
                  <InlineConfirm
                    title="Remove this device sign-in?"
                    description="This saved device will no longer sign in to this account. Keep another saved device before removing it."
                    actions={
                      <>
                        <Button variant="ghost" type="button" onClick={() => setPendingPasskeyRemovalID(null)} disabled={revokingID === passkey.id}>
                          Keep device
                        </Button>
                        <Button
                          variant="danger"
                          type="button"
                          onClick={() => {
                            onRevoke(passkey.id);
                            setPendingPasskeyRemovalID(null);
                          }}
                          disabled={revokingID === passkey.id}
                         loading={Boolean(revokingID === passkey.id)} loadingLabel="Removing">
                          {revokingID === passkey.id ? "Removing" : "Remove device"}
                        </Button>
                      </>
                    }
                  />
                )}
              </DataRowListItem>
            );
          })}
        </DataRowList>
      )}
      {passkeys.length === 1 && <EmptyNote>Add another saved device before removing this one.</EmptyNote>}
    </PanelSection>
  );
}

function passkeyLabel(passkey: { backedUp?: boolean; deviceType?: string }) {
  if (passkey.backedUp) {
    return "Synced device";
  }
  if (passkey.deviceType === "singleDevice") {
    return "This device";
  }
  return "Saved device";
}

function passkeyActivityLabel(passkey: { createdAt: string; lastUsedAt?: string }) {
  const added = `Added ${formatSessionDate(passkey.createdAt)}`;
  return passkey.lastUsedAt ? `${added} · Last used ${formatSessionDate(passkey.lastUsedAt)}` : added;
}

function passkeyErrorMessage(caught: unknown, fallback: string) {
  if (!(caught instanceof Error)) {
    return fallback;
  }
  if (["AbortError", "NotAllowedError", "NotFoundError"].includes(caught.name) || /^not found\.?$/i.test(caught.message)) {
    return fallback;
  }
  return userSafeErrorMessage(caught, fallback);
}

function formatSessionDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatInvitationDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    month: "short",
    day: "numeric"
  }).format(new Date(value));
}

function validEmailAddress(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function workspaceRoleForStore(store: ReturnType<typeof useReOS>): WorkspaceRole {
  const currentMember = store.workspaceMembers.find(
    (member) => member.current || member.userID === store.user?.id
  );
  if (currentMember) {
    return currentMember.role;
  }
  return store.workspace?.ownerUserID === store.user?.id ? "owner" : "member";
}

function workspaceRoleLabel(role: WorkspaceRole) {
  switch (role) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "member":
      return "Member";
  }
}

function initialsForName(value: string) {
  return (
    value
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "R"
  );
}

type AddItemDraft = {
  step: "details" | "photos" | "drafts";
  photoPurpose?: "capture" | "listings";
  input: FormState;
  photoDrafts: PhotoDraft[];
  selectedMarketplaces: Marketplace[];
};

function isAddItemDraft(value: unknown): value is AddItemDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as AddItemDraft;
  return ["details", "photos", "drafts"].includes(draft.step)
    && Boolean(draft.input && ["title", "categoryID", "brand", "size", "color", "material", "sku", "stockLocation", "purchaseCost", "targetPrice", "minimumPrice", "notes"].every((key) => typeof draft.input[key as keyof FormState] === "string")
      && (draft.input.condition === "" || Object.keys(conditionLabels).includes(draft.input.condition)))
    && Array.isArray(draft.photoDrafts) && draft.photoDrafts.length <= MAX_ITEM_PHOTOS
    && draft.photoDrafts.every((photo) => photo && typeof photo.id === "string" && typeof photo.url === "string" && photo.treatment)
    && Array.isArray(draft.selectedMarketplaces)
    && draft.selectedMarketplaces.every((channel) => marketplaces.includes(channel));
}

function AddItemWizard({ onClose, onCreated, onOpenChannels, onCheckInventory }: { onClose: () => void; onCreated: (itemID?: string) => void; onOpenChannels: (itemID: string) => void; onCheckInventory: () => void }) {
  const store = useReOS();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const purchaseCostInputRef = useRef<HTMLInputElement | null>(null);
  const conditionInputRef = useRef<HTMLButtonElement | null>(null);
  const wizardBodyRef = useRef<HTMLDivElement | null>(null);
  const cropToolButtonRef = useRef<HTMLButtonElement | null>(null);
  const backgroundToolButtonRef = useRef<HTMLButtonElement | null>(null);
  const photoToolReturnFocusRef = useRef<"crop" | "background" | null>(null);
  const toast = useToast();
  const returnFocusRef = useRef<HTMLElement | null>(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const saveBusyRef = useRef(false);
  const wizardMountedRef = useRef(true);
  const draftCloseRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    wizardMountedRef.current = true;
    return () => { wizardMountedRef.current = false; };
  }, []);
  const [closing, setClosing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const draft = useWorkspaceDraft<AddItemDraft>(
    `add-item:${store.persistenceMode}:${store.workspace?.id ?? "local"}:${store.user?.id ?? "local"}`,
    () => ({
      step: "details",
      photoPurpose: "capture",
      photoDrafts: [],
      selectedMarketplaces: [],
      input: {
    title: "",
    categoryID: "",
    condition: "",
    brand: "",
    size: "",
    color: "",
    material: "",
    sku: "",
    stockLocation: "",
    purchaseCost: "",
    targetPrice: "",
    minimumPrice: "",
    notes: ""
  }
    }),
    isAddItemDraft
  );
  const [step, setStep] = useDraftField(draft, "step");
  const [photoPurpose, setPhotoPurpose] = useDraftField(draft, "photoPurpose");
  const [photoDrafts, setPhotoDrafts] = useDraftField(draft, "photoDrafts");
  const [selectedMarketplaces, setSelectedMarketplaces] = useDraftField(draft, "selectedMarketplaces");
  const [input, setInput] = useDraftField(draft, "input");

  const [error, setError] = useState<string | null>(null);
  const [photoNotice, setPhotoNotice] = useState<{ tone: "success" | "warning"; message: string } | null>(null);
  const [photoProgress, setPhotoProgress, reportPhotoFailure] = usePhotoUploadProgress("New item photos");
  const [selectedPhotoID, setSelectedPhotoID] = useState<string | undefined>();
  const [pendingPhotoRemovalID, setPendingPhotoRemovalID] = useState<string | null>(null);
  const [showingAIPhotoTools, setShowingAIPhotoTools] = useState(false);
  const [showingCropEditor, setShowingCropEditor] = useState(false);
  const [isRestoringPhoto, setIsRestoringPhoto] = useState(false);
  const [isPreparingPhotos, setIsPreparingPhotos] = useState(false);
  const [isSaving, setIsSaving] = useState(false);


  const selectedPhoto = photoDrafts.find((photo) => photo.id === selectedPhotoID) ?? photoDrafts[0];
  const selectedPhotoIndex = selectedPhoto ? photoDrafts.findIndex((photo) => photo.id === selectedPhoto.id) : -1;
  const canMoveSelectedLeft = selectedPhotoIndex > 0;
  const canMoveSelectedRight = selectedPhotoIndex >= 0 && selectedPhotoIndex < photoDrafts.length - 1;
  const selectedPhotoHasRecoverableEdits = selectedPhoto ? photoHasRecoverableEdits(selectedPhoto) : false;
  const photoEditorOpen = showingAIPhotoTools || showingCropEditor;
  // Invalid partial input must remain editable rather than throw during render.
  let preparedInput: InventoryItemInput;
  try { preparedInput = buildInventoryInput(input, store.generateSKU(input.title, input.size)); }
  catch { preparedInput = buildInventoryInput({ ...input, condition: input.condition || "good", purchaseCost: "", acquiredDate: "", targetPrice: "", minimumPrice: "" }, store.generateSKU(input.title, input.size)); }
  const drafts = store.previewListingDrafts(preparedInput, photoDrafts.length, selectedMarketplaces, store.marketplaceAccounts, store.workspace?.defaultCurrency ?? "GBP");
  const connectedSalesChannels = marketplaces.filter((marketplace) =>
    marketplaceIntegrationIsActive(
      marketplace,
      marketplaceAccountFor(store.marketplaceAccounts, marketplace),
      store.ebayIntegration
    )
  );

  useLayoutEffect(() => {
    const tool = photoToolReturnFocusRef.current;
    if (photoEditorOpen || !tool) {
      return;
    }
    photoToolReturnFocusRef.current = null;
    const trigger = tool === "crop" ? cropToolButtonRef.current : backgroundToolButtonRef.current;
    trigger?.focus({ preventScroll: true });
  }, [photoEditorOpen]);

  function updateField<K extends keyof FormState>(field: K, value: FormState[K]) {
    setInput((current) => ({ ...current, [field]: value }));
  }

  function manageCapturePhotos(photoID?: string) {
    if (photoID) setSelectedPhotoID(photoID);
    setPhotoPurpose("capture");
    setStep("photos");
  }

  function continueToPhotos() {
    try {
      const prepared = buildInventoryInput(input, store.generateSKU(input.title, input.size));
      if (!prepared.title) {
        throw new Error("Item title is required.");
      }
      setError(null);
      setPhotoPurpose("listings");
      setStep("photos");
    } catch (caught) {
      const message = userSafeErrorMessage(caught, "Check the item details.");
      setError(message);
      window.requestAnimationFrame(() => {
        const target = /title/i.test(message) ? titleInputRef.current : /purchase cost/i.test(message) ? purchaseCostInputRef.current : /condition/i.test(message) ? conditionInputRef.current : null;
        target?.scrollIntoView({ block: "center" });
        target?.focus({ preventScroll: true });
      });
    }
  }

  const busy = isSaving || isPreparingPhotos || isRestoringPhoto || closing;
  const closeOnly = draft.invalidated || (draft.ready && !draft.canEdit && !draft.submitting);
  const hasDraft = draft.restored || draft.dirty || input.condition !== "" || Object.entries(input).some(([key, value]) => key !== "condition" && Boolean(value)) || photoDrafts.length > 0;

  useEffect(() => {
    if (!draft.ready || closeOnly) return;
    const frame = window.requestAnimationFrame(() => {
      wizardBodyRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [draft.ready, closeOnly]);

  useEffect(() => {
    if (!closeOnly || busy) return;
    const frame = window.requestAnimationFrame(() => draftCloseRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [busy, closeOnly, draft.pendingSubmission, draft.invalidated]);

  useEffect(() => {
    if (!busy) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  async function requestClose() {
    if (busy || saveBusyRef.current) return;
    if (!draft.ready || closeOnly) { onClose(); return; }
    setClosing(true);
    const saved = await draft.saveNow();
    if (!wizardMountedRef.current) return;
    setClosing(false);
    if (saved) onClose();
    else setDiscardOpen(true);
  }

  async function discardDraft() {
    if (busy || saveBusyRef.current || !draft.canDiscard || draft.invalidated) return;
    setClosing(true);
    setError(null);
    try {
      const cleared = await draft.finish();
      if (!wizardMountedRef.current) return;
      onClose();
      if (!cleared) toast.notify({ title: "Draft discarded", description: "A saved device copy could not be removed. It will not be offered as an unfinished draft.", tone: "warning", durationMs: 0 });
    } catch (caught) {
      if (wizardMountedRef.current) setError(userSafeErrorMessage(caught, "The draft could not be discarded safely. Keep it open and try again."));
    } finally {
      if (wizardMountedRef.current) setClosing(false);
    }
  }

  async function saveItem(channels: Marketplace[], openChannels = false) {
    if (busy || saveBusyRef.current || !draft.ready || !draft.canSubmit || draft.invalidated) return;
    if (openChannels && store.persistenceMode !== "api") return;
    let validatedInput: InventoryItemInput;
    try {
      validatedInput = buildInventoryInput(input, store.generateSKU(input.title, input.size));
      if (!validatedInput.title.trim()) throw new Error("Item title is required.");
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Check the item details."));
      setStep("details");
      window.requestAnimationFrame(() => (/cost/i.test(caught instanceof Error ? caught.message : "") ? purchaseCostInputRef.current : /condition/i.test(caught instanceof Error ? caught.message : "") ? conditionInputRef.current : titleInputRef.current)?.focus());
      return;
    }
    saveBusyRef.current = true;
    setIsSaving(true);
    setError(null);
    try {
      const preparedPhotos = await Promise.all(photoDrafts.map(materializePhotoDraft));
      const result = await measureTask("capture", () => draft.submit(() => store.createInventoryWorkflow(validatedInput, preparedPhotos.map(toPhotoInput), channels, setPhotoProgress)));
      if (!wizardMountedRef.current) return;
      if (openChannels) onOpenChannels(result.value.id);
      else onCreated(result.value.id);
      if (!result.cleared) toast.notify({ title: "Item added", description: "The saved draft could not be cleared. Do not add it again.", tone: "warning", durationMs: 0 });
    } catch (caught) {
      reportPhotoFailure(photoUploadErrorMessage(caught, "Unable to save item."));
      setError(photoUploadErrorMessage(caught, "Unable to save item."));
    } finally {
      saveBusyRef.current = false;
      setPhotoProgress(null);
      setIsSaving(false);
    }
  }

  function saveWorkflow() { return saveItem(selectedMarketplaces); }
  function saveDraftOnly() { return saveItem([]); }
  function saveAndOpenChannels() { return saveItem([], true); }

  async function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (busy || saveBusyRef.current || !draft.ready || !draft.canEdit || selectedFiles.length === 0) {
      return;
    }

    const availableSlots = Math.max(0, MAX_ITEM_PHOTOS - photoDrafts.length);
    if (availableSlots === 0) {
      setError(`This item already has the maximum of ${MAX_ITEM_PHOTOS} photos.`);
      return;
    }

    const files = selectedFiles.slice(0, availableSlots);
    const skippedCount = selectedFiles.length - files.length;
    setError(null);
    setPhotoNotice(null);
    setIsPreparingPhotos(true);
    try {
      const result = await readPhotoDraftBatch(files, setPhotoProgress);
      if (result.drafts.length > 0) {
        setPhotoDrafts((current) => {
          const next = [...current, ...result.drafts].slice(0, MAX_ITEM_PHOTOS);
          setSelectedPhotoID((existing) => existing ?? next[0]?.id);
          return next;
        });
      }
      if (result.errors.length > 0) {
        setError(photoDraftReadErrorMessage(result.errors));
        requestAnimationFrame(() => wizardBodyRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" }));
      }
      const notice = photoDraftReadNotice(result.convertedHeicCount, skippedCount, result.drafts.length);
      if (notice) {
        setPhotoNotice(notice);
      }
    } finally {
      setPhotoProgress(null);
      setIsPreparingPhotos(false);
    }
  }

  function updateTreatment(patch: Partial<PhotoTreatment>) {
    if (!selectedPhoto) {
      return;
    }
    setPhotoDrafts((current) =>
      current.map((photo) =>
        photo.id === selectedPhoto.id
          ? {
              ...photo,
              type: "edited",
              treatment: { ...photo.treatment, ...patch }
            }
          : photo
      )
    );
  }

  function moveSelected(offset: number) {
    if (!selectedPhoto) {
      return;
    }
    setPhotoDrafts((current) => {
      const index = current.findIndex((photo) => photo.id === selectedPhoto.id);
      const target = Math.min(Math.max(index + offset, 0), current.length - 1);
      if (index === target) {
        return current;
      }
      const next = [...current];
      const [photo] = next.splice(index, 1);
      next.splice(target, 0, photo);
      return next;
    });
  }

  function removeSelected() {
    if (!selectedPhoto) {
      return;
    }
    setPhotoDrafts((current) => {
      const next = current.filter((photo) => photo.id !== selectedPhoto.id);
      setSelectedPhotoID(next[0]?.id);
      return next;
    });
    setPendingPhotoRemovalID(null);
    setShowingAIPhotoTools(false);
    setShowingCropEditor(false);
  }

  function appendProcessedPhoto(result: AiPhotoOutput, source: PhotoDraft, label: string) {
    const variant = draftFromAiResult(result, source, label);
    setPhotoDrafts((current) => [...current, variant].slice(0, MAX_ITEM_PHOTOS));
    setSelectedPhotoID(variant.id);
  }

  function closePhotoTool(tool: "crop" | "background") {
    photoToolReturnFocusRef.current = tool;
    if (tool === "crop") {
      setShowingCropEditor(false);
    } else {
      setShowingAIPhotoTools(false);
    }
  }

  function applyCroppedPhoto(result: CropApplyResult) {
    if (!selectedPhoto) {
      return;
    }
    setPhotoDrafts((current) =>
      current.map((photo) =>
        photo.id === selectedPhoto.id ? croppedPhotoDraft(photo, result) : photo
      )
    );
    closePhotoTool("crop");
  }

  async function restoreSelectedPhoto() {
    if (!selectedPhoto || !selectedPhotoHasRecoverableEdits) {
      return;
    }
    try {
      setIsRestoringPhoto(true);
      setError(null);
      const image = await loadImageElement(selectedPhoto.originalUrl);
      setPhotoDrafts((current) =>
        current.map((photo) =>
          photo.id === selectedPhoto.id
            ? {
                ...photo,
                url: selectedPhoto.originalUrl,
                thumbnailURL: thumbnailForImageElement(image),
                width: image.naturalWidth,
                height: image.naturalHeight,
                type: "original",
                label: "Original",
                mimeType: mimeTypeFromDataURL(selectedPhoto.originalUrl) ?? photo.mimeType,
                byteSize: undefined,
                aiOperation: undefined,
                backgroundStyle: undefined,
                treatment: {
                  crop: "original",
                  brightness: 100,
                  contrast: 100,
                  rotation: 0
                }
              }
            : photo
        )
      );
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to restore this photo."));
    } finally {
      setIsRestoringPhoto(false);
    }
  }

  const stepIndex = step === "details" ? 0 : step === "photos" ? 1 : 2;
  const stepTitle = step === "details" ? "Add item" : step === "photos" ? "Product photos" : "Sales channels";
  const stepDescription =
    step === "details"
      ? "Add photos and a working title. Save the item now or prepare listings."
      : step === "photos"
        ? "Add and prepare product photos before choosing where to list."
        : "Choose where to prepare a listing, or add this item to Inventory.";
  const wizardSteps: Array<{ key: typeof step; label: string }> = [
    { key: "details", label: "Details" },
    { key: "photos", label: "Photos" },
    { key: "drafts", label: "Sell" }
  ];
  const photoUploadHelpID = "add-item-photo-upload-help";
  const wizardBodyLayout = step === "photos" ? (photoEditorOpen ? "photoEditor" : "photo") : step === "drafts" ? "drafts" : "default";
  const wizardDialogLayout = showingAIPhotoTools ? "photoBackground" : step === "drafts" ? "drafts" : "default";
  const wizardBodyClassName = [
    "ui-dialog-body-wizard",
    wizardBodyLayout === "photo" ? "ui-dialog-body-wizard-photo" : "",
    wizardBodyLayout === "photoEditor" ? "ui-dialog-body-wizard-photo-editor" : "",
    wizardBodyLayout === "drafts" ? "ui-dialog-body-wizard-drafts" : ""
  ].filter(Boolean).join(" ");
  const wizardDialogClassName = [
    "ui-dialog-content-wizard",
    wizardDialogLayout === "drafts" ? "ui-dialog-content-wizard-drafts" : "",
    wizardDialogLayout === "photoBackground" ? "ui-dialog-content-photo-background" : ""
  ].filter(Boolean).join(" ");

  useEffect(() => {
    wizardBodyRef.current?.scrollTo({ top: 0, left: 0 });
  }, [step]);

  function goBackOrClose() {
    if (busy || saveBusyRef.current) return;
    if (closeOnly) { onClose(); return; }
    if (!draft.ready) return;
    if (step === "details") {
      void requestClose();
      return;
    }
    setStep(step === "drafts" ? "photos" : "details");
  }

  const draftsBlockedByMissingPhotos = drafts.some((draft) => draft.validationWarnings.includes(noProductPhotoDraftWarning));
  const draftsBlockedByMissingPrice = selectedMarketplaces.length > 0 && preparedInput.targetPriceMinor <= 0;
  const draftsBlocked = draftsBlockedByMissingPhotos || draftsBlockedByMissingPrice;
  const canCreateMarketplaceDrafts = selectedMarketplaces.length > 0 && !draftsBlocked;

  function performPrimaryAction() {
    if (busy || saveBusyRef.current || !draft.ready || !draft.canEdit || draft.invalidated) return;
    if (step === "details") {
      void saveDraftOnly();
      return;
    }
    if (step === "photos") {
      setStep(photoPurpose === "capture" ? "details" : "drafts");
      return;
    }
    void (draftsBlocked ? saveDraftOnly() : saveWorkflow());
  }

  function performSecondaryAction() {
    if (busy || saveBusyRef.current || !draft.ready) return;
    goBackOrClose();
  }

  const draftsPrimaryLabel =
    draftsBlocked
      ? isSaving
        ? "Adding item"
        : "Add to inventory"
      : selectedMarketplaces.length > 0
      ? isSaving
        ? "Creating drafts"
        : "Add item and prepare listings"
      : isSaving
        ? "Saving item"
        : "Add to inventory";
  const selectedMarketplaceLabels = selectedMarketplaces.map((marketplace) => marketplaceLabels[marketplace]).join(", ");
  const draftMarketplaceHelper =
    draftsBlockedByMissingPhotos
      ? "Add a product photo before creating marketplace drafts, or save this item to inventory."
      : preparedInput.targetPriceMinor <= 0
        ? "Enter a target price to prepare listings."
      : selectedMarketplaces.length > 0
      ? `Drafts will be prepared for ${selectedMarketplaceLabels}.`
      : "Choose a marketplace, or add this item to Inventory for now.";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) void requestClose(); }}>
      <DialogContent className={wizardDialogClassName} leadingClose showClose={false}
        aria-busy={!draft.ready || busy}
        onOpenAutoFocus={(event) => { event.preventDefault(); window.requestAnimationFrame(() => wizardBodyRef.current?.focus()); }}
        onCloseAutoFocus={(event) => { event.preventDefault(); window.requestAnimationFrame(() => returnFocusRef.current?.isConnected && returnFocusRef.current.focus()); }}
        onEscapeKeyDown={(event) => { event.preventDefault(); void requestClose(); }}
        onInteractOutside={(event) => { event.preventDefault(); }}
      >
        <DialogHeader>
          <Button variant="secondary" size="icon" type="button" aria-label={closeOnly || step === "details" ? "Close add item" : "Back"} onClick={goBackOrClose} disabled={busy}>
            {closeOnly || step === "details" ? <X size={20} /> : <ArrowLeft size={20} />}
          </Button>
          <DialogTitle>{draft.pendingSubmission && !draft.submitting && !draft.invalidated ? "Check this item" : stepTitle}</DialogTitle>
          <DialogDescription className="sr-only">{draft.pendingSubmission && !draft.submitting && !draft.invalidated ? "Check inventory before starting this item again." : stepDescription}</DialogDescription>
          {step !== "details" && !(step === "photos" && photoPurpose === "capture") && <Badge tone="neutral">{stepIndex + 1} of 3</Badge>}
        </DialogHeader>

        <DialogBody tabIndex={-1} ref={wizardBodyRef} className={wizardBodyClassName}>
          {!draft.ready ? <p role="status">Opening draft...</p> : (
            <div className="item-draft-status">
              <span role="status">{draft.invalidated ? "This draft is no longer available" : draft.submitting ? "Adding item..." : draft.pendingSubmission ? "Submission needs checking" : !draft.canEdit ? "Draft recovery unavailable" : draft.saveError ? "Draft not saved" : draft.saving || draft.dirty ? "Saving draft..." : draft.restored || hasDraft ? "Draft saved on this device" : "Your progress saves on this device"}</span>
              {hasDraft && draft.canDiscard && <Button variant="ghost" size="compact" type="button" onClick={() => setDiscardOpen(true)} disabled={busy}>Discard draft</Button>}
            </div>
          )}
          {draft.invalidated && <Alert tone="warning">This draft was submitted or discarded in another tab. Close it to return to your workspace. Changes made here have not been saved.</Alert>}
          {draft.pendingSubmission && !draft.submitting && !draft.invalidated && <><p className="item-draft-summary"><strong>{input.title}</strong></p><Alert tone="warning">This item may already have been added. Check inventory before discarding this saved draft.</Alert></>}
          {draft.saveError && !draft.invalidated && <Alert tone="warning">{draft.saveError}</Alert>}
          {draft.submitError && <Alert tone="warning">{draft.submitError}</Alert>}
          {error && <Alert tone="danger" role="alert">{error}</Alert>}
          {discardOpen && draft.canDiscard && <InlineConfirm title="Discard this draft?" description={draft.pendingSubmission ? "An item may already have been added. This removes only the saved draft from this device, not the inventory item." : "The item details and photos in this draft will be removed."} actions={<>
            <Button variant="secondary" type="button" onClick={() => setDiscardOpen(false)} disabled={busy}>{draft.pendingSubmission ? "Keep draft" : "Keep editing"}</Button>
            <Button variant="danger" type="button" onClick={() => void discardDraft()} disabled={busy}>Discard draft</Button>
          </>} />}
          <fieldset className="item-draft-fields" hidden={draft.pendingSubmission && !draft.submitting && !draft.invalidated} disabled={!draft.ready || !draft.canEdit || busy || discardOpen}>

          {step !== "details" && !(step === "photos" && photoPurpose === "capture") && <Stepper steps={wizardSteps} currentStep={step} aria-label="Add item progress" />}
          {photoNotice && <Alert tone={photoNotice.tone}>{photoNotice.message}</Alert>}
          <PhotoUploadStatus progress={photoProgress} />

            <FileInput
              ref={fileInputRef}
              accept={PHOTO_FILE_ACCEPT}
              multiple
              aria-label="Choose product photos"
              onChange={handleFiles}
              disabled={!draft.ready || busy || discardOpen}
            />

        {step === "details" && (
          <div className="quick-item-capture">
            <section className="capture-photos" aria-label="Item photos">
              {photoDrafts.length > 0 && <div className="capture-photo-strip">{photoDrafts.slice(0, 4).map((photo, index) => <button type="button" key={photo.id} onClick={() => manageCapturePhotos(photo.id)} aria-label={`Edit photo ${index + 1}`}><img src={photo.thumbnailURL || photo.url} alt="" style={photoStyle(photo.treatment)} /></button>)}</div>}
              <Button variant="secondary" type="button" loading={isPreparingPhotos} loadingLabel="Preparing photos" onClick={() => fileInputRef.current?.click()} disabled={busy || photoDrafts.length >= MAX_ITEM_PHOTOS}><ImagePlus size={22} />{photoDrafts.length ? `Add photos (${photoDrafts.length}/${MAX_ITEM_PHOTOS})` : "Add photos"}</Button>
              {photoDrafts.length > 0 && <Button variant="ghost" onClick={() => manageCapturePhotos()}>Manage photos</Button>}
            </section>
            <div className="form-grid">
              <Field label="Working title" wide>
                <Input ref={titleInputRef} required aria-invalid={error?.includes("title") || undefined} value={input.title} onChange={(event) => updateField("title", event.target.value)} placeholder="Vintage Levi's 501 Denim Jeans" />
              </Field>
              <Field label="Condition" wide error={error?.includes("condition") ? error : undefined}>
                <SelectControl ref={conditionInputRef} required value={input.condition} onValueChange={(value) => updateField("condition", value as ItemCondition)} options={[{ value: "", label: "Choose condition", disabled: true }, ...Object.entries(conditionLabels).map(([value, label]) => ({ value, label }))]} />
              </Field>
              <Field label="Purchase cost" helper="Blank means unknown; 0 means free.">
                <Input ref={purchaseCostInputRef} aria-invalid={error?.includes("Purchase cost") || undefined} inputMode="decimal" value={input.purchaseCost} onChange={(event) => updateField("purchaseCost", event.target.value)} placeholder="Unknown" />
              </Field>
              <Field label="Stock location">
                <Input value={input.stockLocation} onChange={(event) => updateField("stockLocation", event.target.value)} placeholder="Row 3, Shelf 4, Box A" />
              </Field>
            </div>
            <Disclosure label="More item details">
              <div className="form-grid">
              <Field label="Category" helper="Leave blank when research should determine it.">
                <Input list="category-options" value={input.categoryID} onChange={(event) => updateField("categoryID", event.target.value)} placeholder="Search or enter a category" />
                <datalist id="category-options">
                  {store.categories.map((category) => (
                    <option key={category.id} value={category.path} />
                  ))}
                </datalist>
              </Field>
              <Field label="Brand">
                <Input value={input.brand} onChange={(event) => updateField("brand", event.target.value)} placeholder="Levi's" />
              </Field>
              <Field label="Size">
                <Input value={input.size} onChange={(event) => updateField("size", event.target.value)} placeholder="W32 / L30" />
              </Field>
              <Field label="Acquisition date" helper="Leave blank if unknown."><Input type="date" value={input.acquiredDate ?? ""} max={new Date().toISOString().slice(0, 10)} onChange={(event) => updateField("acquiredDate", event.target.value)} /></Field>
              <Field label="Colour">
                <Input value={input.color} onChange={(event) => updateField("color", event.target.value)} placeholder="Blue" />
              </Field>
              <Field label="Material">
                <Input value={input.material} onChange={(event) => updateField("material", event.target.value)} placeholder="Denim" />
              </Field>
              <Field label="Notes" wide>
                <Textarea value={input.notes} onChange={(event) => updateField("notes", event.target.value)} maxLength={220} placeholder="Measurements, flaws, styling notes" />
              </Field>
              </div>
            </Disclosure>
            <div className="capture-secondary-actions"><span className="capture-stock-note">Opening stock: 1 unit</span><Button variant="ghost" type="button" onClick={continueToPhotos} disabled={!draft.ready || busy || discardOpen}>Prepare listings <ChevronRight size={18} /></Button></div>
          </div>
        )}

        {step === "photos" && (
          <>
            <p className="sr-only" id={photoUploadHelpID}>
              Add up to {MAX_ITEM_PHOTOS} product photos from this device. Photos can be cropped, reordered, edited, or removed before the item is saved.
            </p>
            {!photoEditorOpen && (
              selectedPhoto ? (
                <Button
                  variant="tool"
                  className="photo-hero"
                  type="button"
                  aria-describedby={photoUploadHelpID}
                  disabled={!draft.ready || busy || discardOpen}
                  onClick={() => {
                    setPendingPhotoRemovalID(null);
                    fileInputRef.current?.click();
                  }}
                >
                  <img src={selectedPhoto.url} alt="Selected product" style={photoStyle(selectedPhoto.treatment)} />
                </Button>
              ) : (
                <section className="photo-upload-surface" aria-labelledby="photo-empty-title">
                  <ImagePlus size={28} aria-hidden="true" />
                  <h3 id="photo-empty-title">Add product photos</h3>
                  <p>Choose up to {MAX_ITEM_PHOTOS} photos. You can crop and reorder them here.</p>
                  <Button variant="secondary" type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>Choose photos</Button>
                  <Disclosure label="Photo tips">
                    <ul><li>Use natural light and a plain background.</li><li>Include labels, details and any wear.</li><li>Put the clearest photo first.</li></ul>
                  </Disclosure>
                </section>
              )
            )}
            {photoDrafts.length > 0 && (
              <div className="thumb-strip">
                {photoDrafts.map((photo, index) => (
                  <ThumbnailButton
                    key={photo.id}
                    type="button"
                    label={`Select product photo ${index + 1}`}
                    imageSrc={photo.thumbnailURL}
                    imageStyle={photoStyle(photo.treatment)}
                    selected={photo.id === selectedPhoto?.id}
                    onClick={() => {
                      setPendingPhotoRemovalID(null);
                      setSelectedPhotoID(photo.id);
                    }}
                  />
                ))}
                <ThumbnailButton
                  type="button"
                  className="add-thumb"
                  label="Add another product photo"
                  aria-describedby={photoUploadHelpID}
                  onClick={() => {
                    setPendingPhotoRemovalID(null);
                    fileInputRef.current?.click();
                  }}
                  disabled={isPreparingPhotos || photoDrafts.length >= MAX_ITEM_PHOTOS}
                >
                  <Plus size={20} />
                </ThumbnailButton>
              </div>
            )}
            {selectedPhoto && !photoEditorOpen && (
              <div className="photo-tools">
                <Button
                  ref={cropToolButtonRef}
                  variant="tool"
                  type="button"
                  onClick={() => {
                    setPendingPhotoRemovalID(null);
                    setShowingAIPhotoTools(false);
                    setShowingCropEditor(true);
                  }}
                >
                  <CropIcon size={18} /> Crop
                </Button>
                <Button variant="tool" type="button" onClick={() => updateTreatment({ rotation: ((selectedPhoto?.treatment.rotation ?? 0) + 90) % 360 })}>
                  <RotateCw size={18} /> Rotate
                </Button>
                <Button variant="tool" type="button" onClick={() => updateTreatment({ brightness: 108, contrast: 106 })}>
                  <Wand2 size={18} /> Enhance
                </Button>
                <Button
                  ref={backgroundToolButtonRef}
                  variant="tool"
                  type="button"
                  onClick={() => {
                    setPendingPhotoRemovalID(null);
                    setShowingCropEditor(false);
                    setShowingAIPhotoTools(true);
                  }}
                >
                  <Sparkles size={18} /> Background
                </Button>
                {selectedPhotoHasRecoverableEdits && (
                  <Button variant="tool" type="button" onClick={() => void restoreSelectedPhoto()} disabled={isRestoringPhoto} loading={Boolean(isRestoringPhoto)} loadingLabel="Restoring">
                    <Undo2 size={18} /> {isRestoringPhoto ? "Restoring" : "Restore original"}
                  </Button>
                )}
                <Button variant="danger" type="button" disabled={pendingPhotoRemovalID === selectedPhoto.id} onClick={() => setPendingPhotoRemovalID(selectedPhoto.id)}>
                  <Trash2 size={18} /> Remove
                </Button>
              </div>
            )}
            {selectedPhoto && pendingPhotoRemovalID === selectedPhoto.id && !photoEditorOpen && (
              <InlineConfirm
                className="photo-remove-confirm"
                title="Remove this photo?"
                description="This only removes the draft photo from this item. Other uploaded photos stay in place."
                actions={
                  <>
                    <Button variant="ghost" type="button" onClick={() => setPendingPhotoRemovalID(null)}>
                      Keep photo
                    </Button>
                    <Button variant="danger" type="button" onClick={removeSelected}>
                      Remove photo
                    </Button>
                  </>
                }
              />
            )}
            {showingAIPhotoTools && selectedPhoto && (
              <AIPhotoToolsPanel
                photo={selectedPhoto}
                onClose={() => closePhotoTool("background")}
                onApply={(result, label) => appendProcessedPhoto(result, selectedPhoto, label)}
              />
            )}
            {showingCropEditor && selectedPhoto && (
              <CropEditor
                photo={selectedPhoto}
                onApply={applyCroppedPhoto}
                onClose={() => closePhotoTool("crop")}
              />
            )}
            {selectedPhoto && !photoEditorOpen && (
              <div className="slider-grid">
                <label>
                  <span>Brightness</span>
                  <RangeInput min={80} max={125} value={selectedPhoto.treatment.brightness} onChange={(event) => updateTreatment({ brightness: Number(event.target.value) })} />
                </label>
                <label>
                  <span>Contrast</span>
                  <RangeInput min={80} max={125} value={selectedPhoto.treatment.contrast} onChange={(event) => updateTreatment({ contrast: Number(event.target.value) })} />
                </label>
                {photoDrafts.length > 1 && (
                  <>
                    <Button type="button" onClick={() => moveSelected(-1)} disabled={!canMoveSelectedLeft}>
                      Move left
                    </Button>
                    <Button type="button" onClick={() => moveSelected(1)} disabled={!canMoveSelectedRight}>
                      Move right
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {step === "drafts" && (
          <>
            <Field label="Target price" helper="Required to prepare listings."><Input inputMode="decimal" value={input.targetPrice} onChange={(event) => updateField("targetPrice", event.target.value)} placeholder="Enter a price" /></Field>
            {store.persistenceMode === "api" && connectedSalesChannels.length === 0 && !store.marketplaceAccounts.some(isManualListingAccount) && selectedMarketplaces.length === 0 && preparedInput.targetPriceMinor > 0 && (
              <div className="channel-setup-prompt">
                <span className="channel-setup-prompt-icon" aria-hidden="true"><Link2 size={18} /></span>
                <div>
                  <strong>No sales channels connected</strong>
                  <span>Save this item, then set up eBay, Etsy, or another channel.</span>
                </div>
                <Button variant="secondary" size="compact" type="button" onClick={() => void saveAndOpenChannels()} disabled={!draft.ready || !draft.canSubmit || busy || discardOpen} loading={isSaving} loadingLabel="Saving">
                  {isSaving ? "Saving" : "Save and set up"}
                </Button>
              </div>
            )}
            <div className="draft-marketplace-picker">
              <div className="section-heading-row">
                <strong>Choose marketplaces</strong>
                {selectedMarketplaces.length > 0 && <Badge tone="info">{selectedMarketplaces.length} selected</Badge>}
              </div>
              <ChoiceGroup label="Marketplaces to prepare">
                {marketplaces.map((marketplace) => {
                  const selected = selectedMarketplaces.includes(marketplace);
                  return (
                    <ChipButton
                      key={marketplace}
                      type="button"
                      selected={selected}
                      onClick={() =>
                        setSelectedMarketplaces((current) =>
                          current.includes(marketplace) ? current.filter((candidate) => candidate !== marketplace) : [...current, marketplace]
                        )
                      }
                    >
                      {marketplaceLabels[marketplace]}
                    </ChipButton>
                  );
                })}
              </ChoiceGroup>
              <EmptyNote>{draftMarketplaceHelper}</EmptyNote>
            </div>
            {drafts.length > 0 && (
              <div className="draft-grid">
                {drafts.map((draft) => (
                  <DataRow as="div" interactive={false} className="ui-data-row-draft-preview" key={draft.marketplace}>
                    <DataRowMedia className="ui-data-row-media-draft-preview-marketplace">
                      <MarketplaceBadge marketplace={draft.marketplace} />
                    </DataRowMedia>
                    <DataRowMain>
                      <strong>{draft.title}</strong>
                      <DataRowMeta className="ui-data-row-meta-draft-preview-description">{draft.description}</DataRowMeta>
                      {draft.validationWarnings.length > 0 && (
                        <DataRowMeta className="ui-data-row-meta-draft-preview-warning">{draft.validationWarnings.join(" · ")}</DataRowMeta>
                      )}
                    </DataRowMain>
                    <DataRowValue className="ui-data-row-value-draft-preview">
                      <Badge tone={draft.validationWarnings.length ? "warning" : "success"}>{draft.validationWarnings.length ? "Review" : "Ready"}</Badge>
                      <span>{formatMoney(draft.price)}</span>
                    </DataRowValue>
                  </DataRow>
                ))}
              </div>
            )}
          </>
        )}
          </fieldset>
        </DialogBody>

        {closeOnly ? (
          <DialogFooter>
            {draft.pendingSubmission && !draft.invalidated && <Button ref={draftCloseRef} variant="primary" type="button" onClick={onCheckInventory} disabled={busy}>Check inventory</Button>}
            <Button ref={draft.pendingSubmission && !draft.invalidated ? undefined : draftCloseRef} variant="secondary" type="button" onClick={onClose} disabled={busy}>Close draft</Button>
          </DialogFooter>
        ) : !photoEditorOpen && (
          <DialogFooter className={step === "details" ? "capture-footer" : undefined}>
            {step === "drafts" && canCreateMarketplaceDrafts ? (
              <Button variant="secondary" type="button" onClick={() => void saveDraftOnly()} disabled={!draft.ready || !draft.canSubmit || busy || discardOpen} aria-label="Add to inventory" loading={isSaving} loadingLabel="Saving">
                {isSaving ? "Saving" : "Add to inventory"}
              </Button>
            ) : step !== "details" ? (
              <Button variant="secondary" type="button" onClick={performSecondaryAction} disabled={!draft.ready || busy || discardOpen}>Back</Button>
            ) : null}
            <Button
              variant="primary"
              type="button"
              onClick={performPrimaryAction}
              disabled={!draft.ready || busy || discardOpen || (step === "drafts" && !draft.canSubmit)}
              aria-describedby={step === "photos" && photoDrafts.length === 0 ? photoUploadHelpID : undefined}
            >
              {step === "drafts" ? draftsPrimaryLabel : step === "photos" ? (isPreparingPhotos ? "Preparing photos" : photoPurpose === "capture" ? "Done with photos" : "Continue") : isSaving ? "Saving item" : "Add to inventory"}
              {step === "photos" ? <ChevronRight size={18} /> : <CheckCircle2 size={18} />}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CropEditor({ photo, onApply, onClose }: { photo: PhotoDraft; onApply: (result: CropApplyResult) => void; onClose: () => void }) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);
  const cropHelpID = `crop-editor-help-${photo.id}`;
  const initialMode = photo.treatment.crop === "original" ? "square" : photo.treatment.crop;
  const [imageSize, setImageSize] = useState({ width: Math.max(1, photo.width), height: Math.max(1, photo.height) });
  const [cropPhoto, setCropPhoto] = useState<PhotoDraft | null>(
    photo.treatment.rotation % 360 === 0 ? photo : null
  );
  const [cropMode, setCropMode] = useState<CropMode>(initialMode);
  const [zoom, setZoom] = useState(1);
  const [cropRect, setCropRect] = useState<CropRect>(() => cropRectFor(imageSize, initialMode, 1));
  const [isPreparingPhoto, setIsPreparingPhoto] = useState(photo.treatment.rotation % 360 !== 0);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const nextMode = photo.treatment.crop === "original" ? "square" : photo.treatment.crop;
    setCropMode(nextMode);
    setZoom(1);
    setError(null);
    setIsPreparingPhoto(true);

    void normalizePhotoRotation(photo)
      .then((normalizedPhoto) => {
        if (cancelled) return;
        const nextSize = {
          width: Math.max(1, normalizedPhoto.width),
          height: Math.max(1, normalizedPhoto.height)
        };
        setCropPhoto(normalizedPhoto);
        setImageSize(nextSize);
        setCropRect(cropRectFor(nextSize, nextMode, 1));
      })
      .catch((caught) => {
        if (cancelled) return;
        setCropPhoto(null);
        setError(userSafeErrorMessage(caught, "Unable to prepare this photo for cropping."));
      })
      .finally(() => {
        if (!cancelled) setIsPreparingPhoto(false);
      });

    return () => {
      cancelled = true;
    };
  }, [photo.id, photo.treatment.crop, photo.treatment.rotation, photo.url, photo.width, photo.height]);

  useEffect(() => {
    setCropRect((current) => cropRectFor(imageSize, cropMode, zoom, cropCenter(current)));
  }, [cropMode, imageSize, zoom]);

  function pointForEvent(event: ReactPointerEvent<HTMLDivElement>) {
    const bounds = stageRef.current?.getBoundingClientRect();
    if (!bounds) {
      return { x: 0.5, y: 0.5 };
    }
    return {
      x: clamp((event.clientX - bounds.left) / bounds.width, 0, 1),
      y: clamp((event.clientY - bounds.top) / bounds.height, 0, 1)
    };
  }

  function startDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!stageRef.current) {
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointForEvent(event);
    let activeRect = cropRect;
    if (!pointInRect(point, cropRect)) {
      activeRect = clampCropRect({
        ...cropRect,
        x: point.x - cropRect.width / 2,
        y: point.y - cropRect.height / 2
      });
      setCropRect(activeRect);
    }
    dragRef.current = {
      offsetX: point.x - activeRect.x,
      offsetY: point.y - activeRect.y
    };
  }

  function moveDrag(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const point = pointForEvent(event);
    setCropRect((current) =>
      clampCropRect({
        ...current,
        x: point.x - drag.offsetX,
        y: point.y - drag.offsetY
      })
    );
  }

  function stopDrag(event: ReactPointerEvent<HTMLDivElement>) {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function moveCropWithKeyboard(event: KeyboardEvent<HTMLDivElement>) {
    const keyOffsets: Record<string, { x: number; y: number }> = {
      ArrowUp: { x: 0, y: -1 },
      ArrowDown: { x: 0, y: 1 },
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 }
    };
    const offset = keyOffsets[event.key];
    if (!offset) {
      return;
    }
    event.preventDefault();
    const step = event.shiftKey ? 0.045 : 0.015;
    setCropRect((current) =>
      clampCropRect({
        ...current,
        x: current.x + offset.x * step,
        y: current.y + offset.y * step
      })
    );
  }

  async function applyCrop() {
    if (!cropPhoto) {
      return;
    }
    try {
      setIsApplying(true);
      setError(null);
      onApply(await renderCroppedPhoto(cropPhoto, cropRect, cropMode));
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to crop this photo."));
    } finally {
      setIsApplying(false);
    }
  }

  const cropStyle: CSSProperties = {
    left: `${cropRect.x * 100}%`,
    top: `${cropRect.y * 100}%`,
    width: `${cropRect.width * 100}%`,
    height: `${cropRect.height * 100}%`
  };
  const cropPositionStatus = `${cropModeLabel(cropMode)} crop: left ${Math.round(cropRect.x * 100)}%, top ${Math.round(cropRect.y * 100)}%, width ${Math.round(cropRect.width * 100)}%, height ${Math.round(cropRect.height * 100)}%.`;
  const cropStatusID = `${cropHelpID}-status`;

  return (
    <section className="crop-editor-section" aria-label="Crop photo">
      <p className="sr-only" id={cropHelpID}>
        Drag the crop area, or focus the crop area and use arrow keys to move it. Hold Shift with an arrow key to move faster.
      </p>
      <p className="sr-only" id={cropStatusID} aria-live="polite" aria-atomic="true">{cropPositionStatus}</p>
      <div className="crop-editor-header">
        <h3>Crop: {cropModeLabel(cropMode)}</h3>
        <Button variant="secondary" size="icon" type="button" onClick={onClose} aria-label="Close crop editor">
          <X size={18} />
        </Button>
      </div>

      <div className="crop-stage-shell">
        {cropPhoto ? (
          <div
            ref={stageRef}
            className="crop-stage"
            role="group"
            tabIndex={0}
            aria-label={`${cropModeLabel(cropMode)} crop area`}
            aria-describedby={`${cropHelpID} ${cropStatusID}`}
            style={{ aspectRatio: `${imageSize.width} / ${imageSize.height}` }}
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={stopDrag}
            onPointerCancel={stopDrag}
            onKeyDown={moveCropWithKeyboard}
          >
            <img
              src={cropPhoto.url}
              alt=""
              draggable={false}
              style={{
                filter: `brightness(${cropPhoto.treatment.brightness}%) contrast(${cropPhoto.treatment.contrast}%)`
              }}
              onLoad={(event) => {
                const image = event.currentTarget;
                if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                  setImageSize({ width: image.naturalWidth, height: image.naturalHeight });
                }
              }}
            />
            <div className="crop-box" style={cropStyle} aria-hidden="true" />
          </div>
        ) : (
          <div className="crop-stage-loading" role="status">
            <LoaderCircle size={22} aria-hidden="true" />
            <span>{isPreparingPhoto ? "Preparing photo" : "Photo unavailable"}</span>
          </div>
        )}
      </div>

      <div className="crop-controls">
        <SegmentedControl
          className="crop-aspect-tabs"
          label="Crop aspect"
          value={cropMode}
          options={(["original", "square", "portrait"] as CropMode[]).map((mode) => ({
            key: mode,
            label: cropModeLabel(mode)
          }))}
          onChange={setCropMode}
        />
        <label className="crop-range">
          <span>Zoom</span>
          <RangeInput min={1} max={4} step={0.05} value={zoom} aria-label="Crop zoom" onChange={(event) => setZoom(Number(event.target.value))} />
        </label>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <div className="crop-actions">
        <Button
          size="compact"
          type="button"
          onClick={() => {
            setZoom(1);
            setCropRect(cropRectFor(imageSize, cropMode, 1));
          }}
        >
          Reset
        </Button>
        <Button variant="ghost" size="compact" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="compact" type="button" onClick={() => void applyCrop()} disabled={isApplying || isPreparingPhoto || !cropPhoto} loading={isApplying} loadingLabel="Applying">
          {isApplying ? "Applying" : "Apply"}
        </Button>
      </div>
    </section>
  );
}

function AIPhotoToolsPanel({
  photo,
  onApply,
  onClose
}: {
  photo: PhotoDraft;
  onApply: (result: AiPhotoOutput, label: string) => void;
  onClose: () => void;
}) {
  const ai = usePhotoAi();
  const lastActionRef = useRef<(() => Promise<void>) | null>(null);
  const [selectedBackground, setSelectedBackground] = useState<AiBackgroundStyle>("marketplace_white");
  const [objects, setObjects] = useState<AiObjectCandidate[]>([]);
  const [removedObjectIDs, setRemovedObjectIDs] = useState<Set<string>>(new Set());
  const [showingRefine, setShowingRefine] = useState(false);
  const [modelStorage, setModelStorage] = useState<PhotoAiModelStorageState | null>(null);
  const [modelStorageError, setModelStorageError] = useState<string | null>(null);
  const [isPreparingModel, setIsPreparingModel] = useState(false);
  const refinementPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    inspectPhotoAiModelStorage()
      .then((nextStorage) => {
        if (!cancelled) {
          setModelStorage(nextStorage);
        }
      })
      .catch(() => undefined);
    ai.getCapabilities().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ai.state.capabilities?.isModelLoaded) {
      return;
    }
    void refreshModelStorage();
  }, [ai.state.capabilities?.isModelLoaded]);

  useEffect(() => {
    if (!showingRefine) {
      return;
    }
    requestAnimationFrame(() => {
      refinementPanelRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }, [showingRefine]);

  const modelLoaded = Boolean(ai.state.capabilities?.isModelLoaded && ai.state.capabilities.supported !== false);
  const modelCached = Boolean(modelStorage?.modelCached);

  async function runAction(label: string, operation: AiOperation, request: () => Parameters<typeof ai.send>[0]) {
    const action = async () => {
      const ready = await ensureModelReady();
      if (!ready) {
        return;
      }
      const response = await ai.send(request());
      if (response.type === "result") {
        onApply(response.result, label);
        if (operation !== "detect-objects") {
          setObjects(response.objects ?? []);
        }
      }
      if (response.type === "objects") {
        setObjects(response.objects);
        setRemovedObjectIDs(new Set());
      }
    };
    lastActionRef.current = action;
    await action().catch(() => undefined);
  }

  async function refreshModelStorage() {
    const nextStorage = await inspectPhotoAiModelStorage().catch(() => null);
    if (nextStorage) {
      setModelStorage(nextStorage);
    }
  }

  async function prepareLocalModel() {
    try {
      setIsPreparingModel(true);
      setModelStorageError(null);
      markPhotoAiModelAccepted();
      await refreshModelStorage();
      await requestPhotoAiPersistentStorage();
      const capabilities = await ai.warmup();
      markPhotoAiModelPrepared(capabilities);
      await refreshModelStorage();
    } catch (caught) {
      console.warn("Listwork photo tools setup failed.", caught);
      setModelStorageError("Setup couldn't finish. Check your connection and try again.");
      await refreshModelStorage();
    } finally {
      setIsPreparingModel(false);
    }
  }

  async function ensureModelReady() {
    if (modelLoaded) {
      return true;
    }
    if (!modelCached) {
      setModelStorageError("Set up private photo tools before using them.");
      return false;
    }
    try {
      setModelStorageError(null);
      const capabilities = await ai.warmup();
      markPhotoAiModelPrepared(capabilities);
      await refreshModelStorage();
      return true;
    } catch (caught) {
      console.warn("Listwork photo tools load failed.", caught);
      setModelStorageError("Photo tools couldn't start. Try again.");
      return false;
    }
  }

  const aiPhoto = aiPhotoInput(photo);
  const processing = ai.state.isLoading || ai.state.isProcessing;
  const supported = ai.state.capabilities?.supported !== false;
  const toolsReady = supported && (modelLoaded || modelCached);
  const actionDisabled = processing || !toolsReady;
  const checkingCapabilities = ai.state.isLoading && /^checking photo tools/i.test(ai.state.status);
  const showAiStatus = processing && !checkingCapabilities && !ai.state.error;
  const showSetupCard = !supported || isPreparingModel || Boolean(modelStorageError) || (!modelLoaded && !modelCached);

  return (
    <section className="photo-tools-section" aria-label="Background tools">
      <div className="photo-tools-heading">
        <h3>Background tools</h3>
        <Button
          variant="secondary"
          size="icon"
          type="button"
          onClick={() => {
            if (processing) {
              ai.cancel();
            }
            onClose();
          }}
          aria-label="Close background tools"
        >
          <X size={18} />
        </Button>
      </div>

      {ai.state.error && supported && (
        <Alert tone="danger" className="ui-alert-ai-recovery" role="alert" aria-live="assertive">
          <span>
            <strong>Photo edit didn't finish</strong>
            <small>{photoToolErrorText(ai.state.error)}</small>
          </span>
          <div className="ai-inline-actions">
            {lastActionRef.current && (
              <Button size="compact" type="button" onClick={() => void lastActionRef.current?.().catch(() => undefined)}>
                Retry
              </Button>
            )}
            <Button variant="ghost" size="compact" type="button" onClick={ai.clearError}>
              Keep editing
            </Button>
          </div>
        </Alert>
      )}

      <div className="ai-workspace">
        <div className={photo.type === "background_removed" ? "ai-preview transparency-preview" : "ai-preview"}>
          <img src={photo.url} alt="Photo preview" style={photoStyle(photo.treatment)} />
        </div>

        {showSetupCard && (
          <div className="ai-setup-column">
            <PrivatePhotoSetupCard
              storage={modelStorage}
              loaded={modelLoaded}
              supported={supported}
              preparing={isPreparingModel}
              error={modelStorageError}
              onPrepare={() => void prepareLocalModel()}
            />
            {showAiStatus && (
              <Alert tone="info" className="ui-alert-photo-progress" role="status">
                <span>{photoToolStatusText(ai.state.status)}</span>
                <progress value={ai.state.progress} max={1} />
              </Alert>
            )}
          </div>
        )}

        {toolsReady && (
          <div className="ai-controls">
            {showAiStatus && (
              <Alert tone="info" className="ui-alert-photo-progress" role="status">
                <span>{photoToolStatusText(ai.state.status)}</span>
                <progress value={ai.state.progress} max={1} />
              </Alert>
            )}
            <div className="ai-action-grid">
              <Button
                variant="tool"
                type="button"
                disabled={actionDisabled}
                onClick={() => runAction("Background removed", "remove-bg", () => ({ type: "remove-bg", photo: aiPhoto }))}
              >
                <ImagePlus size={18} /> Remove background
              </Button>
              <Button
                variant="tool"
                type="button"
                disabled={actionDisabled}
                onClick={() => runAction("Product selection", "detect-objects", () => ({ type: "detect-objects", photo: aiPhoto }))}
              >
                <ListFilter size={18} /> Select product
              </Button>
              <Button variant="tool" type="button" disabled={actionDisabled} onClick={() => setShowingRefine((current) => !current)}>
                <Wand2 size={18} /> Refine
              </Button>
              <Button
                variant="tool"
                type="button"
                disabled={actionDisabled}
                onClick={() => runAction("Remove background + shadow", "studio-clean", () => ({ type: "studio-clean", photo: aiPhoto, backgroundStyle: selectedBackground }))}
              >
                <Sparkles size={18} /> Remove background + shadow
              </Button>
              <Button
                variant="tool"
                type="button"
                disabled={actionDisabled}
                onClick={() => runAction("Custom background", "set-bg", () => ({ type: "set-bg", photo: aiPhoto, backgroundStyle: selectedBackground }))}
              >
                <Package size={18} /> Set background
              </Button>
              <Button
                variant="tool"
                type="button"
                disabled={actionDisabled}
                onClick={() => runAction("Natural shadow", "shadow", () => ({ type: "shadow", photo: aiPhoto, backgroundStyle: selectedBackground }))}
              >
                <Eye size={18} /> Shadow
              </Button>
            </div>

            <BackgroundPicker value={selectedBackground} onChange={setSelectedBackground} />

            {processing && (
              <Button full type="button" onClick={ai.cancel}>
                Cancel
              </Button>
            )}
          </div>
        )}
      </div>

      {objects.length > 0 && (
        <ObjectSelectionPanel
          objects={objects}
          removedObjectIDs={removedObjectIDs}
          onToggle={(id) =>
            setRemovedObjectIDs((current) => {
              const next = new Set(current);
              if (next.has(id)) {
                next.delete(id);
              } else {
                next.add(id);
              }
              return next;
            })
          }
          onApply={() => {
            const keepObjectIDs = objects.filter((object) => !removedObjectIDs.has(object.id)).map((object) => object.id);
            void runAction("Product cutout", "object-cutout", () => ({ type: "object-cutout", photo: aiPhoto, keepObjectIDs }));
          }}
        />
      )}

      {showingRefine && (
        <div ref={refinementPanelRef}>
          <MaskRefinementPanel
            photo={photo}
            disabled={processing || !supported}
            onCancel={() => setShowingRefine(false)}
            onApply={(strokes) =>
              runAction("Refined cutout", "refine-mask", () => ({
                type: "refine-mask",
                photo: aiPhoto,
                strokes
              })).then(() => setShowingRefine(false))
            }
          />
        </div>
      )}
    </section>
  );
}

function PrivatePhotoSetupCard({
  storage,
  loaded,
  supported,
  preparing,
  error,
  onPrepare
}: {
  storage: PhotoAiModelStorageState | null;
  loaded: boolean;
  supported: boolean;
  preparing: boolean;
  error: string | null;
  onPrepare: () => void;
}) {
  const cached = Boolean(storage?.modelCached);
  const accepted = Boolean(storage?.acceptedAt);
  const hasError = Boolean(error);
  const statusClass = !supported || hasError ? "unavailable" : loaded || cached ? "ready" : accepted ? "warning" : "pending";
  const heading = !supported
    ? "Background editing unavailable"
    : hasError
      ? "Background tools need setup"
      : loaded || cached
        ? "Background tools ready"
        : accepted
          ? "Download background tools"
          : "Set up background editing";
  const detail = !supported
    ? "This device cannot run private background editing."
    : hasError
      ? "Try the one-time download again."
      : loaded
      ? "Background edits run privately on this device."
      : cached
        ? "Background edits run privately on this device."
        : accepted
          ? "Download once; edits then run on this device."
          : "Download once; photos are processed on this device.";
  const primaryLabel = hasError ? "Try again" : accepted ? "Download" : "Set up";
  const showSetupAction = supported && !loaded && (!cached || hasError);

  return (
    <div className={`private-photo-setup ${statusClass}`}>
      <div className="private-photo-setup-summary">
        <span className="private-photo-setup-mark" aria-hidden="true">
          {loaded || cached ? <CheckCircle2 size={18} /> : <Sparkles size={18} />}
        </span>
        <span>
          <strong>{heading}</strong>
          <small>{detail}</small>
        </span>
      </div>

      {showSetupAction && (
        <div className="private-photo-setup-actions">
          <Button variant="primary" size="compact" type="button" onClick={onPrepare} disabled={preparing} loading={Boolean(preparing)} loadingLabel="Setting up">
            <Sparkles size={16} />
            {preparing ? "Setting up" : primaryLabel}
          </Button>
        </div>
      )}

      {error && <Alert tone="danger">{error}</Alert>}
    </div>
  );
}

function photoToolStatusText(status: string) {
  const normalized = status.toLowerCase();
  if (!status.trim()) {
    return "Preparing background tools";
  }
  if (hasTechnicalPhotoToolText(status)) {
    return "Preparing background tools";
  }
  if (normalized.includes("download")) {
    return "Setting up background tools";
  }
  if (normalized.includes("setup") || normalized.includes("setting up")) {
    return "Setting up background tools";
  }
  if (normalized.includes("backup") || normalized.includes("another private")) {
    return "Trying another editing method";
  }
  if (normalized.includes("ready")) {
    return "Background tools ready";
  }
  return status.replace(/^photo tools/i, "Background tools");
}

function photoToolErrorText(error: string) {
  if (!error.trim()) {
    return "Photo tools couldn't finish. Try again.";
  }
  if (hasTechnicalPhotoToolText(error)) {
    return "Photo tools couldn't finish. Try again.";
  }
  return error
    .replace(/this browser/gi, "this device")
    .replace(/baseline photo tools/gi, "basic photo edits")
    .replace(/^photo processing failed/gi, "Photo tools couldn't finish");
}

function hasTechnicalPhotoToolText(value: string) {
  return /\b(onnx|webgpu|wasm|ben2|rmbg|modnet|birefnet|model|segmentation|matting|tensor|alpha mask|shader|storage buffer|storage-buffer|adapter|runtime|webassembly|worker|offscreen|navigator\.gpu)\b/i.test(value);
}

function BackgroundPicker({ value, onChange }: { value: AiBackgroundStyle; onChange: (value: AiBackgroundStyle) => void }) {
  const options: Array<{ value: AiBackgroundStyle; label: string; swatch: string }> = [
    { value: "marketplace_white", label: "White", swatch: "#FFFFFF" },
    { value: "soft_neutral", label: "Neutral", swatch: "#F2F4F7" },
    { value: "warm_studio", label: "Warm", swatch: "#F4EEE3" },
    { value: "transparent", label: "Clear", swatch: "transparent" }
  ];
  return (
    <div className="background-picker">
      <span>Background</span>
      <div>
        {options.map((option) => (
          <SwatchButton
            key={option.value}
            label={option.label}
            swatch={option.swatch}
            selected={value === option.value}
            type="button"
            aria-label={`Use ${option.label.toLowerCase()} background`}
            onClick={() => onChange(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

function ObjectSelectionPanel({
  objects,
  removedObjectIDs,
  onToggle,
  onApply
}: {
  objects: AiObjectCandidate[];
  removedObjectIDs: Set<string>;
  onToggle: (id: string) => void;
  onApply: () => void;
}) {
  const selectedObjectCount = objects.filter((object) => !removedObjectIDs.has(object.id)).length;
  const allObjectsExcluded = objects.length > 0 && selectedObjectCount === 0;

  return (
    <section className="product-selection-section" aria-label="Product selection">
      <PanelHeader
        title="Choose what stays"
        action={<Badge tone={allObjectsExcluded ? "warning" : "info"}>{selectedObjectCount} selected</Badge>}
      />
      <p className="ui-panel-instruction">Select the product areas to keep in the cutout.</p>
      <div className="object-grid">
        {objects.map((object, index) => {
          const removed = removedObjectIDs.has(object.id);
          return (
            <Button
              key={object.id}
              variant="tool"
              type="button"
              className={removed ? "object-choice removed" : "object-choice"}
              aria-pressed={!removed}
              aria-label={`${removed ? "Include" : "Exclude"} product area ${index + 1}`}
              onClick={() => onToggle(object.id)}
            >
              <img src={object.thumbnailURL} alt="" />
              <span>{removed ? "Excluded" : "Included"}</span>
            </Button>
          );
        })}
      </div>
      {allObjectsExcluded ? (
        <Alert tone="warning" className="ui-alert-photo-guidance" role="status">
          <ListFilter size={18} />
          <span>
            <strong>Keep at least one product area</strong>
            <small>Include an area above before applying the cutout.</small>
          </span>
        </Alert>
      ) : (
        <Button variant="primary" full type="button" onClick={onApply}>
          Apply selection
        </Button>
      )}
    </section>
  );
}

function MaskRefinementPanel({
  photo,
  disabled,
  onApply,
  onCancel
}: {
  photo: PhotoDraft;
  disabled: boolean;
  onApply: (strokes: AiBrushStroke[]) => Promise<void>;
  onCancel: () => void;
}) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const refinementHelpID = `cutout-refinement-help-${photo.id}`;
  const [mode, setMode] = useState<AiBrushMode>("remove");
  const [brushSize, setBrushSize] = useState(0.045);
  const [strokes, setStrokes] = useState<AiBrushStroke[]>([]);
  const [activeStroke, setActiveStroke] = useState<AiBrushStroke | null>(null);
  const [keyboardPoint, setKeyboardPoint] = useState({ x: 0.5, y: 0.5 });
  const [isCanvasFocused, setIsCanvasFocused] = useState(false);
  const activeStrokeRef = useRef<AiBrushStroke | null>(null);

  function pointFromClient(element: HTMLDivElement, clientX: number, clientY: number) {
    const rect = element.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height))
    };
  }

  function startStroke(event: ReactPointerEvent<HTMLDivElement>) {
    if (disabled) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const stroke: AiBrushStroke = {
      mode,
      radius: brushSize,
      points: [pointFromClient(event.currentTarget, event.clientX, event.clientY)]
    };
    activeStrokeRef.current = stroke;
    setActiveStroke(stroke);
    setStrokes((current) => [...current, stroke]);
  }

  function extendStroke(event: ReactPointerEvent<HTMLDivElement>) {
    if (!activeStrokeRef.current) {
      return;
    }
    const next = { ...activeStrokeRef.current, points: [...activeStrokeRef.current.points, pointFromClient(event.currentTarget, event.clientX, event.clientY)] };
    activeStrokeRef.current = next;
    setActiveStroke(next);
    setStrokes((current) => (current.length === 0 ? [next] : [...current.slice(0, -1), next]));
  }

  function startMouseStroke(event: MouseEvent<HTMLDivElement>) {
    if (activeStrokeRef.current || disabled) {
      return;
    }
    const stroke: AiBrushStroke = {
      mode,
      radius: brushSize,
      points: [pointFromClient(event.currentTarget, event.clientX, event.clientY)]
    };
    activeStrokeRef.current = stroke;
    setActiveStroke(stroke);
    setStrokes((current) => [...current, stroke]);
  }

  function extendMouseStroke(event: MouseEvent<HTMLDivElement>) {
    if (!activeStrokeRef.current) {
      return;
    }
    const next = { ...activeStrokeRef.current, points: [...activeStrokeRef.current.points, pointFromClient(event.currentTarget, event.clientX, event.clientY)] };
    activeStrokeRef.current = next;
    setActiveStroke(next);
    setStrokes((current) => (current.length === 0 ? [next] : [...current.slice(0, -1), next]));
  }

  function endStroke() {
    if (!activeStrokeRef.current) {
      return;
    }
    activeStrokeRef.current = null;
    setActiveStroke(null);
  }

  function placeKeyboardStroke(point = keyboardPoint) {
    if (disabled) {
      return;
    }
    const stroke: AiBrushStroke = {
      mode,
      radius: brushSize,
      points: [point]
    };
    setStrokes((current) => [...current, stroke]);
  }

  function handleRefinementKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const offsetByKey: Record<string, { x: number; y: number }> = {
      ArrowUp: { x: 0, y: -1 },
      ArrowDown: { x: 0, y: 1 },
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 }
    };
    const offset = offsetByKey[event.key];
    if (offset) {
      event.preventDefault();
      const step = event.shiftKey ? 0.08 : 0.025;
      setKeyboardPoint((current) => ({
        x: clamp(current.x + offset.x * step, 0, 1),
        y: clamp(current.y + offset.y * step, 0, 1)
      }));
      return;
    }
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      placeKeyboardStroke();
      return;
    }
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      setStrokes((current) => current.slice(0, -1));
    }
  }

  const renderedStrokes = activeStroke ? [...strokes, activeStroke] : strokes;
  const hasCleanupMarks = strokes.length > 0;
  const guidanceTitle = disabled ? "Cleanup is paused" : "Mark the edge to clean up";
  const guidanceDetail = disabled
    ? "Wait for the current photo edit to finish, then mark the product edge."
    : "Drag on the product preview, or focus it and press Enter, before applying cleanup.";
  const refinementStatusID = `${refinementHelpID}-status`;
  const refinementStatus = `${mode === "remove" ? "Remove" : "Restore"} mode. Brush at ${Math.round(keyboardPoint.x * 100)}% across and ${Math.round(keyboardPoint.y * 100)}% down. ${strokes.length} ${strokes.length === 1 ? "mark" : "marks"} placed. Brush size ${Math.round(brushSize * 200)}%.`;

  return (
    <section className="cutout-refinement-section" aria-label="Refine cutout">
      <p className="sr-only" id={refinementHelpID}>
        Draw on the product preview to remove or restore parts of the cutout. Keyboard users can focus the cutout editor, move the brush with arrow keys, place a mark with Enter or Space, and undo with Backspace.
      </p>
      <p className="sr-only" id={refinementStatusID} aria-live="polite" aria-atomic="true">{refinementStatus}</p>
      <PanelHeader
        title="Clean up the product edge"
        action={
          <Button variant="ghost" size="compact" type="button" onClick={onCancel}>
            Close
          </Button>
        }
      />
      <div className="refine-preview-grid">
        <div className="refine-source-preview">
          <span>Original</span>
          <img src={photo.originalUrl ?? photo.url} alt="Original photo before refinement" />
        </div>
        <div className="refine-editing-surface">
          <span>Cutout editor</span>
          <div
            className="refine-canvas"
            role="group"
            tabIndex={disabled ? -1 : 0}
            aria-label={`${mode === "remove" ? "Remove from" : "Restore to"} cutout brush`}
            aria-describedby={`${refinementHelpID} ${refinementStatusID}`}
            onPointerDown={startStroke}
            onPointerMove={extendStroke}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onMouseDown={startMouseStroke}
            onMouseMove={extendMouseStroke}
            onMouseUp={endStroke}
            onMouseLeave={endStroke}
            onFocus={() => setIsCanvasFocused(true)}
            onBlur={() => setIsCanvasFocused(false)}
            onKeyDown={handleRefinementKeyDown}
          >
            <img ref={imageRef} src={photo.url} alt="Editable product cutout" />
            <svg viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
              {renderedStrokes.map((stroke, index) => (
                <polyline
                  key={`${stroke.mode}-${index}`}
                  points={stroke.points.map((point) => `${point.x},${point.y}`).join(" ")}
                  stroke={stroke.mode === "remove" ? "#C2413B" : "#2E9B68"}
                  strokeWidth={stroke.radius * 2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  opacity="0.72"
                />
              ))}
              {isCanvasFocused && (
                <circle
                  className="refine-keyboard-cursor"
                  cx={keyboardPoint.x}
                  cy={keyboardPoint.y}
                  r={brushSize}
                />
              )}
            </svg>
          </div>
        </div>
      </div>
      <div className="refine-controls">
        <SegmentedControl
          label="Cutout brush mode"
          value={mode}
          options={[
            { key: "remove", label: "Remove" },
            { key: "restore", label: "Restore" }
          ]}
          onChange={setMode}
        />
        <label>
          <span>Brush size</span>
          <RangeInput min={0.015} max={0.12} step={0.005} value={brushSize} aria-label="Brush size" onChange={(event) => setBrushSize(Number(event.target.value))} />
        </label>
      </div>
      {hasCleanupMarks ? (
        <div className="ai-inline-actions">
          <Button type="button" onClick={() => setStrokes([])}>
            Reset
          </Button>
          <Button variant="primary" type="button" onClick={() => void onApply(strokes)} disabled={disabled}>
            Apply cleanup
          </Button>
        </div>
      ) : (
        <Alert tone="info" className="ui-alert-photo-guidance" role="status">
          <Wand2 size={18} />
          <span>
            <strong>{guidanceTitle}</strong>
            <small>{guidanceDetail}</small>
          </span>
        </Alert>
      )}
    </section>
  );
}

function RecordSaleDialog({
  preset,
  onClose
}: {
  preset: SalePreset;
  onClose: () => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const isMarketplaceOrder = Boolean(preset.externalOrderID && preset.marketplace);
  const isEbayReview = Boolean(preset.marketplace === "ebay" && preset.orderReview);
  const [stockAlreadyUpdated, setStockAlreadyUpdated] = useState(false);
  const [itemID, setItemID] = useState(
    preset.itemID ??
      (isMarketplaceOrder
        ? ""
        : store.inventoryItems.find((item) => item.status !== "sold")?.id ?? store.inventoryItems[0]?.id ?? "")
  );
  const availableListings = store.listingsFor(itemID);
  const saleListings = availableListings.filter(
    (listing) => (listing.status === "live" || (isEbayReview && listing.status === "ended" && listing.id === preset.listingID)) && (!isMarketplaceOrder || listing.marketplace === preset.marketplace)
  );
  const [listingID, setListingID] = useState(preset.listingID && saleListings.some((listing) => listing.id === preset.listingID) ? preset.listingID : saleListings[0]?.id ?? "");
  const selectedListing = saleListings.find((listing) => listing.id === listingID);
  const selectedItem = store.itemFor(itemID);
  const [reviewedItem, setReviewedItem] = useState(selectedItem);
  const [purchaseCost, setPurchaseCost] = useState("");
  const [needsItemReview, setNeedsItemReview] = useState(false);
  const [isRefreshingItem, setIsRefreshingItem] = useState(false);
  const purchaseCostInputRef = useRef<HTMLInputElement>(null);
  const canAddPurchaseCost = Boolean(reviewedItem && !purchaseCostIsKnown(reviewedItem));
  let purchaseCostMinor: number | undefined;
  let purchaseCostError: string | undefined;
  if (canAddPurchaseCost && purchaseCost.trim()) {
    try { purchaseCostMinor = parseCatalogueCost(purchaseCost); }
    catch { purchaseCostError = "Purchase cost must be 0 or more, with at most two decimal places."; }
  }
  const costIsKnown = Boolean(reviewedItem && (purchaseCostIsKnown(reviewedItem) || purchaseCostMinor !== undefined));
  const [marketplace, setMarketplace] = useState<Marketplace>(preset.marketplace ?? selectedListing?.marketplace ?? "other");
  const [salePrice, setSalePrice] = useState(
    preset.salePriceMinor !== undefined
      ? (preset.salePriceMinor / 100).toFixed(2)
      : selectedListing
        ? moneyInputValue(selectedListing.price)
        : selectedItem
          ? moneyInputValue(selectedItem.targetPrice)
          : ""
  );
  const [shippingCharged, setShippingCharged] = useState(((preset.orderReview?.shippingChargedMinor ?? 0) / 100).toFixed(2));
  const [marketplaceFees, setMarketplaceFees] = useState(isEbayReview ? preset.orderReview?.marketplaceFeesMinor === undefined ? "" : (preset.orderReview.marketplaceFeesMinor / 100).toFixed(2) : "0.00");
  const [processingFees, setProcessingFees] = useState("0.00");
  const [shippingCost, setShippingCost] = useState(isEbayReview ? "" : "0.00");
  const [packagingCost, setPackagingCost] = useState("0.00");
  const [buyerName, setBuyerName] = useState("");
  const [buyerAddress, setBuyerAddress] = useState("");
  const [buyerPostcode, setBuyerPostcode] = useState("");
  const salePriceInputRef = useRef<HTMLInputElement>(null);
  const shippingChargedInputRef = useRef<HTMLInputElement>(null);
  const marketplaceFeesInputRef = useRef<HTMLInputElement>(null);
  const processingFeesInputRef = useRef<HTMLInputElement>(null);
  const shippingCostInputRef = useRef<HTMLInputElement>(null);
  const packagingCostInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [soldAt, setSoldAt] = useState(preset.soldAt && Number.isFinite(new Date(preset.soldAt).getTime()) ? preset.soldAt.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [shipBy, setShipBy] = useState(preset.orderReview?.shipped ? "" : defaultShipByDate());

  useEffect(() => {
    if (!listingID || saleListings.some((listing) => listing.id === listingID)) {
      return;
    }
    const replacement = saleListings[0];
    setListingID(replacement?.id ?? "");
    if (!replacement && !isMarketplaceOrder) setMarketplace("other");
  }, [isMarketplaceOrder, listingID, saleListings]);

  const currency = store.workspace?.defaultCurrency ?? "GBP";
  const salePriceMinor = parseMoneyInput(salePrice, currency).amountMinor;
  const shippingChargedMinor = parseMoneyInput(shippingCharged, currency).amountMinor;
  const marketplaceFeesMinor = parseMoneyInput(marketplaceFees, currency).amountMinor;
  const processingFeesMinor = parseMoneyInput(processingFees, currency).amountMinor;
  const shippingCostMinor = parseMoneyInput(shippingCost, currency).amountMinor;
  const packagingCostMinor = parseMoneyInput(packagingCost, currency).amountMinor;
  const preview = calculateNetProfit({
    salePriceMinor: Math.max(0, salePriceMinor),
    shippingChargedMinor: Math.max(0, shippingChargedMinor),
    purchaseCostMinor: purchaseCostMinor ?? reviewedItem?.purchaseCost.amountMinor ?? 0,
    marketplaceFeesMinor: Math.max(0, marketplaceFeesMinor),
    processingFeesMinor: Math.max(0, processingFeesMinor),
    shippingCostMinor: Math.max(0, shippingCostMinor),
    packagingCostMinor: Math.max(0, packagingCostMinor),
    currency
  });

  useEffect(() => {
    const listing = store.listings.find((candidate) => candidate.id === listingID);
    if (listing) {
      if (!isMarketplaceOrder) {
        setMarketplace(listing.marketplace);
      }
      if (preset.salePriceMinor === undefined) {
        setSalePrice(moneyInputValue(listing.price));
      }
      return;
    }

    if (selectedItem && preset.salePriceMinor === undefined) {
      setSalePrice(moneyInputValue(selectedItem.targetPrice));
    }
  }, [isMarketplaceOrder, listingID, preset.salePriceMinor, selectedItem, store.listings]);

  async function submit() {
    try {
      if (!itemID) {
        throw new Error("Select an item.");
      }
      if (needsItemReview) throw new Error("This item changed. Review the latest item before recording the sale.");
      if (purchaseCostError) throw new Error(purchaseCostError);
      if (salePriceMinor <= 0) {
        throw new Error("Sale price must be greater than zero.");
      }
      const negativeMoneyError = saleMoneyValidationError([
        ["Shipping charged", shippingChargedMinor],
        ["Marketplace fees", marketplaceFeesMinor],
        ["Processing fees", processingFeesMinor],
        ["Shipping cost", shippingCostMinor],
        ["Packaging", packagingCostMinor]
      ]);
      if (negativeMoneyError) {
        throw new Error(negativeMoneyError);
      }
      if (listingID && !saleListings.some((listing) => listing.id === listingID)) {
        throw new Error("Choose a live listing, or switch to Manual sale.");
      }
      if (isMarketplaceOrder && !isEbayReview && !listingID) {
        throw new Error(`Match this ${marketplaceLabels[preset.marketplace!]} order to its live listing.`);
      }
      if (isMarketplaceOrder && marketplace !== preset.marketplace) {
        throw new Error("The marketplace cannot be changed for a verified marketplace order.");
      }
      setIsSaving(true);
      await measureTask("sale", () => store.recordSale({
        itemID,
        expectedItemUpdatedAt: reviewedItem?.updatedAt,
        ...(purchaseCostMinor !== undefined ? { purchaseCostMinor } : {}),
        listingID: listingID || undefined,
        externalOrderID: preset.externalOrderID,
        sourceNotificationID: preset.notificationID,
        stockAlreadyUpdated: isEbayReview ? stockAlreadyUpdated : undefined,
        marketplace,
        salePriceMinor,
        shippingChargedMinor,
        marketplaceFeesMinor,
        marketplaceFeesStatus: isEbayReview && !marketplaceFees.trim() ? "unknown" : "known",
        shippingCostStatus: isEbayReview && !shippingCost.trim() ? "unknown" : "known",
        processingFeesMinor,
        shippingCostMinor,
        packagingCostMinor,
        soldAt: isEbayReview && preset.soldAt?.slice(0, 10) === soldAt ? preset.soldAt : new Date(`${soldAt}T12:00:00`).toISOString(),
        buyerName,
        buyerAddress,
        buyerPostcode,
        shipBy: shipBy ? new Date(`${shipBy}T12:00:00`).toISOString() : undefined
      }));
      if (preset.notificationID) {
        await store.markNotificationRead(preset.notificationID).catch(() => undefined);
      }
      const remainingLive = store.listings.filter((listing) => listing.itemID === itemID && listing.id !== listingID && listing.status === "live").length;
      const finalUnit = (store.itemFor(itemID)?.quantity ?? 0) <= 1;
      toast.notify({ title: "Sale recorded", description: finalUnit && remainingLive ? `End ${remainingLive} other live ${remainingLive === 1 ? "listing" : "listings"} on the marketplace. Find them in Needs attention.` : "Continue fulfilment from Orders.", tone: finalUnit && remainingLive ? "warning" : "success" });
      onClose();
    } catch (caught) {
      const message = userSafeErrorMessage(caught, "Unable to record sale.");
      setError(message);
      if (message.startsWith("This item")) setNeedsItemReview(true);
      window.requestAnimationFrame(() => {
        const target = message.startsWith("Purchase cost") ? purchaseCostInputRef.current
          : message.startsWith("Sale price") ? salePriceInputRef.current
          : message.startsWith("Shipping charged") ? shippingChargedInputRef.current
          : message.startsWith("Marketplace fees") ? marketplaceFeesInputRef.current
          : message.startsWith("Processing fees") ? processingFeesInputRef.current
          : message.startsWith("Shipping cost") ? shippingCostInputRef.current
          : message.startsWith("Packaging") ? packagingCostInputRef.current
          : null;
        target?.focus({ preventScroll: true });
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => {
      if (!open && !isSaving) {
        onClose();
      }
    }}>
      <DialogContent className={isEbayReview ? "ui-dialog-content-sale ui-dialog-content-ebay-sale" : "ui-dialog-content-sale"} leadingClose showClose={false}>
        <DialogHeader>
          <Button variant="secondary" size="icon" type="button" onClick={onClose} aria-label="Close" disabled={isSaving}>
            <X size={20} />
          </Button>
          <DialogTitle>{isEbayReview ? "Review eBay sale" : "Record sale"}</DialogTitle>
          <DialogDescription className="sr-only">
            {isMarketplaceOrder
              ? `Review verified ${marketplaceLabels[preset.marketplace!]} order ${preset.externalOrderID}, confirm the costs and choose how to update stock.`
              : "Choose the sold item, marketplace, price, costs, buyer, and delivery promise to update inventory, fulfilment, and profit."}
          </DialogDescription>
          {!purchaseCostError && (costIsKnown && (!isEbayReview || (marketplaceFees.trim() && shippingCost.trim()))
            ? <Badge tone={preview.amountMinor >= 0 ? "success" : "danger"}>Est. profit {formatMoney(preview)}</Badge>
            : <Badge tone="warning">Profit provisional</Badge>)}
        </DialogHeader>
        <DialogBody className="ui-dialog-body-sale">
          {error && <Alert tone="danger" role="alert">{error}
            {needsItemReview && <Button variant="secondary" type="button" loading={isRefreshingItem} onClick={async () => {
              setIsRefreshingItem(true);
              try {
                await store.refreshWorkspace();
                setNeedsItemReview(false);
                setPurchaseCost("");
                // Reopen with current item, listing and marketplace order facts.
                onClose();
              } catch (caught) { setError(userSafeErrorMessage(caught, "Could not reload the item. Try again.")); }
              finally { setIsRefreshingItem(false); }
            }}>Refresh and close</Button>}
          </Alert>}
          {preset.notificationID && (
            <Alert tone="info" className="ui-alert-photo-guidance" role="status">
              {isEbayReview ? <ShoppingBag size={18} /> : <Mail size={18} />}
              <span>
                <strong>{isEbayReview ? preset.orderReview?.shipped ? "Paid · Shipped on eBay" : "Paid on eBay" : "Detected from marketplace"}</strong>
                <small>{preset.signalSubject || "Check the item and amount before recording this sale."}</small>
              </span>
            </Alert>
          )}
          <div className="sale-form-sections">
            <section className="sale-form-section">
              <div className="sale-form-section-heading">
                <h3>What sold</h3>
              </div>
              <div className="form-grid sale-form-grid-two">
                {isEbayReview && preset.itemID && selectedItem ? <div className="sale-order-item"><strong>{selectedItem.title}</strong><small>{selectedItem.sku}</small></div> : <>
                <Field label="Item" wide helper={selectedItem ? recordSaleItemHelper(selectedItem) : undefined}>
                  <SelectControl
                    className="ui-select-sale-item"
                    disabled={isSaving || (isEbayReview && Boolean(preset.itemID))}
                    value={itemID}
                    onValueChange={(value) => {
                      setItemID(value);
                      setReviewedItem(store.itemFor(value));
                      setPurchaseCost("");
                      setNeedsItemReview(false);
                      setError(null);
                    }}
                    options={store.inventoryItems.map((item) => ({ value: item.id, label: item.title }))}
                  />
                </Field>
                <Field label="Listing">
                  <SelectControl
                    value={listingID}
                    disabled={isEbayReview && Boolean(preset.listingID)}
                    onValueChange={(value) => {
                      setListingID(value);
                      if (!value && !isMarketplaceOrder) setMarketplace("other");
                    }}
                    options={[
                      ...(!isEbayReview || !preset.listingID ? [{ value: "", label: isEbayReview ? "No linked listing" : "Manual sale" }] : []),
                      ...saleListings.map((listing) => ({
                        value: listing.id,
                        label: `${marketplaceLabels[listing.marketplace]} - ${listing.status === "ended" ? "Ended" : "Live"}`
                      }))
                    ]}
                  />
                  {saleListings.length === 0 && <small className="ui-field-helper">{isEbayReview ? "Choose the inventory item that sold on eBay." : "No live listings yet. Use Manual sale for a sale outside a connected marketplace or in person."}</small>}
                </Field>
                </>}
                {isEbayReview && <Field label="Stock" wide><CheckboxField label="Stock already updated for this sale" checked={stockAlreadyUpdated} onChange={(event) => setStockAlreadyUpdated(event.target.checked)} /><small className="ui-field-helper">{stockAlreadyUpdated ? "Records the sale without deducting stock again." : `Deducts 1 unit${selectedItem ? ` · ${Math.max(0, selectedItem.quantity - 1)} remaining` : ""}.`}</small></Field>}
                {!listingID && !isMarketplaceOrder ? (
                  <Field label="Marketplace">
                    <SelectControl
                      value={marketplace}
                      onValueChange={(value) => setMarketplace(value as Marketplace)}
                      options={manualSaleMarketplaces.map((candidate) => ({
                        value: candidate,
                        label: candidate === "other" ? "Other / in person" : marketplaceLabels[candidate]
                      }))}
                    />
                  </Field>
                ) : null}
              </div>
            </section>

            <section className="sale-form-section">
              <div className="sale-form-section-heading">
                <h3>Sale details</h3>
              </div>
              <div className="form-grid sale-form-grid-two">
                <MoneyField label="Sale price" value={salePrice} onChange={setSalePrice} required inputRef={salePriceInputRef} invalid={error?.startsWith("Sale price")} />
                {reviewedItem && (canAddPurchaseCost ? <Field
                  label="Purchase cost (optional)"
                  helper="Per item · also saved to inventory. Blank means unknown; 0 means free."
                  error={error?.startsWith("Purchase cost") ? error : undefined}
                >
                  <Input ref={purchaseCostInputRef} inputMode="decimal" placeholder="Unknown" value={purchaseCost}
                    disabled={isSaving || needsItemReview} onChange={(event) => {
                      setPurchaseCost(event.target.value);
                      if (error?.startsWith("Purchase cost")) setError(null);
                    }} />
                </Field> : <Field label="Purchase cost"><output>{formatMoney(reviewedItem.purchaseCost)}</output></Field>)}
                <Field label="Sold at">
                  <Input type="date" value={soldAt} onChange={(event) => setSoldAt(event.target.value)} />
                </Field>
                {!preset.orderReview?.shipped && <Field label="Ship by">
                  <Input type="date" value={shipBy} onChange={(event) => setShipBy(event.target.value)} />
                </Field>}
              </div>
            </section>

            <Disclosure
              className="sale-form-disclosure"
              label="Costs and shipping"
              description="Optional adjustments to estimated profit"
            >
              <div className="form-grid">
                <MoneyField label="Shipping charged" value={shippingCharged} onChange={setShippingCharged} inputRef={shippingChargedInputRef} invalid={error?.startsWith("Shipping charged")} />
                <MoneyField label="Marketplace fees" value={marketplaceFees} onChange={setMarketplaceFees} helper={isEbayReview ? "Leave blank to use eBay’s cost updates. Enter 0 only if eBay confirms no fee." : undefined} inputRef={marketplaceFeesInputRef} invalid={error?.startsWith("Marketplace fees")} />
                <MoneyField label="Processing fees" value={processingFees} onChange={setProcessingFees} inputRef={processingFeesInputRef} invalid={error?.startsWith("Processing fees")} />
                <MoneyField label="Shipping cost" value={shippingCost} onChange={setShippingCost} helper={isEbayReview ? "Leave blank to use eBay postage-label costs, or enter what you paid elsewhere." : undefined} inputRef={shippingCostInputRef} invalid={error?.startsWith("Shipping cost")} />
                <MoneyField label="Packaging" value={packagingCost} onChange={setPackagingCost} inputRef={packagingCostInputRef} invalid={error?.startsWith("Packaging")} />
              </div>
            </Disclosure>

            <Disclosure
              className="sale-form-disclosure"
              label="Buyer and delivery"
              description="Optional fulfilment details"
            >
              <div className="form-grid">
                <Field label="Buyer">
                  <Input value={buyerName} onChange={(event) => setBuyerName(event.target.value)} />
                </Field>
                <Field label="Postcode">
                  <Input value={buyerPostcode} onChange={(event) => setBuyerPostcode(event.target.value)} />
                </Field>
                <Field label="Delivery address" wide>
                  <Textarea value={buyerAddress} onChange={(event) => setBuyerAddress(event.target.value)} rows={3} />
                </Field>
              </div>
            </Disclosure>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" type="button" onClick={() => void submit()} disabled={isSaving || needsItemReview} loading={isSaving} loadingLabel="Recording...">
            {isSaving ? "Recording..." : "Record sale"} <CheckCircle2 size={18} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function inventoryStatusTone(status: InventoryItem["status"]): ComponentProps<typeof Badge>["tone"] {
  switch (status) {
    case "listed":
      return "success";
    case "ready_to_list":
      return "info";
    case "sold":
      return "strong";
    case "archived":
      return "neutral";
    case "draft":
    default:
      return "warning";
  }
}

function stockAgeDays(item: InventoryItem) { return inventoryAgeDays(item) ?? -1; }

function isAgingInventoryItem(item: InventoryItem) {
  return item.status !== "sold" && item.status !== "archived" && stockAgeDays(item) >= 60;
}

function inventoryItemMatchesAgeFilter(item: InventoryItem, filter: InventoryAgeFilterKey) {
  if (filter === "all") {
    return true;
  }
  if (item.status === "sold" || item.status === "archived") {
    return false;
  }

  const days = stockAgeDays(item);
  switch (filter) {
    case "0-30":
      return days >= 0 && days <= 30;
    case "31-60":
      return days >= 31 && days <= 60;
    case "61-90":
      return days >= 61 && days <= 90;
    case "90-plus":
      return days >= 91;
  }
}

function formatStockAge(item: InventoryItem) { return inventoryAgeLabel(item); }

function targetPriceLabel(item: InventoryItem) {
  return item.targetPrice.amountMinor > 0 ? `Target ${formatMoney(item.targetPrice)}` : "Price not set";
}

function InventoryRow({
  item,
  selected,
  rowRef,
  onClick
}: {
  item: InventoryItem;
  selected: boolean;
  rowRef?: Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  const store = useReOS();
  const listings = store.listingsFor(item.id);
  const photos = store.photosFor(item.id);
  const photoImport = pendingPhotoImport(item, store.database.channelPhotoJobs);
  const attention = inventoryAttention(item, photos.length, listings, store.database.channelPhotoJobs);
  const metadata = [item.brand, item.size].filter(Boolean).join(" · ");
  return (
    <DataRow ref={rowRef} className="inventory-item-row" selected={selected} onClick={onClick} aria-current={selected ? "true" : undefined}>
      <span className="inventory-row-identity">
        <PhotoFrame photo={store.primaryPhoto(item.id)} />
        <DataRowMain>
          <strong title={item.title}>{item.title}</strong>
          {(metadata || item.quantity > 1) && <DataRowMeta>{[metadata, item.quantity > 1 ? `${item.quantity} available` : ""].filter(Boolean).join(" · ")}</DataRowMeta>}
          {attention && <span className="inventory-row-attention">{attention}</span>}
          {photoImport && <DataRowMeta>Importing photos…</DataRowMeta>}
          {["sold", "archived"].includes(item.status) && <DataRowMeta>{inventoryStatusLabels[item.status]}</DataRowMeta>}
        </DataRowMain>
      </span>
      <span className="inventory-row-listings">{inventoryListingSummary(listings)}</span>
      <span className="inventory-row-price"><span className="sr-only">Target price: </span>{item.targetPrice.amountMinor > 0 ? formatMoney(item.targetPrice) : "Not set"}</span>
    </DataRow>
  );
}

function InventoryDetail({
  item,
  onAdjustStock,
  onMarkAsListed,
  onPrepareListing,
  focus,
  onBack,
  onOpenListing,
  onOpenOrder,
  onOpenResearch,
  onRecordSale
}: {
  item: InventoryItem;
  onAdjustStock: () => void;
  onMarkAsListed: () => void;
  onPrepareListing: (accountID?: string) => void;
  focus: Extract<NotificationFocus, { view: "inventory" }> | null;
  onBack: () => void;
  onOpenListing: (listingID: string, itemID?: string) => void;
  onOpenOrder: (saleID: string) => void;
  onOpenResearch: () => void;
  onRecordSale: () => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const photo = store.primaryPhoto(item.id);
  const photos = store.photosFor(item.id);
  const listings = store.listingsFor(item.id);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const primaryPhotoActionRef = useRef<HTMLButtonElement | null>(null);
  const photoReviewReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const detailPhotoUploadHelpID = `inventory-photo-upload-help-${item.id}`;
  const [photoProgress, setPhotoProgress, reportPhotoFailure, sharedPhotoError] = usePhotoUploadProgress(item.title, item.id);
  const photoUploadOwnerRef = useRef(item.id);
  photoUploadOwnerRef.current = item.id;
  const listingCoverageHeadingID = `listing-coverage-${item.id}`;
  const [archivePromptOpen, setArchivePromptOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [localPhotoError, setPhotoError] = useState<string | null>(null);
  const photoError = localPhotoError ?? sharedPhotoError;
  const [photoNotice, setPhotoNotice] = useState<{ tone: "success" | "warning"; message: string } | null>(null);
  const [photoActionStarted, setIsAddingPhotos] = useState(false);
  const isAddingPhotos = photoActionStarted || photoProgress !== null;
  const [photoReviewID, setPhotoReviewID] = useState<string | null>(null);
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editDetailField, setEditDetailField] = useState<"notes" | "stockLocation" | null>(null);
  const [stockHistoryOpen, setStockHistoryOpen] = useState(false);
  const canArchive = item.status !== "sold" && item.status !== "archived";
  const canRecordSale = item.status !== "sold" && item.status !== "archived" && item.quantity > 0;
  const currentRole = workspaceRoleForStore(store);
  const canArchiveForRole = currentRole === "owner" || currentRole === "admin";
  const relatedSale = store.sales.find((sale) => sale.itemID === item.id);
  const preparationAccounts = store.marketplaceAccounts.filter((account) => !manualListingEligibility(store.database, item.id, account));
  const researchActionLabel = !item.research ? "Research item" : item.research.provider.id === "local_research_preview" ? "Continue research" : "Review research";
  const [targetPriceOpen, setTargetPriceOpen] = useState(false);
  const photoImport = pendingPhotoImport(item, store.database.channelPhotoJobs);
  const attention = inventoryAttention(item, photos.length, listings, store.database.channelPhotoJobs);
  const metadata = [item.color, item.size].filter(Boolean).join(" · ");
  const itemDescription = /^Imported from (?:an? (?:active |sold )?)?(?:eBay|Etsy|Shopify)(?: listing)?\.?$/i.test(item.description.trim()) ? "" : item.description;

  useEffect(() => {
    setArchivePromptOpen(false);
    setIsAddingPhotos(false);
    setArchiveError(null);
    setPhotoError(null);
    setPhotoNotice(null);
    setPhotoReviewID(null);
    setEditItemOpen(false);
    setEditDetailField(null);
    setStockHistoryOpen(false);
    setTargetPriceOpen(false);
    photoReviewReturnFocusRef.current = null;
  }, [item.id]);

  async function addPhotoFiles(event: ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selectedFiles.length === 0 || isAddingPhotos) {
      return;
    }
    const availableSlots = Math.max(0, MAX_ITEM_PHOTOS - photos.length);
    if (availableSlots === 0) {
      setPhotoError(`This item already has the maximum of ${MAX_ITEM_PHOTOS} photos.`);
      return;
    }
    const files = selectedFiles.slice(0, availableSlots);
    const skippedCount = selectedFiles.length - files.length;
    setIsAddingPhotos(true);
    setPhotoError(null);
    setPhotoNotice(null);
    try {
      const result = await readPhotoDraftBatch(files, setPhotoProgress);
      if (result.drafts.length > 0) {
        const savedPhotos = await store.addItemPhotos(item.id, result.drafts.map(toPhotoInput), setPhotoProgress);
        toast.notify({ title: savedPhotos.length === 1 ? "Photo added" : "Photos added", tone: "success" });
      }
      if (photoUploadOwnerRef.current !== item.id) return;
      if (result.errors.length > 0) {
        setPhotoError(photoDraftReadErrorMessage(result.errors));
      }
      setPhotoNotice(photoDraftReadNotice(result.convertedHeicCount, skippedCount, result.drafts.length));
    } catch (caught) {
      reportPhotoFailure(photoUploadErrorMessage(caught, "Unable to add these photos."));
      if (photoUploadOwnerRef.current === item.id) setPhotoError(photoUploadErrorMessage(caught, "Unable to add these photos."));
    } finally {
      if (photoUploadOwnerRef.current === item.id) setIsAddingPhotos(false);
      setPhotoProgress(null);
    }
  }

  async function archiveItem() {
    setIsArchiving(true);
    setArchiveError(null);
    try {
      await store.archiveInventoryItem(item.id);
      setArchivePromptOpen(false);
    } catch (error: unknown) {
      setArchiveError(userSafeErrorMessage(error, "Unable to archive this item."));
    } finally {
      setIsArchiving(false);
    }
  }

  function restorePhotoActionFocus() {
    window.requestAnimationFrame(() => {
      const reviewTrigger = photoReviewReturnFocusRef.current;
      const target =
        reviewTrigger?.isConnected
          ? reviewTrigger
          : primaryPhotoActionRef.current;
      target?.focus();
    });
  }

  function openItemListing(listing: MarketplaceListing) {
    const account = store.marketplaceAccounts.find((candidate) => candidate.id === listing.marketplaceAccountID);
    if (account && isManualListingAccount(account) && ["draft", "ready", "failed"].includes(listing.status)) onPrepareListing(account.id);
    else onOpenListing(listing.id, item.id);
  }

  function startListing() {
    const soleAccount = preparationAccounts.length === 1 ? preparationAccounts[0] : undefined;
    onPrepareListing(soleAccount && isManualListingAccount(soleAccount) ? soleAccount.id : undefined);
  }

  async function copySKU() {
    try { await navigator.clipboard.writeText(item.sku); toast.notify({ title: "SKU copied", tone: "success" }); }
    catch { toast.notify({ title: "Copy unavailable. Select the SKU to copy it.", tone: "warning" }); }
  }

  return (
    <Panel as="article" className="ui-panel-detail-card inventory-item-detail">
      <nav className="inventory-item-nav" aria-label="Item navigation">
        <Button variant="ghost" className="mobile-back" type="button" onClick={onBack} aria-label="Back to inventory">
          <ArrowLeft size={18} /> Inventory
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" type="button" aria-label={`More actions for ${item.title}`}><MoreHorizontal size={19} /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent aria-label={`Actions for ${item.title}`}>
            <DropdownMenuGroup>
              <DropdownMenuItem onSelect={() => setEditItemOpen(true)}><Pencil size={17} />Edit item</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setStockHistoryOpen(true)}><Clock3 size={17} />Stock history</DropdownMenuItem>
              {photos.length < MAX_ITEM_PHOTOS && <DropdownMenuItem disabled={isAddingPhotos} onSelect={() => window.setTimeout(() => photoInputRef.current?.click(), 0)}><ImagePlus size={17} />Add photos</DropdownMenuItem>}
              {canRecordSale && <DropdownMenuItem onSelect={onOpenResearch}><ScanSearch size={17} />{researchActionLabel}</DropdownMenuItem>}
              {canRecordSale && <DropdownMenuItem onSelect={onRecordSale}><CreditCard size={17} />Record sale</DropdownMenuItem>}
              {relatedSale && <DropdownMenuItem onSelect={() => onOpenOrder(relatedSale.id)}><CreditCard size={17} />View sale</DropdownMenuItem>}
            </DropdownMenuGroup>
            {canArchive && canArchiveForRole && <><DropdownMenuSeparator /><DropdownMenuGroup><DropdownMenuItem tone="danger" onSelect={() => setArchivePromptOpen(true)} disabled={isArchiving}><Archive size={17} />Archive item</DropdownMenuItem></DropdownMenuGroup></>}
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
      <header className="inventory-detail-identity">
        <Button ref={primaryPhotoActionRef} variant="tool" className="inventory-cover-button" aria-label={photo ? "Manage item photos" : "Add photos"} aria-describedby={detailPhotoUploadHelpID} onClick={(event) => {
          photoReviewReturnFocusRef.current = event.currentTarget;
          if (photo) setPhotoReviewID(photo.id); else photoInputRef.current?.click();
        }} disabled={isAddingPhotos}>
          {photo ? <><PhotoFrame photo={photo} /><span className="inventory-cover-count">{photos.length} {photos.length === 1 ? "photo" : "photos"}</span></> : <span className="inventory-cover-empty"><ImagePlus size={24} /><span>Add photos</span></span>}
        </Button>
        <div className="inventory-detail-name">
          {item.brand && <p className="inventory-item-brand">{item.brand}</p>}
          <h2>{item.title}</h2>
          {metadata && <p>{metadata}</p>}
          {["sold", "archived"].includes(item.status) && <Badge tone={inventoryStatusTone(item.status)}>{inventoryStatusLabels[item.status]}</Badge>}
        </div>
      </header>
      {focus?.notice && <NotificationFocusCallout notice={focus.notice} />}
      {archivePromptOpen && (
        <InlineConfirm
          title="Archive this item?"
          description={`${item.title} will leave active stock. Sales history, photos, and existing records remain available.`}
          actions={
            <>
              <Button variant="ghost" type="button" onClick={() => setArchivePromptOpen(false)} disabled={isArchiving}>
                Cancel
              </Button>
              <Button variant="danger" type="button" onClick={() => void archiveItem()} disabled={isArchiving} loading={isArchiving} loadingLabel="Archiving...">
                {isArchiving ? "Archiving..." : "Archive item"}
              </Button>
            </>
          }
        />
      )}
      {archiveError && <Alert tone="danger">{archiveError}</Alert>}
      {photoImport && <p role="status" className="marketplace-import-photo-status">Importing photos… <a href={`/channels?channelActivity=${encodeURIComponent(photoImport.marketplaceAccountID)}&activityTab=photos`}>View progress</a></p>}
      {photos.length === 0 && !photoImport && <ItemChannelPhotosLink itemID={item.id} />}
      <PhotoUploadStatus progress={photoProgress} />
      {photoError && <Alert tone="danger">{photoError}</Alert>}
      {photoNotice && <Alert tone={photoNotice.tone}>{photoNotice.message}</Alert>}
      <div className="inventory-metrics">
        <div>
          <div className="inventory-metric-label"><span>Shared stock</span>{canArchive && <Button variant="ghost" size="icon" data-stock-trigger={item.id} aria-label={item.quantity === 0 || item.openingStockStatus === "review_required" ? "Check stock" : "Adjust stock"} onClick={onAdjustStock}><Pencil size={15} /></Button>}</div>
          <p><strong>{item.quantity}</strong> {item.quantity === 1 ? "unit" : "units"}</p>
          <small>Across all listings</small>
        </div>
        <div>
          <div className="inventory-metric-label"><span>Target price</span><Button variant="ghost" size="icon" aria-label="Edit target price" onClick={() => setTargetPriceOpen(true)}><Pencil size={15} /></Button></div>
          <p><strong>{item.targetPrice.amountMinor > 0 ? formatMoney(item.targetPrice) : "Not set"}</strong></p>
          <small>For new drafts</small>
        </div>
      </div>
      {attention && ["Check opening stock", "Check stock", "Stock review due"].includes(attention) && <div className="inventory-item-task"><span>{attention}</span><Button variant="ghost" onClick={onAdjustStock}>Check stock<ChevronRight size={16} /></Button></div>}
      {attention && ["Research ready", "Retry research", "Research needs your input", "Check condition"].includes(attention) && <div className="inventory-item-task"><span>{attention}</span><Button variant="ghost" onClick={onOpenResearch}>Review<ChevronRight size={16} /></Button></div>}
      <section className="inventory-listings-section" aria-labelledby={listingCoverageHeadingID}>
        <div className="inventory-section-heading">
          <h3 id={listingCoverageHeadingID}>Listings</h3>
          {listings.length > 0 && canRecordSale && preparationAccounts.length > 0 && <Button variant="ghost" className="inventory-create-listing" data-create-listing onClick={startListing}><Plus size={16} />Create listing</Button>}
        </div>
        {listings.length ? <>
          <div className="inventory-channel-list">
            {listings.map((listing) => {
              const account = store.marketplaceAccounts.find((candidate) => candidate.id === listing.marketplaceAccountID);
              const state = inventoryListingPresentation(listing, account);
              const action = state.draft ? "Edit draft" : state.attention ? "Review" : "Manage";
              return <Button variant="ghost" className="inventory-channel-row" key={listing.id} aria-label={`${action} ${marketplaceLabels[listing.marketplace]} listing`} onClick={() => openItemListing(listing)}>
                <span className="inventory-channel-identity">
                  <span className="inventory-channel-mark"><MarketplaceMark marketplace={listing.marketplace} /></span>
                  <span><strong className="inventory-channel-name">{marketplaceLabels[listing.marketplace]}</strong>
                    <small className={state.attention ? "inventory-row-attention" : state.live ? "inventory-listing-live" : undefined}><span>{state.status}</span>{state.note && !state.attention ? ` · ${state.note}` : ""}</small>
                    {state.attention && state.note && <small className="inventory-row-attention">{state.note}</small>}
                  </span>
                </span>
                <span className="inventory-channel-price"><span className="sr-only">{state.priceLabel}: </span><span>{formatMoney(state.price)}</span>{state.savedPrice && <small>Saved {formatMoney(state.savedPrice)}</small>}</span>
                <ChevronRight size={18} aria-hidden="true" />
              </Button>;
            })}
          </div>
        </> : <EmptyState as="h3" className="inventory-no-listings" icon={null} title="No listings yet"
          message={canRecordSale && preparationAccounts.length > 0 ? "Choose a channel to start a draft." : undefined}
          action={canRecordSale && preparationAccounts.length > 0 ? <Button variant="primary" data-create-listing onClick={startListing}><Plus size={18} />Create listing</Button> : undefined} />}
        {canRecordSale && preparationAccounts.some(isManualListingAccount) && <Button variant="ghost" className="inventory-record-listing" onClick={onMarkAsListed}><Link2 size={17} />Link an existing listing</Button>}
      </section>
      <div className="inventory-item-sections">
      <Disclosure className="inventory-detail-disclosure inventory-item-facts" label="Item details" description={[item.condition !== "unknown" ? conditionLabels[item.condition] : undefined, purchaseCostIsKnown(item) ? `${formatMoney(item.purchaseCost)} cost` : undefined].filter(Boolean).join(" · ")}>
        <Button variant="ghost" className="inventory-edit-details" aria-label="Edit item details" onClick={() => setEditItemOpen(true)}>Edit</Button>
        <DetailList aria-label="Inventory details" items={[
          { label: "Purchase cost", value: purchaseCostIsKnown(item) ? formatMoney(item.purchaseCost) : "Unknown" },
          { label: "Condition", value: conditionLabels[item.condition] },
          { label: "Category", value: item.categoryID || "Not set" },
          { label: "Location", value: item.stockLocation || <Button variant="ghost" className="inventory-fact-action" onClick={() => setEditDetailField("stockLocation")}>Set location</Button> },
          { label: "Time in stock", value: formatStockAge(item) },
          { label: "SKU", value: <span className="inventory-sku"><span>{item.sku}</span><Button variant="ghost" size="icon" aria-label="Copy SKU" onClick={() => void copySKU()}><Copy size={16} /></Button></span> }
        ]} />
      </Disclosure>
      <Disclosure className="inventory-detail-disclosure" label="Notes & research">
        {itemDescription && <p className="inventory-item-description">{itemDescription}</p>}
        {item.notes && <p className="inventory-item-description">{item.notes}</p>}
        {!item.notes && !itemDescription && <p className="inventory-notes-empty">No notes added.</p>}
        <div className="inventory-notes-actions"><Button variant="ghost" className="inventory-fact-action" onClick={() => setEditDetailField("notes")}>{item.notes ? "Edit notes" : "Add notes"}</Button>
          {!item.research && canRecordSale && <Button variant="ghost" onClick={onOpenResearch}><Search size={16} />Research item</Button>}</div>
        {item.research && <ItemResearchSummary item={item} onOpen={onOpenResearch} showAction={canRecordSale} />}
      </Disclosure>
      {Boolean(item.lifecycleEvents?.length) && <Disclosure className="inventory-detail-disclosure" label="Imported history" meta={item.lifecycleEvents!.length}>
        <p>Events from your spreadsheet. Current stock uses the opening count you reviewed.</p>
        <dl className="inventory-fact-list">{[...item.lifecycleEvents!].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)).map((event) => <div key={event.id}><dt>{new Date(event.occurredAt).toLocaleDateString("en-GB")} · {event.source}</dt><dd>{migrationStatuses.find(([kind]) => kind === event.kind)?.[1]} · {event.quantity} {event.quantity === 1 ? "unit" : "units"}{event.amount ? ` · ${formatMoney(event.amount)} refunded in total` : ""}{event.notes && <p>{event.notes}</p>}</dd></div>)}</dl>
      </Disclosure>}
      </div>
      <p className="sr-only" id={detailPhotoUploadHelpID}>Add product photos from this device. You can save up to {MAX_ITEM_PHOTOS} photos for this item.</p>
      <FileInput ref={photoInputRef} className="sr-only" tabIndex={-1} accept={PHOTO_FILE_ACCEPT} multiple aria-label={`Choose photos for ${item.title}`} onChange={(event) => void addPhotoFiles(event)} />
      {targetPriceOpen && <InventoryTargetPriceDialog item={item} onClose={() => setTargetPriceOpen(false)} />}
      {editItemOpen && <EditInventoryItemDialog item={item} onClose={() => setEditItemOpen(false)} />}
      {editDetailField && <InventoryDetailFieldDialog item={item} field={editDetailField} onClose={() => setEditDetailField(null)} />}
      {stockHistoryOpen && <Dialog open onOpenChange={(open) => !open && setStockHistoryOpen(false)}><DialogContent><DialogHeader><DialogTitle>Stock history</DialogTitle><DialogDescription>{item.title}</DialogDescription></DialogHeader><DialogBody><ItemStockControls item={item} onAdjust={() => { setStockHistoryOpen(false); onAdjustStock(); }} />{!item.stockHistory?.length && <p>No stock corrections yet.</p>}</DialogBody><DialogFooter><Button onClick={() => setStockHistoryOpen(false)}>Done</Button></DialogFooter></DialogContent></Dialog>}
      {photoReviewID && photos.length > 0 ? (
        <ItemPhotoReviewDialog
          item={item}
          photos={photos}
          initialPhotoID={photoReviewID}
          onAddPhotos={() => {
            setPhotoReviewID(null);
            window.setTimeout(() => photoInputRef.current?.click(), 0);
          }}
          onClose={() => setPhotoReviewID(null)}
          onReturnFocus={restorePhotoActionFocus}
        />
      ) : null}
    </Panel>
  );
}

function InventoryDetailFieldDialog({ item, field, onClose }: { item: InventoryItem; field: "notes" | "stockLocation"; onClose: () => void }) {
  const store = useReOS(), toast = useToast();
  const [value, setValue] = useState(item[field] ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [discard, setDiscard] = useState(false);
  const lock = useRef(false);
  const original = useRef({ value: item[field] ?? "", updatedAt: item.updatedAt });
  const notes = field === "notes";
  function close() { if (!saving) value !== original.current.value ? setDiscard(true) : onClose(); }
  async function save(event: FormEvent) {
    event.preventDefault();
    if (lock.current) return;
    lock.current = true; setSaving(true); setError(undefined);
    try {
      await store.updateInventoryItem(item.id, { [field]: value.trim() || null, expectedUpdatedAt: original.current.updatedAt });
      toast.notify({ title: notes ? "Notes saved" : "Location saved", tone: "success" }); onClose();
    } catch (caught) { setError(userSafeErrorMessage(caught, "Couldn’t save this change. Try again.")); }
    finally { lock.current = false; setSaving(false); }
  }
  return <Dialog open onOpenChange={(open) => !open && close()}><DialogContent presentation="compact" showClose={!saving}>
    <DialogHeader><DialogTitle>{notes ? "Item notes" : "Stock location"}</DialogTitle><DialogDescription>{notes ? "Private notes, kept separately from your listings." : item.title}</DialogDescription></DialogHeader>
    <FormLayout onSubmit={(event) => void save(event)}><DialogBody>
      <Field label={notes ? "Notes" : "Location"}>{notes ? <Textarea autoFocus rows={6} value={value} disabled={saving} onChange={(event) => setValue(event.target.value)} /> : <Input autoFocus value={value} disabled={saving} onChange={(event) => setValue(event.target.value)} />}</Field>
      {error && <Alert tone="danger">{error}</Alert>}
      {discard && <InlineConfirm title="Discard changes?" description="Your unsaved changes will be lost." actions={<><Button variant="ghost" type="button" onClick={() => setDiscard(false)}>Keep editing</Button><Button variant="danger" type="button" onClick={onClose}>Discard changes</Button></>} />}
    </DialogBody><DialogFooter><Button variant="secondary" type="button" disabled={saving} onClick={close}>Cancel</Button><Button variant="primary" type="submit" disabled={saving} loading={saving} loadingLabel="Saving">{notes ? "Save notes" : "Save location"}</Button></DialogFooter></FormLayout>
  </DialogContent></Dialog>;
}

function ItemPhotoReviewDialog({
  item,
  photos,
  initialPhotoID,
  onAddPhotos,
  onClose,
  onReturnFocus
}: {
  item: InventoryItem;
  photos: ItemPhoto[];
  initialPhotoID: string;
  onAddPhotos: () => void;
  onClose: () => void;
  onReturnFocus: () => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const editButtonRef = useRef<HTMLButtonElement | null>(null);
  const restoreEditFocusRef = useRef(false);
  const returnToPhotoActionRef = useRef(false);
  const [selectedPhotoID, setSelectedPhotoID] = useState(initialPhotoID);
  const [pendingRemoval, setPendingRemoval] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [photoProgress, setPhotoProgress, reportPhotoFailure] = usePhotoUploadProgress(`${item.title}: replacement photo`, `replace:${item.id}`);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<{ tone: "danger" | "warning"; text: string } | null>(null);
  const selectedPhoto = photos.find((photo) => photo.id === selectedPhotoID) ?? photos[0];
  const selectedIndex = selectedPhoto ? photos.findIndex((photo) => photo.id === selectedPhoto.id) : -1;
  const canChangePhoto = !isRemoving && !isReplacing && !isEditing;

  useEffect(() => {
    if (photos.length === 0) {
      onClose();
      return;
    }
    if (!photos.some((photo) => photo.id === selectedPhotoID)) {
      setSelectedPhotoID(photos[0].id);
    }
  }, [onClose, photos, selectedPhotoID]);

  useEffect(() => {
    if (isEditing || !restoreEditFocusRef.current) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      editButtonRef.current?.focus({ preventScroll: true });
      restoreEditFocusRef.current = false;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isEditing]);

  function closeEditor() {
    restoreEditFocusRef.current = true;
    setIsEditing(false);
  }

  function selectRelativePhoto(offset: number) {
    if (selectedIndex < 0 || photos.length < 2) {
      return;
    }
    const nextIndex = (selectedIndex + offset + photos.length) % photos.length;
    setPendingRemoval(false);
    setIsEditing(false);
    setMessage(null);
    setSelectedPhotoID(photos[nextIndex].id);
  }

  async function removeSelectedPhoto() {
    if (!selectedPhoto) {
      return;
    }
    const remainingPhotos = photos.filter((photo) => photo.id !== selectedPhoto.id);
    const nextPhoto = remainingPhotos[Math.min(selectedIndex, remainingPhotos.length - 1)];
    setIsRemoving(true);
    setMessage(null);
    try {
      await store.removeItemPhoto(selectedPhoto.id);
      toast.notify({ title: "Photo removed", tone: "success" });
      setPendingRemoval(false);
      if (nextPhoto) {
        setSelectedPhotoID(nextPhoto.id);
      } else {
        returnToPhotoActionRef.current = true;
        onClose();
        onReturnFocus();
      }
    } catch (caught) {
      setMessage({ tone: "danger", text: userSafeErrorMessage(caught, "Unable to remove this photo.") });
    } finally {
      setIsRemoving(false);
    }
  }

  async function replaceSelectedPhoto(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedPhoto) {
      return;
    }
    const replacedPhotoID = selectedPhoto.id;
    setIsReplacing(true);
    setMessage(null);
    try {
      const result = await readPhotoDraftBatch([file], setPhotoProgress);
      if (result.drafts.length === 0) {
        setMessage({
          tone: "danger",
          text: result.errors[0] ?? "This photo could not be prepared."
        });
        return;
      }
      await store.updateItemPhoto(replacedPhotoID, toPhotoInput(result.drafts[0]), setPhotoProgress);
      toast.notify({
        title: "Photo replaced",
        description: result.convertedHeicCount > 0 ? "The HEIC photo was converted and saved." : undefined,
        tone: "success"
      });
    } catch (caught) {
      reportPhotoFailure(photoUploadErrorMessage(caught, "Unable to replace this photo."));
      setMessage({ tone: "danger", text: photoUploadErrorMessage(caught, "Unable to replace this photo.") });
    } finally {
      setIsReplacing(false);
      setPhotoProgress(null);
    }
  }

  if (!selectedPhoto) {
    return null;
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (open) return;
        if (isEditing) {
          closeEditor();
        } else if (canChangePhoto) {
          returnToPhotoActionRef.current = true;
          onClose();
        }
      }}
    >
      <DialogContent
        className="inventory-photo-review-dialog"
        presentation="media"
        leadingClose={isEditing}
        showClose={!isEditing}
        onCloseAutoFocus={(event) => {
          if (!returnToPhotoActionRef.current) {
            return;
          }
          event.preventDefault();
          onReturnFocus();
        }}
      >
        <DialogHeader>
          {isEditing ? (
            <Button variant="secondary" size="icon" type="button" onClick={closeEditor} aria-label="Back to photo review">
              <ArrowLeft size={20} />
            </Button>
          ) : null}
          <DialogTitle>{isEditing ? "Edit photo" : "Manage photos"}</DialogTitle>
          <DialogDescription>{item.title}</DialogDescription>
        </DialogHeader>
        {isEditing ? (
          <SavedPhotoEditor
            photo={selectedPhoto}
            itemTitle={item.title}
            onCancel={closeEditor}
            onSaved={() => {
              setMessage(null);
              closeEditor();
            }}
          />
        ) : (
          <>
        <DialogBody className="inventory-photo-review-body">
          <ItemChannelPhotosLink itemID={item.id} />
          <PhotoUploadStatus progress={photoProgress} />
          {message ? <Alert tone={message.tone}>{message.text}</Alert> : null}
          <div
            className="inventory-photo-review-stage"
            role="group"
            aria-label={`Product photo ${selectedIndex + 1} of ${photos.length}`}
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "ArrowLeft") {
                event.preventDefault();
                selectRelativePhoto(-1);
              } else if (event.key === "ArrowRight") {
                event.preventDefault();
                selectRelativePhoto(1);
              }
            }}
          >
            <img
              src={selectedPhoto.url}
              alt={`${item.title}, product photo ${selectedIndex + 1} of ${photos.length}`}
              style={photoStyle(selectedPhoto.treatment, "fit")}
            />
            {photos.length > 1 ? (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  className="inventory-photo-review-previous"
                  type="button"
                  aria-label="Previous photo"
                  onClick={() => selectRelativePhoto(-1)}
                >
                  <ChevronLeft size={20} />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  className="inventory-photo-review-next"
                  type="button"
                  aria-label="Next photo"
                  onClick={() => selectRelativePhoto(1)}
                >
                  <ChevronRight size={20} />
                </Button>
              </>
            ) : null}
          </div>
          <div className="inventory-photo-review-summary">
            <div>
              <strong>Photo {selectedIndex + 1} of {photos.length}</strong>
              <span>
                {selectedIndex === 0 ? "Cover photo" : researchPhotoTypeLabels[selectedPhoto.type]}
                {selectedPhoto.width > 0 && selectedPhoto.height > 0 ? ` · ${selectedPhoto.width} × ${selectedPhoto.height}px` : ""}
              </span>
            </div>
            <div className="inventory-photo-review-summary-actions">
              <Button
                ref={editButtonRef}
                variant="secondary"
                size="compact"
                type="button"
                onClick={() => {
                  setPendingRemoval(false);
                  setMessage(null);
                  setIsEditing(true);
                }}
                disabled={!canChangePhoto}
              >
                <Pencil size={16} /> Edit
              </Button>
              <Button
                variant="danger"
                size="compact"
                type="button"
                onClick={() => setPendingRemoval(true)}
                disabled={!canChangePhoto || pendingRemoval}
              >
                <Trash2 size={16} /> Remove
              </Button>
            </div>
          </div>
          {pendingRemoval ? (
            <InlineConfirm
              title="Remove this photo?"
              description={photos.length === 1
                ? "The item will have no product photos, and draft listings will need a new photo before publishing."
                : "It will be removed from the item and its draft listing previews."}
              actions={
                <>
                  <Button variant="ghost" type="button" onClick={() => setPendingRemoval(false)} disabled={isRemoving}>
                    Keep photo
                  </Button>
                  <Button variant="danger" type="button" onClick={() => void removeSelectedPhoto()} disabled={isRemoving} loading={isRemoving} loadingLabel="Removing photo">
                    {isRemoving ? "Removing photo" : "Remove photo"}
                  </Button>
                </>
              }
            />
          ) : null}
          {photos.length > 1 ? (
            <div className="inventory-photo-review-thumbnails" role="list" aria-label="Saved product photos">
              {photos.map((photo, index) => (
                <div role="listitem" key={photo.id}>
                  <ThumbnailButton
                    type="button"
                    label={`Show product photo ${index + 1}${index === 0 ? ", cover photo" : ""}`}
                    imageSrc={photo.thumbnailURL || photo.url}
                    imageStyle={photoStyle(photo.treatment)}
                    selected={photo.id === selectedPhoto.id}
                    onClick={() => {
                      setPendingRemoval(false);
                      setMessage(null);
                      setSelectedPhotoID(photo.id);
                    }}
                  />
                </div>
              ))}
            </div>
          ) : null}
          <FileInput
            ref={replaceInputRef}
            className="sr-only"
            accept={PHOTO_FILE_ACCEPT}
            aria-label={`Choose a replacement for product photo ${selectedIndex + 1}`}
            onChange={(event) => void replaceSelectedPhoto(event)}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            type="button"
            onClick={() => replaceInputRef.current?.click()}
            disabled={!canChangePhoto}
            loading={isReplacing} loadingLabel="Replacing photo">
            <Upload size={17} /> {isReplacing ? "Replacing photo" : "Replace photo"}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={onAddPhotos}
            disabled={!canChangePhoto || photos.length >= MAX_ITEM_PHOTOS}
          >
            <ImagePlus size={17} /> Add photos
          </Button>
        </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SavedPhotoEditor({
  photo,
  itemTitle,
  onCancel,
  onSaved
}: {
  photo: ItemPhoto;
  itemTitle: string;
  onCancel: () => void;
  onSaved: (photo: ItemPhoto) => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const cropButtonRef = useRef<HTMLButtonElement | null>(null);
  const backgroundButtonRef = useRef<HTMLButtonElement | null>(null);
  const persistedDraft = useMemo(() => photoDraftFromItemPhoto(photo), [photo]);
  const [photoProgress, setPhotoProgress, reportPhotoFailure] = usePhotoUploadProgress(`${itemTitle}: edited photo`, `edit:${photo.id}`);
  const [draft, setDraft] = useState(persistedDraft);
  const [mode, setMode] = useState<"adjust" | "crop" | "background">("adjust");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasChanges =
    JSON.stringify(toPhotoInput(draft)) !== JSON.stringify(toPhotoInput(persistedDraft)) ||
    photoTreatmentNeedsMaterialization(draft.treatment);

  useEffect(() => {
    setDraft(persistedDraft);
    setMode("adjust");
    setError(null);
  }, [persistedDraft]);

  function updateTreatment(patch: Partial<PhotoTreatment>) {
    setDraft((current) => ({
      ...current,
      type: "edited",
      treatment: { ...current.treatment, ...patch }
    }));
  }

  function applyCrop(result: CropApplyResult) {
    setDraft((current) => croppedPhotoDraft(current, result));
    closeNestedTool(cropButtonRef);
  }

  function applyBackground(result: AiPhotoOutput, label: string) {
    setDraft((current) => {
      const variant = draftFromAiResult(result, current, label);
      return {
        ...variant,
        id: current.id,
        originalUrl: current.originalUrl
      };
    });
  }

  function closeNestedTool(triggerRef: { current: HTMLButtonElement | null }) {
    setMode("adjust");
    window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  }

  async function save() {
    if (!hasChanges) {
      onCancel();
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      const materializedDraft = await materializePhotoDraft(draft);
      const updatedPhoto = await store.updateItemPhoto(photo.id, toPhotoInput(materializedDraft), setPhotoProgress);
      toast.notify({ title: "Photo updated", tone: "success" });
      onSaved(updatedPhoto);
    } catch (caught) {
      reportPhotoFailure(photoUploadErrorMessage(caught, "Unable to update this photo."));
      setError(photoUploadErrorMessage(caught, "Unable to update this photo."));
    } finally {
      setPhotoProgress(null);
      setIsSaving(false);
    }
  }

  return (
    <>
      <DialogBody className="saved-photo-editor-body">
        <PhotoUploadStatus progress={photoProgress} />
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {mode === "crop" ? (
          <CropEditor photo={draft} onApply={applyCrop} onClose={() => closeNestedTool(cropButtonRef)} />
        ) : mode === "background" ? (
          <AIPhotoToolsPanel
            photo={draft}
            onApply={applyBackground}
            onClose={() => closeNestedTool(backgroundButtonRef)}
          />
        ) : (
          <div className="saved-photo-editor-layout">
            <div className="saved-photo-editor-preview">
              <img src={draft.url} alt={`Editing ${itemTitle}`} style={photoStyle(draft.treatment, "fit")} />
            </div>
            <div className="saved-photo-editor-controls">
              <div className="saved-photo-editor-tools" role="group" aria-label="Photo editing tools">
                <Button ref={cropButtonRef} variant="secondary" type="button" onClick={() => setMode("crop")}>
                  <CropIcon size={17} /> Crop
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => updateTreatment({ rotation: (draft.treatment.rotation + 90) % 360 })}
                >
                  <RotateCw size={17} /> Rotate
                </Button>
                <Button variant="secondary" type="button" onClick={() => updateTreatment({ brightness: 108, contrast: 106 })}>
                  <Wand2 size={17} /> Enhance
                </Button>
                <Button ref={backgroundButtonRef} variant="secondary" type="button" onClick={() => setMode("background")}>
                  <Sparkles size={17} /> Background
                </Button>
              </div>
              <div className="saved-photo-editor-sliders">
                <label>
                  <span>Brightness</span>
                  <output>{draft.treatment.brightness}%</output>
                  <RangeInput
                    min={80}
                    max={125}
                    aria-label="Brightness"
                    value={draft.treatment.brightness}
                    onChange={(event) => updateTreatment({ brightness: Number(event.target.value) })}
                  />
                </label>
                <label>
                  <span>Contrast</span>
                  <output>{draft.treatment.contrast}%</output>
                  <RangeInput
                    min={80}
                    max={125}
                    aria-label="Contrast"
                    value={draft.treatment.contrast}
                    onChange={(event) => updateTreatment({ contrast: Number(event.target.value) })}
                  />
                </label>
              </div>
              <Button
                variant="ghost"
                size="compact"
                type="button"
                onClick={() => {
                  setDraft(persistedDraft);
                  setError(null);
                }}
                disabled={!hasChanges}
              >
                <Undo2 size={16} /> Reset changes
              </Button>
            </div>
          </div>
        )}
      </DialogBody>
      {mode === "adjust" ? (
        <DialogFooter>
          <Button variant="secondary" type="button" onClick={onCancel} disabled={isSaving}>Cancel</Button>
          <Button variant="primary" type="button" onClick={() => void save()} disabled={isSaving || !hasChanges} loading={isSaving} loadingLabel="Saving">
            {isSaving ? "Saving" : "Save changes"}
          </Button>
        </DialogFooter>
      ) : null}
    </>
  );
}

const researchConfidenceLabels: Record<ResearchConfidence, string> = {
  low: "Low confidence",
  medium: "Medium confidence",
  high: "High confidence"
};

const researchEvidenceTypeLabels: Record<ResearchEvidenceType, string> = {
  active_listing: "Active listing",
  sold_comparable: "Sold comparable",
  reference: "Reference"
};

const researchSearchMethodLabels = {
  visual: "Visual",
  web: "Web",
  marketplace: "Marketplace",
  internal: "Listwork"
} as const;

const researchDepthOptions: Array<{ key: ResearchDepth; label: string }> = [
  { key: "quick", label: "Quick" },
  { key: "standard", label: "Standard" },
  { key: "deep", label: "Thorough" }
];

const researchDepthDescriptions: Record<ResearchDepth, string> = {
  quick: "Checks the strongest visual and web leads.",
  standard: "Compares visual leads with multiple web sources.",
  deep: "Expands the search to specialist references, alternatives, and contradictions."
};

const researchFocusOptions: Array<{ key: ResearchFocus; label: string; description: string; recommended?: boolean }> = [
  { key: "identify", label: "Identify the item", description: "Resolve maker, model, period, material and close alternatives.", recommended: true },
  { key: "verify", label: "Verify an attribution", description: "Test a signature, mark, attribution or seller lead." },
  { key: "complete", label: "Complete item research", description: "Identify, verify, value and prepare specialist positioning." },
  { key: "price_position", label: "Price and position it", description: "Find relevant comparables and build a defensible price." },
  { key: "custom", label: "Custom research", description: "Ask a specific question outside these workflows." }
];

const researchFocusLabels: Record<ResearchFocus, string> = {
  complete: "Complete research",
  identify: "Identification",
  verify: "Attribution check",
  price_position: "Price and position",
  custom: "Custom research"
};

function researchQuestionForDisplay(
  itemTitle: string,
  focus: ResearchFocus | undefined,
  customPriority: string | undefined
) {
  if (customPriority?.trim()) return customPriority.trim();
  if (focus === "verify") return `Does the available evidence support the proposed attribution for ${itemTitle}?`;
  if (focus === "complete") return `What is ${itemTitle}, what is it worth, and how should it be positioned?`;
  if (focus === "price_position") return `Which comparable items support the strongest price and position for ${itemTitle}?`;
  if (focus === "custom") return `What should be established about ${itemTitle}?`;
  return `What is ${itemTitle}, as precisely as the available evidence allows?`;
}

const MAX_ITEM_RESEARCH_PHOTOS = 8;
const researchPhotoTypeLabels: Record<PhotoType, string> = {
  original: "Original",
  cropped: "Detail or crop",
  edited: "Edited",
  background_removed: "Background removed"
};

function researchPhotoDisplayLabel(photos: ItemPhoto[], photo: ItemPhoto, index: number) {
  const typeIndex = photos.slice(0, index + 1).filter((candidate) => candidate.type === photo.type).length;
  if (photo.type === "original") {
    return typeIndex === 1 ? "Primary view" : `Additional view ${typeIndex - 1}`;
  }
  if (photo.type === "cropped") {
    return `Detail crop ${typeIndex}`;
  }
  if (photo.type === "background_removed") {
    return `Cutout ${typeIndex}`;
  }
  return `Edited view ${typeIndex}`;
}

function recommendedResearchPhotoIDs(photos: ItemPhoto[]) {
  const typePriority: Record<PhotoType, number> = {
    original: 0,
    cropped: 1,
    edited: 2,
    background_removed: 3
  };
  return [...photos]
    .sort((left, right) => typePriority[left.type] - typePriority[right.type] || left.sortOrder - right.sortOrder)
    .slice(0, MAX_ITEM_RESEARCH_PHOTOS)
    .map((photo) => photo.id);
}

function initialResearchPhotoIDs(photos: ItemPhoto[], persistedPhotoIDs: string[] | undefined) {
  const available = new Set(photos.map((photo) => photo.id));
  const persisted = persistedPhotoIDs?.filter((photoID) => available.has(photoID)).slice(0, MAX_ITEM_RESEARCH_PHOTOS) ?? [];
  return persisted.length > 0 ? persisted : recommendedResearchPhotoIDs(photos);
}

function addPreferredResearchPhotoIDs(current: string[], added: string[]) {
  return Array.from(new Set([...added, ...current])).slice(0, MAX_ITEM_RESEARCH_PHOTOS);
}

const researchSourceCategoryLabels = {
  primary: "Primary source",
  institutional: "Institutional",
  specialist: "Specialist",
  community: "Community",
  marketplace: "Marketplace",
  unverified: "Unverified"
} as const;

const researchRelationshipLabels = {
  supports: "Supports",
  contradicts: "Contradicts",
  context: "Context"
} as const;

function researchSourceDisplayName(record: ItemResearchEvidence) {
  const source = record.sourceName.toLowerCase();
  if (source.includes("ebay")) return "eBay";
  if (source.includes("etsy")) return "Etsy";
  if (source.includes("ehive")) return "eHive";
  if (source.includes("collections.vam")) return "V&A";
  return record.sourceName;
}

function researchEvidenceDisplayTitle(record: ItemResearchEvidence, identity: string | undefined) {
  if (!/^(?:visual|image) matches?$/i.test(record.title.trim())) return record.title;
  return identity ? `${researchSourceDisplayName(record)} image result` : `Image result from ${researchSourceDisplayName(record)}`;
}

function researchEvidenceExplanation(record: ItemResearchEvidence) {
  if (record.excerpt?.trim()) return record.excerpt;
  if (record.notes?.trim()) return record.notes;
  if (record.condition?.trim()) return `Condition recorded as ${record.condition.trim()}.`;
  return undefined;
}

function researchEvidenceImageDisclosure(record: ItemResearchEvidence, presentSyntheticAsSource = false) {
  if (record.imageProvenance === "synthetic_demo") {
    return presentSyntheticAsSource
      ? undefined
      : "Representative image for this demo; not source evidence.";
  }
  if (record.imageURL && record.imageProvenance !== "source") {
    return "Image provenance was not recorded. Verify it against the linked source before relying on it.";
  }
  return undefined;
}

function researchEvidenceSourceActionLabel(record: ItemResearchEvidence, presentSyntheticAsSource = false) {
  if (record.imageProvenance === "source" || (presentSyntheticAsSource && record.imageProvenance === "synthetic_demo")) {
    return "Open original source";
  }
  if (record.imageProvenance === "synthetic_demo") return "Open collection record";
  return "Open source record";
}

function researchEvidenceImageEyebrow(record: ItemResearchEvidence, presentSyntheticAsSource = false) {
  const source = researchSourceDisplayName(record);
  if (record.imageProvenance === "source" || (presentSyntheticAsSource && record.imageProvenance === "synthetic_demo")) {
    return `${source} source image`;
  }
  if (record.imageProvenance === "synthetic_demo") return "Representative demo image";
  return `${source} image · provenance unknown`;
}

function researchSourceDomain(value: string | undefined) {
  if (!value) return undefined;
  try {
    return new URL(value).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
}

function researchEvidenceResultID(record: ItemResearchEvidence) {
  return record.id.match(/(?:^|_)(retrieval_\d+)$/)?.[1];
}

function researchRationaleForDisplay(value: string, evidence: ItemResearchEvidence[]) {
  const byResultID = new Map(evidence.flatMap((record) => {
    const resultID = researchEvidenceResultID(record);
    return resultID ? [[resultID, record] as const] : [];
  }));
  return value.replace(/\bretrieval_\d+\b/g, (resultID) => {
    const record = byResultID.get(resultID);
    if (!record) return "a retrieved result";
    const source = researchSourceDisplayName(record);
    if (record.imageURL && record.decision === "included") return `the selected ${source} visual match`;
    if (record.imageURL) return `a supporting ${source} image`;
    return `the ${source} source`;
  });
}

function researchClaimEvidenceState(verdict: ItemResearchClaim["verdict"]) {
  if (verdict === "supported") return "Supported by evidence";
  if (verdict === "contested") return "Conflicting evidence";
  return "Not yet verified";
}

function researchEvidenceMentionedByClaims(research: ItemResearch) {
  const rationale = research.run?.claims.map((claim) => claim.rationale).join(" ") ?? "";
  const mentioned = new Set(rationale.match(/\bretrieval_\d+\b/g) ?? []);
  return research.evidence.filter((record) => {
    const resultID = researchEvidenceResultID(record);
    return Boolean(resultID && mentioned.has(resultID));
  });
}

function uniqueResearchEvidence(records: ItemResearchEvidence[]) {
  return records.filter((record, index) => records.findIndex((candidate) => candidate.id === record.id) === index);
}

function researchStatusLabel(research: ItemResearch | undefined) {
  if (!research) return "Not started";
  if (research.status === "refined") return "Applied";
  if (research.provider.id === "local_research_preview") return "Ready to research";
  if (research.run?.status === "failed") return "Research failed";
  if (research.run?.status === "needs_input") return "Needs input";
  if (research.run?.status === "queued" || research.run?.status === "researching" || research.run?.status === "verifying") return "Researching";
  if (research.run?.status === "ready") return "Ready to review";
  if (research.status === "ready_to_refine") return "Ready for listing";
  return "In review";
}

function researchStatusTone(research: ItemResearch | undefined): ComponentProps<typeof Badge>["tone"] {
  if (!research) return "neutral";
  if (research.status === "refined") return "success";
  if (research.provider.id === "local_research_preview") return "info";
  if (research.run?.status === "failed") return "warning";
  if (research.run?.status === "needs_input") return "warning";
  if (research.run?.status === "queued" || research.run?.status === "researching" || research.run?.status === "verifying") return "info";
  if (research.status === "ready_to_refine") return "info";
  return "warning";
}

function ItemResearchSummary({
  item,
  onOpen,
  showAction
}: {
  item: InventoryItem;
  onOpen: () => void;
  showAction: boolean;
}) {
  const research = item.research;
  const includedEvidence = includedResearchEvidence(research);
  const identityClaim = primaryIdentityResearchClaim(research);
  const identity = research?.run?.focus !== "price_position" && (!identityClaim || identityClaim.sellerDecision === "accepted")
    ? researchIdentityLabel(research)
    : undefined;
  const reviewCandidate = sellerReviewCandidateIdentification(research);
  const targetPrice = research?.refinement.targetPrice;
  const minimumPrice = research?.refinement.minimumPrice;
  const evidenceTypeSummary = Array.from(new Set(includedEvidence.map((record) => researchEvidenceTypeLabels[record.type]))).join(" · ");
  const positioning = researchProse(research?.refinement.positioning);
  const summaryItems: DetailListItem[] = [
    identity
      ? {
          label: reviewCandidate && research?.run?.status === "needs_input" ? "Candidate" : "Identification",
          value: identity,
          description: `${researchConfidenceLabels[research?.identification.confidence ?? "low"]}${reviewCandidate && research?.run?.status === "needs_input" ? " · Needs confirmation" : ""}`
        }
      : undefined,
    includedEvidence.length > 0
      ? {
          label: "Sources",
          value: `${includedEvidence.length} ${includedEvidence.length === 1 ? "source" : "sources"}`,
          description: evidenceTypeSummary
        }
      : undefined,
    targetPrice ? { label: "Suggested price", value: formatMoney(targetPrice) } : undefined,
    minimumPrice ? { label: "Minimum price", value: formatMoney(minimumPrice) } : undefined,
    positioning
      ? {
          label: "Selling advice",
          value: positioning,
          fullWidth: true
        }
      : undefined
  ].filter((item) => item !== undefined);

  return (
    <section className="item-research-section" aria-labelledby={`item-research-${item.id}`}>
      <div className="item-research-heading">
        <div>
          <SectionLabel id={`item-research-${item.id}`}>Research</SectionLabel>
          <Badge tone={researchStatusTone(research)}>{researchStatusLabel(research)}</Badge>
        </div>
        {showAction && (
          <Button variant="secondary" size="compact" type="button" onClick={onOpen}>
            <Search size={16} /> {research ? "Review" : "Research"}
          </Button>
        )}
      </div>
      {summaryItems.length > 0 && (
        <DetailList items={summaryItems} />
      )}
    </section>
  );
}

type ResearchEvidenceDraft = {
  type: ResearchEvidenceType;
  sourceCategory: ResearchSourceCategory;
  relationship: ResearchEvidenceRelationship;
  confidence: ResearchConfidence;
  included: boolean;
  sourceName: string;
  title: string;
  sourceURL: string;
  price: string;
  condition: string;
  observedDate: string;
  notes: string;
};

type ResearchEvidenceDraftErrors = Partial<Record<"sourceName" | "title" | "sourceURL" | "price", string>>;

const emptyResearchEvidenceDraft = (): ResearchEvidenceDraft => ({
  type: "active_listing",
  sourceCategory: "unverified",
  relationship: "context",
  confidence: "medium",
  included: true,
  sourceName: "",
  title: "",
  sourceURL: "",
  price: "",
  condition: "",
  observedDate: dateInputValue(new Date().toISOString()),
  notes: ""
});

function researchDraftForItem(item: InventoryItem): ItemResearch {
  if (item.research) {
    return structuredClone(item.research);
  }
  const now = new Date().toISOString();
  return {
    status: "in_review",
    identification: {
      maker: item.brand,
      material: item.material,
      confidence: "medium"
    },
    evidence: [],
    refinement: {
      title: item.title,
      description: item.description,
      targetPrice: item.targetPrice.amountMinor > 0 ? item.targetPrice : undefined,
      minimumPrice: item.minimumPrice
    },
    provider: manualResearchProvider,
    updatedAt: now
  };
}

function researchEditorStateJSON(research: ItemResearch) {
  return JSON.stringify({
    ...research,
    refinement: {
      ...research.refinement,
      targetPrice: research.refinement.targetPrice ? moneyInputValue(research.refinement.targetPrice) : "",
      minimumPrice: research.refinement.minimumPrice ? moneyInputValue(research.refinement.minimumPrice) : ""
    }
  });
}

function evidenceDraftFor(record: ItemResearchEvidence): ResearchEvidenceDraft {
  return {
    type: record.type,
    sourceCategory: record.sourceCategory ?? "unverified",
    relationship: record.relationship ?? "context",
    confidence: record.confidence ?? "medium",
    included: record.decision === "included",
    sourceName: record.sourceName,
    title: record.title,
    sourceURL: record.sourceURL ?? "",
    price: record.price ? moneyInputValue(record.price) : "",
    condition: record.condition ?? "",
    observedDate: dateInputValue(record.observedAt),
    notes: record.notes ?? ""
  };
}

type ResearchReviewImage = {
  id: string;
  group: "seller" | "source";
  imageURL: string;
  title: string;
  eyebrow: string;
  description?: string;
  imageDisclosure?: string;
  sourceURL?: string;
  sourceActionLabel?: string;
  metadata?: string;
};

function ResearchEvidenceThumbnail({
  imageDisclosure,
  label,
  imageURL,
  onPreview
}: {
  imageDisclosure?: string;
  label: string;
  imageURL: string;
  onPreview: (trigger: HTMLButtonElement) => void;
}) {
  const [previewAvailable, setPreviewAvailable] = useState(true);

  if (!previewAvailable) {
    return <span className="research-source-card-placeholder"><ImagePlus size={20} /><small>Preview unavailable</small></span>;
  }
  return (
    <button
      className="research-evidence-thumbnail"
      type="button"
      onClick={(event) => onPreview(event.currentTarget)}
      aria-haspopup="dialog"
      aria-label={`Enlarge evidence image: ${label}${imageDisclosure ? `. ${imageDisclosure}` : ""}`}
    >
      <img src={imageURL} alt="" onError={() => setPreviewAvailable(false)} />
      <span aria-hidden="true"><Maximize2 size={16} /></span>
    </button>
  );
}

function ResearchEvidenceSourceCard({
  cited,
  identity,
  label,
  onCancelRemove,
  onConfirmRemove,
  onEdit,
  onPreview,
  onRemove,
  presentSyntheticAsSource = false,
  record,
  removePending = false
}: {
  cited: boolean;
  identity: string | undefined;
  label: string;
  onCancelRemove?: () => void;
  onConfirmRemove?: () => void;
  onEdit?: (trigger: HTMLButtonElement) => void;
  onPreview?: (trigger: HTMLButtonElement) => void;
  onRemove?: () => void;
  presentSyntheticAsSource?: boolean;
  record: ItemResearchEvidence;
  removePending?: boolean;
}) {
  const removalConfirmRef = useRef<HTMLDivElement | null>(null);
  const removeButtonRef = useRef<HTMLButtonElement | null>(null);
  const title = researchEvidenceDisplayTitle(record, identity);
  const explanation = researchEvidenceExplanation(record);
  const imageDisclosure = researchEvidenceImageDisclosure(record, presentSyntheticAsSource);

  useEffect(() => {
    if (removePending) {
      removalConfirmRef.current?.focus();
    }
  }, [removePending]);

  return (
    <article className="research-source-card">
      {record.imageURL && onPreview
        ? (
          <ResearchEvidenceThumbnail
            imageDisclosure={imageDisclosure}
            label={title}
            imageURL={record.imageURL}
            onPreview={onPreview}
          />
        )
        : <span className="research-source-card-placeholder" aria-hidden="true"><Link2 size={20} /></span>}
      <div className="research-source-card-copy">
        <div className="research-source-card-meta">
          <span>{researchSourceDisplayName(record)} · {researchEvidenceTypeLabels[record.type]}</span>
          <Badge tone={!presentSyntheticAsSource && record.imageProvenance === "synthetic_demo" ? "neutral" : record.decision === "included" || cited ? "info" : "neutral"}>{label}</Badge>
        </div>
        <strong>{title}</strong>
        {imageDisclosure ? (
          <small className="research-image-provenance">{imageDisclosure}</small>
        ) : null}
        <small>{record.price ? `${formatMoney(record.price)} · ` : ""}Checked {formatCompactDate(record.observedAt)}</small>
        <div className="research-source-card-actions">
          {record.sourceURL
            ? <Button variant="ghost" size="compact" asChild><a href={record.sourceURL} target="_blank" rel="noreferrer">Open source <ExternalLink size={15} /><span className="sr-only">on {researchSourceDisplayName(record)} (opens in a new tab)</span></a></Button>
            : <span className="research-source-unavailable">Source page unavailable</span>}
          <Disclosure className="research-source-details" label="Details">
            <div className="research-source-details-body">
              {explanation ? <p>{explanation}</p> : null}
              <dl>
                <div>
                  <dt>Classification</dt>
                  <dd>{record.sourceCategory ? researchSourceCategoryLabels[record.sourceCategory] : "Seller source"}</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd>{record.relationship ? researchRelationshipLabels[record.relationship] : "Context"}</dd>
                </div>
                <div>
                  <dt>Confidence</dt>
                  <dd>{record.confidence ? researchConfidenceLabels[record.confidence] : "Not recorded"}</dd>
                </div>
              </dl>
            </div>
          </Disclosure>
          {(onEdit || onRemove) && (
            <span className="research-source-admin-actions">
              {onEdit && <Button variant="ghost" size="icon" type="button" aria-label={`Edit evidence: ${title}`} title="Edit evidence" onClick={(event) => onEdit(event.currentTarget)}><Pencil size={15} /></Button>}
              {onRemove && <Button ref={removeButtonRef} className="research-source-remove-action" variant="ghost" size="icon" type="button" aria-label={`Remove evidence: ${title}`} title="Remove evidence" onClick={onRemove}><Trash2 size={16} /></Button>}
            </span>
          )}
        </div>
        {removePending ? (
          <InlineConfirm
            ref={removalConfirmRef}
            title="Remove this source?"
            description="It will also be removed from every finding that cites it."
            actions={
              <>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    onCancelRemove?.();
                    window.requestAnimationFrame(() => removeButtonRef.current?.focus());
                  }}
                >
                  Keep source
                </Button>
                <Button variant="danger" type="button" onClick={onConfirmRemove}>
                  Remove source
                </Button>
              </>
            }
          />
        ) : null}
      </div>
    </article>
  );
}

function ResearchImagePreviewDialog({
  activeID,
  images,
  onActiveIDChange,
  onClose
}: {
  activeID: string | null;
  images: ResearchReviewImage[];
  onActiveIDChange: (imageID: string) => void;
  onClose: () => void;
}) {
  const activeIndex = images.findIndex((image) => image.id === activeID);
  const activeImage = activeIndex >= 0 ? images[activeIndex] : undefined;
  const [zoom, setZoom] = useState(1);
  const [previewAvailable, setPreviewAvailable] = useState(true);
  const [loadedImageID, setLoadedImageID] = useState<string | null>(null);

  useEffect(() => {
    setZoom(1);
    setPreviewAvailable(true);
  }, [activeID]);

  if (!activeImage) return null;
  const imageSequence = images.filter((image) => image.group === activeImage.group);
  const sequenceIndex = imageSequence.findIndex((image) => image.id === activeID);
  const hasSequence = imageSequence.length > 1;
  const previousImage = imageSequence[(sequenceIndex - 1 + imageSequence.length) % imageSequence.length];
  const nextImage = imageSequence[(sequenceIndex + 1) % imageSequence.length];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="research-image-preview-dialog" overlayClassName="research-image-preview-overlay" presentation="media" leadingClose showClose={false}>
        <DialogHeader className="research-image-preview-header">
          <Button variant="secondary" size="icon" type="button" onClick={onClose} aria-label="Close image preview" autoFocus><X size={20} /></Button>
          <DialogTitle>{activeImage.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {[activeImage.description, activeImage.imageDisclosure].filter(Boolean).join(" ")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="research-image-preview-body">
          <div className="research-image-preview-stage">
            {previewAvailable && loadedImageID !== activeImage.id && (
              <div className="research-image-preview-loading" role="status">
                <LoaderCircle size={24} />
                <span>Loading image</span>
              </div>
            )}
            {previewAvailable
              ? <img
                  className={[
                    loadedImageID === activeImage.id ? "is-loaded" : "",
                    zoom === 1 ? "is-fit" : "is-zoomed"
                  ].filter(Boolean).join(" ")}
                  src={activeImage.imageURL}
                  alt={`${activeImage.eyebrow}: ${activeImage.title}${activeImage.imageDisclosure ? `. ${activeImage.imageDisclosure}` : ""}`}
                  style={zoom === 1
                    ? undefined
                    : { width: `${zoom * 100}%`, height: "auto", maxWidth: "none", maxHeight: "none", objectFit: "contain" }}
                  onLoad={() => setLoadedImageID(activeImage.id)}
                  onError={() => setPreviewAvailable(false)}
                />
              : <div className="research-image-preview-unavailable"><ImagePlus size={28} /><strong>Preview unavailable</strong><p>The original source page may still contain the image.</p></div>}
            {hasSequence && (
              <>
                <Button variant="secondary" size="icon" className="research-image-preview-previous" type="button" onClick={() => previousImage && onActiveIDChange(previousImage.id)} aria-label="Previous image"><ChevronLeft size={20} /></Button>
                <Button variant="secondary" size="icon" className="research-image-preview-next" type="button" onClick={() => nextImage && onActiveIDChange(nextImage.id)} aria-label="Next image"><ChevronRight size={20} /></Button>
              </>
            )}
          </div>
          <aside className="research-image-preview-details">
            <div>
              <span>{activeImage.eyebrow}</span>
              <strong>{activeImage.title}</strong>
              {activeImage.description ? <p>{activeImage.description}</p> : null}
              {activeImage.imageDisclosure ? (
                <p className="research-image-provenance">{activeImage.imageDisclosure}</p>
              ) : null}
              {activeImage.metadata && <small>{activeImage.metadata}</small>}
            </div>
            <div className="research-image-preview-zoom" aria-label="Image zoom controls">
              <Button variant="secondary" size="icon" type="button" onClick={() => setZoom((current) => Math.max(1, current - 0.5))} disabled={zoom <= 1} aria-label="Zoom out"><ZoomOut size={17} /></Button>
              <span aria-live="polite">{Math.round(zoom * 100)}%</span>
              <Button variant="secondary" size="icon" type="button" onClick={() => setZoom((current) => Math.min(3, current + 0.5))} disabled={zoom >= 3} aria-label="Zoom in"><ZoomIn size={17} /></Button>
            </div>
            {activeImage.sourceURL
              ? <Button variant="primary" asChild><a href={activeImage.sourceURL} target="_blank" rel="noreferrer">{activeImage.sourceActionLabel ?? "Open original source"} <ExternalLink size={16} /></a></Button>
              : <p className="research-source-unavailable">This image has no original source page attached.</p>}
            {hasSequence && <small>{sequenceIndex + 1} of {imageSequence.length} {activeImage.group === "seller" ? "seller" : "source"} images</small>}
          </aside>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

type ResearchWorkspaceView = "brief" | "run" | "findings" | "listing";

function ItemResearchDialog({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const store = useReOS();
  const toast = useToast();
  const usesCompactResearchLayout = useMediaQuery("(max-width: 1039px)");
  const photos = store.photosFor(item.id);
  const initialResearch = useMemo(() => researchDraftForItem(item), [item]);
  const savedResearchJSONRef = useRef(researchEditorStateJSON(initialResearch));
  const [research, setResearch] = useState(initialResearch);
  const [activeView, setActiveView] = useState<ResearchWorkspaceView>(() => {
    const status = initialResearch.run?.status;
    return initialResearch.provider.id === "local_research_preview"
      ? "run"
      : status && ["queued", "researching", "verifying", "failed"].includes(status)
      ? "run"
      : initialResearch.run ? "findings" : "brief";
  });
  const [depth, setDepth] = useState<ResearchDepth>(initialResearch.run?.depth ?? "standard");
  const [focus, setFocus] = useState<ResearchFocus>(initialResearch.run?.focus ?? "identify");
  const [customPriority, setCustomPriority] = useState(initialResearch.run?.customPriority ?? "");
  const [sellerContext, setSellerContext] = useState(initialResearch.run?.sellerContext ?? item.notes ?? "");
  const [selectedPhotoIDs, setSelectedPhotoIDs] = useState(() => initialResearchPhotoIDs(photos, initialResearch.run?.photoIDs));
  const [photoPickerSelection, setPhotoPickerSelection] = useState(() => initialResearchPhotoIDs(photos, initialResearch.run?.photoIDs));
  const [photoPickerOpen, setPhotoPickerOpen] = useState(false);
  const [editingResearchPhotoID, setEditingResearchPhotoID] = useState<string | null>(null);
  const [photoPickerError, setPhotoPickerError] = useState<string | null>(null);
  const savedSetupJSONRef = useRef(JSON.stringify({
    depth: initialResearch.run?.depth ?? "standard",
    focus: initialResearch.run?.focus ?? "identify",
    customPriority: initialResearch.run?.customPriority ?? "",
    sellerContext: initialResearch.run?.sellerContext ?? item.notes ?? "",
    photoIDs: initialResearchPhotoIDs(photos, initialResearch.run?.photoIDs)
  }));
  const [targetPrice, setTargetPrice] = useState(() => initialResearch.refinement.targetPrice ? moneyInputValue(initialResearch.refinement.targetPrice) : "");
  const [minimumPrice, setMinimumPrice] = useState(() => initialResearch.refinement.minimumPrice ? moneyInputValue(initialResearch.refinement.minimumPrice) : "");
  const [evidenceDraft, setEvidenceDraft] = useState<ResearchEvidenceDraft>(emptyResearchEvidenceDraft);
  const [evidenceDraftErrors, setEvidenceDraftErrors] = useState<ResearchEvidenceDraftErrors>({});
  const [editingEvidenceID, setEditingEvidenceID] = useState<string | null>(null);
  const [evidenceEditorOpen, setEvidenceEditorOpen] = useState(false);
  const [sourceManagementOpen, setSourceManagementOpen] = useState(false);
  const [previewImageID, setPreviewImageID] = useState<string | null>(null);
  const [comparisonPhotoID, setComparisonPhotoID] = useState<string | null>(null);
  const [comparisonEvidenceID, setComparisonEvidenceID] = useState<string | null>(null);
  const [secondaryFindingsOpen, setSecondaryFindingsOpen] = useState(false);
  const [evidenceRemovalID, setEvidenceRemovalID] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savingDecisionClaimID, setSavingDecisionClaimID] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isAddingResearchPhoto, setIsAddingResearchPhoto] = useState(false);
  const [photoProgress, setPhotoProgress, reportPhotoFailure] = usePhotoUploadProgress(`${item.title}: research photos`, `research:${item.id}`);
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const [researchClock, setResearchClock] = useState(() => Date.now());
  const refreshResearchRef = useRef(store.refreshItemResearch);
  const researchDialogBodyRef = useRef<HTMLDivElement | null>(null);
  const researchHeaderControlRef = useRef<HTMLButtonElement | null>(null);
  const researchPhotoInputRef = useRef<HTMLInputElement | null>(null);
  const researchPhotoEditButtonRefs = useRef(new Map<string, HTMLButtonElement>());
  const researchPhotoEditReturnIDRef = useRef<string | null>(null);
  const evidenceEditorFirstFieldRef = useRef<HTMLButtonElement | null>(null);
  const evidenceSourceNameRef = useRef<HTMLInputElement | null>(null);
  const evidenceTitleRef = useRef<HTMLInputElement | null>(null);
  const evidenceSourceURLRef = useRef<HTMLInputElement | null>(null);
  const evidencePriceRef = useRef<HTMLInputElement | null>(null);
  const evidenceEditorReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const sourceManagementTriggerRef = useRef<HTMLButtonElement | null>(null);
  const sourceManagementFocusPendingRef = useRef(false);
  const sourceLibraryRef = useRef<HTMLDetailsElement | null>(null);
  const previewReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const pendingDecisionFocusRef = useRef<{ claimID: string; decision: ResearchSellerDecision } | null>(null);
  const previousResearchViewRef = useRef(activeView);
  const photoUploadPurposeRef = useRef<"setup" | "picker" | "followup">("setup");
  const currency = item.targetPrice.currency;
  const run = research.run;
  const selectedPhotos = selectedPhotoIDs.flatMap((photoID) => {
    const photo = photos.find((candidate) => candidate.id === photoID);
    return photo ? [photo] : [];
  });
  const isResearchRunning = run?.status === "queued" || run?.status === "researching" || run?.status === "verifying";
  const isResearchStalled = isResearchRunning && researchRunIsStale(run, researchClock);

  useLayoutEffect(() => {
    if (sourceManagementOpen || !sourceManagementFocusPendingRef.current) return;
    const trigger = sourceManagementTriggerRef.current;
    if (!trigger) return;
    sourceManagementFocusPendingRef.current = false;
    trigger.scrollIntoView({ block: "nearest" });
    trigger.focus({ preventScroll: true });
  }, [sourceManagementOpen]);

  const currentResearchJSON = JSON.stringify({
    ...research,
    refinement: { ...research.refinement, targetPrice, minimumPrice }
  });
  const researchHasChanges = currentResearchJSON !== savedResearchJSONRef.current;
  const setupHasChanges = JSON.stringify({ depth, focus, customPriority, sellerContext, photoIDs: selectedPhotoIDs }) !== savedSetupJSONRef.current;
  const hasChanges = researchHasChanges || setupHasChanges;

  useEffect(() => {
    refreshResearchRef.current = store.refreshItemResearch;
  }, [store.refreshItemResearch]);

  useEffect(() => {
    researchDialogBodyRef.current?.scrollTo({ top: 0, left: 0 });
    if (previousResearchViewRef.current !== activeView) {
      previousResearchViewRef.current = activeView;
      window.requestAnimationFrame(() => {
        document.getElementById(`research-tab-${activeView === "findings" ? "review" : activeView}`)?.focus({
          preventScroll: true
        });
      });
    }
  }, [activeView]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const returnPhotoID = researchPhotoEditReturnIDRef.current;
      if (!editingResearchPhotoID && photoPickerOpen && returnPhotoID) {
        const editButton = researchPhotoEditButtonRefs.current.get(returnPhotoID);
        if (editButton) {
          editButton.focus({ preventScroll: true });
          researchPhotoEditReturnIDRef.current = null;
          return;
        }
      }
      researchHeaderControlRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editingResearchPhotoID, photoPickerOpen]);

  useEffect(() => {
    const pendingFocus = pendingDecisionFocusRef.current;
    if (!pendingFocus) return;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`research-decision-${pendingFocus.claimID}`)
        ?? document.getElementById(pendingFocus.decision === "needs_evidence"
          ? "research-unresolved-heading"
          : "research-decision-recorded-heading");
      target?.focus({ preventScroll: true });
      pendingDecisionFocusRef.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [research]);

  useEffect(() => {
    if (!isResearchRunning) return;
    setResearchClock(Date.now());
    const intervalID = window.setInterval(() => setResearchClock(Date.now()), 15_000);
    return () => window.clearInterval(intervalID);
  }, [isResearchRunning]);

  useEffect(() => {
    if (!isResearchRunning || isResearchStalled) return;
    let cancelled = false;
    let requestInFlight = false;
    const refresh = async () => {
      if (requestInFlight) return;
      requestInFlight = true;
      try {
        const updatedItem = await refreshResearchRef.current(item.id);
        if (cancelled || !updatedItem.research) return;
        syncResearchState(updatedItem.research, true);
        if (updatedItem.research.run?.status === "failed") {
          setError(userSafeErrorMessage(new Error(updatedItem.research.run.error ?? ""), "Research stopped before it finished. Try again or review the brief."));
          setActiveView("run");
        } else {
          setError(null);
          const nextStatus = updatedItem.research.run?.status;
          if (nextStatus && !["queued", "researching", "verifying"].includes(nextStatus)) {
            setActiveView("findings");
          }
        }
      } catch (caught) {
        if (!cancelled) setError(userSafeErrorMessage(caught, "Unable to refresh item research."));
      } finally {
        requestInFlight = false;
      }
    };
    void refresh();
    const intervalID = window.setInterval(() => void refresh(), 2_000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalID);
    };
  }, [isResearchRunning, isResearchStalled, item.id]);

  function syncResearchState(nextResearch: ItemResearch, markSaved = false) {
    if (markSaved) {
      savedResearchJSONRef.current = researchEditorStateJSON(nextResearch);
      savedSetupJSONRef.current = JSON.stringify({ depth, focus, customPriority, sellerContext, photoIDs: selectedPhotoIDs });
    }
    setResearch(structuredClone(nextResearch));
    setTargetPrice(nextResearch.refinement.targetPrice ? moneyInputValue(nextResearch.refinement.targetPrice) : "");
    setMinimumPrice(nextResearch.refinement.minimumPrice ? moneyInputValue(nextResearch.refinement.minimumPrice) : "");
    if (nextResearch.run?.photoIDs) {
      const nextPhotoIDs = initialResearchPhotoIDs(photos, nextResearch.run.photoIDs);
      setSelectedPhotoIDs(nextPhotoIDs);
      setPhotoPickerSelection(nextPhotoIDs);
    }
  }

  function openReviewImage(imageID: string, trigger: HTMLButtonElement) {
    previewReturnFocusRef.current = trigger;
    setPreviewImageID(imageID);
  }

  function closeReviewImage() {
    setPreviewImageID(null);
    window.requestAnimationFrame(() => previewReturnFocusRef.current?.focus());
  }

  function updateIdentification(update: Partial<ItemResearch["identification"]>) {
    setResearch((current) => ({ ...current, identification: { ...current.identification, ...update } }));
  }

  function updateRefinement(update: Partial<ItemResearch["refinement"]>) {
    setResearch((current) => ({ ...current, refinement: { ...current.refinement, ...update } }));
  }

  async function updateClaimDecision(claimID: string, decision: ResearchSellerDecision) {
    if (isSaving) return;
    try {
      setIsSaving(true);
      setSavingDecisionClaimID(claimID);
      setError(null);
      const nextResearch = buildResearch(false, withResearchClaimDecision(research, claimID, decision));
      const updatedItem = await store.updateInventoryItem(item.id, { research: nextResearch });
      if (!updatedItem.research) throw new Error("The saved item did not include its research.");
      pendingDecisionFocusRef.current = { claimID, decision };
      savedResearchJSONRef.current = researchEditorStateJSON(updatedItem.research);
      setResearch(structuredClone(updatedItem.research));
      setTargetPrice(updatedItem.research.refinement.targetPrice ? moneyInputValue(updatedItem.research.refinement.targetPrice) : "");
      setMinimumPrice(updatedItem.research.refinement.minimumPrice ? moneyInputValue(updatedItem.research.refinement.minimumPrice) : "");
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to save this decision."));
    } finally {
      setIsSaving(false);
      setSavingDecisionClaimID(null);
    }
  }

  function updateQuestionAnswer(questionID: string, answer: string) {
    setResearch((current) => current.run ? {
      ...current,
      run: {
        ...current.run,
        questions: current.run.questions.map((question) => question.id === questionID
          ? { ...question, answer, answeredAt: answer.trim() ? new Date().toISOString() : undefined }
          : question)
      }
    } : current);
  }

  async function startResearch() {
    if (selectedPhotos.length === 0) {
      setError(photos.length === 0 ? "Add at least one item photo before starting research." : "Select at least one item photo for research.");
      return;
    }
    if (focus === "custom" && !customPriority.trim()) {
      setError("Describe what you want this research to find out.");
      return;
    }
    try {
      setIsStarting(true);
      setError(null);
      const answeredQuestions = research.run?.questions
        .filter((question) => question.answer?.trim())
        .map((question) => `${question.prompt}: ${question.answer?.trim()}`)
        .join("\n");
      const combinedSellerContext = researchSellerContextForNextRun(research, sellerContext, answeredQuestions);
      const updatedItem = await store.startItemResearch(item.id, {
        depth,
        focus,
        customPriority: customPriority.trim() || undefined,
        sellerContext: combinedSellerContext,
        photoIDs: selectedPhotos.map((photo) => photo.id)
      });
      if (!updatedItem.research) throw new Error("The research provider returned no findings.");
      syncResearchState(updatedItem.research, true);
      const nextStatus = updatedItem.research.run?.status;
      setActiveView(
        updatedItem.research.provider.id === "local_research_preview"
          ? "run"
          : nextStatus && ["queued", "researching", "verifying", "failed"].includes(nextStatus)
            ? "run"
            : "findings"
      );
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to start item research."));
    } finally {
      setIsStarting(false);
    }
  }

  function chooseResearchPhotos(purpose: "setup" | "picker" | "followup") {
    photoUploadPurposeRef.current = purpose;
    if (purpose === "picker") setPhotoPickerError(null);
    else setError(null);
    researchPhotoInputRef.current?.click();
  }

  function openResearchPhotoPicker() {
    setPhotoPickerSelection(selectedPhotoIDs);
    setPhotoPickerError(null);
    setEditingResearchPhotoID(null);
    setPhotoPickerOpen(true);
  }

  async function addResearchPhotos(event: ChangeEvent<HTMLInputElement>) {
    const purpose = photoUploadPurposeRef.current;
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selectedFiles.length === 0) return;
    const availableSlots = Math.max(0, MAX_ITEM_PHOTOS - photos.length);
    if (availableSlots === 0) {
      const message = `This item already has the maximum of ${MAX_ITEM_PHOTOS} photos.`;
      if (purpose === "picker") setPhotoPickerError(message);
      else setError(message);
      return;
    }
    const files = selectedFiles.slice(0, availableSlots);
    const skippedCount = selectedFiles.length - files.length;
    try {
      setIsAddingResearchPhoto(true);
      if (purpose === "picker") setPhotoPickerError(null);
      else setError(null);
      const result = await readPhotoDraftBatch(files, setPhotoProgress);
      const savedPhotos = result.drafts.length > 0 ? await store.addItemPhotos(item.id, result.drafts.map(toPhotoInput), setPhotoProgress) : [];
      if (savedPhotos.length > 0) {
        const addedPhotoIDs = savedPhotos.map((photo) => photo.id);
        if (purpose === "picker") {
          setPhotoPickerSelection((current) => addPreferredResearchPhotoIDs(current, addedPhotoIDs));
        } else {
          setSelectedPhotoIDs((current) => addPreferredResearchPhotoIDs(current, addedPhotoIDs));
        }
        if (purpose === "followup") {
          setResearch((current) => current.run ? {
            ...current,
            run: {
              ...current.run,
              questions: current.run.questions.map((question) => question.requestedInput === "photo" && !question.answer
                ? { ...question, answer: `${savedPhotos.length} additional ${savedPhotos.length === 1 ? "photo" : "photos"} added after this run.`, answeredAt: new Date().toISOString() }
                : question)
            }
          } : current);
          setActiveView("brief");
        }
        toast.notify({
          title: savedPhotos.length === 1 ? "Photo added" : "Photos added",
          description: photoDraftReadNotice(result.convertedHeicCount, skippedCount, result.drafts.length)?.message ?? (purpose === "picker" ? "New photos are selected in this research set." : purpose === "followup" ? "The new evidence is selected. Run research again when ready." : "New photos are selected for research."),
          tone: "success"
        });
      }
      if (result.errors.length > 0) {
        const message = photoDraftReadErrorMessage(result.errors);
        if (purpose === "picker") setPhotoPickerError(message);
        else setError(message);
      }
    } catch (caught) {
      const message = photoUploadErrorMessage(caught, "Unable to add these research photos.");
      reportPhotoFailure(message);
      if (purpose === "picker") setPhotoPickerError(message);
      else setError(message);
    } finally {
      setIsAddingResearchPhoto(false);
      setPhotoProgress(null);
    }
  }

  function requestClose() {
    if (isSaving || isStarting || isAddingResearchPhoto) return;
    if (hasChanges) {
      setClosePromptOpen(true);
      return;
    }
    onClose();
  }

  function isValidResearchEvidenceURL(value: string) {
    try {
      const url = new URL(value);
      return (url.protocol === "http:" || url.protocol === "https:") && Boolean(url.hostname);
    } catch {
      return false;
    }
  }

  function saveEvidenceDraft() {
    const sourceName = evidenceDraft.sourceName.trim();
    const title = evidenceDraft.title.trim();
    const sourceURL = evidenceDraft.sourceURL.trim();
    const draftErrors: ResearchEvidenceDraftErrors = {};
    if (!sourceName) draftErrors.sourceName = "Enter the source name.";
    if (!title) draftErrors.title = "Enter a title for this evidence.";
    if (sourceURL && !isValidResearchEvidenceURL(sourceURL)) {
      draftErrors.sourceURL = "Enter a full http or https URL.";
    }
    const parsedPrice = evidenceDraft.price.trim() ? parseMoneyInput(evidenceDraft.price, currency) : undefined;
    if (parsedPrice && parsedPrice.amountMinor <= 0) {
      draftErrors.price = "Enter a price greater than zero.";
    }
    const firstInvalidField = (["sourceName", "title", "sourceURL", "price"] as const)
      .find((field) => draftErrors[field]);
    if (firstInvalidField) {
      setEvidenceDraftErrors(draftErrors);
      setError(null);
      focusEvidenceDraftError(firstInvalidField);
      return;
    }
    const observedAt = new Date(`${evidenceDraft.observedDate}T12:00:00.000Z`).toISOString();
    const existingRecord = editingEvidenceID ? research.evidence.find((record) => record.id === editingEvidenceID) : undefined;
    const record: ItemResearchEvidence = {
      ...existingRecord,
      id: editingEvidenceID ?? makeResearchEvidenceID(),
      type: evidenceDraft.type,
      decision: evidenceDraft.included ? "included" : "excluded",
      sourceName,
      title,
      sourceURL: sourceURL || undefined,
      price: parsedPrice,
      condition: evidenceDraft.condition.trim() || undefined,
      sourceCategory: evidenceDraft.sourceCategory,
      relationship: evidenceDraft.relationship,
      confidence: evidenceDraft.confidence,
      observedAt,
      notes: evidenceDraft.notes.trim() || undefined
    };
    setResearch((current) => ({
      ...current,
      evidence: editingEvidenceID
        ? current.evidence.map((candidate) => candidate.id === editingEvidenceID ? record : candidate)
        : [...current.evidence, record],
      run: current.run ? {
        ...current.run,
        claims: current.run.claims.map((claim) => ({
          ...claim,
          evidenceIDs: evidenceDraft.included
            ? claim.id === activeClaim?.id
              ? Array.from(new Set([...claim.evidenceIDs, record.id]))
              : claim.evidenceIDs
            : claim.evidenceIDs.filter((evidenceID) => evidenceID !== record.id)
        }))
      } : undefined
    }));
    closeEvidenceEditor();
    setError(null);
  }

  function editEvidence(record: ItemResearchEvidence, trigger: HTMLButtonElement) {
    evidenceEditorReturnFocusRef.current = trigger;
    setEditingEvidenceID(record.id);
    setEvidenceDraft(evidenceDraftFor(record));
    setEvidenceDraftErrors({});
    setEvidenceEditorOpen(true);
    setError(null);
    focusEvidenceEditor();
  }

  function focusEvidenceControl(resolveControl: () => HTMLElement | null) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const body = researchDialogBodyRef.current;
        const control = resolveControl();
        if (!body || !control) return;
        const bodyBounds = body.getBoundingClientRect();
        const controlBounds = control.getBoundingClientRect();
        body.scrollTo({
          left: 0,
          top: Math.max(0, body.scrollTop + controlBounds.top - bodyBounds.top - 16)
        });
        control.focus({ preventScroll: true });
      });
    });
  }

  function focusEvidenceEditor() {
    focusEvidenceControl(() => evidenceEditorFirstFieldRef.current);
  }

  function focusEvidenceDraftError(field: keyof ResearchEvidenceDraftErrors) {
    const resolveControl = () => field === "sourceName"
      ? evidenceSourceNameRef.current
      : field === "title"
        ? evidenceTitleRef.current
        : field === "sourceURL"
          ? evidenceSourceURLRef.current
          : evidencePriceRef.current;
    resolveControl()?.focus({ preventScroll: true });
    focusEvidenceControl(resolveControl);
  }

  function clearEvidenceDraftError(field: keyof ResearchEvidenceDraftErrors) {
    setEvidenceDraftErrors((current) => current[field] ? { ...current, [field]: undefined } : current);
  }

  function restoreEvidenceEditorFocus() {
    const trigger = evidenceEditorReturnFocusRef.current;
    if (!trigger?.isConnected) return;
    trigger.focus({ preventScroll: true });
    window.requestAnimationFrame(() => {
      if (!trigger.isConnected) return;
      trigger.scrollIntoView({ block: "nearest" });
      trigger.focus({ preventScroll: true });
    });
  }

  function closeEvidenceEditor() {
    setEvidenceDraft(emptyResearchEvidenceDraft());
    setEvidenceDraftErrors({});
    setEditingEvidenceID(null);
    setEvidenceEditorOpen(false);
    restoreEvidenceEditorFocus();
  }

  function beginAddingEvidence(trigger: HTMLButtonElement) {
    evidenceEditorReturnFocusRef.current = trigger;
    setEditingEvidenceID(null);
    setEvidenceDraft(emptyResearchEvidenceDraft());
    setEvidenceDraftErrors({});
    setEvidenceEditorOpen(true);
    setError(null);
    focusEvidenceEditor();
  }

  function closeSourceManagement() {
    sourceManagementFocusPendingRef.current = true;
    setSourceManagementOpen(false);
    setEvidenceRemovalID(null);
    setEvidenceEditorOpen(false);
    setEditingEvidenceID(null);
    setEvidenceDraft(emptyResearchEvidenceDraft());
    setEvidenceDraftErrors({});
  }

  function removeEvidence(evidenceID: string) {
    setResearch((current) => ({
      ...current,
      evidence: current.evidence.filter((record) => record.id !== evidenceID),
      run: current.run ? {
        ...current.run,
        claims: current.run.claims.map((claim) => ({ ...claim, evidenceIDs: claim.evidenceIDs.filter((id) => id !== evidenceID) }))
      } : undefined
    }));
    if (editingEvidenceID === evidenceID) {
      setEditingEvidenceID(null);
      setEvidenceDraft(emptyResearchEvidenceDraft());
      setEvidenceEditorOpen(false);
    }
    setEvidenceRemovalID(null);
    window.requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(".research-source-library > summary")?.focus({ preventScroll: true });
    });
  }

  function buildResearch(apply: boolean, sourceResearch = research) {
    const parsedTarget = targetPrice.trim() ? parseMoneyInput(targetPrice, currency) : undefined;
    const parsedMinimum = minimumPrice.trim() ? parseMoneyInput(minimumPrice, currency) : undefined;
    if (parsedTarget && parsedTarget.amountMinor <= 0) throw new Error("Target price must be greater than zero.");
    if (parsedMinimum && parsedMinimum.amountMinor <= 0) throw new Error("Minimum price must be greater than zero.");
    if (parsedTarget && parsedMinimum && parsedMinimum.amountMinor > parsedTarget.amountMinor) {
      throw new Error("Minimum price cannot exceed the target price.");
    }
    const hasIdentification = Boolean(
      sourceResearch.identification.maker?.trim() ||
      sourceResearch.identification.model?.trim() ||
      sourceResearch.identification.period?.trim() ||
      sourceResearch.identification.origin?.trim() ||
      sourceResearch.identification.identifiers?.trim() ||
      sourceResearch.identification.rationale?.trim()
    );
    const hasIncludedEvidence = sourceResearch.evidence.some((record) => record.decision === "included");
    const identityAccepted = primaryIdentityResearchClaim(sourceResearch)?.sellerDecision === "accepted";
    const hasRefinement = Boolean(
      parsedTarget
      && (!identityAccepted || (sourceResearch.refinement.title?.trim() && sourceResearch.refinement.description?.trim()))
    );
    const unresolvedClaims = pendingResearchClaims(sourceResearch);
    if (apply && isResearchRunning) throw new Error("Wait for research and verification to finish before applying findings.");
    if (apply && researchNeedsRerun(research, selectedPhotoIDs)) {
      throw new Error("Run research again so the new evidence can be checked before applying findings.");
    }
    if (apply && unresolvedClaims.length > 0) {
      throw new Error(`Review ${unresolvedClaims.length} research ${unresolvedClaims.length === 1 ? "claim" : "claims"} before applying findings.`);
    }
    if (apply && primaryIdentityResearchClaim(sourceResearch)?.sellerDecision === "accepted" && !sourceResearch.identification.sellerConfidence) {
      throw new Error("Choose your confidence in the selected identification before applying it.");
    }
    if (apply && !hasRefinement) {
      throw new Error(identityAccepted
        ? "Confirm the prepared title, description, and target price before applying research."
        : "Confirm the target price before applying research.");
    }
    const now = new Date().toISOString();
    return {
      ...sourceResearch,
      status: apply ? "refined" : hasIdentification && hasIncludedEvidence && hasRefinement ? "ready_to_refine" : "in_review",
      identification: {
        ...sourceResearch.identification,
        confirmedAt: primaryIdentityResearchClaim(sourceResearch)?.sellerDecision === "accepted"
          ? sourceResearch.identification.confirmedAt ?? now
          : undefined
      },
      refinement: {
        ...sourceResearch.refinement,
        targetPrice: parsedTarget,
        minimumPrice: parsedMinimum
      },
      updatedAt: now
    } satisfies ItemResearch;
  }

  async function persistResearch(apply: boolean) {
    try {
      setIsSaving(true);
      setError(null);
      const nextResearch = buildResearch(apply);
      await store.updateInventoryItem(item.id, apply ? itemUpdateFromResearch(nextResearch) : { research: nextResearch });
      const nextIdentityClaim = primaryIdentityResearchClaim(nextResearch);
      const appliedIdentity = !nextIdentityClaim || nextIdentityClaim.sellerDecision === "accepted"
        ? researchIdentityLabel(nextResearch)
        : undefined;
      const appliedPricing = researchPricingIsApplicable(nextResearch);
	      toast.notify({
	        title: apply ? "Research applied to item" : "Research saved",
	        description: apply
          ? appliedIdentity && appliedPricing
            ? "The item now uses the selected identification, pricing, and prepared copy."
            : appliedIdentity
              ? "The selected identification and prepared copy were applied. Pricing was left unchanged."
              : "The independently reviewed pricing was applied. Identification and prepared copy were left unchanged."
	          : undefined,
	        tone: "success"
	      });
      onClose();
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to save this research."));
    } finally {
      setIsSaving(false);
    }
  }

  const reviewClaims = reviewableResearchClaims(research);
  const identityClaim = primaryIdentityResearchClaim(research);
  const secondaryReviewClaims = reviewClaims.filter((claim) => claim.id !== identityClaim?.id);
  const pendingSecondaryClaims = secondaryReviewClaims.filter((claim) => claim.sellerDecision === "pending");
  const pendingCount = pendingResearchClaims(research).length;
  const findingsMode = researchFindingsMode(research);
  const hasNewSellerEvidence = researchNeedsRerun(research, selectedPhotoIDs);
  const isPreviewOnlyRun = research.provider.id === "local_research_preview";
  const openQuestions = run?.questions.filter((question) =>
    !question.answer && (run.status === "needs_input" || question.priority === "high")
  ) ?? [];
  const reviewQuestions = isPreviewOnlyRun ? [] : openQuestions;
  const openPhotoQuestions = reviewQuestions.filter((question) => question.requestedInput === "photo");
  const primaryReviewQuestion = reviewQuestions[0];
  const researchRunHeading = !run
    ? "No run started"
    : isResearchStalled
      ? "Research stopped updating"
      : isResearchRunning
      ? run.phase
      : run.status === "failed"
        ? "Research stopped"
        : openQuestions.length > 0
          ? `${openQuestions.length} ${openQuestions.length === 1 ? "follow-up" : "follow-ups"} needed`
          : "Research ready to review";
  const identityLabel = researchIdentityLabel(research);
  const reviewCandidate = sellerReviewCandidateIdentification(research);
  const reviewCandidateLabel = [reviewCandidate?.maker, reviewCandidate?.model].filter(Boolean).join(" · ") || undefined;
  const candidateIdentityLabel = identityLabel ?? reviewCandidateLabel;
  const proposedIdentityLabel = candidateIdentityLabel ?? identityClaim?.statement ?? "Proposed identification";
  const identityKeptUnresolved = identityClaim?.sellerDecision === "needs_evidence";
  const includedEvidence = research.evidence.filter((record) => record.decision === "included");
  const rationaleEvidence = researchEvidenceMentionedByClaims(research);
  const decisionEvidence = uniqueResearchEvidence([...includedEvidence, ...rationaleEvidence]);
  const decisionEvidenceIDs = new Set(decisionEvidence.map((record) => record.id));
  const otherEvidence = research.evidence.filter((record) => !decisionEvidenceIDs.has(record.id));
  const activeClaim = identityClaim;
  const activeClaimEvidence = activeClaim
    ? uniqueResearchEvidence([
        ...activeClaim.evidenceIDs.flatMap((evidenceID) => {
          const record = research.evidence.find((candidate) => candidate.id === evidenceID);
          return record ? [record] : [];
        }),
        ...(activeClaim.kind === "identity" ? [...includedEvidence, ...rationaleEvidence] : [])
      ])
    : [];
  const comparisonEvidence = activeClaimEvidence.length > 0 ? activeClaimEvidence : decisionEvidence;
  const isDemoResearch = research.provider.id === "listwork_demo_research";
  const visualComparisonEvidence = comparisonEvidence.filter((record) =>
    isSourceBackedVisualEvidence(record)
    || (
      isDemoResearch
      && record.imageProvenance === "synthetic_demo"
      && Boolean(record.imageURL)
    )
  );
  const primaryEvidence = comparisonEvidence.find((record) => record.decision === "included" && record.relationship !== "contradicts" && record.imageURL)
    ?? comparisonEvidence.find((record) => record.decision === "included" && record.relationship !== "contradicts")
    ?? comparisonEvidence.find((record) => record.relationship !== "contradicts" && record.imageURL)
    ?? comparisonEvidence.find((record) => record.imageURL)
    ?? comparisonEvidence[0];
  const activeComparisonEvidence = visualComparisonEvidence.find((record) => record.id === comparisonEvidenceID)
    ?? visualComparisonEvidence[0];
  const activeComparisonPhoto = selectedPhotos.find((photo) => photo.id === comparisonPhotoID) ?? selectedPhotos[0];
  const comparisonPhotos: ResearchComparisonPhoto[] = selectedPhotos.map((photo, index) => ({
    id: photo.id,
    label: researchPhotoDisplayLabel(selectedPhotos, photo, index),
    src: photo.url || photo.thumbnailURL,
    style: researchEvidencePhotoStyle(photo.treatment)
  }));
  const activeSupportingEvidenceCount = activeClaimEvidence.filter((record) => !record.relationship || record.relationship === "supports").length;
  const activeContextEvidenceCount = activeClaimEvidence.filter((record) => record.relationship === "context").length;
  const activeConflictingEvidenceCount = activeClaimEvidence.filter((record) => record.relationship === "contradicts").length;
  const activeClaimEvidenceSummary = [
    activeSupportingEvidenceCount > 0
      ? `${activeSupportingEvidenceCount} supporting ${activeSupportingEvidenceCount === 1 ? "reference" : "references"}`
      : "",
    activeContextEvidenceCount > 0
      ? `${activeContextEvidenceCount} contextual ${activeContextEvidenceCount === 1 ? "reference" : "references"}`
      : "",
    activeConflictingEvidenceCount > 0
      ? `${activeConflictingEvidenceCount} conflicting ${activeConflictingEvidenceCount === 1 ? "reference" : "references"}`
      : ""
  ].filter(Boolean).join(" · ");
  const discoveredSearchResultCount = run?.searches.reduce(
    (total, search) => total + (search.resultCount ?? 0),
    0
  ) ?? 0;
  const excludedEvidenceCount = research.evidence.length - includedEvidence.length;
  const findingsCoverage = {
    photos: `${selectedPhotos.length} inspected`,
    results: `${discoveredSearchResultCount} found`,
    sources: includedEvidence.length > 0
      ? `${includedEvidence.length} used${excludedEvidenceCount > 0 ? ` · ${excludedEvidenceCount} excluded` : ""}`
      : excludedEvidenceCount > 0 ? `${excludedEvidenceCount} excluded` : "None used"
  };
  const reviewImages: ResearchReviewImage[] = [
    ...selectedPhotos.map((photo, index) => ({
      id: `seller-photo-${photo.id}`,
      group: "seller" as const,
      imageURL: photo.url || photo.thumbnailURL,
      title: researchPhotoDisplayLabel(selectedPhotos, photo, index),
      eyebrow: "Your item",
      description: `${researchPhotoTypeLabels[photo.type]} selected for this research run.`
    })),
    ...research.evidence.flatMap((record) => {
      if (!record.imageURL) return [];
      return [{
        id: `evidence-${record.id}`,
        group: "source" as const,
        imageURL: record.imageURL,
        title: researchEvidenceDisplayTitle(record, identityLabel),
        eyebrow: researchEvidenceImageEyebrow(record, isDemoResearch),
        description: researchEvidenceExplanation(record),
        imageDisclosure: researchEvidenceImageDisclosure(record, isDemoResearch),
        sourceURL: record.sourceURL,
        sourceActionLabel: researchEvidenceSourceActionLabel(record, isDemoResearch),
        metadata: `${record.sourceCategory ? researchSourceCategoryLabels[record.sourceCategory] : "Seller source"} · ${record.relationship ? researchRelationshipLabels[record.relationship] : "Context"}`
      }];
    })
  ];
  const alternativeHypotheses = alternativeResearchHypotheses(research);
  const evidenceRoleLabel = (record: ItemResearchEvidence) => {
    if (record.decision === "excluded") return "Not used";
    if (record.relationship === "contradicts") return "Conflicting reference";
    if (record.relationship === "context") return "Background source";
    if (record.imageProvenance === "synthetic_demo" && !isDemoResearch) return "Collection reference";
    if (record.id === primaryEvidence?.id) return "Closest visual comparison";
    return record.decision === "included" ? "Supporting reference" : "Referenced";
  };
  const comparisonSources: ResearchComparisonSource[] = visualComparisonEvidence.map((record) => ({
    checkedLabel: `Checked ${formatCompactDate(record.observedAt)}`,
    comparisonLabel: isDemoResearch ? "Reference" : record.imageProvenance === "synthetic_demo" ? "Representative image" : "Reference",
    id: record.id,
    imageDisclosure: researchEvidenceImageDisclosure(record, isDemoResearch),
    imageSourceLabel: researchSourceDisplayName(record),
    imageURL: record.imageURL,
    label: researchEvidenceDisplayTitle(record, identityLabel),
    priceLabel: record.price ? formatMoney(record.price) : undefined,
    role: evidenceRoleLabel(record),
    sourceType: researchEvidenceTypeLabels[record.type],
    summary: researchEvidenceExplanation(record) ?? "No descriptive notes were retained for this source.",
    sourceName: researchSourceDisplayName(record),
    sourceActionLabel: researchEvidenceSourceActionLabel(record, isDemoResearch),
    sourceURL: record.sourceURL,
    tone: record.imageProvenance === "synthetic_demo" && !isDemoResearch
      ? "neutral"
      : record.relationship === "contradicts"
      ? "warning"
      : record.decision === "included" || rationaleEvidence.some((candidate) => candidate.id === record.id)
        ? "info"
        : "neutral"
  }));
  const activeComparisonPhotoView = comparisonPhotos.find((photo) => photo.id === activeComparisonPhoto?.id);
  const activeComparisonSourceView = comparisonSources.find((source) => source.id === activeComparisonEvidence?.id);
  const hasVisualComparison = Boolean(activeComparisonPhoto && activeComparisonEvidence);
  const candidateComparisonWorkbench = hasVisualComparison ? (
    <ResearchFindingsWorkbench
      activePhoto={activeComparisonPhotoView}
      activeSource={activeComparisonSourceView}
      photos={comparisonPhotos}
      sources={comparisonSources}
      onPhotoSelect={setComparisonPhotoID}
      onSourceSelect={setComparisonEvidenceID}
      onPhotoPreview={(photoID, trigger) => openReviewImage(`seller-photo-${photoID}`, trigger)}
      onSourcePreview={(sourceID, trigger) => openReviewImage(`evidence-${sourceID}`, trigger)}
    />
  ) : null;
  const supportingVisualEvidenceCount = activeClaimEvidence.filter(
    (record) =>
      (!record.relationship || record.relationship === "supports")
      && isSourceBackedVisualEvidence(record)
  ).length;
  const identityPhotoEvidenceOutstanding = openQuestions.some((question) => question.requestedInput === "photo");
  const candidateCanBeSelected =
    supportingVisualEvidenceCount > 0
    && activeConflictingEvidenceCount === 0
    && !identityPhotoEvidenceOutstanding
    && activeClaim?.confidence !== "low";
  const candidateRecommendation = !candidateCanBeSelected
    ? "gather" as const
    : activeClaim?.confidence === "high"
      ? "use" as const
      : "provisional" as const;
  const hasSelectedResearchIdentity = identityClaim?.sellerDecision === "accepted";
  const canApplyResearchPricing = researchPricingIsApplicable(research);
  const selectedIdentityIsProvisional =
    hasSelectedResearchIdentity
    && research.identification.sellerConfidence !== "high";
  const completedRunOutcome = findingsMode === "candidate_review"
    ? candidateCanBeSelected
      ? {
          title: activeClaim?.confidence === "high" ? "Candidate found" : "Provisional candidate found",
          description: activeClaim?.confidence === "high"
            ? `${proposedIdentityLabel} is ready for you to compare with the retrieved evidence.`
            : `${proposedIdentityLabel} has visual support but still needs your judgement before it can be used.`,
          tone: activeClaim?.confidence === "high" ? "success" as const : "warning" as const
        }
      : {
          title: "Possible lead found",
          description: `${proposedIdentityLabel} needs stronger evidence before it can be used as the item identification.`,
          tone: "warning" as const
        }
    : findingsMode === "accepted"
      ? {
          title: proposedIdentityLabel,
          description: selectedIdentityIsProvisional
            ? "Selected provisionally. Record your own confidence in Listing before applying it."
            : `Evidence strength: ${researchConfidenceLabels[identityClaim?.confidence ?? research.identification.confidence].replace(" confidence", "").toLowerCase()}. Apply it in Listing when you are ready.`,
          tone: selectedIdentityIsProvisional ? "warning" as const : "success" as const
        }
      : findingsMode === "rejected"
        ? {
            title: proposedIdentityLabel,
            description: "Ruled out as this item's identification unless you undo that decision.",
            tone: "warning" as const
          }
        : {
            title: "Identification unresolved",
            description: "Listwork could not establish a maker and model from the available evidence.",
            tone: "warning" as const
          };
  const findingsOutcomeMedia = activeComparisonPhoto ? (
    <figure className="research-findings-photo">
      <button
        type="button"
        onClick={(event) => openReviewImage(`seller-photo-${activeComparisonPhoto.id}`, event.currentTarget)}
        aria-haspopup="dialog"
        aria-label="Enlarge the current item photograph"
      >
        <img
          src={activeComparisonPhoto.thumbnailURL || activeComparisonPhoto.url}
          alt=""
          style={photoStyle(activeComparisonPhoto.treatment)}
        />
        <span aria-hidden="true"><Maximize2 size={16} /></span>
      </button>
      <figcaption>
        <strong>Your item</strong>
        <span>{selectedPhotos.length === 1 ? "1 photo inspected" : `${selectedPhotos.length} photos inspected`}</span>
      </figcaption>
    </figure>
  ) : undefined;
  const sparseCandidateEvidence = activeClaimEvidence[0];

  function revealSecondaryFindings() {
    setSecondaryFindingsOpen(true);
    window.requestAnimationFrame(() => {
      const targetClaim = pendingSecondaryClaims[0] ?? secondaryReviewClaims[0];
      const target = targetClaim
        ? document.getElementById(`research-decision-${targetClaim.id}`)
        : document.querySelector<HTMLElement>("#research-secondary-findings > summary");
      target?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start"
      });
      target?.focus({ preventScroll: true });
    });
  }

  function revealSourcesChecked() {
    const sourceLibrary = sourceLibraryRef.current;
    if (!sourceLibrary) return;
    sourceLibrary.open = true;
    window.requestAnimationFrame(() => {
      const trigger = sourceLibrary.querySelector<HTMLElement>(":scope > summary");
      trigger?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "start"
      });
      trigger?.focus({ preventScroll: true });
    });
  }

  function openResearchPhotoEditor(photoID: string) {
    researchPhotoEditReturnIDRef.current = photoID;
    setEditingResearchPhotoID(photoID);
  }

  function closeResearchPhotoEditor() {
    setEditingResearchPhotoID(null);
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (open) return;
        if (editingResearchPhotoID) {
          closeResearchPhotoEditor();
        } else {
          requestClose();
        }
      }}
    >
      <DialogContent className={`ui-dialog-content-wizard item-research-dialog${photoPickerOpen ? " research-photo-picker-mode" : ""}`} presentation="workflow" leadingClose showClose={false}>
        <DialogHeader>
          <Button
            ref={researchHeaderControlRef}
            variant="secondary"
            size="icon"
            type="button"
            onClick={photoPickerOpen ? () => {
              if (editingResearchPhotoID) {
                closeResearchPhotoEditor();
              } else {
                setPhotoPickerOpen(false);
                setPhotoPickerError(null);
              }
            } : requestClose}
            aria-label={editingResearchPhotoID ? "Back to research photos" : photoPickerOpen ? "Back to research brief" : "Close item research"}
            disabled={isSaving || isStarting || isAddingResearchPhoto}
          >
            {photoPickerOpen ? <ArrowLeft size={20} /> : <X size={20} />}
          </Button>
          <DialogTitle>
            {editingResearchPhotoID ? "Edit research photo" : photoPickerOpen ? "Choose research photos" : <><span className="research-dialog-context">Research: </span>{item.title}</>}
          </DialogTitle>
          <DialogDescription className={photoPickerOpen ? undefined : "sr-only"}>
            {editingResearchPhotoID
              ? `Adjust the saved photograph for ${item.title}.`
              : photoPickerOpen
              ? `Select the views Listwork should inspect for ${item.title}.`
              : "Select photographs and questions, then review sources before applying findings."}
          </DialogDescription>
        </DialogHeader>
        <FileInput ref={researchPhotoInputRef} className="sr-only" accept={PHOTO_FILE_ACCEPT} multiple aria-label="Choose additional research photos" onChange={(event) => void addResearchPhotos(event)} />
        <PhotoUploadStatus progress={photoProgress} />
        {photoPickerOpen ? (
          editingResearchPhotoID && photos.some((photo) => photo.id === editingResearchPhotoID) ? (
            <SavedPhotoEditor
              photo={photos.find((photo) => photo.id === editingResearchPhotoID)!}
              itemTitle={item.title}
              onCancel={closeResearchPhotoEditor}
              onSaved={closeResearchPhotoEditor}
            />
          ) : (
            <ResearchPhotoPicker
              photos={photos}
              selectedPhotoIDs={photoPickerSelection}
              error={photoPickerError}
              isAdding={isAddingResearchPhoto}
              onSelectionChange={setPhotoPickerSelection}
              onAddPhotos={() => chooseResearchPhotos("picker")}
              onEditPhoto={openResearchPhotoEditor}
              onEditButtonRef={(photoID, button) => {
                if (button) {
                  researchPhotoEditButtonRefs.current.set(photoID, button);
                } else {
                  researchPhotoEditButtonRefs.current.delete(photoID);
                }
              }}
              onConfirm={() => {
                if (photoPickerSelection.length === 0) {
                  setPhotoPickerError("Select at least one photo for research.");
                  return;
                }
                setSelectedPhotoIDs(photoPickerSelection);
                setPhotoPickerError(null);
                setPhotoPickerOpen(false);
              }}
            />
          )
        ) : (
          <Tabs
          className="ui-form-layout"
          value={activeView}
          onValueChange={(value) => setActiveView(value as typeof activeView)}
        >
          <TabsList className="research-workspace-tabs" aria-label="Item research workspace">
            <TabsTrigger id="research-tab-brief" aria-controls="research-panel-brief" value="brief">
              <span className="research-workspace-tab-content"><ScanSearch size={16} /><span>Brief</span></span>
            </TabsTrigger>
            <TabsTrigger id="research-tab-run" aria-controls="research-panel-run" value="run">
              <span className="research-workspace-tab-content"><Clock3 size={16} /><span>Research</span>{isResearchRunning ? <ButtonCountBadge>Active</ButtonCountBadge> : null}</span>
            </TabsTrigger>
            <TabsTrigger id="research-tab-review" aria-controls="research-panel-review" value="findings" disabled={!run || isResearchRunning || run.status === "failed"}>
              <span className="research-workspace-tab-content">
                <FileSearch size={16} />
                <span>Findings</span>
                {pendingCount ? <span className="sr-only">, {pendingCount} {pendingCount === 1 ? "decision" : "decisions"} pending</span> : null}
              </span>
            </TabsTrigger>
            <TabsTrigger id="research-tab-listing" aria-controls="research-panel-listing" value="listing" disabled={!run || isResearchRunning || run.status === "failed"}>
              <span className="research-workspace-tab-content"><Pencil size={16} /><span>Listing</span></span>
            </TabsTrigger>
          </TabsList>
          <span className="sr-only" role="status" aria-live="polite">
            {savingDecisionClaimID ? "Saving decision" : ""}
          </span>
          <DialogBody ref={researchDialogBodyRef} className="ui-dialog-body-wizard item-research-dialog-body">
            {error && <Alert tone="danger">{error}</Alert>}

            {activeView === "brief" && (
              <div id="research-panel-brief" aria-labelledby="research-tab-brief" className="research-brief-layout" role="tabpanel">
                <section className="research-workspace-section" aria-labelledby="research-brief-heading">
                  <div className="research-workspace-heading">
                    <h3 id="research-brief-heading">Research focus</h3>
                  </div>
                  <fieldset className="research-focus-fieldset">
                    <legend className="sr-only">Research focus</legend>
                    <div className="research-focus-options">
                      {researchFocusOptions.map((option) => (
                        <label className={`research-focus-option${focus === option.key ? " is-selected" : ""}`} key={option.key}>
                          <RadioInput
                            name="research-focus"
                            value={option.key}
                            checked={focus === option.key}
                            onChange={() => {
                              setFocus(option.key);
                              setError(null);
                            }}
                          />
                          <span className="research-focus-copy"><strong>{option.label}</strong><small>{option.description}</small></span>
                          {option.recommended ? <Badge tone="info">Recommended</Badge> : null}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  {focus === "custom" ? (
                    <Field
                      label="What do you want to find out?"
                      helper="Ask one clear question or describe the decision this research should support."
                      wide
                    >
                      <Textarea
                        rows={4}
                        maxLength={2000}
                        required
                        value={customPriority}
                        onChange={(event) => {
                          setCustomPriority(event.target.value);
                          setError(null);
                        }}
                      />
                    </Field>
                  ) : null}
                  <Field label="Context (optional)" helper="Measurements, visible marks, acquisition history, condition details, or a lead to check." wide>
                    <Textarea rows={4} value={sellerContext} onChange={(event) => setSellerContext(event.target.value)} />
                  </Field>
                  <Disclosure
                    className="research-settings-disclosure"
                    label="Research settings"
                    meta={depth === "deep" ? "Thorough" : depth === "standard" ? "Standard" : "Quick"}
                  >
                    <div className="research-settings-content">
                      <SegmentedControl label="Research depth" value={depth} options={researchDepthOptions} onChange={setDepth} />
                      <p>{researchDepthDescriptions[depth]}</p>
                    </div>
                  </Disclosure>
                  <div className={`research-photo-strip${selectedPhotos.length === 0 ? " is-empty" : ""}`} aria-label="Photos selected for research">
                    <div className="research-photo-strip-head">
                      <div className="research-photo-summary">
                        <Camera size={18} />
                        <div>
                          <strong>{photos.length === 0 ? "No photos added" : selectedPhotos.length === 0 ? "No photos selected" : `${selectedPhotos.length} of ${photos.length} selected`}</strong>
                          {selectedPhotos.length === 0 && <small>{photos.length === 0 ? "Add an overall view, marks and distinguishing details." : "Choose up to eight photos from this item."}</small>}
                        </div>
                      </div>
                      <div className="research-photo-actions">
                        {photos.length > 0 && <Button variant="secondary" size="compact" type="button" onClick={openResearchPhotoPicker} disabled={isAddingResearchPhoto}><Camera size={16} /> Manage photos</Button>}
                        {photos.length < MAX_ITEM_PHOTOS && <Button variant={photos.length === 0 ? "primary" : "ghost"} size="compact" type="button" onClick={() => chooseResearchPhotos("setup")} disabled={isAddingResearchPhoto} loading={isAddingResearchPhoto} loadingLabel="Adding photos"><ImagePlus size={16} /> {isAddingResearchPhoto ? "Adding photos" : "Add photos"}</Button>}
                      </div>
                    </div>
                    {selectedPhotos.length > 0 && (
                      <div className="research-photo-thumbnails">
                        {selectedPhotos.map((photo, index) => <img key={photo.id} src={photo.thumbnailURL || photo.url} alt={`Selected research photo ${index + 1}, ${researchPhotoTypeLabels[photo.type]}`} style={photoStyle(photo.treatment)} />)}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}

            {activeView === "run" && (
              <div id="research-panel-run" aria-labelledby="research-tab-run" className="research-run-workspace" role="tabpanel">
                <section className="research-run-status" aria-label="Research run">
                  {run && isPreviewOnlyRun ? (
                    <ResearchRunSummary
                      media={selectedPhotos.length > 0 ? (
                        <div className="research-run-summary-photo">
                          <img src={selectedPhotos[0].thumbnailURL || selectedPhotos[0].url} alt={`Selected photograph of ${item.title}`} style={photoStyle(selectedPhotos[0].treatment)} />
                          <span>{selectedPhotos.length === 1 ? "1 selected photo" : `${selectedPhotos.length} selected photos`}</span>
                        </div>
                      ) : (
                        <div className="research-run-summary-photo is-empty"><Camera size={24} /><span>No photo selected</span></div>
                      )}
                      facts={[
                        { label: "Focus", value: run.focus ? researchFocusLabels[run.focus] : "Identification" },
                        { label: "Depth", value: run.depth === "deep" ? "Thorough" : run.depth === "standard" ? "Standard" : "Quick" },
                        { label: "Photos", value: String(selectedPhotos.length) }
                      ]}
                      question={researchQuestionForDisplay(item.title, run.focus, run.customPriority)}
                      actions={(
                        <>
                          <Button variant="secondary" type="button" onClick={() => setActiveView("brief")}><Pencil size={16} /> Edit brief</Button>
                          <Button variant="primary" type="button" onClick={() => void startResearch()} disabled={isStarting} loading={isStarting} loadingLabel="Starting"><Sparkles size={16} /> {isStarting ? "Starting" : "Start research"}</Button>
                        </>
                      )}
                    />
                  ) : run ? (
                    isResearchRunning || run.status === "failed" ? (
                      <ResearchRunStatusView
                        error={run.status === "failed"
                          ? userSafeErrorMessage(new Error(run.error ?? ""), "Research stopped before it finished. Try again or review the brief.")
                          : undefined}
                        stalled={isResearchStalled}
                        lastActivity={formatResearchActivity(run.updatedAt, isResearchRunning)}
                        onEditBrief={() => setActiveView("brief")}
                        onRetry={() => void startResearch()}
                        stages={run.plan}
                        statusLabel={isResearchStalled ? "Needs attention" : researchRunStatusLabel(run.status)}
                        statusTone={isResearchStalled ? "warning" : researchRunStatusTone(run.status)}
                        title={researchRunHeading}
                      />
	                    ) : (
	                      <ResearchRunOutcome
	                        tone={completedRunOutcome.tone}
	                        icon={completedRunOutcome.tone === "warning" ? <CircleHelp size={20} /> : <CheckCircle2 size={20} />}
	                        title={completedRunOutcome.title}
	                        description={completedRunOutcome.description}
                        meta={[
                          run.searches.length > 0 ? `${run.searches.length} ${run.searches.length === 1 ? "search" : "searches"}` : "",
                          research.evidence.length > 0 ? `${research.evidence.length} ${research.evidence.length === 1 ? "source" : "sources"} reviewed` : ""
                        ].filter(Boolean).join(" · ") || undefined}
                        actions={(
                          <>
                            {openPhotoQuestions.length > 0 && (
                              <Button variant="primary" type="button" onClick={() => chooseResearchPhotos("followup")} disabled={isAddingResearchPhoto || photos.length >= MAX_ITEM_PHOTOS} loading={isAddingResearchPhoto} loadingLabel="Adding photo">
                                <ImagePlus size={16} /> {isAddingResearchPhoto ? "Adding photo" : "Add detail photo"}
                              </Button>
                            )}
                            <Button variant={openPhotoQuestions.length > 0 ? "secondary" : "primary"} type="button" onClick={() => setActiveView("findings")}>
                              <FileSearch size={16} /> {reviewQuestions.length > 0 ? "Review current evidence" : "Review candidate"}
                            </Button>
                          </>
                        )}
                      />
                    )
                  ) : (
                    <EmptyState icon={<Clock3 size={28} />} title="No research run yet" message="Choose a focus and photos in Brief." action={<Button variant="primary" type="button" onClick={() => setActiveView("brief")}>Prepare brief</Button>} />
                  )}
                </section>
                {run && !isPreviewOnlyRun && (
                  <div className="research-run-record">
                    <Disclosure
                      className="research-run-disclosure"
                      label="Run details"
                      meta={formatCompactDateTime(run.startedAt)}
                    >
                      <dl className="research-run-inputs">
                        <div><dt>Focus</dt><dd>{run.focus ? researchFocusLabels[run.focus] : "Identification"}</dd></div>
                        <div><dt>Depth</dt><dd>{run.depth === "deep" ? "Thorough" : run.depth === "standard" ? "Standard" : "Quick"}</dd></div>
                        <div><dt>Photographs</dt><dd>{run.photoIDs?.length ?? 0} supplied</dd></div>
                        <div><dt>Source coverage</dt><dd>{research.provider.mode === "connected" ? "Live web and image search" : "No live web search"}</dd></div>
                        <div><dt>Brief</dt><dd>{run.brief}</dd></div>
                      </dl>
                    </Disclosure>
                    {run.searches.length > 0 && (
                      <Disclosure
                        className="research-run-disclosure"
                        label="Search activity"
                        meta={`${run.searches.length} ${run.searches.length === 1 ? "search" : "searches"}`}
                      >
                        <div className="research-search-log">
                          {run.searches.map((search) => (
                            <div key={search.id}>
                              <span>{search.method === "visual" ? <ScanSearch size={15} /> : <Search size={15} />}</span>
                              <div><strong>{search.query}</strong><small>{researchSearchMethodLabels[search.method]} search · {search.resultCount ?? 0} results found</small></div>
                            </div>
                          ))}
                        </div>
                      </Disclosure>
                    )}
	                    {!isResearchRunning && run.status !== "failed" && run.plan.length > 0 && (
	                      <Disclosure
	                        className="research-run-disclosure"
	                        label="Research steps"
	                        meta={run.status === "ready" ? undefined : `${run.plan.filter((step) => step.status === "complete").length} of ${run.plan.length} complete`}
                      >
                        <div className="research-plan-list">
                          {run.plan.map((step) => (
                            <div className={`research-plan-step research-plan-step-${step.status}`} key={step.id}>
                              <span aria-hidden="true">{step.status === "complete" ? <Check size={14} /> : step.status === "blocked" ? <AlertTriangle size={14} /> : <span />}</span>
                              <div><strong>{step.label}</strong><small>{step.detail}</small></div>
                            </div>
                          ))}
                        </div>
                      </Disclosure>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeView === "findings" && (
              <div id="research-panel-review" aria-labelledby="research-tab-review" className="research-findings" role="tabpanel">
                {!run ? (
                  <EmptyState icon={<FileSearch size={28} />} title="No findings yet" message="Choose a research focus and add item photographs." action={<Button variant="primary" type="button" onClick={() => setActiveView("brief")}>Set research focus</Button>} />
                ) : isPreviewOnlyRun ? (
                  <div className="research-review-empty">
                    <EmptyState
                      as="h3"
                      icon={<FileSearch size={24} />}
                      title="Nothing to review yet"
                      message="Start research to check the selected photographs against visual matches and supporting sources."
                    />
                  </div>
                ) : (
                  <div className="research-review-stack">
                    {run.status === "failed" && <Alert tone="danger">{userSafeErrorMessage(new Error(run.error ?? ""), "Research stopped before it finished. Try again or review the brief.")}</Alert>}

                    {hasNewSellerEvidence ? (
                      <ContentFrame size="task">
                        <ResearchFindingsOutcome
                          coverage={findingsCoverage}
                          headingID="research-rerun-heading"
                          media={findingsOutcomeMedia}
                          primaryAction={(
                            <Button variant="primary" type="button" onClick={() => void startResearch()} disabled={isStarting || isResearchRunning} loading={isStarting} loadingLabel="Starting">
                              <RotateCcw size={16} /> {isStarting ? "Starting" : "Run research again"}
                            </Button>
                          )}
                          secondaryAction={<Button variant="secondary" type="button" onClick={() => setActiveView("brief")}><Pencil size={16} /> Review brief</Button>}
                          state="stale"
                          summary="Check the new evidence before choosing an identification."
                          title="New evidence is ready"
                        />
                      </ContentFrame>
                    ) : findingsMode === "needs_evidence" ? (
                      <ContentFrame size="task" className="research-findings-state research-findings-state-unresolved">
                        <ResearchFindingsOutcome
                          coverage={findingsCoverage}
                          headingID="research-unresolved-heading"
                          media={findingsOutcomeMedia}
                          primaryAction={primaryReviewQuestion?.requestedInput === "photo" ? (
                            <Button variant="primary" type="button" onClick={() => chooseResearchPhotos("followup")} disabled={isAddingResearchPhoto || photos.length >= MAX_ITEM_PHOTOS} loading={isAddingResearchPhoto} loadingLabel="Adding photos">
                              <ImagePlus size={16} /> {isAddingResearchPhoto ? "Adding photos" : "Add detail photos"}
                            </Button>
                          ) : (
                            <Button variant="primary" type="button" onClick={() => setActiveView("brief")}>
                              <RotateCcw size={16} /> Add evidence and research again
                            </Button>
                          )}
                          request={primaryReviewQuestion ? {
                            control: primaryReviewQuestion.requestedInput !== "photo" ? (
                              <Input
                                className="research-question-answer"
                                aria-label={`Answer: ${primaryReviewQuestion.prompt}`}
                                placeholder={primaryReviewQuestion.requestedInput === "measurement" ? "Add measurement" : "Add your answer"}
                                value={primaryReviewQuestion.answer ?? ""}
                                onChange={(event) => updateQuestionAnswer(primaryReviewQuestion.id, event.target.value)}
                              />
                            ) : undefined,
                            kind: primaryReviewQuestion.requestedInput === "photo" ? "photo" : "information",
                            prompt: primaryReviewQuestion.prompt
                          } : undefined}
                          secondaryAction={identityKeptUnresolved && identityClaim ? (
                            <Button variant="secondary" type="button" onClick={() => void updateClaimDecision(identityClaim.id, "pending")} disabled={isSaving}>
                              <RotateCcw size={16} /> Reconsider candidate
                            </Button>
                          ) : (
                            <Button variant="secondary" type="button" onClick={() => setActiveView("listing")}>Continue without identification <ChevronRight size={16} /></Button>
                          )}
                          state={identityKeptUnresolved ? "provisional" : "unsupported"}
                          summary={identityKeptUnresolved
                            ? `${proposedIdentityLabel} remains a lead, not a verified identification. Unsupported maker and model details will stay out of the item record.`
                            : primaryReviewQuestion?.reason ?? run.verification?.summary ?? "The available evidence does not support a specific maker or model."}
                          title={identityKeptUnresolved ? "More evidence needed" : "Maker and model not established"}
                        />
                        {reviewQuestions.length > 1 ? (
                          <Disclosure
                            className="research-findings-secondary-questions"
                            label="Other information requested"
                            meta={`${reviewQuestions.length - 1} more`}
                          >
                            <div className="research-question-list">
                              {reviewQuestions.slice(1).map((question) => (
                                <ActionPrompt
                                  key={question.id}
                                  tone="warning"
                                  icon={<CircleHelp size={18} />}
                                  title={question.prompt}
                                  description={question.reason}
                                  action={question.requestedInput === "photo"
                                    ? undefined
                                    : <Input className="research-question-answer" aria-label={`Answer: ${question.prompt}`} placeholder={question.requestedInput === "measurement" ? "Add measurement" : "Add your answer"} value={question.answer ?? ""} onChange={(event) => updateQuestionAnswer(question.id, event.target.value)} />}
                                />
                              ))}
                            </div>
                          </Disclosure>
                        ) : null}
                      </ContentFrame>
	                    ) : findingsMode === "candidate_review" && activeClaim ? (
	                      <ContentFrame size="workbench" className="research-findings-state research-findings-state-candidate">
	                        <div className="research-workbench-layout">
                          <section className="research-decision-rail research-primary-finding" aria-labelledby="research-decision-heading">
                            <h3 className="sr-only" id="research-decision-heading">Identification decision</h3>

                            <ResearchDecisionCard
                              id={`research-decision-${activeClaim.id}`}
                              busy={savingDecisionClaimID === activeClaim.id}
                              disabled={isSaving}
                              basis={identityPhotoEvidenceOutstanding
                                ? "The available evidence does not show a maker's mark or another detail that can verify this match."
                                : researchRationaleForDisplay(activeClaim.rationale, activeClaimEvidence)}
                              claim={{ ...activeClaim, statement: proposedIdentityLabel }}
                              gatherEvidenceDisabled={isAddingResearchPhoto || photos.length >= MAX_ITEM_PHOTOS}
                              gatherEvidenceLabel={identityPhotoEvidenceOutstanding ? "Photograph mark or base" : "Add evidence"}
                              onGatherEvidence={identityPhotoEvidenceOutstanding
                                ? () => chooseResearchPhotos("followup")
                                : () => setActiveView("brief")}
                              recommendation={candidateRecommendation}
                              onDecision={(decision) => void updateClaimDecision(activeClaim.id, decision)}
                            />
                          </section>
                          {hasVisualComparison ? (
                            <ResearchEvidencePreview
                                photo={activeComparisonPhotoView}
                                source={activeComparisonSourceView}
                                onPhotoPreview={(photoID, trigger) => openReviewImage(`seller-photo-${photoID}`, trigger)}
                                onSourcePreview={(sourceID, trigger) => openReviewImage(`evidence-${sourceID}`, trigger)}
                              />
                          ) : null}
                          {hasVisualComparison ? (
                            usesCompactResearchLayout ? null : candidateComparisonWorkbench
                          ) : (
                            <section className="research-candidate-evidence" aria-labelledby="research-candidate-evidence-heading">
                              <header>
                                <div>
                                  <h3 id="research-candidate-evidence-heading">Evidence to inspect</h3>
                                  <p>No comparable source image was available, so this lead cannot yet be selected as the item identity.</p>
                                </div>
                              </header>
                              <div className="research-candidate-evidence-body">
                                <div>{findingsOutcomeMedia}</div>
                                {sparseCandidateEvidence ? (
                                  <article className="research-candidate-source-summary">
                                    <Badge tone="neutral">
                                      {sparseCandidateEvidence.imageProvenance === "synthetic_demo" && !isDemoResearch
                                        ? "Context source"
                                        : evidenceRoleLabel(sparseCandidateEvidence)}
                                    </Badge>
                                    <h4>{researchEvidenceDisplayTitle(sparseCandidateEvidence, proposedIdentityLabel)}</h4>
                                    <p>{sparseCandidateEvidence.excerpt ?? researchEvidenceExplanation(sparseCandidateEvidence)}</p>
                                    <dl>
                                      <div><dt>Source</dt><dd>{researchSourceDisplayName(sparseCandidateEvidence)}</dd></div>
                                      <div><dt>Source type</dt><dd>{sparseCandidateEvidence.sourceCategory ? researchSourceCategoryLabels[sparseCandidateEvidence.sourceCategory] : "Not classified"}</dd></div>
                                      <div><dt>Source confidence</dt><dd>{sparseCandidateEvidence.confidence ? researchConfidenceLabels[sparseCandidateEvidence.confidence] : "Not recorded"}</dd></div>
                                    </dl>
                                    {sparseCandidateEvidence.sourceURL ? (
                                      <Button variant="secondary" size="compact" asChild>
                                        <a href={sparseCandidateEvidence.sourceURL} target="_blank" rel="noreferrer">Open source <ExternalLink size={15} /></a>
                                      </Button>
                                    ) : null}
                                  </article>
                                ) : (
                                  <div className="research-candidate-source-summary is-empty">
                                    <strong>No supporting source attached</strong>
                                    <p>Add an independent source or a clearer identity-bearing photograph before using this lead.</p>
                                  </div>
                                )}
                              </div>
                            </section>
                          )}
                          {usesCompactResearchLayout && candidateComparisonWorkbench ? (
                            <Disclosure
                              className="research-compact-comparison-disclosure"
                              label="Full evidence comparison"
                              description="Inspect every photograph and reference"
                            >
                              {candidateComparisonWorkbench}
                            </Disclosure>
                          ) : null}
                        </div>
                      </ContentFrame>
                    ) : activeClaim ? (
                      <ContentFrame size="task" className={`research-findings-state research-findings-state-${findingsMode}`}>
                        <ResearchFindingsOutcome
                          coverage={findingsCoverage}
                          headingID="research-decision-recorded-heading"
                          media={findingsOutcomeMedia}
                          primaryAction={findingsMode === "accepted" ? (
                            pendingSecondaryClaims.length > 0 ? (
                              <Button variant="primary" type="button" onClick={revealSecondaryFindings}>
                                <FileSearch size={16} /> Review {pendingSecondaryClaims.length} other {pendingSecondaryClaims.length === 1 ? "finding" : "findings"}
                              </Button>
                            ) : (
                              <Button variant="primary" type="button" onClick={() => setActiveView("listing")}><Pencil size={16} /> Apply finding</Button>
                            )
                          ) : (
                            <Button variant="primary" type="button" onClick={() => setActiveView("brief")}><Search size={16} /> Research another identification</Button>
                          )}
                          secondaryAction={findingsMode === "accepted" ? (
                            <Button variant="secondary" type="button" onClick={() => void updateClaimDecision(activeClaim.id, "pending")} disabled={isSaving}><RotateCcw size={16} /> Change identification</Button>
                          ) : (
                            <Button variant="secondary" type="button" onClick={() => void updateClaimDecision(activeClaim.id, "pending")} disabled={isSaving}><RotateCcw size={16} /> Undo rejection</Button>
                          )}
                          state={findingsMode === "accepted" ? "accepted" : "rejected"}
                          summary={findingsMode === "accepted"
                            ? activeClaim.confidence === "high"
                              ? `Evidence strength: ${researchConfidenceLabels[activeClaim.confidence].replace(" confidence", "").toLowerCase()}. Apply it to the item when ready.`
                              : "Selected provisionally. Record your own confidence before applying it to the item."
		                            : "Ruled out as this item's identification until you undo the decision."}
                          title={proposedIdentityLabel}
                        />
                        {hasVisualComparison ? (
	                          <Disclosure
                            className="research-decided-evidence"
                            label="Evidence comparison"
                            description={activeClaimEvidenceSummary || `${activeClaimEvidence.length} sources reviewed`}
                          >
	                            <ResearchFindingsWorkbench
	                              activePhoto={activeComparisonPhotoView}
	                              activeSource={activeComparisonSourceView}
                              photos={comparisonPhotos}
                              sources={comparisonSources}
                              onPhotoSelect={setComparisonPhotoID}
                              onSourceSelect={setComparisonEvidenceID}
                              onPhotoPreview={(photoID, trigger) => openReviewImage(`seller-photo-${photoID}`, trigger)}
                              onSourcePreview={(sourceID, trigger) => openReviewImage(`evidence-${sourceID}`, trigger)}
                            />
                          </Disclosure>
                        ) : null}
                      </ContentFrame>
                    ) : null}

                    {secondaryReviewClaims.length > 0 ? (
                      <Disclosure
                        id="research-secondary-findings"
                        className="research-secondary-findings"
                        label="Additional research findings"
                        description={pendingSecondaryClaims.length > 0
                          ? `${pendingSecondaryClaims.length} ${pendingSecondaryClaims.length === 1 ? "needs" : "need"} your decision`
                          : `${secondaryReviewClaims.length} reviewed`}
                        open={secondaryFindingsOpen}
                        onToggle={(event) => setSecondaryFindingsOpen(event.currentTarget.open)}
                      >
                        <div className="research-secondary-findings-list">
                          {secondaryReviewClaims.map((claim) => {
                            const claimEvidenceCount = claim.evidenceIDs.filter((evidenceID) =>
                              research.evidence.some((record) => record.id === evidenceID && record.decision === "included")
                            ).length;
                            return (
                              <ResearchDecisionCard
                                id={`research-decision-${claim.id}`}
                                key={claim.id}
                                busy={savingDecisionClaimID === claim.id}
                                basis={researchRationaleForDisplay(
                                  claim.rationale,
                                  claim.evidenceIDs.flatMap((evidenceID) => {
                                    const record = research.evidence.find((candidate) => candidate.id === evidenceID);
                                    return record ? [record] : [];
                                  })
                                )}
                                claim={claim}
                                disabled={isSaving}
                                evidenceSummary={`${claimEvidenceCount} ${claimEvidenceCount === 1 ? "source" : "sources"}`}
                                onReviewEvidence={claimEvidenceCount > 0 ? revealSourcesChecked : undefined}
                                recommendation={claim.verdict !== "contested" && claimEvidenceCount > 0
                                  ? claim.confidence === "high"
                                    ? "use"
                                    : claim.confidence === "medium"
                                      ? "provisional"
                                      : "gather"
                                  : "gather"}
                                onDecision={(decision) => void updateClaimDecision(claim.id, decision)}
                              />
                            );
                          })}
                        </div>
                      </Disclosure>
                    ) : null}

	                    {(research.evidence.length > 0 || alternativeHypotheses.length > 0 || evidenceEditorOpen) && (
		                      <ContentFrame
	                          size={findingsMode === "candidate_review" ? "workbench" : "task"}
	                          className="research-findings-supporting"
                            aria-labelledby="research-record-heading"
                            role="region"
	                        >
                          <h3 id="research-record-heading">Research record</h3>
	                          {alternativeHypotheses.length > 0 && (
                            <Disclosure
                              className="research-alternatives"
                              label={identityLabel ? "Other leads considered" : "Leads considered"}
                              meta={`${alternativeHypotheses.length}`}
                            >
                              <ul className="research-hypothesis-list">
                                {alternativeHypotheses.map((hypothesis) => (
                                  <li className="research-hypothesis-row" key={hypothesis.id}>
                                    <div><strong>{hypothesis.label}</strong><p>{researchRationaleForDisplay(hypothesis.summary, research.evidence)}</p></div>
                                    <Badge tone={confidenceTone(hypothesis.confidence)}>{researchConfidenceLabels[hypothesis.confidence]}</Badge>
                                  </li>
                                ))}
                              </ul>
                            </Disclosure>
                          )}
                          {research.evidence.length > 0 && (
	                        <Disclosure
                            ref={sourceLibraryRef}
	                          className="research-source-library"
	                          label="Sources checked"
                          meta={`${research.evidence.length}`}
                        >
	                          <div className="research-source-library-body">
                            <div className="research-source-library-heading">
                              <h4 id="research-primary-sources-heading">
                                {decisionEvidence.length > 0 ? "Reviewed for finding" : "Other sources checked"}
                              </h4>
                              <div className="research-source-library-toolbar">
                                {sourceManagementOpen ? (
                                  <>
                                    <Button variant="secondary" size="compact" type="button" onClick={(event) => beginAddingEvidence(event.currentTarget)}>
                                      <Plus size={15} /> Add source
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="compact"
                                      type="button"
                                      onClick={closeSourceManagement}
                                    >
                                      <Check size={15} /> Done
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    ref={sourceManagementTriggerRef}
                                    variant="ghost"
                                    size="compact"
                                    type="button"
                                    onClick={() => setSourceManagementOpen(true)}
                                  >
                                    <Settings size={15} /> Manage sources
                                  </Button>
                                )}
                              </div>
                            </div>
                          {decisionEvidence.length > 0 && (
                            <section aria-labelledby="research-primary-sources-heading">
                              <div className="research-source-list">
                                {decisionEvidence.map((record) => {
                                  const cited = rationaleEvidence.some((candidate) => candidate.id === record.id);
                                  return (
                                    <ResearchEvidenceSourceCard
                                      key={record.id}
                                      cited={cited}
                                      identity={identityLabel}
                                      label={evidenceRoleLabel(record)}
                                      presentSyntheticAsSource={isDemoResearch}
                                      record={record}
                                      onPreview={record.imageURL ? (trigger) => openReviewImage(`evidence-${record.id}`, trigger) : undefined}
                                      onEdit={sourceManagementOpen ? (trigger) => editEvidence(record, trigger) : undefined}
                                      onRemove={sourceManagementOpen ? () => setEvidenceRemovalID(record.id) : undefined}
                                      removePending={evidenceRemovalID === record.id}
                                      onCancelRemove={() => setEvidenceRemovalID(null)}
                                      onConfirmRemove={() => removeEvidence(record.id)}
                                    />
                                  );
                                })}
                              </div>
                            </section>
                          )}
	                          {otherEvidence.length > 0 && (
	                            <section aria-labelledby={decisionEvidence.length > 0 ? "research-checked-sources-heading" : "research-primary-sources-heading"}>
                                {decisionEvidence.length > 0 ? <h4 id="research-checked-sources-heading">Other sources checked</h4> : null}
                              <div className="research-source-list">
                                {otherEvidence.map((record) => {
                                  const cited = rationaleEvidence.some((candidate) => candidate.id === record.id);
                                  return (
                                    <ResearchEvidenceSourceCard
                                      key={record.id}
                                      cited={cited}
                                      identity={identityLabel}
	                                      label={record.relationship === "context"
	                                        ? "Background source"
	                                        : record.relationship === "contradicts"
	                                          ? "Conflicting"
	                                          : cited
	                                            ? "Cited in comparison"
	                                            : "Not used"}
                                      presentSyntheticAsSource={isDemoResearch}
                                      record={record}
                                      onPreview={record.imageURL ? (trigger) => openReviewImage(`evidence-${record.id}`, trigger) : undefined}
                                      onEdit={sourceManagementOpen ? (trigger) => editEvidence(record, trigger) : undefined}
                                      onRemove={sourceManagementOpen ? () => setEvidenceRemovalID(record.id) : undefined}
                                      removePending={evidenceRemovalID === record.id}
                                      onCancelRemove={() => setEvidenceRemovalID(null)}
                                      onConfirmRemove={() => removeEvidence(record.id)}
                                    />
                                  );
                                })}
                              </div>
                            </section>
                          )}
	                          </div>
	                        </Disclosure>
                          )}

	                      {evidenceEditorOpen && (
	                      <section className="research-evidence-editor" aria-labelledby="research-manual-evidence-heading">
                        <div className="research-workspace-heading">
                          <h3 id="research-manual-evidence-heading">{editingEvidenceID ? "Edit source" : "Add source"}</h3>
                          <Button variant="ghost" size="icon" type="button" onClick={closeEvidenceEditor} aria-label="Close source editor"><X size={17} /></Button>
                        </div>
                        <div className="form-grid research-form-grid">
                          <Field label="Evidence type">
                            <SelectControl
                              ref={evidenceEditorFirstFieldRef}
                              autoFocus
                              value={evidenceDraft.type}
                              onValueChange={(value) => setEvidenceDraft((current) => ({ ...current, type: value as ResearchEvidenceType }))}
                              options={Object.entries(researchEvidenceTypeLabels).map(([value, label]) => ({ value, label }))}
                            />
                          </Field>
                          <Field label="Source class">
                            <SelectControl
                              value={evidenceDraft.sourceCategory}
                              onValueChange={(value) => setEvidenceDraft((current) => ({ ...current, sourceCategory: value as ResearchSourceCategory }))}
                              options={Object.entries(researchSourceCategoryLabels).map(([value, label]) => ({ value, label }))}
                            />
                          </Field>
                          <Field label="Role in decision">
                            <SelectControl
                              value={evidenceDraft.relationship}
                              onValueChange={(value) => setEvidenceDraft((current) => ({ ...current, relationship: value as ResearchEvidenceRelationship }))}
                              options={Object.entries(researchRelationshipLabels).map(([value, label]) => ({ value, label }))}
                            />
                          </Field>
                          <Field label="Confidence">
                            <SelectControl
                              value={evidenceDraft.confidence}
                              onValueChange={(value) => setEvidenceDraft((current) => ({ ...current, confidence: value as ResearchConfidence }))}
                              options={Object.entries(researchConfidenceLabels).map(([value, label]) => ({ value, label }))}
                            />
                          </Field>
                          <Field label="Source" error={evidenceDraftErrors.sourceName}>
                            <Input
                              ref={evidenceSourceNameRef}
                              placeholder="eBay, auction house, catalogue"
                              value={evidenceDraft.sourceName}
                              onChange={(event) => {
                                clearEvidenceDraftError("sourceName");
                                setEvidenceDraft((current) => ({ ...current, sourceName: event.target.value }));
                              }}
                            />
                          </Field>
                          <Field label="Evidence title" error={evidenceDraftErrors.title} wide>
                            <Input
                              ref={evidenceTitleRef}
                              value={evidenceDraft.title}
                              onChange={(event) => {
                                clearEvidenceDraftError("title");
                                setEvidenceDraft((current) => ({ ...current, title: event.target.value }));
                              }}
                            />
                          </Field>
                          <Field label="Source link" error={evidenceDraftErrors.sourceURL}>
                            <Input
                              ref={evidenceSourceURLRef}
                              type="url"
                              placeholder="https://"
                              value={evidenceDraft.sourceURL}
                              onChange={(event) => {
                                clearEvidenceDraftError("sourceURL");
                                setEvidenceDraft((current) => ({ ...current, sourceURL: event.target.value }));
                              }}
                            />
                          </Field>
                          <Field label={`Price (${currency})`} error={evidenceDraftErrors.price}>
                            <Input
                              ref={evidencePriceRef}
                              inputMode="decimal"
                              value={evidenceDraft.price}
                              onChange={(event) => {
                                clearEvidenceDraftError("price");
                                setEvidenceDraft((current) => ({ ...current, price: event.target.value }));
                              }}
                            />
                          </Field>
                          <Field label="Condition"><Input value={evidenceDraft.condition} onChange={(event) => setEvidenceDraft((current) => ({ ...current, condition: event.target.value }))} /></Field>
                          <Field label="Observed date"><Input type="date" value={evidenceDraft.observedDate} onChange={(event) => setEvidenceDraft((current) => ({ ...current, observedDate: event.target.value }))} /></Field>
                          <Field label="Evidence notes" wide><Textarea rows={3} value={evidenceDraft.notes} onChange={(event) => setEvidenceDraft((current) => ({ ...current, notes: event.target.value }))} /></Field>
                        </div>
                        <div className="research-evidence-editor-actions">
                          <CheckboxField checked={evidenceDraft.included} onChange={(event) => setEvidenceDraft((current) => ({ ...current, included: event.target.checked }))} label="Include in the decision" />
                          <Button variant="primary" type="button" onClick={saveEvidenceDraft}><Plus size={16} /> {editingEvidenceID ? "Update source" : "Add source"}</Button>
	                        </div>
	                      </section>
	                      )}
	                      </ContentFrame>
	                    )}
                  </div>
                )}
              </div>
            )}

	            {activeView === "listing" && (
	              <div id="research-panel-listing" aria-labelledby="research-tab-listing" className="research-listing-workspace" role="tabpanel">
                  <ContentFrame size="task" className="research-listing-frame">
                    <Card variant="elevated" className="research-listing-card">
	                  <section className="research-workspace-section" aria-labelledby="research-identify-heading">
	                  <div className="research-workspace-heading">
	                    <div>
	                      <h3 id="research-identify-heading">Identification</h3>
	                      {hasSelectedResearchIdentity ? (
                          <p>
                            {selectedIdentityIsProvisional
                              ? `${proposedIdentityLabel} is selected provisionally.`
                              : `${proposedIdentityLabel} is selected from the research findings.`}
                          </p>
                        ) : null}
	                    </div>
	                    {!hasSelectedResearchIdentity
	                      ? <Badge tone="neutral">Not selected</Badge>
	                      : selectedIdentityIsProvisional
	                        ? <Badge tone="warning">Provisional</Badge>
	                        : null}
	                  </div>
	                  {hasSelectedResearchIdentity ? <div className="form-grid research-form-grid">
                    <Field label="Maker or brand"><Input value={research.identification.maker ?? ""} onChange={(event) => updateIdentification({ maker: event.target.value })} /></Field>
                    <Field label="Model or style"><Input value={research.identification.model ?? ""} onChange={(event) => updateIdentification({ model: event.target.value })} /></Field>
                    <Field label="Period or date"><Input value={research.identification.period ?? ""} onChange={(event) => updateIdentification({ period: event.target.value })} /></Field>
                    <Field label="Origin"><Input value={research.identification.origin ?? ""} onChange={(event) => updateIdentification({ origin: event.target.value })} /></Field>
                    <Field label="Material"><Input value={research.identification.material ?? ""} onChange={(event) => updateIdentification({ material: event.target.value })} /></Field>
	                    <Field label="Your confidence" helper="Record your judgement after reviewing the evidence.">
                      <SelectControl
                        required={hasSelectedResearchIdentity}
                        value={research.identification.sellerConfidence ?? ""}
                        onValueChange={(value) => updateIdentification({ sellerConfidence: value ? value as ResearchConfidence : undefined })}
                        options={[
                          { value: "", label: "Choose confidence" },
                          ...Object.entries(researchConfidenceLabels).map(([value, label]) => ({ value, label }))
                        ]}
                      />
                    </Field>
                    <Field
                      label="Marks or identifiers you verified"
                      helper="Only include details you can see on the item or have verified yourself."
                      wide
                    >
                      <Textarea rows={3} value={research.identification.identifiers ?? ""} onChange={(event) => updateIdentification({ identifiers: event.target.value })} />
                    </Field>
                    <Field label="Identification rationale" wide><Textarea rows={4} value={research.identification.rationale ?? ""} onChange={(event) => updateIdentification({ rationale: event.target.value })} /></Field>
	                  </div> : (
	                    <ActionPrompt
	                      icon={<CircleHelp size={18} />}
	                      title="No identification will be applied"
	                      description={canApplyResearchPricing
                          ? "An independently accepted valuation can still be applied. Maker, model, material, title, and description will remain unchanged."
                          : "No independent valuation has been accepted. Pricing can be saved as a draft, but this run will not change the item."}
	                      action={(
	                        <Button variant="secondary" size="compact" type="button" onClick={() => setActiveView("findings")}>
	                          Review findings
	                        </Button>
	                      )}
                    />
                  )}
                  </section>
                  <section className="research-workspace-section" aria-labelledby="research-refine-heading">
	                  <div className="research-workspace-heading"><h3 id="research-refine-heading">{hasSelectedResearchIdentity ? "Prepared listing" : canApplyResearchPricing ? "Pricing" : "Pricing draft"}</h3></div>
                  <div className="form-grid research-form-grid">
                    {hasSelectedResearchIdentity ? <Field label="Positioning" wide><Input placeholder="What makes this example worth choosing" value={research.refinement.positioning ?? ""} onChange={(event) => updateRefinement({ positioning: event.target.value })} /></Field> : null}
                    <Field label={`Target price (${currency})`}><Input inputMode="decimal" value={targetPrice} onChange={(event) => setTargetPrice(event.target.value)} /></Field>
                    <Field label={`Minimum price (${currency})`}><Input inputMode="decimal" value={minimumPrice} onChange={(event) => setMinimumPrice(event.target.value)} /></Field>
                    <Field label="Pricing rationale" wide><Textarea rows={3} value={research.refinement.pricingRationale ?? ""} onChange={(event) => updateRefinement({ pricingRationale: event.target.value })} /></Field>
                    {hasSelectedResearchIdentity ? (
                      <>
                        <Field label="Prepared title" wide><Input value={research.refinement.title ?? ""} onChange={(event) => updateRefinement({ title: event.target.value })} /></Field>
                        <Field label="Prepared description" wide><Textarea rows={8} value={research.refinement.description ?? ""} onChange={(event) => updateRefinement({ description: event.target.value })} /></Field>
                      </>
                    ) : null}
                  </div>
                  </section>
                    </Card>
                </ContentFrame>
              </div>
            )}

            {closePromptOpen && (
              <InlineConfirm title="Discard research changes?" description="Unsaved claim decisions, evidence, and refinement changes will be lost." actions={<><Button variant="ghost" type="button" onClick={() => setClosePromptOpen(false)}>Keep editing</Button><Button variant="danger" type="button" onClick={onClose}>Discard changes</Button></>} />
            )}
          </DialogBody>
          <DialogFooter className="research-dialog-footer">
            {hasChanges && <Button variant="secondary" type="button" onClick={() => void persistResearch(false)} disabled={isSaving || isStarting || isAddingResearchPhoto} loading={isSaving} loadingLabel="Saving">{isSaving ? "Saving" : "Save draft"}</Button>}
            {activeView === "brief" && selectedPhotos.length > 0 ? (
              <Button variant="primary" type="button" onClick={() => void startResearch()} disabled={isStarting || isResearchRunning || isAddingResearchPhoto} loading={isStarting} loadingLabel="Starting research">
                <Sparkles size={17} /> {isStarting ? "Starting research" : isResearchRunning ? "Research in progress" : run ? "Run research again" : "Start research"}
              </Button>
            ) : null}
            {activeView === "findings" && run && isPreviewOnlyRun ? (
              <Button variant="primary" type="button" onClick={() => void startResearch()} disabled={isStarting || isResearchRunning} loading={isStarting} loadingLabel="Starting"><Sparkles size={16} /> {isStarting ? "Starting" : "Start research"}</Button>
            ) : null}
            {activeView === "listing" && (hasSelectedResearchIdentity || canApplyResearchPricing) ? <Button variant="primary" type="button" onClick={() => void persistResearch(true)} disabled={isSaving || isStarting || isAddingResearchPhoto || isResearchRunning || (hasSelectedResearchIdentity && !research.identification.sellerConfidence)} loading={isSaving} loadingLabel="Applying">{isSaving ? "Applying" : hasSelectedResearchIdentity ? "Apply to item" : "Apply pricing"}</Button> : null}
          </DialogFooter>
          </Tabs>
        )}
      </DialogContent>
      <ResearchImagePreviewDialog
        activeID={previewImageID}
        images={reviewImages}
        onActiveIDChange={setPreviewImageID}
        onClose={closeReviewImage}
      />
    </Dialog>
  );
}

function ResearchPhotoPicker({
  photos,
  selectedPhotoIDs,
  error,
  isAdding,
  onSelectionChange,
  onAddPhotos,
  onEditPhoto,
  onEditButtonRef,
  onConfirm
}: {
  photos: ItemPhoto[];
  selectedPhotoIDs: string[];
  error: string | null;
  isAdding: boolean;
  onSelectionChange: (photoIDs: string[]) => void;
  onAddPhotos: () => void;
  onEditPhoto: (photoID: string) => void;
  onEditButtonRef: (photoID: string, button: HTMLButtonElement | null) => void;
  onConfirm: () => void;
}) {
  const selected = new Set(selectedPhotoIDs);
  const selectionFull = selectedPhotoIDs.length >= MAX_ITEM_RESEARCH_PHOTOS;

  function togglePhoto(photoID: string, checked: boolean) {
    if (checked) {
      if (selectionFull || selected.has(photoID)) return;
      onSelectionChange([...selectedPhotoIDs, photoID]);
      return;
    }
    onSelectionChange(selectedPhotoIDs.filter((candidate) => candidate !== photoID));
  }

  return (
    <>
      <DialogBody className="research-photo-picker-body">
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="research-photo-picker-head">
          <div><strong>{selectedPhotoIDs.length} selected</strong><small>Up to {MAX_ITEM_RESEARCH_PHOTOS} per research run. Originals are recommended; use close-ups for marks and distinguishing details.</small></div>
          {photos.length < MAX_ITEM_PHOTOS && <Button variant="secondary" size="compact" type="button" onClick={onAddPhotos} disabled={isAdding} loading={isAdding} loadingLabel="Adding photos"><ImagePlus size={16} /> {isAdding ? "Adding photos" : "Add new photos"}</Button>}
        </div>
        <div className="research-photo-picker-grid" role="group" aria-label="Item photos available for research">
          {photos.map((photo, index) => {
            const checked = selected.has(photo.id);
            return (
              <div className={`research-photo-picker-card${checked ? " is-selected" : ""}`} key={photo.id}>
                <CheckboxField
                  className="research-photo-picker-option"
                  checked={checked}
                  disabled={!checked && selectionFull}
                  onChange={(event) => togglePhoto(photo.id, event.target.checked)}
                  label={
                    <span className="research-photo-picker-option-content">
                      <img src={photo.thumbnailURL || photo.url} alt="" style={photoStyle(photo.treatment)} />
                      <span><strong>{researchPhotoDisplayLabel(photos, photo, index)}</strong><small>{researchPhotoTypeLabels[photo.type]}</small></span>
                    </span>
                  }
                />
                <Button
                  ref={(button) => onEditButtonRef(photo.id, button)}
                  variant="secondary"
                  size="compact"
                  type="button"
                  className="research-photo-picker-edit"
                  aria-label={`Edit ${researchPhotoDisplayLabel(photos, photo, index)}`}
                  onClick={() => onEditPhoto(photo.id)}
                  disabled={isAdding}
                >
                  <Pencil size={15} /> Edit
                </Button>
              </div>
            );
          })}
        </div>
        {photos.length >= MAX_ITEM_PHOTOS && <EmptyNote>This item has the maximum of {MAX_ITEM_PHOTOS} saved photos.</EmptyNote>}
      </DialogBody>
      <DialogFooter>
        <Button variant="primary" type="button" onClick={onConfirm} disabled={selectedPhotoIDs.length === 0 || isAdding}>Use {selectedPhotoIDs.length} {selectedPhotoIDs.length === 1 ? "photo" : "photos"}</Button>
      </DialogFooter>
    </>
  );
}

function makeResearchEvidenceID() {
  return `evidence_${globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`}`;
}

function researchRunStatusLabel(status: ResearchRunStatus | undefined) {
  const labels: Record<ResearchRunStatus, string> = {
    queued: "Queued",
    researching: "Researching",
    verifying: "Verifying",
    needs_input: "More evidence needed",
    ready: "Ready",
    failed: "Failed"
  };
  return status ? labels[status] ?? "Not started" : "Not started";
}

function researchRunStatusTone(status: ResearchRunStatus | undefined): ComponentProps<typeof Badge>["tone"] {
  if (status === "ready") return "success";
  if (status === "failed") return "danger";
  if (status === "needs_input") return "warning";
  if (status === "queued" || status === "researching" || status === "verifying") return "info";
  return "neutral";
}

function confidenceTone(confidence: ResearchConfidence): ComponentProps<typeof Badge>["tone"] {
  if (confidence === "high") return "success";
  if (confidence === "medium") return "info";
  return "warning";
}

function claimVerdictTone(verdict: ItemResearchClaim["verdict"]): ComponentProps<typeof Badge>["tone"] {
  if (verdict === "supported") return "success";
  if (verdict === "contested") return "warning";
  return "neutral";
}

function formatCompactDate(value: string) {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "Unknown date";
}

function formatCompactDateTime(value: string) {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "Unknown date";
}

function formatResearchActivity(value: string, running: boolean) {
  const updatedAt = new Date(value).getTime();
  if (!Number.isFinite(updatedAt)) return "Activity time unavailable";
  if (!running) return `Updated ${formatCompactDateTime(value)}`;
  const elapsedSeconds = Math.max(0, Math.round((Date.now() - updatedAt) / 1_000));
  if (elapsedSeconds < 10) return "Last activity just now";
  if (elapsedSeconds < 60) return `Last activity ${elapsedSeconds} seconds ago`;
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return `Last activity ${elapsedMinutes} ${elapsedMinutes === 1 ? "minute" : "minutes"} ago`;
  return `Last activity ${formatCompactDateTime(value)}`;
}

function EditInventoryItemDialog({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const store = useReOS();
  const toast = useToast();
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description);
  const [sku, setSku] = useState(item.sku);
  const [categoryID, setCategoryID] = useState(item.categoryID);
  const [condition, setCondition] = useState<ItemCondition>(item.condition);
  const [brand, setBrand] = useState(item.brand ?? "");
  const [size, setSize] = useState(item.size ?? "");
  const [color, setColor] = useState(item.color ?? "");
  const [material, setMaterial] = useState(item.material ?? "");
  const [stockLocation, setStockLocation] = useState(item.stockLocation ?? "");
  const [purchaseCost, setPurchaseCost] = useState(purchaseCostInput(item));
  const [targetPrice, setTargetPrice] = useState(moneyInputValue(item.targetPrice));
  const [notes, setNotes] = useState(item.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const currency = item.targetPrice.currency;
  const hasChanges =
    title !== item.title ||
    description !== item.description ||
    sku !== item.sku ||
    categoryID !== item.categoryID ||
    condition !== item.condition ||
    brand !== (item.brand ?? "") ||
    size !== (item.size ?? "") ||
    color !== (item.color ?? "") ||
    material !== (item.material ?? "") ||
    stockLocation !== (item.stockLocation ?? "") ||
    purchaseCost !== purchaseCostInput(item) ||
    targetPrice !== moneyInputValue(item.targetPrice) ||
    notes !== (item.notes ?? "");

  function requestClose() {
    if (isSaving) {
      return;
    }
    if (hasChanges) {
      setClosePromptOpen(true);
      return;
    }
    onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    let purchaseCostMinor = 0;
    try { purchaseCostMinor = purchaseCost.trim() ? parseCatalogueCost(purchaseCost) : 0; }
    catch (caught) { setError(userSafeErrorMessage(caught, "Check the purchase cost.")); return; }
    const targetPriceMinor = parseMoneyInput(targetPrice, currency).amountMinor;
    if (!title.trim() || !description.trim() || !sku.trim() || !categoryID.trim()) {
      setError("Complete the title, description, SKU, and category.");
      return;
    }
    if (targetPrice !== moneyInputValue(item.targetPrice) && targetPriceMinor <= 0) {
      setError("Target price must be greater than zero.");
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      await store.updateInventoryItem(item.id, {
        title,
        description,
        sku,
        categoryID,
        condition,
        brand: brand || null,
        size: size || null,
        color: color || null,
        material: material || null,
        stockLocation: stockLocation || null,
        ...(purchaseCost !== purchaseCostInput(item) ? { purchaseCostMinor, purchaseCostStatus: purchaseCost.trim() ? "known" as const : "unknown" as const } : {}),
        ...(targetPrice !== moneyInputValue(item.targetPrice) ? { targetPriceMinor } : {}),
        notes: notes || null
      });
      toast.notify({ title: "Item updated", tone: "success" });
      onClose();
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to update this item."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent className="ui-dialog-content-wizard" leadingClose showClose={false}>
        <DialogHeader>
          <Button variant="secondary" size="icon" type="button" onClick={requestClose} aria-label="Close edit item" disabled={isSaving}><X size={20} /></Button>
          <DialogTitle>Edit item</DialogTitle>
          <DialogDescription className="sr-only">Update the canonical inventory details used by Listwork.</DialogDescription>
        </DialogHeader>
        <FormLayout onSubmit={(event) => void submit(event)}>
          <DialogBody className="ui-dialog-body-wizard">
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="form-grid">
              <Field label="Item title" wide><Input required value={title} onChange={(event) => setTitle(event.target.value)} /></Field>
              <Field label="SKU"><Input required value={sku} onChange={(event) => setSku(event.target.value)} /></Field>
              <Field label="Category"><Input required list="edit-item-category-options" value={categoryID} onChange={(event) => setCategoryID(event.target.value)} /></Field>
              <datalist id="edit-item-category-options">{store.categories.map((category) => <option key={category.id} value={category.path} />)}</datalist>
              <Field label="Condition">
                <SelectControl
                  required
                  value={condition}
                  onValueChange={(value) => setCondition(value as ItemCondition)}
                  options={Object.entries(conditionLabels).map(([value, label]) => ({ value, label }))}
                />
              </Field>
              <Field label="Brand"><Input value={brand} onChange={(event) => setBrand(event.target.value)} /></Field>
              <Field label="Size"><Input value={size} onChange={(event) => setSize(event.target.value)} /></Field>
              <Field label="Colour"><Input value={color} onChange={(event) => setColor(event.target.value)} /></Field>
              <Field label="Material"><Input value={material} onChange={(event) => setMaterial(event.target.value)} /></Field>
              <Field label="Purchase cost" helper="Leave blank if unknown. Enter 0 for free stock."><Input inputMode="decimal" placeholder="Unknown" value={purchaseCost} onChange={(event) => setPurchaseCost(event.target.value)} /></Field>
              <Field label="Target price"><Input required inputMode="decimal" value={targetPrice} onChange={(event) => setTargetPrice(event.target.value)} /></Field>
              <Field label="Stock location" wide><Input value={stockLocation} onChange={(event) => setStockLocation(event.target.value)} /></Field>
              <Field label="Item description" wide><Textarea required rows={5} value={description} onChange={(event) => setDescription(event.target.value)} /></Field>
              <Field label="Notes" wide><Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} /></Field>
            </div>
            {closePromptOpen && (
              <InlineConfirm
                title="Discard item changes?"
                description="Your unsaved inventory edits will be lost."
                actions={
                  <>
                    <Button variant="ghost" type="button" onClick={() => setClosePromptOpen(false)}>Keep editing</Button>
                    <Button variant="danger" type="button" onClick={onClose}>Discard changes</Button>
                  </>
                }
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={requestClose} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={isSaving} loading={isSaving} loadingLabel="Saving">{isSaving ? "Saving" : "Save item"}</Button>
          </DialogFooter>
        </FormLayout>
      </DialogContent>
    </Dialog>
  );
}

function InventoryListingChannelDialog({ item, mobile, onClose, onManual, onCreated }: {
  item: InventoryItem;
  mobile: boolean;
  onClose: () => void;
  onManual: (accountID: string) => void;
  onCreated: (listing: MarketplaceListing) => void;
}) {
  const store = useReOS();
  const [accountID, setAccountID] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const returnFocus = useRef(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  const [mobilePresentation, setMobilePresentation] = useState(mobile);
  useEffect(() => {
    // The detail sheet's portal mounts after its parent render. Re-mount the
    // chooser above it on the following frame while retaining the chosen channel.
    const frame = window.requestAnimationFrame(() => setMobilePresentation(mobile));
    return () => window.cancelAnimationFrame(frame);
  }, [mobile]);
  const accountOrder: Marketplace[] = ["ebay", "etsy", "shopify", "vinted", "depop"];
  const accounts = [...store.marketplaceAccounts].sort((a, b) => accountOrder.indexOf(a.marketplace) - accountOrder.indexOf(b.marketplace));
  async function proceed(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const account = accounts.find((entry) => entry.id === accountID);
    if (!account || saving) return;
    const reason = manualListingEligibility(store.database, item.id, account);
    if (reason) { setError(reason); return; }
    if (isManualListingAccount(account)) { onManual(account.id); return; }
    setSaving(true); setError(undefined);
    try {
      const existing = store.listingsFor(item.id).find((listing) => listing.marketplaceAccountID === account.id && ["draft", "ready", "failed"].includes(listing.status));
      const listing = existing ?? (await store.createListingDrafts(item.id, [account.marketplace]))[0];
      if (!listing) throw new Error("The draft could not be opened. Try again.");
      onCreated(listing);
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "The draft could not be saved. Try again."));
    } finally { setSaving(false); }
  }
  return <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
    {/* Re-establish the top modal when the underlying mobile detail sheet mounts.
        Selection and save state stay in this component across the presentation change. */}
    <DialogContent key={mobilePresentation ? "mobile" : "desktop"} className="inventory-channel-dialog" showClose={!saving} onCloseAutoFocus={(event) => {
      event.preventDefault();
      window.requestAnimationFrame(() => {
        if (document.querySelector(".inventory-channel-dialog")) return;
        const trigger = returnFocus.current?.isConnected ? returnFocus.current : document.querySelector<HTMLElement>(".inventory-item-detail [data-create-listing]");
        trigger?.focus({ preventScroll: true });
      });
    }}>
      <DialogHeader><DialogTitle>Create a listing</DialogTitle><DialogDescription>Choose a channel. You can tailor the draft before publishing.</DialogDescription></DialogHeader>
      <FormLayout onSubmit={(event) => void proceed(event)}>
        <DialogBody>
          {error && <Alert tone="danger">{error}</Alert>}
          <fieldset className="inventory-channel-choices" disabled={saving}>
            <legend className="sr-only">Sales channel</legend>
            {accounts.map((account) => {
              const reason = manualListingEligibility(store.database, item.id, account);
              const draft = store.listingsFor(item.id).some((listing) => listing.marketplaceAccountID === account.id && ["draft", "ready", "failed"].includes(listing.status));
              const state = reason ?? (draft ? "Draft saved" : isManualListingAccount(account) ? "Manual listing" : account.connectionStatus === "connected" ? "Connected" : "Not connected");
              return <label key={account.id} className="inventory-channel-choice" data-selected={account.id === accountID} data-disabled={Boolean(reason)}>
                <span className="inventory-channel-mark"><MarketplaceMark marketplace={account.marketplace} /></span>
                <span><strong>{marketplaceLabels[account.marketplace]}</strong><small>{state}</small></span>
                <RadioInput name="listing-channel" value={account.id} checked={account.id === accountID} disabled={Boolean(reason)} aria-label={marketplaceLabels[account.marketplace]} onChange={() => { setAccountID(account.id); setError(undefined); }} />
              </label>;
            })}
          </fieldset>
        </DialogBody>
        <DialogFooter><Button type="submit" variant="primary" disabled={!accountID || saving} loading={saving} loadingLabel="Opening draft">Continue<ChevronRight size={18} /></Button><p className="inventory-draft-hint">Saving a draft does not publish it.</p></DialogFooter>
      </FormLayout>
    </DialogContent>
  </Dialog>;
}

function CreateListingDraftsDialog({
  item,
  existingListings,
  onClose,
  onCreated,
  onlyDirect = false
}: {
  onlyDirect?: boolean;
  item: InventoryItem;
  existingListings: MarketplaceListing[];
  onClose: () => void;
  onCreated: (listings: MarketplaceListing[]) => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const unavailable = new Set(existingListings.filter((listing) => !["sold", "ended", "delisted", "archived"].includes(listing.status)).map((listing) => listing.marketplace));
  const available = marketplaces.filter((marketplace) => !unavailable.has(marketplace) && (!onlyDirect || store.marketplaceAccounts.some((account) => account.marketplace === marketplace && !isManualListingAccount(account))));
  const [selected, setSelected] = useState<Marketplace[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selected.length === 0) {
      setError("Choose at least one marketplace.");
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      const listings = await store.createListingDrafts(item.id, selected);
      toast.notify({ title: listings.length === 1 ? "Listing draft prepared" : `${listings.length} listing drafts prepared`, tone: "success" });
      onCreated(listings);
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to prepare these listings."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
      <DialogContent className="ui-dialog-content-sale" leadingClose showClose={false}>
        <DialogHeader>
          <Button variant="secondary" size="icon" type="button" onClick={onClose} aria-label="Close prepare listing" disabled={isSaving}><X size={20} /></Button>
          <DialogTitle>Prepare listings</DialogTitle>
          <DialogDescription>Choose marketplaces for {item.title}. Drafts can be edited before posting.</DialogDescription>
        </DialogHeader>
        <FormLayout onSubmit={(event) => void submit(event)}>
          <DialogBody className="ui-dialog-body-sale">
            {error && <Alert tone="danger">{error}</Alert>}
            {available.length > 0 ? (
              <ChoiceGroup label="Marketplaces to prepare">
                {available.map((marketplace) => (
                  <ChipButton
                    key={marketplace}
                    type="button"
                    selected={selected.includes(marketplace)}
                    onClick={() => {
                      setError(null);
                      setSelected((current) => current.includes(marketplace) ? current.filter((candidate) => candidate !== marketplace) : [...current, marketplace]);
                    }}
                  >
                    {marketplaceLabels[marketplace]}
                  </ChipButton>
                ))}
              </ChoiceGroup>
            ) : (
              <EmptyNote>Every supported marketplace already has an active listing record for this item.</EmptyNote>
            )}
            {store.photosFor(item.id).length === 0 && <Alert tone="warning">This draft will still need a product photo before it can be posted.</Alert>}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={onClose} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={isSaving || available.length === 0 || selected.length === 0} loading={isSaving} loadingLabel="Preparing">{isSaving ? "Preparing" : "Create drafts"}</Button>
          </DialogFooter>
        </FormLayout>
      </DialogContent>
    </Dialog>
  );
}

function ListingRow({
  listing,
  selected,
  rowRef,
  onClick
}: {
  listing: MarketplaceListing;
  selected: boolean;
  rowRef?: Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  const store = useReOS();
  const item = store.itemFor(listing.itemID);
  const photo = store.primaryPhoto(listing.itemID);
  const account = store.marketplaceAccounts.find((candidate) => candidate.id === listing.marketplaceAccountID);
  const content = connectedListingContent(listing);
  return (
    <DataRow className="listing-compact-row" responsive="inline" ref={rowRef} selected={selected} onClick={onClick}>
      <PhotoFrame photo={photo} previewURL={content.photos[0]} />
      <DataRowMain>
        <strong>{content.title}</strong>
        <DataRowBadges>
          <MarketplaceBadge marketplace={listing.marketplace} />
          <Badge tone={listingStatusTone(listing.status)}>{listingStatusLabels[listing.status]}</Badge>
        </DataRowBadges>
        <DataRowMeta>{listing.status === "failed" ? "Update needs attention" : listingTelemetryLabel(listing, account) ?? (item ? `${item.quantity} ${item.quantity === 1 ? "unit" : "units"} in stock` : "Inventory item unavailable")}</DataRowMeta>
      </DataRowMain>
      <DataRowValue className="ui-data-row-value-workbench">
        <span>{formatMoney(content.price)}</span>
        <ChevronRight size={16} aria-hidden="true" />
      </DataRowValue>
    </DataRow>
  );
}

function listingOperationalMeta(listing: MarketplaceListing, item?: InventoryItem, account?: MarketplaceAccount) {
  const sku = item?.sku ?? "No SKU";
  const telemetry = listingTelemetryLabel(listing, account);
  return `${sku} · ${telemetry ?? listingManualStatusLabel(listing)}`;
}

function listingTelemetryLabel(listing: MarketplaceListing, account?: MarketplaceAccount) {
  const hasConnectedTelemetry = account?.integrationMode === "api" && account.connectionStatus === "connected" && !isManualListingAccount(account) && !listing.manualRecordedAt;
  const views = listing.views;
  const watchers = listing.watchers ?? listing.likes;
  if (!hasConnectedTelemetry || (views === undefined && watchers === undefined)) {
    return undefined;
  }
  return `${views ?? 0} views · ${watchers ?? 0} watchers`;
}

function listingManualStatusLabel(listing: MarketplaceListing) {
  if (listing.status === "draft") {
    return "Draft";
  }
  if (listing.status === "ready") {
    return listing.externalURL ? "URL saved" : "Ready for marketplace";
  }
  if (listing.status === "live") {
    if (listing.manualRecordedAt) return listing.externalURL ? "Recorded manually · URL saved" : "Recorded manually";
    return listing.externalURL ? "Live URL saved" : "Needs live URL";
  }
  if (listing.status === "sold") {
    return "Sale recorded";
  }
  if (["ended", "archived", "delisted"].includes(listing.status)) {
    return "Closed in Listwork";
  }
  if (listing.status === "failed") {
    return "Needs changes";
  }
  return "Tracked listing";
}

const directlyPublishableMarketplaces = new Set<Marketplace>(["ebay", "etsy", "shopify"]);

const guidedMarketplacePublishURLs: Partial<Record<Marketplace, string>> = {
  ebay: "https://www.ebay.co.uk/sl/sell",
  etsy: "https://www.etsy.com/your/shops/me/tools/listings/create",
  shopify: "https://admin.shopify.com/",
  vinted: "https://www.vinted.co.uk/items/new",
  depop: "https://www.depop.com/products/create/"
};

function ListingDetail({
  listing,
  focus,
  onBack,
  onOpenItem,
  onOpenOrder,
  onRecordSale,
  onEditConnected,
  onPublishEbay
}: {
  listing: MarketplaceListing;
  focus: Extract<NotificationFocus, { view: "listings" }> | null;
  onBack: () => void;
  onOpenItem: (itemID: string) => void;
  onOpenOrder: (saleID: string) => void;
  onRecordSale: () => void;
  onEditConnected: () => void;
  onPublishEbay: () => void;
}) {
  const store = useReOS();
  const toast = useToast();
  const externalURLInputRef = useRef<HTMLInputElement>(null);
  const [manualListingOpen, setManualListingOpen] = useState(false);
  const [photoProgress, setPhotoProgress, reportPhotoFailure, sharedPhotoError] = usePhotoUploadProgress(`${listing.title}: listing photos`, listing.id);
  const photoUploadOwnerRef = useRef(listing.id);
  photoUploadOwnerRef.current = listing.id;
  const item = store.itemFor(listing.itemID);
  const account = store.marketplaceAccounts.find((candidate) => candidate.id === listing.marketplaceAccountID);
  const photo = store.primaryPhoto(listing.itemID);
  const itemPhotos = store.photosFor(listing.itemID);
  const [externalURL, setExternalURL] = useState(listing.externalURL ?? "");
  const [endPromptOpen, setEndPromptOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isApplyingResearch, setIsApplyingResearch] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isSavingURL, setIsSavingURL] = useState(false);
  const [isMarkingLive, setIsMarkingLive] = useState(false);
  const [editDraftOpen, setEditDraftOpen] = useState(false);
  const [draftUpdatedAfterFailure, setDraftUpdatedAfterFailure] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [localPhotoError, setPhotoError] = useState<string | null>(null);
  const photoError = localPhotoError ?? sharedPhotoError;
  const [photoNotice, setPhotoNotice] = useState<{ tone: "success" | "warning"; message: string } | null>(null);
  const [photoReviewID, setPhotoReviewID] = useState<string | null>(null);
  const [photoActionStarted, setIsAddingPhotos] = useState(false);
  const isAddingPhotos = photoActionStarted || photoProgress !== null;
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const listingPhotoInputRef = useRef<HTMLInputElement>(null);
  const listingPhotoActionRef = useRef<HTMLButtonElement>(null);
  const listingPhotoReturnFocusRef = useRef<HTMLButtonElement | null>(null);
  const listingCopyHeadingID = `listing-copy-${listing.id}`;

  useEffect(() => {
    setExternalURL(listing.externalURL ?? "");
    setIsAddingPhotos(false);
    setManualListingOpen(false);
    setEndPromptOpen(false);
    setEditDraftOpen(false);
    setDraftUpdatedAfterFailure(false);
    setActionError(null);
    setPhotoError(null);
    setPhotoNotice(null);
    setPhotoReviewID(null);
    listingPhotoReturnFocusRef.current = null;
  }, [listing.id, listing.externalURL]);

  const listingIsClosed = ["sold", "ended", "archived", "delisted"].includes(listing.status);
  const listingIsLive = listing.status === "live";
  const listingCanBeEdited = ["draft", "ready", "failed"].includes(listing.status);
  const researchedCopy = item?.research?.status === "refined" ? item.research.refinement : undefined;
  const researchCopyDiffers = Boolean(
    listingCanBeEdited &&
      researchedCopy?.title &&
      researchedCopy.description &&
      (researchedCopy.title !== listing.title ||
        researchedCopy.description !== cleanSellerListingDescription(listing.description, listing.title) ||
        (researchedCopy.targetPrice && researchedCopy.targetPrice.amountMinor !== listing.price.amountMinor))
  );
  const itemIsSold = item?.status === "sold";
  const trimmedExternalURL = externalURL.trim();
  const hasExternalURL = trimmedExternalURL.length > 0;
  const listingURLValidationError = hasExternalURL ? marketplaceListingURLValidationError(listing.marketplace, trimmedExternalURL) : undefined;
  const hasValidExternalURL = hasExternalURL && !listingURLValidationError;
  const listingURLIsInvalid = hasExternalURL && !hasValidExternalURL;
  const canMarkLive = !itemIsSold && !listingIsClosed && !listingIsLive && hasValidExternalURL;
  const canRecordSale = Boolean(item && item.status !== "sold" && item.status !== "archived" && listingIsLive);
  const canEndListing = !itemIsSold && listingIsLive;
  const supportsDirectPublishing = listing.marketplace === "ebay"
    ? account?.connectionStatus === "connected" && account.integrationMode === "api" && store.persistenceMode === "api"
    : listing.marketplace === "etsy"
      ? store.etsyIntegration?.readiness.ready === true
    : directlyPublishableMarketplaces.has(listing.marketplace) && account?.integrationMode === "api" && account.connectionStatus === "connected";
  const guidedPublishURL = guidedMarketplacePublishURLs[listing.marketplace];
  const hasPublishCategory = listing.marketplace !== "etsy" || /^\d+$/.test(listing.categoryExternalID?.trim() ?? "");
  const hasEtsyProvenance = listing.marketplace !== "etsy" || Boolean(
    listing.etsyWhoMade && listing.etsyWhenMade && typeof listing.etsyIsSupply === "boolean"
  );
  const canPublish = !itemIsSold && !listingIsClosed && !listingIsLive && Boolean(item) && itemPhotos.length > 0 && hasPublishCategory && hasEtsyProvenance;
  const showExternalURLField = !listingIsClosed || hasExternalURL;
  const liveSaleUnavailable = item?.status === "archived"
    ? {
        title: "Restore the item before recording a sale",
        body: "Archived stock cannot be marked sold from a listing."
      }
    : {
        title: "Inventory item needed before sale",
        body: "Reconnect this listing to its inventory item before recording an order."
      };
  const sellerDescription = cleanSellerListingDescription(listing.description, listing.title);
  const relatedSale = useMemo(
    () =>
      store.sales.find((sale) => sale.listingID === listing.id) ??
      (listing.status === "sold" ? store.sales.find((sale) => sale.itemID === listing.itemID && sale.marketplace === listing.marketplace) : undefined),
    [listing.id, listing.itemID, listing.marketplace, listing.status, store.sales]
  );
  const externalURLHelper =
    listingURLIsInvalid
      ? listingURLValidationError
      : itemIsSold && !listingIsClosed
      ? "Paste the live URL if you need to open the marketplace listing before ending it."
      : account && isManualListingAccount(account)
        ? "Optional — add the marketplace link when available."
      : !listingIsClosed && listing.status !== "live" && trimmedExternalURL.length === 0
        ? "Paste live URL to mark live."
        : undefined;

  function openExternalListing() {
    window.open(trimmedExternalURL, "_blank", "noopener,noreferrer");
  }

  async function saveExternalURL() {
    if (isSavingURL) return;
    if ((listing.externalURL ?? "") === trimmedExternalURL) {
      return;
    }
    if (listingURLIsInvalid) {
      setActionError(listingURLValidationError ?? "Paste a valid marketplace listing URL.");
      window.requestAnimationFrame(() => externalURLInputRef.current?.focus({ preventScroll: true }));
      return;
    }
    try {
      setIsSavingURL(true);
      setActionError(null);
      await store.updateListingURL(listing.id, trimmedExternalURL);
      toast.notify({ title: "Listing URL saved", tone: "success" });
    } catch (error: unknown) {
      setActionError(userSafeErrorMessage(error, "Unable to save this listing URL."));
    } finally {
      setIsSavingURL(false);
    }
  }

  async function markListingLive() {
    if (isMarkingLive) return;
    if (!hasValidExternalURL) {
      setActionError(listingURLValidationError ?? "Paste the live marketplace listing URL before marking this listing live.");
      window.requestAnimationFrame(() => externalURLInputRef.current?.focus({ preventScroll: true }));
      return;
    }
    try {
      setIsMarkingLive(true);
      setActionError(null);
      await store.markListingLive(listing.id, trimmedExternalURL);
      toast.notify({ title: "Listing marked live", tone: "success" });
    } catch (error: unknown) {
      setActionError(userSafeErrorMessage(error, "Unable to mark this listing live."));
    } finally {
      setIsMarkingLive(false);
    }
  }

  async function publishListing() {
    setIsPublishing(true);
    try {
      setActionError(null);
      const publication = await store.publishListing(listing.id);
      setExternalURL(publication.externalURL);
      toast.notify({
        title: publication.mode === "sandbox" ? "Sandbox listing complete" : `Listed on ${marketplaceLabels[listing.marketplace]}`,
        description: publication.mode === "sandbox" ? "The local connector completed the full listing workflow without contacting the marketplace." : "The live listing and stock record are now linked.",
        tone: "success"
      });
    } catch (error: unknown) {
      setActionError(userSafeErrorMessage(error, "Unable to list this item."));
    } finally {
      setIsPublishing(false);
    }
  }

  async function applyResearchedCopy() {
    if (!researchedCopy?.title || !researchedCopy.description) return;
    try {
      setIsApplyingResearch(true);
      setActionError(null);
      await store.updateListingDraft(listing.id, {
        title: researchedCopy.title,
        description: researchedCopy.description,
        priceMinor: researchedCopy.targetPrice?.amountMinor ?? listing.price.amountMinor
      });
      toast.notify({ title: "Researched copy applied", description: `The ${marketplaceLabels[listing.marketplace]} draft now uses the confirmed item research.`, tone: "success" });
    } catch (caught) {
      setActionError(userSafeErrorMessage(caught, "Unable to apply the researched copy."));
    } finally {
      setIsApplyingResearch(false);
    }
  }

  function openGuidedPublish() {
    if (guidedPublishURL) {
      window.open(guidedPublishURL, "_blank", "noopener,noreferrer");
    }
  }

  async function endListing() {
    setIsEnding(true);
    try {
      setActionError(null);
      await store.endListing(listing.id);
      setEndPromptOpen(false);
      toast.notify({ title: "Listing ended", tone: "success" });
    } catch (error: unknown) {
      setActionError(userSafeErrorMessage(error, "Unable to end this listing."));
    } finally {
      setIsEnding(false);
    }
  }

  async function addListingPhotoFiles(event: ChangeEvent<HTMLInputElement>) {
    if (isAddingPhotos) return;
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (selectedFiles.length === 0 || !item) {
      return;
    }
    const availableSlots = Math.max(0, MAX_ITEM_PHOTOS - itemPhotos.length);
    if (availableSlots === 0) {
      setPhotoError(`This item already has the maximum of ${MAX_ITEM_PHOTOS} photos.`);
      return;
    }
    const files = selectedFiles.slice(0, availableSlots);
    const skippedCount = selectedFiles.length - files.length;
    setIsAddingPhotos(true);
    setPhotoError(null);
    setPhotoNotice(null);
    try {
      const result = await readPhotoDraftBatch(files, setPhotoProgress);
      if (result.drafts.length > 0) {
        const savedPhotos = await store.addItemPhotos(item.id, result.drafts.map(toPhotoInput), setPhotoProgress);
        toast.notify({ title: savedPhotos.length === 1 ? "Photo added" : "Photos added", tone: "success" });
      }
      if (photoUploadOwnerRef.current !== listing.id) return;
      if (result.errors.length > 0) {
        setPhotoError(photoDraftReadErrorMessage(result.errors));
      }
      setPhotoNotice(photoDraftReadNotice(result.convertedHeicCount, skippedCount, result.drafts.length));
    } catch (caught) {
      reportPhotoFailure(photoUploadErrorMessage(caught, "Unable to add these photos."));
      if (photoUploadOwnerRef.current === listing.id) setPhotoError(photoUploadErrorMessage(caught, "Unable to add these photos."));
    } finally {
      if (photoUploadOwnerRef.current === listing.id) setIsAddingPhotos(false);
      setPhotoProgress(null);
    }
  }

  async function copyListingDescription() {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard unavailable.");
      }
      await navigator.clipboard.writeText(sellerDescription);
      toast.notify({ title: "Description copied", tone: "success" });
    } catch {
      descriptionTextareaRef.current?.focus();
      descriptionTextareaRef.current?.select();
      toast.notify({
        title: "Description selected",
        description: "Use your device copy command to place it on the clipboard.",
        tone: "info"
      });
    }
  }

  if (item && listing.marketplace === "ebay" && !listing.externalListingID && ["draft", "ready", "failed"].includes(listing.status) && !["sold", "archived"].includes(item.status)) return <>
    <ListingDraftWorkbench key={listing.id} listing={listing} item={item} photos={itemPhotos} addingPhotos={isAddingPhotos}
      onBack={onBack} onOpenItem={() => onOpenItem(item.id)} onReview={onPublishEbay}
      onAddPhotos={() => listingPhotoInputRef.current?.click()}
      onPhoto={(selected, trigger) => { listingPhotoReturnFocusRef.current = trigger; setPhotoReviewID(selected.id); }}
      photoFeedback={<><PhotoUploadStatus progress={photoProgress} />{photoError && <Alert tone="danger">{photoError}</Alert>}{photoNotice && <Alert tone={photoNotice.tone}>{photoNotice.message}</Alert>}</>} />
    <FileInput ref={listingPhotoInputRef} className="sr-only" accept={PHOTO_FILE_ACCEPT} multiple aria-label={`Choose photos for ${item.title}`} onChange={(event) => void addListingPhotoFiles(event)} />
    {photoReviewID && itemPhotos.length > 0 && <ItemPhotoReviewDialog item={item} photos={itemPhotos} initialPhotoID={photoReviewID}
      onAddPhotos={() => { setPhotoReviewID(null); window.setTimeout(() => listingPhotoInputRef.current?.click(), 0); }}
      onClose={() => setPhotoReviewID(null)} onReturnFocus={() => window.requestAnimationFrame(() => listingPhotoReturnFocusRef.current?.focus())} />}
  </>;
  if ((listing.managementMode === "read_only" || listing.managementMode === "managed" && listing.observedChannel) && ["ebay", "etsy"].includes(listing.marketplace) && listing.externalListingID) return <ConnectedListingDetail key={listing.id} listing={listing} onEdit={onEditConnected} onBack={onBack} onOpenItem={onOpenItem} onRecordSale={onRecordSale} />;
  return (
    <Panel as="article" className="ui-panel-detail-card">
      <Button variant="ghost" className="mobile-back" type="button" onClick={onBack} aria-label="Back to listings">
        <ArrowLeft size={18} /> Listings
      </Button>
      {photo && <PhotoFrame photo={photo} large alt={listing.title} />}
      <div className="detail-title">
        <h2>{listing.title}</h2>
        <strong>{formatMoney(listing.price)}</strong>
      </div>
      <DataRowBadges>
        <MarketplaceBadge marketplace={listing.marketplace} />
        <Badge tone={listingStatusTone(listing.status)}>{listing.status === "failed" ? "Publish failed" : listingStatusLabels[listing.status]}</Badge>
      </DataRowBadges>
      {focus?.notice && <NotificationFocusCallout notice={focus.notice} />}
      {showExternalURLField && (
        <Field label="Marketplace listing URL" className="ui-field-listing-url" helper={isSavingURL ? <span role="status">Saving listing link…</span> : externalURLHelper}>
          <Input
            ref={externalURLInputRef}
            disabled={isSavingURL}
            value={externalURL}
            onChange={(event) => setExternalURL(event.target.value)}
            onBlur={() => void saveExternalURL()}
            placeholder="Paste marketplace URL"
            aria-invalid={listingURLIsInvalid || undefined}
          />
        </Field>
      )}
      <div className="detail-actions listing-detail-actions">
        {listingCanBeEdited && (
          <Button variant="secondary" type="button" onClick={() => setEditDraftOpen(true)}>
            <Pencil size={18} /> Edit listing
          </Button>
        )}
        {researchCopyDiffers && (
          <Button variant="secondary" type="button" onClick={() => void applyResearchedCopy()} disabled={isApplyingResearch} loading={isApplyingResearch} loadingLabel="Applying">
            <Sparkles size={18} /> {isApplyingResearch ? "Applying" : "Use researched copy"}
          </Button>
        )}
        {listingIsClosed ? (
          <>
            {relatedSale && (
              <Button variant="primary" type="button" onClick={() => onOpenOrder(relatedSale.id)}>
                <CreditCard size={18} /> View order
              </Button>
            )}
            {hasExternalURL && (
              <Button variant="secondary" type="button" onClick={openExternalListing}>
                <Share2 size={18} /> Open marketplace
              </Button>
            )}
          </>
        ) : itemIsSold ? (
          <>
            <Button
              variant="danger"
              type="button"
              onClick={() => {
                setEndPromptOpen(true);
              }}
              disabled={isEnding}
            >
              <X size={18} /> End listing
            </Button>
            {hasExternalURL && (
              <Button variant="secondary" type="button" onClick={openExternalListing}>
                <Share2 size={18} /> Open marketplace
              </Button>
            )}
          </>
        ) : listingIsLive ? (
          <>
            {canRecordSale ? (
              <Button variant="primary" type="button" onClick={onRecordSale}>
                <CreditCard size={18} /> Mark sold
              </Button>
            ) : (
              <div className="listing-action-guidance">
                <CreditCard size={18} />
                <span>
                  <strong>{liveSaleUnavailable.title}</strong>
                  <small>{liveSaleUnavailable.body}</small>
                </span>
              </div>
            )}
            {hasExternalURL ? (
              <Button variant="secondary" type="button" onClick={openExternalListing}>
                <Share2 size={18} /> Open marketplace
              </Button>
            ) : (
              <div className="listing-action-guidance">
                <Link2 size={18} />
                <span>
                  <strong>Add the marketplace URL when available</strong>
                  <small>Save the live listing URL so orders and listing details stay linked.</small>
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            {supportsDirectPublishing ? (
              <Button variant="primary" type="button" data-ebay-publication={listing.marketplace === "ebay" ? "true" : undefined} onClick={() => listing.marketplace === "ebay" ? onPublishEbay() : void publishListing()} disabled={listing.marketplace !== "ebay" && (!canPublish || isPublishing)} loading={isPublishing} loadingLabel="Publishing">
                <Share2 size={18} /> {listing.marketplace === "ebay" ? "Review publication" : isPublishing ? "Publishing" : listing.status === "failed" ? "Retry publishing" : `List on ${marketplaceLabels[listing.marketplace]}`}
              </Button>
            ) : guidedPublishURL ? (
              <Button variant="primary" type="button" onClick={openGuidedPublish}>
                <Share2 size={18} /> Continue on {marketplaceLabels[listing.marketplace]}
              </Button>
            ) : null}
            {account && isManualListingAccount(account) && !itemIsSold && listing.status !== "publishing" ? (
              <Button variant="secondary" type="button" onClick={() => setManualListingOpen(true)}><CheckCircle2 size={18} /> Mark as listed</Button>
            ) : canMarkLive && (
              <Button variant="secondary" type="button" onClick={() => void markListingLive()} loading={isMarkingLive} loadingLabel="Saving listing status">
                <CheckCircle2 size={18} /> Mark live
              </Button>
            )}
          </>
        )}
        <Button type="button" onClick={copyListingDescription}>
          <Copy size={18} aria-hidden="true" /> Copy description
        </Button>
        {canEndListing && (
          <Button
            variant="danger"
            type="button"
            onClick={() => {
              setEndPromptOpen(true);
            }}
            disabled={isEnding}
          >
            <X size={18} /> End
          </Button>
        )}
      </div>
      {manualListingOpen && <ManualListingDialog itemIDs={[listing.itemID]} initialAccountID={listing.marketplaceAccountID} onClose={() => setManualListingOpen(false)} />}
      {listing.manualRecordedAt && <p className="listing-manual-status">Recorded manually in Listwork. Update this record when the marketplace listing changes.</p>}
      {!listingIsClosed && !listingIsLive && !supportsDirectPublishing && (
        <Alert tone="info">
          <strong>Finish on {marketplaceLabels[listing.marketplace]}</strong>
          <p>{account && isManualListingAccount(account)
            ? "Complete the listing in your seller account, then choose Mark as listed. You can add its marketplace link now or later."
            : "Complete the listing in your seller account, then paste its live URL here and mark it live."}</p>
        </Alert>
      )}
      {endPromptOpen && (
        <InlineConfirm
          title="End this listing?"
          description={`This moves the ${marketplaceLabels[listing.marketplace]} record to Closed in Listwork. If it is still live on the marketplace, end it there too.`}
          actions={
            <>
              <Button variant="ghost" type="button" onClick={() => setEndPromptOpen(false)} disabled={isEnding}>
                Keep listing
              </Button>
              <Button variant="danger" type="button" onClick={() => void endListing()} disabled={isEnding} loading={isEnding} loadingLabel="Ending...">
                {isEnding ? "Ending..." : "End listing"}
              </Button>
            </>
          }
        />
      )}
      {actionError && <Alert tone="danger">{actionError}</Alert>}
      {listing.status === "failed" && (
        <div className="listing-recovery" role="status">
          <strong>{draftUpdatedAfterFailure ? "Draft changes saved" : "The last publish attempt failed"}</strong>
          <p>{supportsDirectPublishing ? "Review the draft, then retry publishing." : `Review the draft, then continue on ${marketplaceLabels[listing.marketplace]} to publish it.`}</p>
          {listing.validationWarnings.length > 0 && (
            <Disclosure label="Previous publishing issue">
              {listing.validationWarnings.map((warning) => <p key={warning}>{warning}</p>)}
            </Disclosure>
          )}
        </div>
      )}
      {supportsDirectPublishing && !hasPublishCategory && !listingIsClosed && !listingIsLive && (
        <Alert tone="warning">Choose an eBay category in Edit listing before listing on eBay.</Alert>
      )}
      {listing.marketplace === "etsy" && (!hasPublishCategory || !hasEtsyProvenance) && (
        <Alert tone="warning">Complete Etsy taxonomy, maker, date, and craft-supply details in Edit listing before listing on Etsy.</Alert>
      )}
      <section className="listing-copy-section" aria-labelledby={listingCopyHeadingID}>
        <SectionLabel id={listingCopyHeadingID}>Description</SectionLabel>
        <Field label="Description" className="ui-field-copy">
          <Textarea ref={descriptionTextareaRef} value={sellerDescription} readOnly rows={8} aria-label="Listing description" />
        </Field>
      </section>
      {listing.validationWarnings.length > 0 && listing.status !== "failed" && !listingIsClosed && !itemIsSold && (
        <Alert tone="warning">
          {listing.validationWarnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </Alert>
      )}
      {item && (
        <div className="linked-item">
          <div className="linked-item-copy">
            <strong>{item.title}</strong>
            <small>{item.sku}</small>
          </div>
          <div className="linked-item-actions">
            <Badge tone="info">{inventoryStatusLabels[item.status]}</Badge>
            <Button variant="secondary" size="compact" type="button" onClick={() => onOpenItem(item.id)}>
              <Package size={16} /> View item
            </Button>
          </div>
        </div>
      )}
      {item && (
        <div className="photo-management">
          <div className="photo-management-head">
            <div>
              <span>Photos</span>
              <strong>{itemPhotos.length} saved</strong>
            </div>
            <div className="photo-management-actions">
              {itemPhotos.length > 0 ? (
                <Button
                  variant="secondary"
                  size="compact"
                  type="button"
                  onClick={(event) => {
                    listingPhotoReturnFocusRef.current = event.currentTarget;
                    setPhotoReviewID(itemPhotos[0].id);
                  }}
                >
                  <Pencil size={16} /> Manage photos
                </Button>
              ) : null}
              <Button
                ref={listingPhotoActionRef}
                variant="secondary"
                size="compact"
                type="button"
                onClick={() => listingPhotoInputRef.current?.click()}
                disabled={isAddingPhotos || itemPhotos.length>= MAX_ITEM_PHOTOS}
                loading={isAddingPhotos} loadingLabel="Adding photos">
                <ImagePlus size={16} /> {isAddingPhotos ? "Adding photos" : "Add photos"}
              </Button>
            </div>
          </div>
          <FileInput
            ref={listingPhotoInputRef}
            className="sr-only"
            accept={PHOTO_FILE_ACCEPT}
            multiple
            aria-label={`Choose photos for ${item.title}`}
            onChange={(event) => void addListingPhotoFiles(event)}
          />
          {itemPhotos.length > 0 ? (
            <div className="saved-photo-grid">
              {itemPhotos.map((itemPhoto, index) => (
                <Button
                  variant="tool"
                  className="saved-photo-tile saved-photo-review-trigger"
                  type="button"
                  key={itemPhoto.id}
                  aria-label={`Manage product photo ${index + 1}${index === 0 ? ", cover photo" : ""}`}
                  onClick={(event) => {
                    listingPhotoReturnFocusRef.current = event.currentTarget;
                    setPhotoReviewID(itemPhoto.id);
                  }}
                >
                  <img src={itemPhoto.thumbnailURL || itemPhoto.url} alt={`Product photo ${index + 1}`} style={photoStyle(itemPhoto.treatment)} />
                  <span className="saved-photo-review-affordance" aria-hidden="true"><Pencil size={16} /></span>
                </Button>
              ))}
            </div>
          ) : null}
          <PhotoUploadStatus progress={photoProgress} />
          {photoError && <Alert tone="danger">{photoError}</Alert>}
          {photoNotice && <Alert tone={photoNotice.tone}>{photoNotice.message}</Alert>}
        </div>
      )}
      {editDraftOpen && <EditListingDraftDialog listing={listing} onSaved={() => setDraftUpdatedAfterFailure(listing.status === "failed")} onClose={() => setEditDraftOpen(false)} />}
      {item && photoReviewID && itemPhotos.length > 0 ? (
        <ItemPhotoReviewDialog
          item={item}
          photos={itemPhotos}
          initialPhotoID={photoReviewID}
          onAddPhotos={() => {
            setPhotoReviewID(null);
            window.setTimeout(() => listingPhotoInputRef.current?.click(), 0);
          }}
          onClose={() => setPhotoReviewID(null)}
          onReturnFocus={() =>
            window.requestAnimationFrame(() => {
              const reviewTrigger = listingPhotoReturnFocusRef.current;
              const target = reviewTrigger?.isConnected
                ? reviewTrigger
                : listingPhotoActionRef.current;
              target?.focus();
            })
          }
        />
      ) : null}
    </Panel>
  );
}

function EditListingDraftDialog({ listing, onClose, onSaved }: { listing: MarketplaceListing; onClose: () => void; onSaved?: () => void }) {
  const store = useReOS();
  const toast = useToast();
  const initialDescription = cleanSellerListingDescription(listing.description, listing.title);
  const [title, setTitle] = useState(listing.title);
  const [description, setDescription] = useState(initialDescription);
  const [price, setPrice] = useState(moneyInputValue(listing.price));
  const [categoryExternalID, setCategoryExternalID] = useState(listing.categoryExternalID ?? "");
  const [etsyWhoMade, setEtsyWhoMade] = useState(listing.etsyWhoMade ?? "");
  const [etsyWhenMade, setEtsyWhenMade] = useState(listing.etsyWhenMade ?? "");
  const [etsySupply, setEtsySupply] = useState(
    typeof listing.etsyIsSupply === "boolean" ? String(listing.etsyIsSupply) : ""
  );
  const [etsyTaxonomyQuery, setEtsyTaxonomyQuery] = useState("");
  const [etsyTaxonomyChoices, setEtsyTaxonomyChoices] = useState<Array<{ id: string; name: string; path: string }>>([]);
  const [etsyTaxonomyLoading, setEtsyTaxonomyLoading] = useState(false);
  const [etsyTaxonomyError, setEtsyTaxonomyError] = useState(false);
  const [etsyTaxonomyRetry, setEtsyTaxonomyRetry] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const hasChanges = title !== listing.title || description !== initialDescription || price !== moneyInputValue(listing.price) ||
    categoryExternalID !== (listing.categoryExternalID ?? "") ||
    etsyWhoMade !== (listing.etsyWhoMade ?? "") ||
    etsyWhenMade !== (listing.etsyWhenMade ?? "") ||
    etsySupply !== (typeof listing.etsyIsSupply === "boolean" ? String(listing.etsyIsSupply) : "");

  useEffect(() => {
    if (listing.marketplace !== "etsy" || etsyTaxonomyQuery.trim().length < 2) {
      setEtsyTaxonomyChoices([]);
      setEtsyTaxonomyError(false);
      return;
    }
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      setEtsyTaxonomyLoading(true);
      setEtsyTaxonomyError(false);
      void store.searchEtsyTaxonomy(etsyTaxonomyQuery)
        .then((choices) => { if (!cancelled) { setEtsyTaxonomyChoices(choices); setEtsyTaxonomyError(false); } })
        .catch(() => { if (!cancelled) { setEtsyTaxonomyChoices([]); setEtsyTaxonomyError(true); } })
        .finally(() => { if (!cancelled) setEtsyTaxonomyLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [etsyTaxonomyQuery, etsyTaxonomyRetry, listing.marketplace, store.searchEtsyTaxonomy]);

  function requestClose() {
    if (isSaving) {
      return;
    }
    if (hasChanges) {
      setClosePromptOpen(true);
      return;
    }
    onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const priceMinor = parseMoneyInput(price, listing.price.currency).amountMinor;
    if (!title.trim() || !description.trim()) {
      setError("Complete the listing title and description.");
      return;
    }
    if (priceMinor <= 0) {
      setError("Listing price must be greater than zero.");
      return;
    }
    if (listing.marketplace === "etsy" && !/^\d+$/.test(categoryExternalID.trim())) {
      setError(`Choose a ${listing.marketplace === "etsy" ? "Etsy taxonomy" : "eBay"} category.`);
      return;
    }
    if (listing.marketplace === "etsy" && (!etsyWhoMade || !etsyWhenMade || !etsySupply)) {
      setError("Complete who made the item, when it was made, and whether it is a craft supply.");
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      await store.updateListingDraft(listing.id, {
        title,
        description,
        priceMinor,
        ...(listing.marketplace === "etsy" ? {
          categoryExternalID,
          etsyWhoMade: etsyWhoMade as MarketplaceListing["etsyWhoMade"],
          etsyWhenMade,
          etsyIsSupply: etsySupply === "true"
        } : {})
      });
      onSaved?.();
      toast.notify({ title: "Listing draft updated", tone: "success" });
      onClose();
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to update this listing draft."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && requestClose()}>
      <DialogContent className="ui-dialog-content-sale" leadingClose showClose={false}>
        <DialogHeader>
          <Button variant="secondary" size="icon" type="button" onClick={requestClose} aria-label="Close edit listing" disabled={isSaving}><X size={20} /></Button>
          <DialogTitle>Edit {marketplaceLabels[listing.marketplace]} draft</DialogTitle>
          <DialogDescription>Changes to this draft do not alter the inventory item.</DialogDescription>
        </DialogHeader>
        <FormLayout onSubmit={(event) => void submit(event)}>
          <DialogBody className="ui-dialog-body-sale">
            {error && <Alert tone="danger">{error}</Alert>}
            <Field label="Listing title">
              <Input required value={title} onChange={(event) => setTitle(event.target.value)} />
            </Field>
            <Field label="Listing price">
              <Input required inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} />
            </Field>
            {listing.marketplace === "etsy" && (
              <PanelSection className="etsy-listing-requirements">
                <SectionLabel>Etsy eligibility and taxonomy</SectionLabel>
                <Alert tone="info">Etsy accepts handmade items, qualifying vintage items, and craft supplies. Choose the factual values for this item; Listwork will not infer marketplace eligibility.</Alert>
                <Field label="Etsy category" helper={categoryExternalID ? `Selected taxonomy ID ${categoryExternalID}. Search again to change it.` : "Search Etsy's current seller taxonomy and choose the closest factual category."}>
                  <Input
                    value={etsyTaxonomyQuery}
                    onChange={(event) => { setEtsyTaxonomyQuery(event.target.value); setEtsyTaxonomyError(false); }}
                    placeholder="Search categories, for example vintage copper pot"
                    autoComplete="off"
                  />
                </Field>
                {etsyTaxonomyQuery.trim().length >= 2 && (
                  <div className="etsy-taxonomy-results" role="region" aria-label="Etsy category results" aria-busy={etsyTaxonomyLoading}>
                    {etsyTaxonomyLoading ? <span>Searching Etsy taxonomy...</span> : etsyTaxonomyError ? (
                      <div className="etsy-taxonomy-error" role="alert">
                        <span>Etsy categories are temporarily unavailable.</span>
                        <Button variant="secondary" size="compact" type="button" onClick={() => setEtsyTaxonomyRetry((value) => value + 1)}>Retry</Button>
                      </div>
                    ) : etsyTaxonomyChoices.length ? etsyTaxonomyChoices.map((choice) => (
                      <Button
                        key={choice.id}
                        variant={categoryExternalID === choice.id ? "primary" : "ghost"}
                        size="compact"
                        type="button"
                        onClick={() => {
                          setCategoryExternalID(choice.id);
                          setEtsyTaxonomyQuery(choice.path);
                          setEtsyTaxonomyChoices([]);
                        }}
                      >
                        <span>{choice.path}</span><small>ID {choice.id}</small>
                      </Button>
                    )) : <span>No matching Etsy categories. Try a broader factual term.</span>}
                  </div>
                )}
                <div className="etsy-listing-fields">
                  <Field label="Who made it?">
                    <SelectControl value={etsyWhoMade} onValueChange={setEtsyWhoMade} options={[
                      { value: "", label: "Choose one" },
                      { value: "i_did", label: "I did" },
                      { value: "collective", label: "A member of my shop" },
                      { value: "someone_else", label: "Another person or company" }
                    ]} />
                  </Field>
                  <Field label="When was it made?">
                    <SelectControl value={etsyWhenMade} onValueChange={setEtsyWhenMade} options={[
                      { value: "", label: "Choose one" },
                      { value: "made_to_order", label: "Made to order" },
                      { value: "2020_2026", label: "2020-2026" },
                      { value: "2010_2019", label: "2010-2019" },
                      { value: "2007_2009", label: "2007-2009" },
                      { value: "before_2007", label: "Before 2007" },
                      { value: "2000_2006", label: "2000-2006" },
                      { value: "1990s", label: "1990s" },
                      { value: "1980s", label: "1980s" },
                      { value: "1970s", label: "1970s" },
                      { value: "1960s", label: "1960s" },
                      { value: "1950s", label: "1950s" },
                      { value: "1940s", label: "1940s" },
                      { value: "1930s", label: "1930s" },
                      { value: "1920s", label: "1920s" },
                      { value: "1910s", label: "1910s" },
                      { value: "1900s", label: "1900s" },
                      { value: "1800s", label: "1800s" },
                      { value: "1700s", label: "1700s" },
                      { value: "before_1700", label: "Before 1700" }
                    ]} />
                  </Field>
                  <Field label="Craft supply?">
                    <SelectControl value={etsySupply} onValueChange={setEtsySupply} options={[
                      { value: "", label: "Choose one" },
                      { value: "false", label: "No" },
                      { value: "true", label: "Yes" }
                    ]} />
                  </Field>
                </div>
              </PanelSection>
            )}
            <Field label="Description">
              <Textarea required rows={9} value={description} onChange={(event) => setDescription(event.target.value)} />
            </Field>
            {closePromptOpen && (
              <InlineConfirm
                title="Discard listing changes?"
                description="Your unsaved marketplace draft and price edits will be lost."
                actions={
                  <>
                    <Button variant="ghost" type="button" onClick={() => setClosePromptOpen(false)}>Keep editing</Button>
                    <Button variant="danger" type="button" onClick={onClose}>Discard changes</Button>
                  </>
                }
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" type="button" onClick={requestClose} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={isSaving} loading={isSaving} loadingLabel="Saving">{isSaving ? "Saving" : "Save listing"}</Button>
          </DialogFooter>
        </FormLayout>
      </DialogContent>
    </Dialog>
  );
}

function SaleRow({
  sale,
  selected,
  selectionMode = false,
  bulkSelected = false,
  rowRef,
  onClick
}: {
  sale: Sale;
  selected: boolean;
  selectionMode?: boolean;
  bulkSelected?: boolean;
  rowRef?: Ref<HTMLButtonElement>;
  onClick: () => void;
}) {
  const store = useReOS();
  const item = store.itemFor(sale.itemID);
  const photo = store.primaryPhoto(sale.itemID);
  const recognized = isRecognizedSale(sale);
  return (
    <DataRow ref={rowRef} selected={selected || bulkSelected} onClick={onClick}>
      {selectionMode ? (
        <span className={`workbench-selection-indicator ${bulkSelected ? "is-selected" : ""}`} aria-hidden="true">
          {bulkSelected ? <Check size={18} /> : null}
        </span>
      ) : <PhotoFrame photo={photo} />}
      <DataRowMain>
        <strong>{item?.title ?? "Sold item"}</strong>
        <DataRowBadges>
          <MarketplaceBadge marketplace={sale.marketplace} />
          <Badge tone={saleStatusTone(sale.status)}>{saleStatusLabels[sale.status]}</Badge>
        </DataRowBadges>
        <DataRowMeta>{selectionMode ? `${bulkSelected ? "Selected" : "Select"} · ${saleRowMetaText(sale)}` : saleRowMetaText(sale)}</DataRowMeta>
      </DataRowMain>
      <DataRowValue className="ui-data-row-value-workbench">
        <span className="sale-row-values">
          <span><small>Sale</small><strong>{formatMoney(sale.salePrice)}</strong></span>
          <span>
            <small>{recognized ? saleProfitIsProvisional(sale) ? "Provisional profit" : "Profit" : "Accounting"}</small>
            <strong className={recognized && !saleProfitIsProvisional(sale) ? (sale.netProfit.amountMinor >= 0 ? "profit" : "loss") : undefined}>
              {recognized ? formatMoney(sale.netProfit) : "Excluded"}
            </strong>
          </span>
        </span>
        <ChevronRight size={16} aria-hidden="true" />
      </DataRowValue>
    </DataRow>
  );
}

function SaleDetail({ sale, notice, onBack }: { sale: Sale; notice?: NotificationFocusMessage; onBack: () => void }) {
  const store = useReOS();
  const toast = useToast();
  const item = store.itemFor(sale.itemID);
  const photo = item ? store.primaryPhoto(item.id) : undefined;
  const recognized = isRecognizedSale(sale);
  const currentRole = workspaceRoleForStore(store);
  const canManageOrderExceptions = currentRole === "owner" || currentRole === "admin";
  const operationBusyRef = useRef(false);
  const trackingChoiceKey = `listwork:dispatch-choice:${store.persistenceMode}:${store.workspace?.id ?? "local"}:${store.user?.id ?? "local"}:${sale.id}`;
  function readTrackingChoice() {
    if (sale.trackingNumber) return false;
    if (sale.shippedAt) return true;
    try { return localStorage.getItem(trackingChoiceKey) === "without-tracking"; } catch { return false; }
  }
  const savedTrackingChoiceRef = useRef(readTrackingChoice());
  const [busyStatus, setBusyStatus] = useState<SaleStatus | null>(null);
  const [pendingStatus, setPendingStatus] = useState<SaleStatus | null>(null);
  const [buyerName, setBuyerName] = useState(sale.buyerName ?? "");
  const [buyerAddress, setBuyerAddress] = useState(sale.buyerAddress ?? "");
  const [buyerPostcode, setBuyerPostcode] = useState(sale.buyerPostcode ?? "");
  const [shipBy, setShipBy] = useState(dateInputValue(sale.shipBy));
  const [shippingCarrier, setShippingCarrier] = useState(sale.shippingCarrier ?? "");
  const [shippingService, setShippingService] = useState(sale.shippingService ?? "");
  const [trackingNumber, setTrackingNumber] = useState(sale.trackingNumber ?? "");
  const [shipWithoutTracking, setShipWithoutTracking] = useState(readTrackingChoice);
  const [fulfilmentNotes, setFulfilmentNotes] = useState(sale.fulfilmentNotes ?? "");
  const [assigneeUserID, setAssigneeUserID] = useState(sale.assigneeUserID ?? "unassigned");
  const buyerAddressRef = useRef<HTMLTextAreaElement>(null);
  const buyerPostcodeRef = useRef<HTMLInputElement>(null);
  const shipByRef = useRef<HTMLInputElement>(null);
  const carrierSelectRef = useRef<HTMLButtonElement>(null);
  const shippingServiceRef = useRef<HTMLInputElement>(null);
  const trackingNumberRef = useRef<HTMLInputElement>(null);
  const [isSavingFulfilment, setIsSavingFulfilment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  const dispatchErrorID = `dispatch-error-${sale.id}`;
  const nextStatus = nextSaleStatus(sale.status);
  const nextAction = salePrimaryFulfilmentAction(sale.status);
  const fulfilmentHeadingID = `order-fulfilment-${sale.id}`;
  const fulfilmentDetailsMissing = saleNeedsDeliveryDetails(sale);
  const fulfilmentDetailsDirty =
    buyerName.trim() !== (sale.buyerName ?? "") ||
    buyerAddress.trim() !== (sale.buyerAddress ?? "") ||
    buyerPostcode.trim() !== (sale.buyerPostcode ?? "") ||
    shipBy !== dateInputValue(sale.shipBy) ||
    shippingCarrier.trim() !== (sale.shippingCarrier ?? "") ||
    shippingService.trim() !== (sale.shippingService ?? "") ||
    trackingNumber.trim() !== (sale.trackingNumber ?? "") ||
    fulfilmentNotes.trim() !== (sale.fulfilmentNotes ?? "") ||
    shipWithoutTracking !== savedTrackingChoiceRef.current;
  const assignmentDirty = assigneeUserID !== (sale.assigneeUserID ?? "unassigned");

  useEffect(() => {
    setBuyerName(sale.buyerName ?? "");
    setBuyerAddress(sale.buyerAddress ?? "");
    setBuyerPostcode(sale.buyerPostcode ?? "");
    setShipBy(dateInputValue(sale.shipBy));
    setShippingCarrier(sale.shippingCarrier ?? "");
    setShippingService(sale.shippingService ?? "");
    setTrackingNumber(sale.trackingNumber ?? "");
    setFulfilmentNotes(sale.fulfilmentNotes ?? "");
    setAssigneeUserID(sale.assigneeUserID ?? "unassigned");
  }, [sale.id, sale.status, sale.buyerName, sale.buyerAddress, sale.buyerPostcode, sale.shipBy, sale.shippingCarrier, sale.shippingService, sale.trackingNumber, sale.shippedAt, sale.fulfilmentNotes, sale.assigneeUserID]);

  useEffect(() => {
    const choice = readTrackingChoice();
    savedTrackingChoiceRef.current = choice;
    setShipWithoutTracking(choice);
    setError(null);
    setErrorField(null);
  }, [trackingChoiceKey, sale.trackingNumber, sale.shippedAt]);

  function rememberTrackingChoice() {
    savedTrackingChoiceRef.current = shipWithoutTracking;
    try {
      if (shipWithoutTracking) localStorage.setItem(trackingChoiceKey, "without-tracking");
      else localStorage.removeItem(trackingChoiceKey);
    } catch {
      toast.notify({ title: "Tracking choice kept for this visit", description: "This device could not save the choice for a later visit.", tone: "warning" });
    }
  }

  function focusDispatchField(element: HTMLElement | null, field: string) {
    setErrorField(field);
    window.requestAnimationFrame(() => {
      element?.scrollIntoView({ block: "center", behavior: "auto" });
      element?.focus({ preventScroll: true });
    });
  }

  async function updateStatus(status: SaleStatus) {
    if (operationBusyRef.current) return;
    if (status === sale.status) {
      setPendingStatus(null);
      return;
    }
    try {
      operationBusyRef.current = true;
      setBusyStatus(status);
      setError(null);
      setErrorField(null);
      await store.updateSaleFulfilment(sale.id, fulfilmentUpdateForStatus(status));
      rememberTrackingChoice();
      setPendingStatus(null);
      toast.notify({ title: "Order status updated", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to update this order."));
    } finally {
      operationBusyRef.current = false;
      setBusyStatus(null);
    }
  }

  function requestStatus(status: SaleStatus) {
    if (operationBusyRef.current) return;
    setError(null);
    if (status === "cancelled" || status === "refunded") {
      if (!canManageOrderExceptions) {
        setError("A workspace owner or admin manages order cancellations and refunds.");
        return;
      }
      setPendingStatus(status);
      return;
    }
    void updateStatus(status);
  }

  function requestNextAction() {
    if (nextAction.status) {
      requestStatus(nextAction.status);
    }
  }

  function currentFulfilmentUpdate() {
    return {
      buyerName,
      buyerAddress,
      buyerPostcode,
      shipBy: shipBy ? new Date(`${shipBy}T12:00:00`).toISOString() : undefined,
      shippingCarrier,
      shippingService: shippingServiceRef.current?.value ?? shippingService,
      trackingNumber: trackingNumberRef.current?.value ?? trackingNumber,
      fulfilmentNotes,
      assigneeUserID: assigneeUserID === "unassigned" ? null : assigneeUserID
    };
  }

  async function saveFulfilmentDetails() {
    if (operationBusyRef.current) return;
    try {
      operationBusyRef.current = true;
      setIsSavingFulfilment(true);
      setError(null);
      setErrorField(null);
      await store.updateSaleFulfilment(sale.id, currentFulfilmentUpdate());
      rememberTrackingChoice();
      toast.notify({ title: "Order details saved", tone: "success" });
    } catch (caught) {
      setError(userSafeErrorMessage(caught, "Unable to save this order."));
    } finally {
      operationBusyRef.current = false;
      setIsSavingFulfilment(false);
    }
  }

  function fulfilmentUpdateForStatus(status: SaleStatus) {
    const update = currentFulfilmentUpdate();
    if ((status === "packed" || status === "shipped") && saleRequiresDeliveryDetails(sale)) {
      if (!update.shipBy) {
        focusDispatchField(shipByRef.current, "ship-by");
        throw new Error("Set a ship-by date before packing this order.");
      }
      if (!update.buyerAddress.trim()) {
        focusDispatchField(buyerAddressRef.current, "delivery address");
        throw new Error("Add the delivery address before packing this order.");
      }
      if (!update.buyerPostcode.trim()) {
        focusDispatchField(buyerPostcodeRef.current, "postcode");
        throw new Error("Add the delivery postcode before packing this order.");
      }
    }

    if (status !== "shipped") {
      return { ...update, status };
    }

    const carrier = update.shippingCarrier.trim();
    const tracking = update.trackingNumber.trim();
    if (!carrier) {
      focusDispatchField(carrierSelectRef.current, "carrier");
      throw new Error("Choose a carrier before marking the order shipped.");
    }
    if (!tracking && !shipWithoutTracking) {
      focusDispatchField(trackingNumberRef.current, "tracking");
      throw new Error("Add tracking, or choose no tracking for this dispatch.");
    }

    return {
      ...update,
      status,
      shippingCarrier: carrier,
      trackingNumber: tracking
    };
  }

  return (
    <Panel as="article" className="ui-panel-detail-card order-detail-card">
      <Button variant="ghost" className="mobile-back" type="button" onClick={onBack} aria-label="Back to orders">
        <ArrowLeft size={18} /> Orders
      </Button>
      <div className="order-identity">
      {photo && <PhotoFrame photo={photo} alt={item?.title ?? "Sold item"} />}
      <div className="detail-title">
        <div>
          <p className="ui-eyebrow">{marketplaceLabels[sale.marketplace]}</p>
          <h2>{item?.title ?? "Sold item"}</h2>
        </div>
        <div className="detail-title-value">
          <span>{recognized ? !saleProfitIsProvisional(sale) ? "Net profit" : "Provisional profit" : "Report impact"}</span>
          <strong className={recognized && !saleProfitIsProvisional(sale) ? (sale.netProfit.amountMinor >= 0 ? "profit" : "loss") : undefined}>
            {recognized ? formatMoney(sale.netProfit) : "Excluded"}
          </strong>
        </div>
      </div>
      </div>
      <DataRowBadges>
        <Badge tone={saleStatusTone(sale.status)}>{saleStatusLabels[sale.status]}</Badge>
      </DataRowBadges>
      {recognized && saleProfitIsProvisional(sale) && <Alert tone="warning">This order has incomplete costs. Profit is provisional; completing current inventory details does not revise this sale.</Alert>}
      {!recognized && <Alert tone="info">Cancelled and refunded orders are omitted from revenue and profit reports.</Alert>}
      {notice ? (
        <NotificationFocusCallout
          notice={notice}
          actionLabel={nextAction.buttonLabel}
          onAction={nextAction.status ? requestNextAction : undefined}
          busy={isSavingFulfilment || Boolean(busyStatus) || Boolean(pendingStatus)}
        />
      ) : (
        <section className="order-next-action" aria-label="Next order action">
          <div className="order-next-copy">
            <span>Next step</span>
            <strong>{nextAction.label}</strong>
            <small>{saleFulfilmentGuidance(sale)}</small>
          </div>
          {nextAction.status && (
            <div className="order-next-button">
              <Button variant="primary" size="compact" type="button" onClick={requestNextAction} disabled={isSavingFulfilment || Boolean(busyStatus) || Boolean(pendingStatus)} loading={Boolean(busyStatus === nextAction.status)} loadingLabel="Updating">
                {busyStatus === nextAction.status ? "Updating" : nextAction.buttonLabel}
              </Button>
            </div>
          )}
        </section>
      )}
      {error && <Alert id={dispatchErrorID} tone="danger">{error}</Alert>}
      {pendingStatus && (
        <InlineConfirm
          title={pendingStatus === "refunded" ? "Mark this order as refunded?" : "Cancel this order?"}
          description="This updates the fulfilment status only. Sale amounts, item history, and profit records stay visible."
          actions={
            <>
              <Button variant="ghost" type="button" onClick={() => setPendingStatus(null)} disabled={Boolean(busyStatus)}>
                Keep current status
              </Button>
              <Button variant="danger" type="button" onClick={() => void updateStatus(pendingStatus)} disabled={Boolean(busyStatus)}>
                {pendingStatus === "refunded" ? "Mark refunded" : "Cancel order"}
              </Button>
            </>
          }
        />
      )}
      {!sale.historyImport && <section className="order-fulfilment-section" aria-labelledby={fulfilmentHeadingID}>
        <div className="order-fulfilment-header">
          <h3 id={fulfilmentHeadingID}>Delivery details</h3>
          {!isSavingFulfilment && !fulfilmentDetailsDirty && fulfilmentDetailsMissing ? (
            <Badge tone="warning">Details needed</Badge>
          ) : null}
        </div>

        <fieldset className="order-fulfilment-form" disabled={isSavingFulfilment || Boolean(busyStatus)}>
          <legend className="sr-only">Dispatch and delivery details</legend>
          <Field error={errorField === "carrier" ? error : undefined} label="Carrier">
            <SelectControl
              ref={carrierSelectRef}
              aria-invalid={errorField === "carrier" || undefined} aria-describedby={errorField === "carrier" ? dispatchErrorID : undefined}
              value={shippingCarrier}
              onValueChange={setShippingCarrier}
              options={shippingCarrierOptions.map((option) => ({
                value: option.key,
                label: option.label
              }))}
            />
          </Field>
          <Field label="Service">
            <Input ref={shippingServiceRef} value={shippingService} onChange={(event) => setShippingService(event.target.value)} placeholder="Tracked 48, Standard, etc." />
          </Field>
          <Field error={errorField === "tracking" ? error : undefined} label="Tracking" className="ui-field-tracking">
            <Input
              value={trackingNumber}
              ref={trackingNumberRef}
              aria-invalid={errorField === "tracking" || undefined} aria-describedby={errorField === "tracking" ? dispatchErrorID : undefined}
              onChange={(event) => setTrackingNumber(event.target.value)}
              placeholder="Required unless no tracking"
              disabled={shipWithoutTracking}
            />
          </Field>
          <div className="tracking-option-row">
            <CheckboxField
              id={`tracking-option-${sale.id}`}
              checked={shipWithoutTracking}
              onChange={(event) => {
                setShipWithoutTracking(event.target.checked);
                if (event.target.checked) {
                  setTrackingNumber("");
                }
              }}
              label="No tracking for this dispatch"
            />
          </div>

          <Field label="Buyer">
            <Input value={buyerName} onChange={(event) => setBuyerName(event.target.value)} placeholder="Not recorded" />
          </Field>
          <Field error={errorField === "postcode" ? error : undefined} label="Postcode">
            <Input ref={buyerPostcodeRef} aria-invalid={errorField === "postcode" || undefined} aria-describedby={errorField === "postcode" ? dispatchErrorID : undefined} value={buyerPostcode} onChange={(event) => setBuyerPostcode(event.target.value)} placeholder="Not recorded" />
          </Field>
          <Field error={errorField === "ship-by" ? error : undefined} label="Ship by">
            <Input ref={shipByRef} aria-invalid={errorField === "ship-by" || undefined} aria-describedby={errorField === "ship-by" ? dispatchErrorID : undefined} type="date" value={shipBy} onChange={(event) => setShipBy(event.target.value)} />
          </Field>
          <Field label="Assigned to">
            <SelectControl
              value={assigneeUserID}
              onValueChange={setAssigneeUserID}
              options={[
                { value: "unassigned", label: "Unassigned" },
                ...store.workspaceMembers.map((member) => ({ value: member.userID, label: member.current ? `${member.name} (you)` : member.name }))
              ]}
            />
          </Field>
          <Field error={errorField === "delivery address" ? error : undefined} label="Delivery address" wide>
            <Textarea ref={buyerAddressRef} aria-invalid={errorField === "delivery address" || undefined} aria-describedby={errorField === "delivery address" ? dispatchErrorID : undefined} value={buyerAddress} onChange={(event) => setBuyerAddress(event.target.value)} placeholder="Not recorded" rows={3} />
          </Field>
          <Field label="Fulfilment notes" wide>
            <Textarea value={fulfilmentNotes} onChange={(event) => setFulfilmentNotes(event.target.value)} placeholder="Packing notes, collection details, or marketplace message" rows={3} />
          </Field>
        </fieldset>
        {(fulfilmentDetailsDirty || assignmentDirty || isSavingFulfilment) && (
          <div className="order-fulfilment-actions" aria-live="polite">
            <span>
              <strong>{isSavingFulfilment ? "Saving details" : "Unsaved changes"}</strong>
            </span>
            <Button variant="secondary" type="button" onClick={() => void saveFulfilmentDetails()} disabled={isSavingFulfilment || Boolean(busyStatus)} loading={isSavingFulfilment} loadingLabel="Saving">
              {isSavingFulfilment ? "Saving" : "Save delivery details"}
            </Button>
          </div>
        )}
        {canManageOrderExceptions ? (
          <Disclosure label="Cancel or refund" className="order-exceptions-disclosure">
          <div className="order-exception-actions" aria-label="Order exceptions">
            <span className="order-exception-copy">
              <strong>Order exceptions</strong>
              <small>Use when the sale is cancelled or money has been returned.</small>
            </span>
            <span className="order-exception-buttons">
              <Button variant="danger" type="button" onClick={() => requestStatus("cancelled")} disabled={isSavingFulfilment || Boolean(busyStatus) || Boolean(pendingStatus)}>
                Cancel order
              </Button>
              <Button variant="danger" type="button" onClick={() => requestStatus("refunded")} disabled={isSavingFulfilment || Boolean(busyStatus) || Boolean(pendingStatus)}>
                Mark refunded
              </Button>
            </span>
          </div>
          </Disclosure>
        ) : (
          <PanelNote>Workspace owners and admins manage order cancellations and refunds.</PanelNote>
        )}
      </section>}
      {sale.historyImport && <>
        <PanelNote>Imported from your eBay sales history · {sale.quantity ?? 1} {(sale.quantity ?? 1) === 1 ? "unit" : "units"} · {formatOrderDate(sale.soldAt)}</PanelNote>
        {canManageOrderExceptions && <Disclosure label="Order options"><div className="order-exception-buttons">
          <Button variant="danger-outline" onClick={() => requestStatus("cancelled")} disabled={Boolean(busyStatus) || Boolean(pendingStatus)}>Cancel order</Button>
          <Button variant="danger-outline" onClick={() => requestStatus("refunded")} disabled={Boolean(busyStatus) || Boolean(pendingStatus)}>Mark refunded</Button>
        </div></Disclosure>}
      </>}
      <Disclosure label="Order details and history" className="order-history-disclosure">
        <dl className="order-fulfilment-summary" aria-label="Delivery summary">
          <div>
            <dt>Sold</dt>
            <dd>{formatOrderDate(sale.soldAt)}</dd>
          </div>
          <div>
            <dt>Ship by</dt>
            <dd className={shipByStatusClass(sale)}>{sale.shipBy ? formatOrderDate(sale.shipBy) : "Not set"}</dd>
          </div>
          <div>
            <dt>Source</dt>
            <dd>{sale.historyImport ? "Imported eBay sale" : sale.listingID ? "Marketplace listing" : "Manual sale"}</dd>
          </div>
          <div>
            <dt>Tracking</dt>
            <dd>{sale.trackingNumber || (sale.shippedAt ? "No tracking" : shipWithoutTracking ? "No tracking selected" : "Not added")}</dd>
          </div>
          <div>
            <dt>Assigned</dt>
            <dd>{store.workspaceMembers.find((member) => member.userID === sale.assigneeUserID)?.name ?? "Unassigned"}</dd>
          </div>
        </dl>
        <FulfilmentTimeline sale={sale} />
      </Disclosure>
      {sale.financialSync && <PanelNote>{sale.financialSync.status === "authorization_required"
        ? <>Allow cost updates in <a href="/channels?setupChannel=ebay">your eBay connection</a> to bring in fees and postage labels.</>
        : sale.financialSync.status === "review_required" ? <>eBay has a refund, adjustment or shared order cost to review. <a href="https://www.ebay.co.uk/sh/fin" target="_blank" rel="noreferrer">View eBay finances</a></>
        : sale.financialSync.status === "unavailable" ? "eBay costs couldn’t be checked. We’ll try again automatically."
        : sale.financialSync.status === "pending" ? "Waiting for eBay costs. Profit is provisional."
        : !sale.shippingCost ? "eBay hasn’t supplied a postage cost. Add it if you bought postage elsewhere. Profit is provisional."
        : "eBay costs checked."}</PanelNote>}
      <Disclosure label="Profit breakdown" className="profit-breakdown">
        <div className="breakdown">
          <BreakdownRow label="Sale price" value={sale.salePrice} />
          <BreakdownRow label="Shipping charged" value={sale.shippingCharged} />
          {sale.refundAmount && <BreakdownRow label="Refunded" value={{ ...sale.refundAmount, amountMinor: -sale.refundAmount.amountMinor }} />}
          {purchaseCostIsKnown(sale) ? <BreakdownRow label="Purchase cost" value={{ ...sale.purchaseCost, amountMinor: -sale.purchaseCost.amountMinor }} /> : <p>Purchase cost: unknown</p>}
          {sale.marketplaceFeesStatus === "unknown" ? <p>Marketplace fees: unknown</p> : <BreakdownRow label="Marketplace fees" value={{ ...sale.marketplaceFees, amountMinor: -sale.marketplaceFees.amountMinor }} />}
          <BreakdownRow label="Processing fees" value={sale.paymentProcessingFees && { ...sale.paymentProcessingFees, amountMinor: -sale.paymentProcessingFees.amountMinor }} />
          {sale.marketplace === "ebay" && !sale.shippingCost ? <p>Shipping cost: unknown</p> : <BreakdownRow label="Shipping cost" value={sale.shippingCost && { ...sale.shippingCost, amountMinor: -sale.shippingCost.amountMinor }} />}
          {sale.marketplace === "ebay" && <SalePostageEditor sale={sale} />}
          <BreakdownRow label="Packaging" value={sale.packagingCost && { ...sale.packagingCost, amountMinor: -sale.packagingCost.amountMinor }} />
          <BreakdownRow label={recognized ? saleProfitIsProvisional(sale) ? "Provisional profit" : "Net profit" : "Net calculation (excluded)"} value={sale.netProfit} emphasized={recognized} />
        </div>
      </Disclosure>
    </Panel>
  );
}

function SalePostageEditor({ sale }: { sale: Sale }) {
  const store = useReOS();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  async function save() {
    if (busy.current) return;
    if (!/^\d+(?:\.\d{1,2})?$/.test(value.trim())) { setError("Enter a postage cost. Use 0 if there was no cost."); return; }
    busy.current = true;
    setSaving(true);
    setError(null);
    try {
      await store.updateSaleFulfilment(sale.id, { shippingCostMinor: parseMoneyInput(value, sale.salePrice.currency).amountMinor });
      setEditing(false);
      toast.notify({ title: "Postage cost saved", tone: "success" });
    } catch (caught) { setError(userSafeErrorMessage(caught, "Postage cost could not be saved. Try again.")); }
    finally { busy.current = false; setSaving(false); }
  }
  return editing ? <div>
    <MoneyField label="Postage paid" value={value} onChange={setValue} helper="Enter what you paid, including postage bought outside eBay." invalid={Boolean(error)} />
    {error && <Alert tone="danger">{error}</Alert>}
    <FormActions className="sale-postage-actions">
      <Button type="button" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
      <Button type="button" variant="primary" onClick={() => void save()} loading={saving} loadingLabel="Saving" disabled={saving}>Save postage cost</Button>
    </FormActions>
  </div> : <Button type="button" variant="ghost" size="compact" onClick={() => { setValue(sale.shippingCost ? moneyInputValue(sale.shippingCost) : ""); setError(null); setEditing(true); }}>{sale.shippingCost ? "Edit postage cost" : "Add postage cost"}</Button>;
}

function FulfilmentTimeline({ sale }: { sale: Sale }) {
  if (sale.status === "cancelled" || sale.status === "refunded") {
    const exceptionItem: TimelineItem = {
      id: sale.status,
      label: saleStatusLabels[sale.status],
      detail: formatOrderDate(sale.cancelledAt ?? sale.refundedAt ?? sale.updatedAt),
      state: "current"
    };
    return <Timeline items={[exceptionItem]} variant="exception" aria-label="Fulfilment timeline" />;
  }

  const steps: Array<{ status: SaleStatus; label: string; timestamp?: string; reached: boolean }> = [
    { status: "sold", label: "Sold", timestamp: sale.soldAt, reached: true },
    { status: "paid", label: "Paid", reached: ["paid", "packed", "shipped", "completed"].includes(sale.status) },
    { status: "packed", label: "Packed", timestamp: sale.packedAt, reached: ["packed", "shipped", "completed"].includes(sale.status) },
    { status: "shipped", label: "Shipped", timestamp: sale.shippedAt, reached: ["shipped", "completed"].includes(sale.status) },
    { status: "completed", label: "Complete", timestamp: sale.completedAt, reached: sale.status === "completed" }
  ];

  const timelineItems: TimelineItem[] = steps.map((step) => ({
    id: step.status,
    label: step.label,
    detail: step.timestamp ? formatOrderDate(step.timestamp) : step.reached ? "Recorded" : "Pending",
    state: step.status === sale.status ? "current" : step.reached ? "done" : "pending"
  }));

  return <Timeline items={timelineItems} aria-label="Fulfilment timeline" />;
}

function compareSalesForFulfilment(left: Sale, right: Sale) {
  return saleFulfilmentPriority(left.status) - saleFulfilmentPriority(right.status) || Date.parse(right.soldAt) - Date.parse(left.soldAt);
}

function saleFulfilmentPriority(status: SaleStatus) {
  switch (status) {
    case "sold":
      return 0;
    case "paid":
      return 1;
    case "packed":
      return 2;
    case "shipped":
      return 3;
    case "completed":
      return 4;
    case "refunded":
      return 5;
    case "cancelled":
      return 6;
  }
}

function saleIsOpen(sale: Sale) {
  return ["sold", "paid", "packed", "shipped"].includes(sale.status);
}

function saleNeedsPacking(sale: Sale) {
  return ["sold", "paid"].includes(sale.status);
}

function saleRequiresDeliveryDetails(sale: Sale) {
  return Boolean(sale.listingID) && !["cancelled", "refunded", "completed"].includes(sale.status);
}

function saleNeedsDeliveryDetails(sale: Sale) {
  if (!saleRequiresDeliveryDetails(sale)) {
    return false;
  }
  return !sale.shipBy || !sale.buyerAddress?.trim() || !sale.buyerPostcode?.trim();
}

function orderEmptyStateCopy(filter: OrderFilterKey) {
  if (filter === "open") {
    return { title: "No open orders", message: "New sales that need payment, packing, dispatch, or completion will appear here." };
  }
  if (filter === "completed") {
    return { title: "No completed orders", message: "Orders will appear here after fulfilment is complete." };
  }
  return { title: "No orders found", message: "Adjust the search, fulfilment, or marketplace filters to return to order work." };
}

function saleStatusTone(status: SaleStatus): ComponentProps<typeof Badge>["tone"] {
  switch (status) {
    case "sold":
    case "paid":
      return "warning";
    case "packed":
    case "shipped":
      return "info";
    case "completed":
      return "success";
    case "refunded":
    case "cancelled":
      return "danger";
  }
}

function nextSaleStatus(status: SaleStatus): SaleStatus | undefined {
  switch (status) {
    case "sold":
      return "paid";
    case "paid":
      return "packed";
    case "packed":
      return "shipped";
    case "shipped":
      return "completed";
    default:
      return undefined;
  }
}

function saleNextStatusButtonLabel(status: SaleStatus) {
  switch (status) {
    case "paid":
      return "Mark paid";
    case "packed":
      return "Mark packed";
    case "shipped":
      return "Mark shipped";
    case "completed":
      return "Complete order";
    default:
      return saleStatusLabels[status];
  }
}

function salePrimaryFulfilmentAction(status: SaleStatus): { label: string; buttonLabel?: string; status?: SaleStatus } {
  const nextStatus = nextSaleStatus(status);
  switch (status) {
    case "sold":
      return { label: "Confirm payment", buttonLabel: "Mark paid", status: nextStatus };
    case "paid":
      return { label: "Pack order", buttonLabel: "Mark packed", status: nextStatus };
    case "packed":
      return { label: "Dispatch order", buttonLabel: "Mark shipped", status: nextStatus };
    case "shipped":
      return { label: "Finish fulfilment", buttonLabel: "Complete order", status: nextStatus };
    case "completed":
      return { label: "Fulfilment complete" };
    case "refunded":
      return { label: "Refunded" };
    case "cancelled":
      return { label: "Cancelled" };
  }
}

function saleFulfilmentGuidance(sale: Sale) {
  switch (sale.status) {
    case "sold":
      return "Check payment, delivery address, and ship-by promise before packing.";
    case "paid":
      return sale.shipBy ? `Pack this order for dispatch by ${formatOrderDate(sale.shipBy)}.` : "Pack the item and set a ship-by date before dispatch.";
    case "packed":
      return sale.trackingNumber ? "Package is ready. Confirm dispatch when it leaves." : "Add carrier and tracking, or confirm no-tracking dispatch.";
    case "shipped":
      return sale.trackingNumber ? `Tracking ${sale.trackingNumber} is saved. Complete after delivery or buyer confirmation.` : "No-tracking dispatch recorded. Complete after buyer confirmation.";
    case "completed":
      return "Fulfilment is complete.";
    case "refunded":
      return "The sale is retained for history and profit review.";
    case "cancelled":
      return "The order is cancelled but the record stays visible.";
  }
}

function saleRowMetaText(sale: Sale) {
  const nextStatus = nextSaleStatus(sale.status);
  const date = new Date(sale.soldAt).toLocaleDateString("en-GB");
  if (sale.status === "packed" && sale.trackingNumber) {
    return `Ready to dispatch · ${sale.trackingNumber}`;
  }
  if (sale.status === "shipped") {
    return sale.trackingNumber ? `In transit · ${sale.trackingNumber}` : `In transit · ${date}`;
  }
  return nextStatus ? `Next: ${saleNextStatusButtonLabel(nextStatus)} · ${shipByMeta(sale) ?? date}` : `${saleStatusLabels[sale.status]} · ${date}`;
}

function shipByMeta(sale: Sale) {
  if (!sale.shipBy || sale.status === "completed" || sale.status === "cancelled" || sale.status === "refunded") {
    return undefined;
  }
  const days = Math.ceil((new Date(sale.shipBy).getTime() - Date.now()) / 86_400_000);
  if (days < 0) {
    return "ship overdue";
  }
  if (days === 0) {
    return "ship today";
  }
  if (days === 1) {
    return "ship tomorrow";
  }
  return `ship in ${days} days`;
}

function shipByStatusClass(sale: Sale) {
  const meta = shipByMeta(sale);
  return meta === "ship overdue" ? "danger-text" : meta === "ship today" ? "warning-text" : undefined;
}

function dateInputValue(value: string | undefined) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  return date.toISOString().slice(0, 10);
}

function defaultShipByDate() {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().slice(0, 10);
}

function marketplaceOptionsFor(values: Marketplace[]): Array<{ key: MarketplaceFilterKey; label: string }> {
  const present = new Set(values);
  return [
    { key: "all", label: "All marketplaces" },
    ...marketplaces
      .filter((marketplace) => present.has(marketplace))
      .map((marketplace) => ({
        key: marketplace,
        label: marketplaceLabels[marketplace]
      }))
  ];
}

function formatOrderDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric"
  }).format(new Date(value));
}

function FirstRunPanel({
  icon,
  title,
  message,
  actionLabel,
  onAction
}: {
  icon: ReactNode;
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <EmptyState
      emphasis="prominent"
      icon={icon}
      title={title}
      message={message}
      action={
        <Button type="button" variant="primary" onClick={onAction}>
          <Plus size={18} />
          {actionLabel}
        </Button>
      }
    />
  );
}

function PhotoFrame({ photo, previewURL, large = false, alt = "" }: { photo?: ItemPhoto; previewURL?: string; large?: boolean; alt?: string }) {
  const [failedURL, setFailedURL] = useState<string>();
  const url = previewURL || photo?.thumbnailURL || photo?.url;
  const hasImage = Boolean(url && url !== failedURL);
  return (
    <div className={`${large ? "photo-frame large" : "photo-frame"} ${hasImage ? "" : "missing"}`}>
      {hasImage ? (
        <img src={url} alt={alt} onError={() => setFailedURL(url)} style={photoStyle(previewURL ? undefined : photo?.treatment, large || previewURL || !photo ? "fit" : "crop")} />
      ) : large ? (
        <span>
          <ImagePlus size={24} />
          <strong>{photo || previewURL ? "Photo unavailable" : "Needs photos"}</strong>
        </span>
      ) : (
        <ImagePlus size={22} />
      )}
    </div>
  );
}

function ItemMiniRow({ item, onClick }: { item: InventoryItem; onClick?: () => void }) {
  const store = useReOS();
  const content = (
    <>
      <PhotoFrame photo={store.primaryPhoto(item.id)} />
      <DataRowMain>
        <strong>{item.title}</strong>
        <DataRowMeta>
          {item.sku} · Cost {purchaseCostIsKnown(item) ? formatMoney(item.purchaseCost) : "unknown"}
        </DataRowMeta>
      </DataRowMain>
      <Badge tone={inventoryStatusTone(item.status)}>{inventoryStatusLabels[item.status]}</Badge>
    </>
  );

  if (onClick) {
    return (
      <DataRow className="ui-data-row-compact" onClick={onClick}>
        {content}
      </DataRow>
    );
  }

  return (
    <DataRow as="div" interactive={false} className="ui-data-row-compact">
      {content}
    </DataRow>
  );
}

function MarketplaceBadge({ marketplace }: { marketplace: Marketplace }) {
  const markIncludesLabel = marketplaceMarkIncludesLabel(marketplace);

  return (
    <Badge
      tone="neutral"
      className={`ui-badge-marketplace ui-badge-marketplace-${marketplace}${markIncludesLabel ? " ui-badge-marketplace-wordmark" : ""}`}
    >
      <MarketplaceMark marketplace={marketplace} labelled={markIncludesLabel} />
      {!markIncludesLabel ? <span>{marketplaceLabels[marketplace]}</span> : null}
    </Badge>
  );
}

function listingStatusTone(status: ListingStatus): ComponentProps<typeof Badge>["tone"] {
  return status === "live"
    ? "success"
    : status === "sold"
      ? "strong"
      : status === "failed"
        ? "danger"
        : status === "ended" || status === "archived" || status === "delisted"
          ? "neutral"
          : status === "draft"
            ? "neutral"
            : "info";
}

function MoneyField({ label, value, onChange, required = false, inputRef, invalid = false, helper, placeholder = "0.00" }: { label: string; value: string; onChange: (value: string) => void; required?: boolean; inputRef?: Ref<HTMLInputElement>; invalid?: boolean; helper?: string; placeholder?: string }) {
  return (
    <Field label={label} helper={helper}>
      <Input ref={inputRef} required={required} aria-invalid={invalid || undefined} inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
    </Field>
  );
}

function recordSaleItemHelper(item: InventoryItem) {
  return `${item.sku} · ${inventoryStatusLabels[item.status]} · ${targetPriceLabel(item)}`;
}

function saleMoneyValidationError(values: Array<[label: string, amountMinor: number]>) {
  const invalid = values.find(([, amountMinor]) => amountMinor < 0);
  return invalid ? `${invalid[0]} cannot be negative.` : undefined;
}

function BreakdownRow({ label, value, emphasized = false }: { label: string; value?: { amountMinor: number; currency: string }; emphasized?: boolean }) {
  return (
    <div className={emphasized ? "breakdown-row emphasized" : "breakdown-row"}>
      <span>{label}</span>
      <strong className={(value?.amountMinor ?? 0) < 0 ? "loss" : ""}>{formatMoney(value ?? { amountMinor: 0, currency: "GBP" })}</strong>
    </div>
  );
}

interface FormState {
  title: string;
  categoryID: string;
  condition: ItemCondition | "";
  brand: string;
  size: string;
  color: string;
  material: string;
  sku: string;
  stockLocation: string;
  purchaseCost: string;
  acquiredDate?: string;
  targetPrice: string;
  minimumPrice: string;
  notes: string;
}

interface PhotoDraft {
  id: string;
  assetID?: string;
  url: string;
  thumbnailURL: string;
  width: number;
  height: number;
  type: PhotoType;
  treatment: PhotoTreatment;
  originalUrl: string;
  fileName?: string;
  mimeType?: string;
  byteSize?: number;
  variantOf?: string;
  label?: string;
  aiOperation?: AiOperation;
  backgroundStyle?: AiBackgroundStyle;
  convertedFromHeic?: boolean;
}

interface PhotoDraftBatchResult {
  drafts: PhotoDraft[];
  errors: string[];
  convertedHeicCount: number;
}

type CropMode = PhotoTreatment["crop"];

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropApplyResult {
  url: string;
  thumbnailURL: string;
  width: number;
  height: number;
  crop: CropMode;
}

function cropModeLabel(mode: CropMode) {
  switch (mode) {
    case "original":
      return "Original";
    case "square":
      return "Square";
    case "portrait":
      return "4:5";
  }
}

function cropRectFor(size: { width: number; height: number }, mode: CropMode, zoom: number, center = { x: 0.5, y: 0.5 }): CropRect {
  const imageAspect = Math.max(size.width, 1) / Math.max(size.height, 1);
  const targetAspect = mode === "portrait" ? 4 / 5 : mode === "square" ? 1 : imageAspect;
  let width = 1;
  let height = 1;
  if (imageAspect > targetAspect) {
    width = targetAspect / imageAspect;
  } else {
    height = imageAspect / targetAspect;
  }
  const safeZoom = Math.max(1, zoom);
  return clampCropRect({
    x: center.x - width / safeZoom / 2,
    y: center.y - height / safeZoom / 2,
    width: width / safeZoom,
    height: height / safeZoom
  });
}

function cropCenter(rect: CropRect) {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2
  };
}

function pointInRect(point: { x: number; y: number }, rect: CropRect) {
  return point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height;
}

function clampCropRect(rect: CropRect): CropRect {
  const width = clamp(rect.width, 0.05, 1);
  const height = clamp(rect.height, 0.05, 1);
  return {
    width,
    height,
    x: clamp(rect.x, 0, 1 - width),
    y: clamp(rect.y, 0, 1 - height)
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

async function renderCroppedPhoto(photo: PhotoDraft, rect: CropRect, mode: CropMode): Promise<CropApplyResult> {
  const image = await loadImageElement(photo.url);
  const sourceX = Math.round(rect.x * image.naturalWidth);
  const sourceY = Math.round(rect.y * image.naturalHeight);
  const sourceWidth = Math.max(1, Math.round(rect.width * image.naturalWidth));
  const sourceHeight = Math.max(1, Math.round(rect.height * image.naturalHeight));
  const scale = Math.min(1, MAX_CROP_EXPORT_EDGE / Math.max(sourceWidth, sourceHeight));
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale));
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Cropping is unavailable on this device.");
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.filter = `brightness(${photo.treatment.brightness}%) contrast(${photo.treatment.contrast}%)`;
  context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);

  return {
    url: canvas.toDataURL("image/jpeg", 0.92),
    thumbnailURL: thumbnailForCanvas(canvas),
    width: targetWidth,
    height: targetHeight,
    crop: mode
  };
}

async function normalizePhotoRotation(photo: PhotoDraft): Promise<PhotoDraft> {
  const rotation = ((photo.treatment.rotation % 360) + 360) % 360;
  if (rotation === 0) {
    return photo;
  }

  const image = await loadImageElement(photo.url);
  const radians = rotation * Math.PI / 180;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(
    1,
    Math.round(Math.abs(image.naturalWidth * Math.cos(radians)) + Math.abs(image.naturalHeight * Math.sin(radians)))
  );
  canvas.height = Math.max(
    1,
    Math.round(Math.abs(image.naturalWidth * Math.sin(radians)) + Math.abs(image.naturalHeight * Math.cos(radians)))
  );
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Photo rotation is unavailable on this device.");
  }

  const sourceMimeType = mimeTypeFromDataURL(photo.url) ?? photo.mimeType;
  const outputMimeType = sourceMimeType === "image/png" ? "image/png" : "image/jpeg";
  if (outputMimeType === "image/jpeg") {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(radians);
  context.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

  return {
    ...photo,
    assetID: undefined,
    url: canvas.toDataURL(outputMimeType, outputMimeType === "image/jpeg" ? 0.92 : undefined),
    thumbnailURL: thumbnailForCanvas(canvas),
    width: canvas.width,
    height: canvas.height,
    treatment: {
      ...photo.treatment,
      rotation: 0
    },
    byteSize: undefined,
    mimeType: outputMimeType
  };
}

function photoTreatmentNeedsMaterialization(treatment: PhotoTreatment) {
  return (
    treatment.crop !== "original" ||
    treatment.brightness !== 100 ||
    treatment.contrast !== 100 ||
    treatment.rotation % 360 !== 0
  );
}

async function materializePhotoDraft(photo: PhotoDraft): Promise<PhotoDraft> {
  if (!photoTreatmentNeedsMaterialization(photo.treatment)) {
    return photo;
  }

  const rotationNormalizedPhoto = await normalizePhotoRotation(photo);
  const image = await loadImageElement(rotationNormalizedPhoto.url);
  const cropRect = cropRectFor(
    { width: image.naturalWidth, height: image.naturalHeight },
    rotationNormalizedPhoto.treatment.crop,
    1
  );
  const sourceX = Math.round(cropRect.x * image.naturalWidth);
  const sourceY = Math.round(cropRect.y * image.naturalHeight);
  const sourceWidth = Math.max(1, Math.round(cropRect.width * image.naturalWidth));
  const sourceHeight = Math.max(1, Math.round(cropRect.height * image.naturalHeight));
  const scale = Math.min(1, MAX_CROP_EXPORT_EDGE / Math.max(sourceWidth, sourceHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(sourceWidth * scale));
  canvas.height = Math.max(1, Math.round(sourceHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Photo adjustments are unavailable on this device.");
  }

  const sourceMimeType = mimeTypeFromDataURL(rotationNormalizedPhoto.url) ?? rotationNormalizedPhoto.mimeType;
  const outputMimeType = sourceMimeType === "image/png" ? "image/png" : "image/jpeg";
  if (outputMimeType === "image/jpeg") {
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.filter = `brightness(${rotationNormalizedPhoto.treatment.brightness}%) contrast(${rotationNormalizedPhoto.treatment.contrast}%)`;
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return {
    ...photo,
    assetID: undefined,
    url: canvas.toDataURL(outputMimeType, outputMimeType === "image/jpeg" ? 0.92 : undefined),
    thumbnailURL: thumbnailForCanvas(canvas),
    width: canvas.width,
    height: canvas.height,
    type: "edited",
    treatment: {
      crop: "original",
      brightness: 100,
      contrast: 100,
      rotation: 0
    },
    originalUrl: photo.originalUrl || photo.url,
    fileName: derivedPhotoFileName(photo.fileName, "edited"),
    mimeType: outputMimeType,
    byteSize: undefined,
    variantOf: photo.variantOf ?? photo.id,
    label: "Edited"
  };
}

function thumbnailForCanvas(source: HTMLCanvasElement) {
  const ratio = Math.min(1, PHOTO_THUMB_EDGE / Math.max(source.width, source.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.width * ratio));
  canvas.height = Math.max(1, Math.round(source.height * ratio));
  const context = canvas.getContext("2d");
  if (!context) {
    return source.toDataURL("image/jpeg", 0.82);
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

function thumbnailForImageElement(image: HTMLImageElement) {
  const ratio = Math.min(1, PHOTO_THUMB_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
  const context = canvas.getContext("2d");
  if (!context) {
    return image.src;
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load this photo for editing."));
    image.crossOrigin = "anonymous";
    image.src = src;
  });
}

function photoHasRecoverableEdits(photo: PhotoDraft) {
  return (
    photo.url !== photo.originalUrl ||
    photo.type !== "original" ||
    photo.treatment.crop !== "original" ||
    photo.treatment.brightness !== 100 ||
    photo.treatment.contrast !== 100 ||
    photo.treatment.rotation !== 0
  );
}

function buildInventoryInput(form: FormState, generatedSKU: string): InventoryItemInput {
  if (!form.condition) throw new Error("Choose the item’s condition.");
  return {
    title: form.title,
    categoryID: form.categoryID.trim() || "Uncategorised",
    condition: form.condition,
    brand: form.brand,
    size: form.size,
    color: form.color,
    material: form.material,
    sku: form.sku.trim() || generatedSKU,
    stockLocation: form.stockLocation,
    purchaseCostMinor: form.purchaseCost.trim() ? parseCatalogueCost(form.purchaseCost) : 0,
    purchaseCostStatus: form.purchaseCost.trim() ? "known" : "unknown",
    acquiredAt: validateAcquiredDate(form.acquiredDate || null)?.slice(0, 10),
    targetPriceMinor: form.targetPrice.trim() ? parseMoneyInput(form.targetPrice).amountMinor : 0,
    minimumPriceMinor: form.minimumPrice.trim() ? parseMoneyInput(form.minimumPrice).amountMinor : undefined,
    notes: form.notes,
    quantity: 1
  };
}

function toPhotoInput(photo: PhotoDraft): PhotoInput {
  return {
    assetID: photo.assetID,
    url: photo.url,
    thumbnailURL: photo.thumbnailURL,
    width: photo.width,
    height: photo.height,
    type: photo.type,
    treatment: photo.treatment,
    fileName: photo.fileName,
    mimeType: photo.mimeType,
    byteSize: photo.byteSize
  };
}

function photoDraftFromItemPhoto(photo: ItemPhoto): PhotoDraft {
  return {
    id: photo.id,
    assetID: photo.assetID,
    url: photo.url,
    thumbnailURL: photo.thumbnailURL || photo.url,
    width: photo.width,
    height: photo.height,
    type: photo.type,
    treatment: photo.treatment ?? {
      crop: "original",
      brightness: 100,
      contrast: 100,
      rotation: 0
    },
    originalUrl: photo.url,
    label: researchPhotoTypeLabels[photo.type]
  };
}

function croppedPhotoDraft(photo: PhotoDraft, result: CropApplyResult): PhotoDraft {
  return {
    ...photo,
    assetID: undefined,
    url: result.url,
    thumbnailURL: result.thumbnailURL,
    width: result.width,
    height: result.height,
    type: "cropped",
    label: "Cropped",
    originalUrl: photo.originalUrl ?? photo.url,
    fileName: derivedPhotoFileName(photo.fileName, "crop"),
    mimeType: "image/jpeg",
    byteSize: undefined,
    treatment: {
      crop: "original",
      brightness: 100,
      contrast: 100,
      rotation: 0
    }
  };
}

function aiPhotoInput(photo: PhotoDraft): AiPhotoInput {
  return {
    url: photo.url,
    originalUrl: photo.originalUrl,
    width: photo.width,
    height: photo.height,
    type: photo.type,
    treatment: photo.treatment
  };
}

function draftFromAiResult(result: AiPhotoOutput, source: PhotoDraft, label: string): PhotoDraft {
  return {
    id: `${source.id}_${result.operation}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    assetID: undefined,
    url: result.url,
    thumbnailURL: result.thumbnailURL,
    width: result.width,
    height: result.height,
    type: result.type,
    treatment: {
      crop: "original",
      brightness: 100,
      contrast: 100,
      rotation: 0
    },
    originalUrl: source.originalUrl ?? source.url,
    fileName: derivedPhotoFileName(source.fileName, result.operation),
    mimeType: mimeTypeFromDataURL(result.url) ?? source.mimeType,
    variantOf: source.variantOf ?? source.id,
    label,
    aiOperation: result.operation,
    backgroundStyle: result.backgroundStyle
  };
}

function derivedPhotoFileName(fileName: string | undefined, suffix: string) {
  const safeSuffix = suffix.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "edited";
  if (!fileName) {
    return `photo-${safeSuffix}.jpg`;
  }
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex <= 0) {
    return `${fileName}-${safeSuffix}.jpg`;
  }
  return `${fileName.slice(0, dotIndex)}-${safeSuffix}${fileName.slice(dotIndex)}`;
}

function mimeTypeFromDataURL(url: string) {
  const match = /^data:([^;,]+)[;,]/.exec(url);
  return match?.[1];
}

async function readPhotoDraft(file: File, onStage?: (stage: PhotoPreparationStage) => void): Promise<PhotoDraft> {
  const prepared = await preparePhotoFile(file, { onStage });
  return {
    id: `${file.name}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    url: prepared.dataURL,
    thumbnailURL: prepared.dataURL,
    width: prepared.width,
    height: prepared.height,
    type: "original",
    originalUrl: prepared.dataURL,
    fileName: prepared.fileName,
    mimeType: prepared.mimeType,
    byteSize: prepared.byteSize,
    label: "Original",
    convertedFromHeic: prepared.convertedFromHeic,
    treatment: {
      crop: "original",
      brightness: 100,
      contrast: 100,
      rotation: 0
    }
  };
}

async function readPhotoDraftBatch(
  files: File[],
  onProgress?: PhotoProgressCallback
): Promise<PhotoDraftBatchResult> {
  const drafts: PhotoDraft[] = [];
  const errors: string[] = [];
  let convertedHeicCount = 0;
  const progressPhotos = files.map((file) => ({ fileName: file.name, stage: "waiting" }));
  onProgress?.({ phase: "preparing", photos: progressPhotos });

  for (const [index, file] of files.entries()) {
    try {
      const draft = await readPhotoDraft(file, (stage) => {
        progressPhotos[index] = { fileName: file.name, stage };
        onProgress?.({ phase: "preparing", photos: [...progressPhotos] });
      });
      progressPhotos[index] = { fileName: file.name, stage: "ready" };
      drafts.push(draft);
      if (draft.convertedFromHeic) {
        convertedHeicCount += 1;
      }
    } catch (caught) {
      progressPhotos[index] = { fileName: file.name, stage: "failed" };
      errors.push(photoUploadErrorMessage(caught, `${file.name} couldn't be added.`));
    }
  }

  return { drafts, errors, convertedHeicCount };
}

function photoDraftReadErrorMessage(errors: string[]) {
  if (errors.length === 1) {
    return errors[0];
  }
  return `${errors.length} photos couldn't be added. ${errors.join(" ")}`;
}

function photoDraftReadNotice(convertedHeicCount: number, skippedCount: number, addedCount: number) {
  const messages: string[] = [];
  if (convertedHeicCount > 0) {
    messages.push(convertedHeicCount === 1 ? "HEIC photo is ready to use." : `${convertedHeicCount} HEIC photos are ready to use.`);
  }
  if (skippedCount > 0) {
    messages.push(`Only ${addedCount} ${addedCount === 1 ? "photo was" : "photos were"} added because each item can have up to ${MAX_ITEM_PHOTOS}.`);
  }
  if (messages.length === 0) {
    return null;
  }
  return {
    tone: skippedCount > 0 ? "warning" as const : "success" as const,
    message: messages.join(" ")
  };
}

function photoStyle(treatment: PhotoTreatment | undefined, mode: "crop" | "fit" = "crop"): CSSProperties {
  if (!treatment) {
    return {};
  }
  return {
    filter: `brightness(${treatment.brightness}%) contrast(${treatment.contrast}%)`,
    transform: `rotate(${treatment.rotation}deg)`,
    aspectRatio: mode === "crop"
      ? treatment.crop === "portrait"
        ? "4 / 5"
        : treatment.crop === "square"
          ? "1 / 1"
          : undefined
      : undefined
  };
}

function researchEvidencePhotoStyle(treatment: PhotoTreatment | undefined): CSSProperties {
  if (!treatment) {
    return {};
  }
  return {
    filter: `brightness(${treatment.brightness}%) contrast(${treatment.contrast}%)`,
    transform: `rotate(${treatment.rotation}deg)`
  };
}

function downloadJsonFile(fileName: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function importSummaryText(
  imported: { categories: number; inventoryItems: number; marketplaceListings: number; sales: number },
  skipped: { itemPhotos: number }
) {
  const added = [
    countLabel(imported.inventoryItems, "item"),
    countLabel(imported.marketplaceListings, "listing"),
    countLabel(imported.sales, "sale"),
    countLabel(imported.categories, "supporting catalogue record")
  ].filter(Boolean);
  const summary = added.length > 0 ? `Imported ${added.join(", ")}.` : "No catalogue records were found in that backup.";
  return skipped.itemPhotos > 0
    ? `${summary} Product photos from the local backup need to be uploaded again.`
    : summary;
}

function importedRecordCount(imported: { categories: number; inventoryItems: number; marketplaceListings: number; sales: number }) {
  return imported.categories + imported.inventoryItems + imported.marketplaceListings + imported.sales;
}

function countLabel(count: number, label: string) {
  return count > 0 ? `${count} ${label}${count === 1 ? "" : "s"}` : "";
}

function countWithFallback(count: number, label: string) {
  return `${count} ${label}${count === 1 ? "" : "s"}`;
}

function slugForFile(value: string) {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "workspace"
  );
}

export default App;
